import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FileText, Users, Calculator, CheckCircle2, Clock, 
    AlertTriangle, RotateCcw, UserPlus, Save, Trash2, 
    RefreshCw, ArrowLeft, Eye, EyeOff, BarChart3,
    UserCheck, UserX, Replace, Search, Filter, Plus, X,
    Printer, Download, Camera, Info, Edit, Settings
} from 'lucide-react';

import config from '../config';
import usePermissions from './usePermissions';

const TRAVEL_DURATIONS = ['3 أشهر', '4 أشهر', '6 أشهر', 'عقد', 'تعقيب'];

// حساب تاريخ الميلاد من العمر (بالنسبة للتاريخ الحالي)
const ageToMinBirthDate = (maxAge) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - maxAge);
    return d.toISOString().split('T')[0];
};
const ageToMaxBirthDate = (minAge) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - minAge);
    return d.toISOString().split('T')[0];
};

const DEFAULT_SELECTION_RULES = {
    '3 أشهر':   { minBirthDate: ageToMinBirthDate(47), maxBirthDate: ageToMaxBirthDate(25), minPassportMonths: 6 },
    '4 أشهر':   { minBirthDate: ageToMinBirthDate(48), maxBirthDate: ageToMaxBirthDate(24), minPassportMonths: 6 },
    '6 أشهر':   { minBirthDate: ageToMinBirthDate(50), maxBirthDate: ageToMaxBirthDate(23), minPassportMonths: 6 },
    'عقد':      { minBirthDate: ageToMinBirthDate(55), maxBirthDate: ageToMaxBirthDate(21), minPassportMonths: 18 },
    'تعقيب':    { minBirthDate: ageToMinBirthDate(55), maxBirthDate: ageToMaxBirthDate(21), minPassportMonths: 6 }
};

const NetRegistration = () => {
    const navigate = useNavigate();
    
    // 🔐 صلاحيات المستخدم
    const { hasPermission } = usePermissions();
    const canViewBalanceBtn        = hasPermission('الاختيار والتجهيز - الرصيد', 'عرض زر فحص رصيد العميل');
    const canViewCurrentBalance    = hasPermission('الاختيار والتجهيز - الرصيد', 'عرض الرصيد الحالي');
    const canViewTotalDeposits     = hasPermission('الاختيار والتجهيز - الرصيد', 'عرض إجمالي الإيداعات');
    const canViewTotalWithdrawals  = hasPermission('الاختيار والتجهيز - الرصيد', 'عرض إجمالي السحوبات');
    const canViewBalanceReportBtn  = hasPermission('الاختيار والتجهيز - الرصيد', 'عرض زر كشف الأرصدة الشامل');
    const canViewTotalBalances     = hasPermission('الاختيار والتجهيز - الرصيد', 'عرض إجمالي الأرصدة في الكشف الشامل');
    const canViewBalanceInReport   = hasPermission('الاختيار والتجهيز - الرصيد', 'عرض الرصيد في الكشف الشامل');

    // 🎯 State Management
    const [companies, setCompanies] = useState([]);
    const [delegates, setDelegates] = useState([]);
    const [professions, setProfessions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [excludedPassports, setExcludedPassports] = useState([]);
    const [delegateHistory, setDelegateHistory] = useState([]); // تاريخ المناديب



    const [selectedListForSending, setSelectedListForSending] = useState(null);
const [delegatesInSelectedList, setDelegatesInSelectedList] = useState([]);
const [loadingDelegates, setLoadingDelegates] = useState(false);


    // 📊 Selection State
const [selectionSettings, setSelectionSettings] = useState({
    companyId: '',
    professionId: '',
    travelDuration: '', // ✅ مدة السفر للاختيار الذكي
    requiredCount: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
});


// ⚙️ معايير الاختيار حسب مدة السفر (قِيَم افتراضية - محسوبة من DEFAULT_SELECTION_RULES الخارجي)

// الحالة
const [selectionRules, setSelectionRules] = useState(DEFAULT_SELECTION_RULES);
const [showSelectionRulesModal, setShowSelectionRulesModal] = useState(false);
const [isSavingSelectionRules, setIsSavingSelectionRules] = useState(false);
const [allowProcessedManualSelection, setAllowProcessedManualSelection] = useState(() => {
    try {
        return localStorage.getItem('allowProcessedManualSelection') === 'true';
    } catch {
        return false;
    }
});

// تحميل المعايير من قاعدة البيانات عند فتح الصفحة
useEffect(() => {
    const loadSelectionRules = async () => {
        try {
            const res = await fetch(`${config.API_BASE_URL}/api/net-registrations/selection-rules`);
            const data = await res.json();

            if (res.ok && data.success && data.data) {
                // ندمج البيانات اللي جاية من السيرفر مع الافتراضي
                setSelectionRules(prev => ({
                    ...prev,
                    ...data.data
                }));
            }
        } catch (error) {
            console.error('خطأ في تحميل معايير الاختيار:', error);
            // لو حصل خطأ هنكمل بالقيم الافتراضية
        }
    };

    loadSelectionRules();
}, []);

// حفظ المعايير في قاعدة البيانات
const saveSelectionRules = async (rules, options = {}) => {
    setSelectionRules(rules);
    setIsSavingSelectionRules(true);
    try {
        const res = await fetch(`${config.API_BASE_URL}/api/net-registrations/selection-rules`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rules })
        });

        const data = await res.json();
        if (!res.ok || !data.success) {
            throw new Error(data.error || 'فشل حفظ المعايير');
        }

        if (Object.prototype.hasOwnProperty.call(options, 'allowProcessedManualSelection')) {
            const allowEmergency = !!options.allowProcessedManualSelection;
            setAllowProcessedManualSelection(allowEmergency);
            try {
                localStorage.setItem('allowProcessedManualSelection', allowEmergency ? 'true' : 'false');
            } catch {}
        }

        showAlert('✅ تم حفظ معايير الاختيار في قاعدة البيانات', 'success');
    } catch (error) {
        console.error('خطأ في حفظ معايير الاختيار:', error);
        showAlert('⚠️ تعذر حفظ معايير الاختيار في قاعدة البيانات', 'error');
    } finally {
        setIsSavingSelectionRules(false);
    }
};


    
    // 📋 Lists Management
    const [currentList, setCurrentList] = useState(null);
    const [savedLists, setSavedLists] = useState([]);
    const [listViewSettings, setListViewSettings] = useState({
    showArchived: false,
    itemsPerPage: 12,
    currentPage: 1,
    sortBy: 'date', // date, name, company
    sortOrder: 'desc', // asc, desc
    filterBy: 'active' // active, archived, all
});

const [archivedLists, setArchivedLists] = useState(new Set());
    
    // Modal states
    const [showReplacementModal, setShowReplacementModal] = useState(false);
    const [selectedPassportForReplacement, setSelectedPassportForReplacement] = useState(null);
    const [availableReplacements, setAvailableReplacements] = useState([]);
    
    // 👥 Passports Management  
    const [selectedPassports, setSelectedPassports] = useState([]);
    const [distributionPreview, setDistributionPreview] = useState({});
    const [showPreview, setShowPreview] = useState(false);
    
    // 🔍 Search & Filter
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    
const [showPaymentPrintModal, setShowPaymentPrintModal] = useState(false);

// 💰 حالة فحص رصيد العميل
const [balanceCheckModal, setBalanceCheckModal] = useState({ isOpen: false, passport: null, balanceData: null, isLoading: false });
const [minBalanceAmount, setMinBalanceAmount] = useState(() => {
    try { return parseFloat(localStorage.getItem('minBalanceAmount') || '1000'); } catch { return 1000; }
});
const [showMinBalanceSettingModal, setShowMinBalanceSettingModal] = useState(false);

// 📊 حالة كشف الرصيد الشامل
const [balanceReportModal, setBalanceReportModal] = useState({ isOpen: false });
const [balanceReportData, setBalanceReportData] = useState([]); // [{passport, balance, status}]
const [balanceReportLoading, setBalanceReportLoading] = useState(false);
const [balanceReportFilter, setBalanceReportFilter] = useState('all'); // all | positive | negative
const [balanceReportProgress, setBalanceReportProgress] = useState({ current: 0, total: 0 });

    const [appointmentSettings, setAppointmentSettings] = useState({
        date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // غداً
        time: '09:00',
        notes: '',
        type: 'medical'
    });
    
    // 🔍 فلترة العملاء
    const [clientFilter, setClientFilter] = useState('all'); // all, confirmed, pending
    
    // 📱 نافذة معاينة الصورة
    const [showImagePreview, setShowImagePreview] = useState(false);
    const [previewImageData, setPreviewImageData] = useState(null);
// ابحث عن const [printSettings, setPrintSettings] = useState وحدثها
// ✅ بدّل التهيئة الحالية بهذا الإصدار المحدث
const [printSettings, setPrintSettings] = useState({
    showTravelDate: false,
    showDelegate: true,
    showPhone: true,
    showCompany: true,
    showProfession: true,
    showPassportID: false,

    // 👇 جديدة لتقرير الدفع فقط
    showPaymentDelegate: true, // إظهار/إخفاء عمود المندوب
    showPaymentAmount: true,   // إظهار/إخفاء عمود المبلغ
    showPaymentNotes: true,    // إظهار/إخفاء عمود ملاحظات الدفع
    showPaymentTotals: true    // إظهار/إخفاء إجمالي المبالغ في الملخص
});

    
    const [showPrintSettingsModal, setShowPrintSettingsModal] = useState(false);
    
    const [showEditListModal, setShowEditListModal] = useState(false);
const [editingList, setEditingList] = useState(null);
const [editListForm, setEditListForm] = useState({
    listName: '',
    requiredCount: '',
    notes: ''
});

// 🧾 بناء اسم القائمة من حقول منفصلة
const [showListNameBuilderModal, setShowListNameBuilderModal] = useState(false);
const [listNameParts, setListNameParts] = useState({
    companyName: '',
    visaType: '',
    visaNumber: '',
    unifiedNumber: '',
    consulate: ''
});


// دالة تبني اسم القائمة بالشكل المطلوب
const buildListNameFromParts = (parts) => {
    const { companyName, visaType, visaNumber, unifiedNumber, consulate } = parts;

    if (!companyName || !visaType || !visaNumber || !unifiedNumber || !consulate) {
        return '';
    }

    return `${companyName.trim()} ${visaType.trim()} ${visaNumber.trim()} - ${unifiedNumber.trim()} (${consulate.trim()})`;
};

// فتح مودال بناء اسم القائمة مع ملء اسم الشركة تلقائياً
const openListNameBuilder = () => {
    // نجيب اسم الشركة المختارة حالياً لو موجودة
    const company = companies.find(
        c => String(c.CompanyID) === String(selectionSettings.companyId)
    );

    setListNameParts(prev => ({
        ...prev,
        companyName: company?.CompanyName || prev.companyName || ''
    }));

    setShowListNameBuilderModal(true);
};

// حفظ الاسم النهائي ثم استدعاء حفظ القائمة
const handleConfirmListNameAndSave = async () => {
    const finalName = buildListNameFromParts(listNameParts);

    if (!finalName) {
        showAlert('من فضلك أكمل جميع حقول اسم القائمة أولاً', 'warning');
        return;
    }

    setShowListNameBuilderModal(false);
    await saveCurrentList(finalName); // هنعدل saveCurrentList بعد شوية تستقبل الاسم جاهز
};


    // 📊 جلب أرصدة جميع الأشخاص في القائمة
    const fetchAllBalancesReport = async () => {
        const passports = getActivePassports(selectedPassports);
        if (passports.length === 0) {
            showAlert('لا توجد أشخاص في القائمة', 'warning');
            return;
        }
        setBalanceReportModal({ isOpen: true });
        setBalanceReportLoading(true);
        setBalanceReportData([]);
        setBalanceReportProgress({ current: 0, total: passports.length });

        const results = [];
        for (let i = 0; i < passports.length; i++) {
            const passport = passports[i];
            setBalanceReportProgress({ current: i + 1, total: passports.length });
            try {
                const res = await fetch(`${config.API_BASE_URL}/api/client-accounts/clients/${passport.PassportID}/account`);
                if (res.ok) {
                    const data = await res.json();
                    const balance = data.CurrentBalance ?? 0;
                    results.push({
                        passport,
                        balance,
                        totalDeposits: data.TotalDeposits ?? 0,
                        totalWithdrawals: data.TotalWithdrawals ?? 0,
                        isPositive: balance >= minBalanceAmount,
                        error: null
                    });
                } else {
                    results.push({ passport, balance: 0, totalDeposits: 0, totalWithdrawals: 0, isPositive: false, error: 'لا يوجد حساب' });
                }
            } catch {
                results.push({ passport, balance: 0, totalDeposits: 0, totalWithdrawals: 0, isPositive: false, error: 'خطأ في الاتصال' });
            }
        }
        setBalanceReportData(results);
        setBalanceReportLoading(false);
    };

    // 💰 دالة فحص رصيد العميل من حسابات العملاء
    const checkClientBalance = async (passport) => {
        setBalanceCheckModal({ isOpen: true, passport, balanceData: null, isLoading: true });
        try {
            const res = await fetch(`${config.API_BASE_URL}/api/client-accounts/clients/${passport.PassportID}/account`);
            if (!res.ok) throw new Error('فشل في جلب البيانات');
            const data = await res.json();
            setBalanceCheckModal(prev => ({ ...prev, balanceData: data, isLoading: false }));
        } catch (error) {
            setBalanceCheckModal(prev => ({ ...prev, isLoading: false, balanceData: { error: error.message } }));
        }
    };

    // 💰 حفظ الحد الأدنى للرصيد
    const saveMinBalanceAmount = (amount) => {
        const parsed = parseFloat(amount);
        if (!isNaN(parsed) && parsed >= 0) {
            setMinBalanceAmount(parsed);
            try { localStorage.setItem('minBalanceAmount', String(parsed)); } catch {}
            setShowMinBalanceSettingModal(false);
            showAlert(`✅ تم تحديث الحد الأدنى للرصيد إلى ${parsed.toLocaleString()} ج.م`, 'success');
        }
    };

    // دالة لحفظ إعدادات الطباعة
    const savePrintSettings = (settings) => {
        setPrintSettings(settings);
        try {
            localStorage.setItem('printSettings', JSON.stringify(settings));
        } catch (error) {
            console.log('تعذر حفظ إعدادات الطباعة');
        }
    };
    
    // دالة لتحميل إعدادات الطباعة
    useEffect(() => {
        try {
            const saved = localStorage.getItem('printSettings');
            if (saved) {
                setPrintSettings(JSON.parse(saved));
            }
        } catch (error) {
            console.log('تعذر تحميل إعدادات الطباعة');
        }
    }, []);


const [manualSelection, setManualSelection] = useState({
    companyId: '',
    professionId: '',
    travelDuration: '', // ✅ جديد
    date: new Date().toISOString().split('T')[0],
    notes: '',
    searchTerm: '',
    selectedIds: new Set(),      // مجموعة الـ IDs المختارة
    availablePassports: [],      // الجوازات المتاحة للاختيار
    isLoading: false,
    requiredCount: ''            // العدد المطلوب
});






    // 🎨 UI State
    const [activeTab, setActiveTab] = useState('selection');

    useEffect(() => {
        if (companies.length > 0 || delegates.length > 0) {
            console.log('=== تشخيص البيانات ===');
            console.log('عدد الشركات:', companies.length);
            console.log('عدد المناديب:', delegates.length);
            console.log('عدد المهن:', professions.length);
            
            if (delegates.length > 0) {
                console.log('المناديب:', delegates.map(d => `${d.DelegateID}: ${d.DelegateName}`));
            }
        }
    }, [companies, delegates, professions]);

    // 🚨 Alert System
    const showAlert = (message, type = 'info') => {
        const id = Date.now();
        setAlerts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setAlerts(prev => prev.filter(alert => alert.id !== id));
        }, 5000);
    };

    // 📡 Data Fetching
    useEffect(() => {
        fetchInitialData();
        fetchSavedLists();
    }, []);


// ✅ استبدل fetchInitialData بهذا الكود الصحيح





const fetchInitialData = async () => {
    try {
        setIsLoading(true);
        const [companiesRes, delegatesRes, professionsRes] = await Promise.all([
            fetch(`${config.API_BASE_URL}/api/companies`),
            fetch(`${config.API_BASE_URL}/api/delegates`),
            fetch(`${config.API_BASE_URL}/api/professions`)
        ]);

        const companiesData = await companiesRes.json();
        const delegatesData = await delegatesRes.json();
        const professionsData = await professionsRes.json();
        
        setCompanies(companiesData);
        setDelegates(delegatesData);
        setProfessions(professionsData);
        
        console.log('✅ تم تحميل:', {
            companies: companiesData.length,
            delegates: delegatesData.length,
            professions: professionsData.length
        });
        
    } catch (error) {
        console.error('❌ خطأ في جلب البيانات:', error);
        showAlert('خطأ في تحميل البيانات الأساسية', 'error');
    } finally {
        setIsLoading(false);
    }
};


    // جلب تاريخ المناديب التراكمي
    const fetchDelegateHistory = async (companyId = null) => {
        try {
            const url = companyId 
                ? `${config.API_BASE_URL}/api/net-registrations/delegate-history/${companyId}`
                : `${config.API_BASE_URL}/api/net-registrations/delegate-history`;
            
            const response = await fetch(url);
            const history = await response.json();
            setDelegateHistory(history);
        } catch (error) {
            console.error('خطأ في جلب تاريخ المناديب:', error);
        }
    };

    // جلب المستبعدين
    const fetchExcludedPassports = async (companyId = '') => {
        try {
            let url = `${config.API_BASE_URL}/api/passports/excluded`;
            if (companyId) {
                url += `?companyId=${companyId}`;
            }
            
            const response = await fetch(url);
            const excluded = await response.json();
            setExcludedPassports(excluded);
        } catch (error) {
            console.error('خطأ في جلب المستبعدين:', error);
            showAlert('خطأ في جلب قائمة المستبعدين', 'error');
        }
    };

 const fetchSavedLists = async () => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists?includeArchived=true`);
        const lists = await response.json();
        setSavedLists(lists);
        
        // تحديث حالة الأرشفة المحلية
        const archived = new Set(lists.filter(list => list.IsArchived).map(list => list.ListID));
        setArchivedLists(archived);
        
    } catch (error) {
        console.error('خطأ في جلب القوائم:', error);
    }
};
// دالة لأرشفة/إلغاء أرشفة القائمة
const toggleListArchive = async (listId, isCurrentlyArchived = false) => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${listId}/archive`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                archived: !isCurrentlyArchived,
                archivedBy: 'المستخدم',
                archiveNotes: !isCurrentlyArchived ? 'تم الأرشفة من واجهة المستخدم' : ''
            })
        });

        const result = await response.json();
        
        if (result.success) {
            // تحديث الحالة المحلية
            setArchivedLists(prev => {
                const newSet = new Set(prev);
                if (!isCurrentlyArchived) {
                    newSet.add(listId);
                } else {
                    newSet.delete(listId);
                }
                return newSet;
            });

            await fetchSavedLists();
            showAlert(
                !isCurrentlyArchived 
                    ? `✅ تم أرشفة القائمة بنجاح 📦`
                    : `✅ تم إلغاء أرشفة القائمة بنجاح 📋`, 
                'success'
            );
        } else {
            throw new Error(result.error || 'خطأ غير معروف');
        }
    } catch (error) {
        console.error('خطأ في أرشفة القائمة:', error);
        showAlert(`❌ خطأ في العملية: ${error.message}`, 'error');
    }
};

// دالة فلترة وترتيب القوائم
const getFilteredAndSortedLists = () => {
    let filtered = savedLists;

    // فلترة حسب النوع
    switch (listViewSettings.filterBy) {
        case 'active':
            filtered = savedLists.filter(list => !list.IsArchived);
            break;
        case 'archived':
            filtered = savedLists.filter(list => list.IsArchived);
            break;
        case 'all':
        default:
            filtered = savedLists;
            break;
    }

    // ترتيب القوائم
    filtered.sort((a, b) => {
        let comparison = 0;
        
        switch (listViewSettings.sortBy) {
            case 'name':
                comparison = a.ListName.localeCompare(b.ListName, 'ar');
                break;
            case 'company':
                comparison = a.CompanyName.localeCompare(b.CompanyName, 'ar');
                break;
            case 'date':
            default:
                comparison = new Date(a.CreatedAt) - new Date(b.CreatedAt);
                break;
        }

        return listViewSettings.sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
};

// دالة التصفح (Pagination)
const getPaginatedLists = () => {
    const filtered = getFilteredAndSortedLists();
    const startIndex = (listViewSettings.currentPage - 1) * listViewSettings.itemsPerPage;
    const endIndex = startIndex + listViewSettings.itemsPerPage;
    
    return {
        lists: filtered.slice(startIndex, endIndex),
        totalLists: filtered.length,
        totalPages: Math.ceil(filtered.length / listViewSettings.itemsPerPage),
        hasNext: endIndex < filtered.length,
        hasPrev: startIndex > 0
    };
};

// دالة تحديث إعدادات العرض
const updateViewSettings = (updates) => {
    setListViewSettings(prev => ({
        ...prev,
        ...updates,
        currentPage: updates.filterBy !== undefined || updates.sortBy !== undefined ? 1 : prev.currentPage
    }));
};

// دالة أرشفة متعددة
const bulkArchiveAction = async (action) => {
    const listsToProcess = action === 'archive' 
        ? savedLists.filter(list => !list.IsArchived)
        : savedLists.filter(list => list.IsArchived);

    if (listsToProcess.length === 0) {
        showAlert(`لا توجد قوائم ${action === 'archive' ? 'نشطة' : 'مؤرشفة'} للمعالجة`, 'warning');
        return;
    }

    const confirmed = window.confirm(
        `هل تريد ${action === 'archive' ? 'أرشفة' : 'إلغاء أرشفة'} ${listsToProcess.length} قائمة؟\n\n` +
        `⚠️ هذا الإجراء سيؤثر على كل القوائم ${action === 'archive' ? 'النشطة' : 'المؤرشفة'} حالياً`
    );

    if (!confirmed) return;

    try {
        setIsLoading(true);
        
        const listIds = listsToProcess.map(list => list.ListID);
        
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/bulk-archive`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                listIds: listIds,
                archived: action === 'archive',
                archivedBy: 'المستخدم',
                archiveNotes: `${action === 'archive' ? 'أرشفة' : 'إلغاء أرشفة'} متعددة من واجهة المستخدم`
            })
        });

        const result = await response.json();
        
        if (result.success) {
            await fetchSavedLists();
            showAlert(`✅ تم ${action === 'archive' ? 'أرشفة' : 'إلغاء أرشفة'} ${result.processedCount} قائمة من أصل ${result.totalCount}`, 'success');
        } else {
            throw new Error(result.error || 'خطأ في العملية المتعددة');
        }
        
    } catch (error) {
        console.error('خطأ في العملية المتعددة:', error);
        showAlert(`❌ خطأ في العملية المتعددة: ${error.message}`, 'error');
    } finally {
        setIsLoading(false);
    }
};



// 🧮 دالة لحساب العمر من تاريخ الميلاد
const calculateAgeFromDOB = (dobString, baseDate = new Date()) => {
    if (!dobString) return null;
    const dob = new Date(dobString);
    if (isNaN(dob.getTime())) return null;

    let age = baseDate.getFullYear() - dob.getFullYear();
    const m = baseDate.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && baseDate.getDate() < dob.getDate())) {
        age--;
    }
    return age;
};

// 🧠 تطبيق معايير الاختيار (سن + صلاحية الجواز) على الجوازات
const applySelectionRules = (passports, travelDuration, baseDateString, rules) => {
    if (!travelDuration || !rules || !rules[travelDuration]) {
        // لو المدة مش محددة → لا نطبق أي فلتر
        return passports;
    }

    const config = rules[travelDuration];
    const baseDateRaw = baseDateString ? new Date(baseDateString) : new Date();
    const baseDate = isNaN(baseDateRaw.getTime()) ? new Date() : baseDateRaw;

    return passports.filter(passport => {
        // ✅ التحقق من تاريخ الميلاد (بدلاً من العمر - لدقة اليوم والشهر)
        const hasBirthDateRule = config.minBirthDate || config.maxBirthDate;
        const hasAgeRule = config.minAge || config.maxAge; // للتوافق مع البيانات القديمة

        if (hasBirthDateRule) {
            if (!passport.DateOfBirth) return false;
            const dob = new Date(passport.DateOfBirth);
            if (isNaN(dob.getTime())) return false;

            // minBirthDate = أقدم تاريخ ميلاد مسموح (الأكبر سناً)
            if (config.minBirthDate) {
                const minDOB = new Date(config.minBirthDate);
                if (dob < minDOB) return false; // مولود قبل الحد → أكبر من المسموح
            }
            // maxBirthDate = أحدث تاريخ ميلاد مسموح (الأصغر سناً)
            if (config.maxBirthDate) {
                const maxDOB = new Date(config.maxBirthDate);
                if (dob > maxDOB) return false; // مولود بعد الحد → أصغر من المسموح
            }
        } else if (hasAgeRule) {
            // ✅ fallback للبيانات القديمة التي تحتوي على minAge/maxAge
            let age = passport.Age;
            if ((age === undefined || age === null) && passport.DateOfBirth) {
                age = calculateAgeFromDOB(passport.DateOfBirth, baseDate);
            }
            if (age === undefined || age === null || isNaN(age)) return false;
            if (config.minAge && age < config.minAge) return false;
            if (config.maxAge && age > config.maxAge) return false;
        }

        // ✅ التحقق من صلاحية الجواز
        if (config.minPassportMonths) {
            if (!passport.PassportExpiryDate) return false;

            const expiryDate = new Date(passport.PassportExpiryDate);
            if (isNaN(expiryDate.getTime())) return false;

            const diffTime = expiryDate - baseDate;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            const diffMonths = Math.ceil(diffDays / 30);

            if (diffMonths < config.minPassportMonths) return false;
        }

        return true;
    });
};

const performSmartSelection = async () => {
    if (!selectionSettings.companyId || !selectionSettings.requiredCount) {
        showAlert('يرجى اختيار الشركة وإدخال العدد المطلوب', 'warning');
        return;
    }

    // ✅ مدة السفر الآن إجبارية للاختيار المطوّر
    if (!selectionSettings.travelDuration) {
        showAlert('يرجى اختيار مدة السفر أولاً', 'warning');
        return;
    }

    const requiredCount = parseInt(selectionSettings.requiredCount);

    if (requiredCount <= 0) {
        showAlert('العدد المطلوب يجب أن يكون أكبر من صفر', 'warning');
        return;
    }

    try {
        setIsLoading(true);
        showAlert('جاري التحليل والاختيار العادل البسيط...', 'info');
        
        // ✅ التأكد من وجود البيانات الأساسية
        if (delegates.length === 0 || companies.length === 0) {
            console.log('إعادة تحميل البيانات الأساسية...');
            await fetchInitialData();
        }

        // جلب الجوازات المتاحة مع فلتر المهنة ومدة السفر
        let url = `${config.API_BASE_URL}/api/net-registrations/available-passports/${selectionSettings.companyId}`;
        const params = [];

        // فلتر المهنة (اختياري)
        if (selectionSettings.professionId) {
            params.push(`professionId=${selectionSettings.professionId}`);
        }

        // ✅ فلتر مدة السفر (اختياري برضه)
        if (selectionSettings.travelDuration && selectionSettings.travelDuration !== 'اختر مدة السفر') {
            params.push(`travelDuration=${encodeURIComponent(selectionSettings.travelDuration)}`);
        }

        // تركيب الكويري سترنج
        if (params.length > 0) {
            url += `?${params.join('&')}`;
        }

const response = await fetch(url);
let availablePassports = await response.json();

console.log('الجوازات المتاحة قبل تطبيق المعايير:', availablePassports.length);

// ✅ تطبيق معايير الاختيار (سن + صلاحية الجواز) بناءً على مدة السفر المختارة
availablePassports = applySelectionRules(
    availablePassports,
    selectionSettings.travelDuration,
    selectionSettings.date,
    selectionRules
);

console.log('الجوازات المطابقة للمعايير:', availablePassports.length);

if (availablePassports.length === 0) {
    showAlert(
        'لا يوجد أي شخص مطابق لمعايير السن وصلاحية الجواز للمدة المختارة',
        'warning'
    );
    return;
}

// جلب تاريخ المناديب للشركة (كما هو)
await fetchDelegateHistory(selectionSettings.companyId);

const finalCount = Math.min(requiredCount, availablePassports.length);
const distribution = calculateSimpleFairDistribution(availablePassports, finalCount);

setDistributionPreview(distribution);
setShowPreview(true);

        
    } catch (error) {
        console.error('خطأ في الاختيار:', error);
        showAlert('حدث خطأ أثناء عملية الاختيار', 'error');
    } finally {
        setIsLoading(false);
    }
};






// دالة فلترة الجوازات حسب النوع المختار
const getFilteredPassportsForDelegate = (passports) => {
    switch (clientFilter) {
        case 'confirmed':
            return passports.filter(p => p.confirmed === true);
        case 'pending':
            return passports.filter(p => p.confirmed !== true);
        case 'all':
        default:
            return passports;
    }
};

// دالة إنشاء صورة للواتساب مع معاينة
const generateImageForWhatsApp = async (delegateGroup, filteredPassports, listInfo) => {
    try {
        showAlert('جاري إنشاء الصورة للواتساب...', 'info');
        
        const html2canvas = await loadHtml2Canvas();
        const container = createWhatsAppImageElement(delegateGroup, filteredPassports, listInfo);
        
        document.body.appendChild(container);
        
        const canvas = await html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: 800,
            height: container.scrollHeight
        });
        
        document.body.removeChild(container);
        
        // تحويل إلى blob وعرض في نافذة معاينة
        canvas.toBlob((blob) => {
            const imageUrl = URL.createObjectURL(blob);
            setPreviewImageData({
                url: imageUrl,
                delegateName: delegateGroup.delegateInfo?.DelegateName || 'مندوب غير معروف',
                clientCount: filteredPassports.length,
                blob: blob
            });
            setShowImagePreview(true);
        }, 'image/png', 0.95);
        
    } catch (error) {
        console.error('خطأ في إنشاء صورة الواتساب:', error);
        showAlert('حدث خطأ أثناء إنشاء الصورة: ' + error.message, 'error');
    }
};

// دالة إنشاء عنصر HTML للواتساب (بدون ملاحظات وبألوان طباعة)
const createWhatsAppImageElement = (delegateGroup, filteredPassports, listInfo) => {
    const container = document.createElement('div');
    container.style.cssText = `
        width: 800px;
        padding: 20px;
        background: white;
        font-family: 'Arial', sans-serif;
        direction: rtl;
        color: black;
        font-size: 14px;
        line-height: 1.5;
        position: absolute;
        left: -9999px;
        top: -9999px;
    `;

    // تحديد نص الموعد
    const appointmentText = getAppointmentText();
    
    container.innerHTML = `
        <div style="text-align: center; margin-bottom: 25px; border-bottom: 2px solid black; padding-bottom: 15px;">
            <div style="font-size: 28px; font-weight: bold; color: black; margin-bottom: 8px;">
                📋 قائمة العملاء - ${appointmentSettings.type === 'medical' ? 'الكشف الطبي' : appointmentSettings.type === 'net' ? 'عمل النت' : appointmentSettings.type === 'both' ? 'الكشف الطبي + النت' : 'موعد هام'}
            </div>
            <div style="font-size: 22px; color: black; margin-bottom: 8px; background: #f0f0f0; padding: 10px; border: 2px solid black; border-radius: 8px;">
                المندوب: ${delegateGroup.delegateInfo?.DelegateName || 'غير محدد'}
            </div>
            <div style="font-size: 16px; color: black; margin-bottom: 5px;">
                الشركة: ${listInfo.CompanyName}
            </div>
        </div>

        <div style="background: #f8f9fa; border: 2px solid black; border-radius: 8px; padding: 20px; margin-bottom: 20px; text-align: center;">
            <div style="font-size: 20px; font-weight: bold; color: black; margin-bottom: 15px;">
                🗓️ موعد الحضور المطلوب
            </div>
            <div style="font-size: 24px; font-weight: bold; color: black; margin-bottom: 10px;">
                ${appointmentText}
            </div>
            ${appointmentSettings.notes ? `
                <div style="font-size: 16px; color: black; margin-top: 15px; padding: 10px; border: 1px solid black; border-radius: 6px; background: white;">
                    📝 ${appointmentSettings.notes}
                </div>
            ` : ''}
        </div>

        <div style="background: white; border: 2px solid black; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
            <div style="font-size: 18px; font-weight: bold; color: black; margin-bottom: 15px; text-align: center; border-bottom: 1px solid black; padding-bottom: 8px;">
                ملخص العملاء
            </div>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; text-align: center;">
                <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                    <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">إجمالي العملاء</div>
                    <div style="font-size: 22px; font-weight: bold; color: black;">${filteredPassports.length}</div>
                </div>
                <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                    <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">نوع الفلتر</div>
                    <div style="font-size: 16px; font-weight: bold; color: black;">${getFilterText()}</div>
                </div>
            </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; background: white; border: 2px solid black;">
            <thead>
                <tr style="background: #f0f0f0;">
                    <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">م</th>
                    <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الاسم</th>
                    <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">رقم الجواز</th>
                    <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">رقم الهاتف</th>
                    <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الحالة</th>
                </tr>
            </thead>
            <tbody>
                ${filteredPassports.map((passport, index) => `
                    <tr style="${index % 2 === 0 ? 'background: #f8f8f8;' : 'background: white;'}">
                        <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold;">${index + 1}</td>
                        <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">${passport.NameArabic || 'غير محدد'}</td>
                        <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">${passport.PassportNumber || 'غير محدد'}</td>
                        <td style="padding: 8px 4px; text-align: right; border: 1px solid black; font-size: 11px; color: black; vertical-align: top;">
    ${(() => {
        const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px; background: #f9f9f9; border-radius: 3px; font-family: monospace;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic;">غير محدد</div>';
    })()}
</td>                        <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">
                            ${passport.confirmed ? '✅ مؤكد' : '⏳ انتظار'}
                        </td>
                    </tr>
                `).join('')}
            </tbody>
        </table>

        <div style="margin-top: 20px; text-align: center; color: black; font-size: 12px; border-top: 1px solid black; padding-top: 10px;">
            نظام إدارة السفر | طُبع بتاريخ: ${new Date().toLocaleString('ar-EG')}
        </div>
    `;

    return container;
};

// دالة الحصول على نص الموعد
const getAppointmentText = () => {
    const dateText = new Date(appointmentSettings.date).toLocaleDateString('ar-EG', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    
    const timeText = appointmentSettings.time ? ` الساعة ${appointmentSettings.time}` : '';
    
    return `${dateText}${timeText}`;
};

// دالة الحصول على نص الفلتر
const getFilterText = () => {
    switch (clientFilter) {
        case 'confirmed':
            return 'المؤكدين فقط';
        case 'pending':
            return 'في الانتظار فقط';
        case 'all':
        default:
            return 'جميع العملاء';
    }
};


// دالة لجلب عدد أعمدة الجدول حسب إعدادات الطباعة
const getColumnsCount = () => {
    let count = 2; // الرقم التسلسلي + الاسم (ثابتان)
    if (printSettings.showDelegate) count++;
    if (printSettings.showCompany) count++;
    if (printSettings.showProfession) count++;
    if (printSettings.showPhone) count++;
    count++; // عمود الحالة (ثابت)
    return count;
};

// دالة للحصول على عرض العمود
const getColumnWidth = () => {
    const totalColumns = getColumnsCount();
    if (totalColumns <= 4) return '200px';
    if (totalColumns <= 6) return '150px';
    return '120px';
};

// دالة لجلب الجوازات المتاحة للاختيار اليدوي
 const fetchAvailableForManualSelection = async (companyId, professionId = '', travelDuration = '') => {
    // ✅ الشركة ومدة السفر إجباريان
    if (!companyId) return;
    if (!travelDuration) {
        showAlert('يرجى اختيار مدة السفر أولاً', 'warning');
        return;
    }
    
    try {
        setManualSelection(prev => ({ ...prev, isLoading: true }));
        
        let url = `${config.API_BASE_URL}/api/net-registrations/available-passports/${companyId}`;
        const params = [];


        if (professionId) {
            params.push(`professionId=${professionId}`);
        }

        if (travelDuration && travelDuration !== 'اختر مدة السفر') {
            params.push(`travelDuration=${encodeURIComponent(travelDuration)}`);
        }

        if (allowProcessedManualSelection) {
            params.push('includeProcessed=true');
        }

        if (params.length > 0) {
            url += `?${params.join('&')}`;
        }
        
      const response = await fetch(url);
let available = await response.json();

console.log('المتاح قبل تطبيق المعايير للاختيار اليدوي:', available.length);

// ✅ تطبيق نفس معايير الاختيار (سن + صلاحية الجواز)
// نستخدم تاريخ الاختيار الموجود في manualSelection.date
available = applySelectionRules(
    available,
    travelDuration,
    manualSelection.date,
    selectionRules
);

console.log('المتاح بعد تطبيق المعايير للاختيار اليدوي:', available.length);

setManualSelection(prev => ({
    ...prev,
    availablePassports: available,
    isLoading: false
}));

showAlert(`تم تحميل ${available.length} شخص مطابق لمعايير الاختيار${allowProcessedManualSelection ? ' (وضع الطوارئ اليدوي مفعّل)' : ''}`, 'success');

        
    } catch (error) {
        console.error('خطأ في جلب الجوازات للاختيار اليدوي:', error);
        setManualSelection(prev => ({ ...prev, isLoading: false }));
        showAlert('خطأ في تحميل البيانات', 'error');
    }
};





// دالة تحديد/إلغاء تحديد شخص مع التحقق من العدد المطلوب
const togglePassportSelection = (passportId) => {
    setManualSelection(prev => {
        const newSelectedIds = new Set(prev.selectedIds);
        const requiredCount = parseInt(prev.requiredCount);
        
        if (newSelectedIds.has(passportId)) {
            // إلغاء التحديد - مسموح دائماً
            newSelectedIds.delete(passportId);
        } else {
            // التحديد - تحقق من العدد المطلوب
            if (requiredCount && newSelectedIds.size >= requiredCount) {
                showAlert(`تم الوصول للعدد المطلوب (${requiredCount} أشخاص)`, 'warning');
                return prev; // لا تغير الحالة
            }
            newSelectedIds.add(passportId);
        }
        
        return {
            ...prev,
            selectedIds: newSelectedIds
        };
    });
};


// دالة تحديد الكل
const selectAllAvailable = () => {
    const filteredPassports = getFilteredManualPassports();
    const allIds = filteredPassports.map(p => p.PassportID);
    setManualSelection(prev => ({
        ...prev,
        selectedIds: new Set(allIds)
    }));
};

// دالة إلغاء تحديد الكل
const clearAllSelection = () => {
    setManualSelection(prev => ({
        ...prev,
        selectedIds: new Set()
    }));
};

// دالة فلترة الجوازات للاختيار اليدوي
const getFilteredManualPassports = () => {
    let filtered = manualSelection.availablePassports;
    
    if (manualSelection.searchTerm) {
        const term = manualSelection.searchTerm.toLowerCase();
        filtered = filtered.filter(p => 
            p.NameArabic?.toLowerCase().includes(term) ||
            p.PassportNumber?.toLowerCase().includes(term) ||
            p.NationalID?.includes(term) ||
            p.DelegateName?.toLowerCase().includes(term)
        );
    }
    
    return filtered;
};

const getProcessedEmergencyBadges = (passport) => {
    const badges = [];
    if (passport?.NetWorkDate) badges.push('تاريخ نت');
    if (passport?.VisaDate) badges.push('تأشيرة');
    if (passport?.TravelDate) badges.push('سفر');
    if (passport?.ApplicationNumber) badges.push('رقم طلب');
    if (passport?.ConsulateEntryDate) badges.push('دخول قنصلية');
    return badges;
};


// إضافة دالة التأكيد البسيط
const confirmPayment = async (passportId, amount = null, notes = '') => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/passports/${passportId}/simple-confirm`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                listId: currentList?.ListID,
                isPaid: true,
                paidAmount: amount,
                notes: notes
            })
        });

        const result = await response.json();
        
        if (result.success) {
            // تحديث الحالة في الواجهة
setSelectedPassports(prev => prev.map(p => 
    p.PassportID === passportId 
        ? { 
            ...p, 
            paidConfirmed: true,
            paidAmount: amount,
            paidNotes: notes,
            paidAt: new Date().toISOString()
        }
        : p
));


            
            showAlert(`✅ تم تأكيد الدفع للشخص`, 'success');
        }
        
    } catch (error) {
        showAlert('خطأ في تأكيد الدفع', 'error');
    }
};

// دالة إلغاء التأكيد
const cancelPaymentConfirmation = async (passportId) => {
    try {
        // توليد رقم عشوائي للتأكيد (6 أرقام)
        const confirmationCode = Math.floor(100000 + Math.random() * 900000);
        
        // عرض رسالة التأكيد مع الرقم
        const userInput = prompt(
            `⚠️ تحذير: إلغاء تأكيد الدفع\n\n` +
            `أنت على وشك إلغاء تأكيد الدفع لهذا الشخص.\n` +
            `للتأكيد، اكتب الرقم التالي:\n\n` +
            `${confirmationCode}\n\n` +
            `أو اضغط إلغاء للتراجع:`
        );
        
        // التحقق من صحة الرقم المدخل
        if (userInput === null) {
            // المستخدم ضغط إلغاء
            return;
        }
        
        if (userInput.toString() !== confirmationCode.toString()) {
            showAlert('❌ رقم التأكيد غير صحيح. لم يتم إلغاء التأكيد.', 'error');
            return;
        }
        
        // تنفيذ إلغاء التأكيد
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/passports/${passportId}/simple-confirm`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                listId: currentList?.ListID,
                isPaid: false
            })
        });
        
        if (response.ok) {
setSelectedPassports(prev => prev.map(p => 
    p.PassportID === passportId 
        ? { 
            ...p, 
            paidConfirmed: false,
            paidAmount: null,
            paidNotes: null,
            paidAt: null
        }
        : p
));


            
            showAlert(`✅ تم إلغاء تأكيد الدفع بنجاح`, 'warning');
        } else {
            showAlert('❌ فشل في إلغاء التأكيد', 'error');
        }
        
    } catch (error) {
        console.error('Error canceling payment confirmation:', error);
        showAlert('❌ خطأ في إلغاء التأكيد', 'error');
    }
};


// ✅ تحديث دالة confirmManualSelection
const confirmManualSelection = async () => {
    const selectedPassports = manualSelection.availablePassports.filter(p => 
        manualSelection.selectedIds.has(p.PassportID)
    );
    
    if (selectedPassports.length === 0) {
        showAlert('يرجى اختيار شخص واحد على الأقل', 'warning');
        return;
    }
    
    const requiredCount = parseInt(manualSelection.requiredCount) || selectedPassports.length;
    
    if (selectedPassports.length > requiredCount) {
        showAlert(`تم اختيار ${selectedPassports.length} شخص، لكن العدد المطلوب هو ${requiredCount} فقط`, 'warning');
        return;
    }

    const canProceed = await checkOtherListMembershipsBeforeAdding(selectedPassports, 'الإضافة اليدوية');
    if (!canProceed) return;
    
    const formattedSelection = selectedPassports.map(passport => ({
        ...passport,
        status: 'pending',
        confirmed: false,
        selectedAt: new Date().toISOString(),
        netDate: null,
        confirmedAt: null,
        notes: '',
        CompanyID: parseInt(manualSelection.companyId)
    }));
    
    setSelectedPassports(prev => {
        const existingIds = new Set(prev.map(p => p.PassportID));
        const newPassports = formattedSelection.filter(p => !existingIds.has(p.PassportID));
        return [...prev, ...newPassports];
    });
    
    setActiveTab('management');
    
    setSelectionSettings(prev => ({
        ...prev,
        companyId: manualSelection.companyId,
        professionId: manualSelection.professionId,
        requiredCount: manualSelection.requiredCount || prev.requiredCount,
        date: manualSelection.date,
        notes: manualSelection.notes
    }));
    
    showAlert(`✅ تم إضافة ${selectedPassports.length} شخص للقائمة`, 'success');

    if (currentList && currentList.ListID) {
        setTimeout(async () => {
            try {
                await autoSaveCurrentList();
                showAlert('✅ تم حفظ الإضافات تلقائياً', 'success');
            } catch (error) {
                showAlert('⚠️ تم الإضافة لكن فشل الحفظ التلقائي', 'warning');
            }
        }, 1000);
    }
};




// دوال جديدة للتحقق من إمكانية الإضافة المستقبلية
const canAddMoreToList = () => {
    if (!selectionSettings.requiredCount) return true;
    const requiredCount = parseInt(selectionSettings.requiredCount);
    const currentCount = selectedPassports.length;
    return currentCount < requiredCount;
};

const getRemainingSlots = () => {
    if (!selectionSettings.requiredCount) return 0;
    const requiredCount = parseInt(selectionSettings.requiredCount);
    const currentCount = selectedPassports.length;
    return Math.max(0, requiredCount - currentCount);
};





  // 🧮 Advanced Fair Distribution Calculator - مُعدل للتوزيع النسبي الحقيقي
  const calculateSimpleFairDistribution = (passports, totalRequired) => {
    // تجميع الجوازات حسب المندوب
    const delegateGroups = {};
    passports.forEach(passport => {
        const delegateId = passport.DelegateID;
        if (!delegateGroups[delegateId]) {
            delegateGroups[delegateId] = [];
        }
        delegateGroups[delegateId].push(passport);
    });

    const totalAvailable = passports.length;
    const totalToLeave = totalAvailable - totalRequired; // اللي هيتسابوا
    const delegateCount = Object.keys(delegateGroups).length;
    const distribution = {};
    
    console.log('=== توزيع عادل للمتروك ===');
    console.log(`إجمالي المتاح: ${totalAvailable}`);
    console.log(`العدد المطلوب: ${totalRequired}`);
    console.log(`العدد المتروك: ${totalToLeave}`);
    console.log(`عدد المناديب: ${delegateCount}`);

    if (totalRequired >= totalAvailable) {
        // ناخد كل المتاح
        Object.entries(delegateGroups).forEach(([delegateId, delegatePassports]) => {
            const delegateInfo = delegates.find(d => d.DelegateID.toString() === delegateId);
            distribution[delegateId] = {
                delegateInfo,
                totalCount: delegatePassports.length,
                allocatedCount: delegatePassports.length,
                excludedCount: 0,
                currentRatio: 1.0,
                passports: delegatePassports.sort((a, b) => a.PassportID - b.PassportID)
            };
        });
        return distribution;
    }

    if (totalToLeave <= 0) {
        // ناخد كل المتاح
        Object.entries(delegateGroups).forEach(([delegateId, delegatePassports]) => {
            const delegateInfo = delegates.find(d => d.DelegateID.toString() === delegateId);
            distribution[delegateId] = {
                delegateInfo,
                totalCount: delegatePassports.length,
                allocatedCount: delegatePassports.length,
                excludedCount: 0,
                currentRatio: 1.0,
               passports: delegatePassports.sort((a, b) => a.PassportID - b.PassportID)

            };
        });
        return distribution;
    }

    // ✅ توزيع المتروك بالعدل بناءً على النسب
    console.log('--- توزيع المتروك بالعدل ---');
    
    Object.entries(delegateGroups).forEach(([delegateId, delegatePassports]) => {
        const delegateInfo = delegates.find(d => d.DelegateID.toString() === delegateId);
        const currentCount = delegatePassports.length;
        
        // حساب نصيب كل مندوب من المتروك بناءً على نسبة جوازاته
        const proportionalLeave = (currentCount / totalAvailable) * totalToLeave;
        let leaveCount = Math.round(proportionalLeave); // نقرب للعدد الصحيح
        
        // التأكد من عدم تجاوز المتاح
        leaveCount = Math.min(leaveCount, currentCount);
        
        const allocatedCount = currentCount - leaveCount;
        
        console.log(`${delegateInfo?.DelegateName || 'مندوب غير معروف'}:`);
        console.log(`  - إجمالي الجوازات: ${currentCount}`);
        console.log(`  - النسبة من الإجمالي: ${((currentCount / totalAvailable) * 100).toFixed(1)}%`);
        console.log(`  - نصيبه من المتروك: ${proportionalLeave.toFixed(2)} → ${leaveCount}`);
        console.log(`  - سيتم ترك: ${leaveCount}`);
        console.log(`  - سيتم اختيار: ${allocatedCount}`);

        distribution[delegateId] = {
            delegateInfo,
            totalCount: currentCount,
            allocatedCount: allocatedCount,
            excludedCount: leaveCount,
            currentRatio: currentCount > 0 ? allocatedCount / currentCount : 0,
            proportionalLeave: proportionalLeave,
            passports: delegatePassports.sort((a, b) => a.PassportID - b.PassportID)
        };
    });

    // ✅ تصحيح الفرق للوصول للعدد المطلوب بالضبط
    let totalAllocated = Object.values(distribution).reduce((sum, d) => sum + d.allocatedCount, 0);
    let totalLeft = Object.values(distribution).reduce((sum, d) => sum + d.excludedCount, 0);
    let difference = totalRequired - totalAllocated;
    
    console.log(`المجموع المختار حالياً: ${totalAllocated}`);
    console.log(`المجموع المتروك حالياً: ${totalLeft}`);
    console.log(`الفرق المطلوب تعديله: ${difference}`);
    
    if (difference !== 0) {
        console.log('--- تصحيح الفرق مع الحفاظ على عدالة المتروك ---');
        
        // ترتيب المناديب حسب الفرق عن النصيب المثالي
        const delegateIds = Object.keys(distribution).sort((a, b) => {
            const delegateA = distribution[a];
            const delegateB = distribution[b];
            
            // الفرق بين المتروك الفعلي والمتروك المثالي
            const idealLeaveA = delegateA.proportionalLeave;
            const actualLeaveA = delegateA.excludedCount;
            const diffA = actualLeaveA - idealLeaveA;
            
            const idealLeaveB = delegateB.proportionalLeave;
            const actualLeaveB = delegateB.excludedCount;
            const diffB = actualLeaveB - idealLeaveB;
            
            if (difference > 0) {
                // محتاجين نزيد المختار = نقلل المتروك
                // نبدأ باللي متروك عندهم أكتر من المفروض
                return diffB - diffA;
            } else {
                // محتاجين نقلل المختار = نزيد المتروك
                // نبدأ باللي متروك عندهم أقل من المفروض
                return diffA - diffB;
            }
        });
        
        if (difference > 0) {
            // نحتاج زيادة في المختار = تقليل في المتروك
            for (let i = 0; i < delegateIds.length && difference > 0; i++) {
                const delegateId = delegateIds[i];
                const delegate = distribution[delegateId];
                
                if (delegate.excludedCount > 0) {
                    const canTake = Math.min(delegate.excludedCount, difference);
                    delegate.allocatedCount += canTake;
                    delegate.excludedCount -= canTake;
                    delegate.currentRatio = delegate.totalCount > 0 ? delegate.allocatedCount / delegate.totalCount : 0;
                    console.log(`تقليل المتروك بـ ${canTake} من ${delegate.delegateInfo?.DelegateName}`);
                    difference -= canTake;
                }
            }
        } else {
            // نحتاج تقليل في المختار = زيادة في المتروك
            for (let i = 0; i < delegateIds.length && difference < 0; i++) {
                const delegateId = delegateIds[i];
                const delegate = distribution[delegateId];
                
                if (delegate.allocatedCount > 0) {
                    const willReturn = Math.min(delegate.allocatedCount, Math.abs(difference));
                    delegate.allocatedCount -= willReturn;
                    delegate.excludedCount += willReturn;
                    delegate.currentRatio = delegate.totalCount > 0 ? delegate.allocatedCount / delegate.totalCount : 0;
                    console.log(`زيادة المتروك بـ ${willReturn} من ${delegate.delegateInfo?.DelegateName}`);
                    difference += willReturn;
                }
            }
        }
    }

    // التحقق النهائي
    const finalTotal = Object.values(distribution).reduce((sum, d) => sum + d.allocatedCount, 0);
    const finalLeft = Object.values(distribution).reduce((sum, d) => sum + d.excludedCount, 0);
    
    console.log('=== التوزيع النهائي (عدالة في المتروك) ===');
    Object.values(distribution).forEach(d => {
        const leftPercentage = d.totalCount > 0 ? (d.excludedCount / d.totalCount * 100).toFixed(1) : '0.0';
        const selectedPercentage = d.totalCount > 0 ? (d.allocatedCount / d.totalCount * 100).toFixed(1) : '0.0';
        console.log(`${d.delegateInfo?.DelegateName}:`);
        console.log(`  - المختار: ${d.allocatedCount}/${d.totalCount} (${selectedPercentage}%)`);
        console.log(`  - المتروك: ${d.excludedCount}/${d.totalCount} (${leftPercentage}%) ← هذا هو الهدف العادل`);
    });
    
    console.log(`إجمالي المختار: ${finalTotal}/${totalRequired}`);
    console.log(`إجمالي المتروك: ${finalLeft}/${totalToLeave}`);
    console.log(`✅ الالتزام بالعدد: ${finalTotal === totalRequired ? 'صحيح ✅' : 'خطأ ❌'}`);
    
    return distribution;
};
    // فحص وجود الأشخاص في قوائم اختيار أخرى قبل إضافتهم للقائمة الحالية
    const checkOtherListMembershipsBeforeAdding = async (passportsToAdd, actionLabel = 'الإضافة') => {
        const passportIds = [...new Set(
            (passportsToAdd || [])
                .map(p => parseInt(p.PassportID))
                .filter(id => id && !Number.isNaN(id))
        )];

        if (passportIds.length === 0) return true;

        try {
            const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/check-existing-list-memberships`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    passportIds,
                    currentListId: currentList?.ListID || null
                })
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result.error || 'فشل فحص القوائم الأخرى');
            }

            const matches = Array.isArray(result.matches) ? result.matches : [];
            if (matches.length === 0) return true;

            const grouped = matches.reduce((acc, item) => {
                const key = item.passportId;
                if (!acc[key]) {
                    acc[key] = {
                        name: item.nameArabic || 'غير محدد',
                        passportNumber: item.passportNumber || '',
                        lists: []
                    };
                }
                acc[key].lists.push(item);
                return acc;
            }, {});

            const warningLines = Object.values(grouped).slice(0, 10).map(person => {
                const listNames = person.lists
                    .slice(0, 3)
                    .map(list => `${list.listName || `قائمة رقم ${list.listId}`}${list.isArchived ? ' (مؤرشفة)' : ''}${list.isConfirmed ? ' - مؤكد' : ''}`)
                    .join(' / ');
                return `- ${person.name}${person.passportNumber ? ` - جواز ${person.passportNumber}` : ''}\n  موجود في: ${listNames}`;
            });

            const remainingCount = Object.keys(grouped).length - warningLines.length;
            const blockMessage =
                `🚫 لا يمكن ${actionLabel}\n\n` +
                `يوجد ${Object.keys(grouped).length} شخص مضاف بالفعل في قائمة أخرى، لذلك تم منع العملية نهائياً:\n\n` +
                warningLines.join('\n') +
                (remainingCount > 0 ? `\n... و ${remainingCount} شخص آخر` : '') +
                `\n\nاحذف الشخص من القائمة الأخرى أولاً أو استخدم نفس القائمة بدلاً من إنشاء/إضافة قائمة جديدة.`;

            window.alert(blockMessage);
            showAlert(`🚫 تم منع ${actionLabel}: يوجد ${Object.keys(grouped).length} شخص مضاف في قائمة أخرى`, 'error');
            return false;
        } catch (error) {
            console.error('خطأ في فحص القوائم الأخرى:', error);
            window.alert(
                `🚫 تم منع ${actionLabel} مؤقتاً لأن فحص القوائم الأخرى لم يكتمل.\n\n` +
                `سبب الخطأ: ${error.message}\n\n` +
                `لن يتم السماح بالإضافة حتى ينجح الفحص، منعاً لتكرار الشخص في أكثر من قائمة.`
            );
            showAlert('🚫 تم منع الإضافة لحين نجاح فحص القوائم الأخرى', 'error');
            return false;
        }
    };

    // ✅ Confirm Fair Selection
    const confirmFairSelection = async () => {
        const selected = [];
        
        Object.entries(distributionPreview).forEach(([delegateId, data]) => {
            const countToSelect = data.allocatedCount;
            const selectedFromDelegate = data.passports.slice(0, countToSelect);
            
            selectedFromDelegate.forEach(passport => {
                selected.push({
                    ...passport,
                    status: 'pending',
                    confirmed: false,
                    selectedAt: new Date().toISOString(),
                    netDate: null,
                    confirmedAt: null,
                    notes: '',
                    // ✅ التأكد من حفظ معرف الشركة
                    CompanyID: parseInt(selectionSettings.companyId)
                });
            });
        });
    
        const canProceed = await checkOtherListMembershipsBeforeAdding(selected, 'الاختيار الذكي');
        if (!canProceed) return;

        setSelectedPassports(selected);
        setShowPreview(false);
        setActiveTab('management');
        
        await saveSelectionHistory(selected);
        
        showAlert(`تم اختيار ${selected.length} شخص بنظام التوزيع العادل البسيط`, 'success');
    };

    // حفظ سجل الاختيار
    const saveSelectionHistory = async (selectedList) => {
        try {
            const historyData = {};
            selectedList.forEach(passport => {
                if (!historyData[passport.DelegateID]) {
                    historyData[passport.DelegateID] = 0;
                }
                historyData[passport.DelegateID]++;
            });

            await fetch(`${config.API_BASE_URL}/api/net-registrations/save-selection-history`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    companyId: selectionSettings.companyId,
                    selectionData: historyData,
                    selectionDate: selectionSettings.date
                })
            });
        } catch (error) {
            console.error('خطأ في حفظ سجل الاختيار:', error);
        }
    };

    const replacePassport = async (passportId) => {
        const currentPassport = selectedPassports.find(p => p.PassportID === passportId);
        if (!currentPassport) return;
    
        // فحص إمكانية التعديل
        const modifyCheck = canModifyPassport(currentPassport);
        if (!modifyCheck.allowed) {
            showAlert(modifyCheck.reason, 'warning');
            return;
        }
    
        try {
            setIsLoading(true);
            
            // ✅ تحديد معرف الشركة بشكل صحيح
            const companyId = currentPassport.CompanyID || selectionSettings.companyId;
            
            if (!companyId) {
                showAlert('❌ لا يمكن تحديد الشركة للبحث عن البدائل', 'error');
                return;
            }
            
            console.log('=== بدء استبدال الجواز ===');
            console.log('معرف الشركة:', companyId);
            console.log('معرف المندوب:', currentPassport.DelegateID);
            
            // ✅ أولاً: جلب البدائل من نفس المندوب
            let sameDelegateReplacements = [];
            
            if (currentPassport.DelegateID) {
                try {
                    const sameDelegateResponse = await fetch(`${config.API_BASE_URL}/api/net-registrations/available-replacements`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            companyId: parseInt(companyId),
                            delegateId: parseInt(currentPassport.DelegateID),
                            excludePassportIds: selectedPassports.map(p => p.PassportID),
                            professionId: selectionSettings.professionId || null // ✅ null بدل من undefined
                        })
                    });
    
                    if (sameDelegateResponse.ok) {
                        sameDelegateReplacements = await sameDelegateResponse.json();
                        console.log(`✅ البدائل من نفس المندوب: ${sameDelegateReplacements.length}`);
                    } else {
                        console.log('⚠️ فشل في جلب بدائل نفس المندوب');
                    }
                } catch (error) {
                    console.error('خطأ في جلب بدائل نفس المندوب:', error);
                }
            }
            
            let allReplacements = [];
            let hasSameDelegateOptions = sameDelegateReplacements.length > 0;
            
            if (hasSameDelegateOptions) {
                // إذا وجدت بدائل من نفس المندوب
                allReplacements = sameDelegateReplacements.map(replacement => ({
                    ...replacement,
                    delegateType: 'same',
                    delegateLabel: 'نفس المندوب'
                }));
            }
            
            // ✅ ثانياً: جلب البدائل من كل المناديب الأخرى
            let groupedOtherReplacements = [];
            
            try {
                // ✅ بناء URL صحيح
                let url = `${config.API_BASE_URL}/api/net-registrations/available-passports/${companyId}`;
                if (selectionSettings.professionId) {
                    url += `?professionId=${selectionSettings.professionId}`;
                }
                
                console.log('URL للبدائل من كل المناديب:', url);
                
                const allDelegatesResponse = await fetch(url);
                
                if (allDelegatesResponse.ok) {
                    const allAvailable = await allDelegatesResponse.json();
                    console.log(`✅ تم جلب ${allAvailable.length} شخص من كل المناديب`);
                    
                    // تصفية البدائل من مناديب أخرى (غير المندوب الحالي والمختارين حالياً)
                    const otherDelegatesReplacements = allAvailable.filter(passport => 
                        passport.DelegateID !== currentPassport.DelegateID && // ليس من نفس المندوب
                        !selectedPassports.some(selected => selected.PassportID === passport.PassportID) // ليس مختار حالياً
                    );
                    
                    // إضافة البدائل من المناديب الأخرى
                    groupedOtherReplacements = otherDelegatesReplacements.map(replacement => {
                        const delegate = delegates.find(d => d.DelegateID === replacement.DelegateID);
                        return {
                            ...replacement,
                            delegateType: 'other',
                            delegateLabel: delegate ? delegate.DelegateName : 'مندوب غير معروف'
                        };
                    });
                    
                    console.log(`✅ البدائل من مناديب أخرى: ${groupedOtherReplacements.length}`);
                    
                } else {
                    console.error('فشل في جلب البدائل من كل المناديب:', allDelegatesResponse.status);
                }
            } catch (error) {
                console.error('خطأ في جلب البدائل من كل المناديب:', error);
            }
            
            allReplacements = [...allReplacements, ...groupedOtherReplacements];
            
            console.log('=== تحليل البدائل النهائي ===');
            console.log(`البدائل من نفس المندوب: ${sameDelegateReplacements.length}`);
            console.log(`البدائل من مناديب أخرى: ${groupedOtherReplacements.length}`);
            console.log(`إجمالي البدائل: ${allReplacements.length}`);
    
            if (allReplacements.length === 0) {
                showAlert('لا توجد بدائل متاحة من أي مندوب', 'warning');
                return;
            }
    
            setAvailableReplacements(allReplacements);
            setSelectedPassportForReplacement({
                ...currentPassport,
                hasSameDelegateOptions: hasSameDelegateOptions,
                sameDelegateCount: sameDelegateReplacements.length,
                otherDelegatesCount: groupedOtherReplacements.length
            });
            setShowReplacementModal(true);
            
        } catch (error) {
            console.error('خطأ في جلب البدائل:', error);
            showAlert('خطأ في جلب البدائل المتاحة: ' + error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleReplacementConfirm = async (replacementData) => {
        const { type, reason, mode, selectedReplacement } = replacementData;
        
        try {
            setIsLoading(true);
    
            // البحث عن بديل تلقائي أو استخدام المختار يدوياً
            let replacement = null;
            if (mode === 'auto') {
                const sameDelegateOptions = availableReplacements.filter(r => r.delegateType === 'same');
                replacement = sameDelegateOptions.length > 0 
                    ? sameDelegateOptions[0] 
                    : availableReplacements[0];
                    
                console.log('اختيار تلقائي:', replacement?.NameArabic, 'من مندوب:', replacement?.delegateLabel);
            } else if (mode === 'manual') {
                replacement = selectedReplacement;
                console.log('اختيار يدوي:', replacement?.NameArabic, 'من مندوب:', replacement?.delegateLabel);
            }
    
            // ✅ فحص شامل للبديل قبل المتابعة
            if (replacement) {
                const delegateId = parseInt(replacement.DelegateID);
                const passportId = parseInt(replacement.PassportID);
                
                console.log('=== فحص بيانات البديل ===');
                console.log('البديل:', replacement.NameArabic);
                console.log('DelegateID من البديل:', replacement.DelegateID);
                console.log('DelegateID بعد parseInt:', delegateId);
                console.log('PassportID:', passportId);
                
                if (!delegateId || isNaN(delegateId)) {
                    console.error('❌ البديل لا يحتوي على DelegateID صحيح:', replacement);
                    
                    // ✅ محاولة إصلاح DelegateID من قاعدة البيانات
                    try {
                        console.log('محاولة جلب DelegateID من قاعدة البيانات...');
                        const fixResponse = await fetch(`${config.API_BASE_URL}/api/passports/${passportId}`);
                        if (fixResponse.ok) {
                            const passportData = await fixResponse.json();
                            if (passportData.DelegateID) {
                                replacement.DelegateID = passportData.DelegateID;
                                console.log('✅ تم إصلاح DelegateID:', passportData.DelegateID);
                            } else {
                                throw new Error(`البديل "${replacement.NameArabic}" لا يحتوي على مندوب في قاعدة البيانات`);
                            }
                        } else {
                            throw new Error(`فشل في جلب بيانات البديل من قاعدة البيانات`);
                        }
                    } catch (fixError) {
                        console.error('فشل في إصلاح DelegateID:', fixError);
                        showAlert(`❌ البديل "${replacement.NameArabic}" لا يحتوي على مندوب صحيح`, 'error');
                        return;
                    }
                }
                
                if (!passportId || isNaN(passportId)) {
                    console.error('❌ البديل لا يحتوي على PassportID صحيح:', replacement);
                    showAlert(`❌ البديل "${replacement.NameArabic}" لا يحتوي على معرف صحيح`, 'error');
                    return;
                }
            }
    
            // تحذير لو البديل موجود في قائمة أخرى قبل استبداله فعلياً
            if (replacement) {
                const canProceedReplacement = await checkOtherListMembershipsBeforeAdding([replacement], 'إضافة البديل');
                if (!canProceedReplacement) return;
            }

            // تنفيذ الاستبعاد إذا كان مطلوباً
            if (type === 'permanent' || type === 'special') {
                await excludePassport(selectedPassportForReplacement.PassportID, type, reason);
            }
    
            // إزالة من القائمة الحالية
            setSelectedPassports(prev => prev.filter(p => p.PassportID !== selectedPassportForReplacement.PassportID));
    
            // ✅ إضافة البديل مع ضمان البيانات الصحيحة
            if (replacement) {
                const isFromSameDelegate = replacement.delegateType === 'same';
                const delegateInfo = delegates.find(d => d.DelegateID === parseInt(replacement.DelegateID));
                
                console.log('✅ إضافة البديل مع بيانات كاملة:', {
                    name: replacement.NameArabic,
                    passportId: replacement.PassportID,
                    delegateId: replacement.DelegateID,
                    delegateName: delegateInfo?.DelegateName || replacement.DelegateName
                });
                
                const newPassport = {
                    ...replacement,
                    status: 'pending',
                    confirmed: false,
                    selectedAt: new Date().toISOString(),
                    replacementFor: selectedPassportForReplacement.PassportID,
                    notes: `بديل عن ${selectedPassportForReplacement.NameArabic}${isFromSameDelegate ? ' (نفس المندوب)' : ` (من مندوب: ${delegateInfo?.DelegateName})`}`,
                    
                    // ✅ ضمان وجود كل البيانات المطلوبة
                    DelegateID: parseInt(replacement.DelegateID), // ✅ مهم جداً
                    CompanyID: parseInt(replacement.CompanyID || selectionSettings.companyId),
                    PassportID: parseInt(replacement.PassportID),
                    
                    // بيانات إضافية
                    DelegateName: delegateInfo?.DelegateName || replacement.DelegateName,
                    CompanyName: replacement.CompanyName,
                    PassportProfessionName: replacement.PassportProfessionName,
                    NameArabic: replacement.NameArabic,
                    NameEnglish: replacement.NameEnglish,
                    PassportNumber: replacement.PassportNumber,
                    PhoneNumbers: replacement.PhoneNumbers
                };
                
                // ✅ فحص نهائي قبل الإضافة
                if (!newPassport.DelegateID || !newPassport.PassportID) {
                    console.error('❌ البيانات النهائية للبديل غير صحيحة:', newPassport);
                    showAlert('❌ فشل في إعداد بيانات البديل', 'error');
                    return;
                }
                
                setSelectedPassports(prev => [...prev, newPassport]);
                
                const delegateNote = isFromSameDelegate ? 'من نفس المندوب' : `من مندوب: ${delegateInfo?.DelegateName}`;
                showAlert(`تم استبدال "${selectedPassportForReplacement.NameArabic}" بـ "${replacement.NameArabic}" (${delegateNote})`, 'success');
            } else {
                showAlert(`تم إزالة "${selectedPassportForReplacement.NameArabic}" - لا يوجد بديل متاح`, 'warning');
            }
    
            // ✅ انتظار أطول للتأكد من تحديث الـ state
            setTimeout(async () => {
                try {
                    console.log('=== بدء الحفظ التلقائي بعد الاستبدال ===');
                    
                    // ✅ فحص إضافي للبيانات قبل الحفظ
                    const currentPassports = selectedPassports.length > 0 ? selectedPassports : [];
                    console.log('عدد الجوازات الحالي قبل الحفظ:', currentPassports.length);
                    
                    if (currentPassports.length === 0) {
                        console.log('⚠️ لا توجد جوازات للحفظ - إنهاء العملية');
                        return;
                    }
                    
                    await autoSaveCurrentList();
                    console.log('✅ تم الحفظ التلقائي بعد الاستبدال');
                } catch (saveError) {
                    console.error('❌ خطأ في الحفظ التلقائي بعد الاستبدال:', saveError);
                    showAlert('تم الاستبدال لكن فشل الحفظ التلقائي - احفظ يدوياً', 'warning');
                }
            }, 1000); // ✅ انتظار أطول
    
            setShowReplacementModal(false);
            
        } catch (error) {
            console.error('خطأ في الاستبدال:', error);
            showAlert('حدث خطأ أثناء الاستبدال: ' + error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // استبعاد الجواز
    const excludePassport = async (passportId, exclusionType, reason) => {
        const response = await fetch(`${config.API_BASE_URL}/api/passports/${passportId}/exclude`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                exclusionType: exclusionType === 'permanent' ? 'استبدال نت' : 'أسباب خاصة',
                reason: reason || 'أسباب خاصة',
                notes: `استبعاد من قائمة النت - ${new Date().toLocaleString('ar-EG')}`
            })
        });

        if (!response.ok) {
            throw new Error('فشل في الاستبعاد');
        }

        // تحديث قائمة المستبعدين
        await fetchExcludedPassports(selectionSettings.companyId);
    };

    const removeFromList = async (passportId) => {
        const passport = selectedPassports.find(p => p.PassportID === passportId);
        if (!passport) return;
        
        // فحص إمكانية التعديل
        const modifyCheck = canModifyPassport(passport);
        if (!modifyCheck.allowed) {
            showAlert(modifyCheck.reason, 'warning');
            return;
        }

        const hasNetOrConfirmed = passport.confirmed === true ||
            passport.status === 'confirmed' ||
            !!passport.NetWorkDate ||
            !!passport.netDate;

        const confirmText = hasNetOrConfirmed
            ? `هل تريد حذف "${passport.NameArabic}" من القائمة؟

سيتم قبل الحذف مسح تاريخ النت/رقم الطلب/دخول القنصلية/التأشيرة/التأشيرة المحملة/السفر فقط، مع الاحتفاظ بالكشف الطبي والبصمة والتأمين الطبي وتسليم الأوراق.`
            : `هل تريد حذف "${passport.NameArabic}" من القائمة؟`;
        
        if (window.confirm(confirmText)) {
            try {
                setIsLoading(true);

                // ✅ حماية إضافية: لو الشخص مؤكد أو له تاريخ نت، نمسح الحقول المطلوبة أولاً
                // حتى لا يحدث حذف من القائمة مع بقاء تاريخ النت مسجل في الجواز/المتابعة.
                if (hasNetOrConfirmed) {
                    const clearResponse = await fetch(`${config.API_BASE_URL}/api/net-registrations/passports/${passportId}/clear-net-date`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' }
                    });

                    if (!clearResponse.ok) {
                        throw new Error('فشل مسح بيانات النت المحددة قبل حذف الشخص من القائمة');
                    }

                    if (currentList && currentList.ListID) {
                        const updateResponse = await fetch(`${config.API_BASE_URL}/api/net-registrations/update-confirmation`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                listId: currentList.ListID,
                                passportId: passportId,
                                isConfirmed: false,
                                netWorkDate: null
                            })
                        });

                        if (!updateResponse.ok) {
                            throw new Error('تم مسح بيانات النت لكن فشل تحديث حالة التأكيد داخل القائمة');
                        }
                    }
                }

                setSelectedPassports(prev => prev.filter(p => p.PassportID !== passportId));
                await autoSaveCurrentList();
                showAlert(
                    hasNetOrConfirmed
                        ? `تم حذف ${passport.NameArabic} من القائمة ومسح بيانات النت المحددة فقط`
                        : `تم حذف ${passport.NameArabic} من القائمة`,
                    'success'
                );
            } catch (error) {
                console.error('خطأ في حذف الشخص من القائمة:', error);
                showAlert(`❌ خطأ: ${error.message}`, 'error');
            } finally {
                setIsLoading(false);
            }
        }
    };

    // إعادة تفعيل المستبعد
    const reactivatePassport = async (exclusionId, passportName) => {
        if (!window.confirm(`هل أنت متأكد من إعادة تفعيل "${passportName}"؟\n\n⚠️ سيصبح متاحاً للاختيار في المرات القادمة فقط`)) {
            return;
        }

        try {
            const response = await fetch(`${config.API_BASE_URL}/api/passports/excluded/${exclusionId}/reactivate`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reactivatedBy: 'المستخدم',
                    notes: 'تم إعادة التفعيل - سيكون متاحاً للاختيار مستقبلاً'
                })
            });

            const result = await response.json();

            if (result.success) {
                setExcludedPassports(prev => prev.filter(excluded => excluded.ExclusionID !== exclusionId));
                showAlert(`تم إعادة تفعيل "${passportName}" بنجاح`, 'success');
            }

        } catch (error) {
            console.error('خطأ في إعادة التفعيل:', error);
            showAlert(`حدث خطأ أثناء إعادة التفعيل: ${error.message}`, 'error');
        }
    };

    // تأكيد الحضور
  // تأكيد الحضور
// ✅ دالة تأكيد الحضور مع تسجيل تاريخ النت
const confirmAttendance = async (passportId) => {
    try {
        console.log(`=== بدء تأكيد حضور الجواز ${passportId} مع تسجيل تاريخ النت ===`);
        
        const passport = selectedPassports.find(p => p.PassportID === passportId);
        if (!passport) {
            showAlert('❌ لم يتم العثور على بيانات الجواز', 'error');
            return;
        }

        // ✅ تاريخ النت = اليوم
        const netWorkDate = new Date().toISOString().split('T')[0];
        
        console.log(`تسجيل تاريخ النت: ${netWorkDate} للشخص: ${passport.NameArabic}`);
        
        // ✅ أولاً - تحديث جدول Passports مع تاريخ النت
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/passports/${passportId}/net-date`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                NetWorkDate: netWorkDate, // ✅ مع تاريخ النت
                confirmed: true,
                confirmedAt: new Date().toISOString(),
                listId: currentList?.ListID || null,
                listName: currentList?.ListName || null,
                requiredCount: parseInt(selectionSettings.requiredCount) || selectedPassports.length
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`فشل في تأكيد الحضور: ${errorText}`);
        }

        const result = await response.json();
        console.log('✅ تم تأكيد الحضور وتسجيل تاريخ النت:', result);

        // ✅ ثانياً - تحديث جدول NetSelectionDetails إذا كانت القائمة محفوظة
        if (currentList && currentList.ListID) {
            console.log('تحديث NetSelectionDetails للقائمة:', currentList.ListID);
            
            const updateResponse = await fetch(`${config.API_BASE_URL}/api/net-registrations/update-confirmation`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    listId: currentList.ListID,
                    passportId: passportId,
                    isConfirmed: true,
                    netWorkDate: netWorkDate // ✅ مع تاريخ النت
                })
            });

            if (updateResponse.ok) {
                const updateResult = await updateResponse.json();
                console.log('✅ تم تحديث NetSelectionDetails:', updateResult);
            } else {
                console.error('❌ فشل تحديث NetSelectionDetails');
            }
        }

        // ✅ ثالثاً - تحديث الـ state المحلي
        setSelectedPassports(prev => {
            const updated = prev.map(p => 
                p.PassportID === passportId 
                    ? { 
                        ...p, 
                        status: 'confirmed',
                        confirmed: true,
                        netDate: netWorkDate, // ✅ مع تاريخ النت
                        confirmedAt: new Date().toISOString()
                    }
                    : p
            );

            const updatedPassport = updated.find(p => p.PassportID === passportId);
            console.log(`✅ تم تحديث الـ state: ${updatedPassport?.NameArabic} - تاريخ النت: ${updatedPassport?.netDate}`);
            
            return updated;
        });

        // ✅ رابعاً - إرسال إشعار لقسم التأشيرات
        try {
            await fetch(`${config.API_BASE_URL}/api/visa-net-registration/notify-new-confirmation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    passportId: passportId,
                    passportName: passport?.NameArabic || 'غير محدد',
                    passportNumber: passport?.PassportNumber || '',
                    companyName: passport?.CompanyName || '',
                    delegateName: passport?.DelegateName || '',
                    professionName: passport?.PassportProfessionName || '',
                    phoneNumbers: getPhoneNumber(passport?.PhoneNumbers) || '',
                    netWorkDate: netWorkDate, // ✅ تاريخ النت
                    confirmedAt: new Date().toISOString(),
                    confirmedBy: 'قسم الجوازات',
                    listName: currentList?.ListName || 'قائمة غير محفوظة'
                })
            });
            console.log('✅ تم إرسال إشعار لقسم التأشيرات مع تاريخ النت');
        } catch (notifyError) {
            console.error('خطأ في إرسال الإشعار:', notifyError);
            // مش مهم لو فشل الإشعار
        }

        // ✅ خامساً - حفظ تلقائي نهائي
        if (currentList && currentList.ListID) {
            console.log('بدء الحفظ التلقائي المُحسّن بدل القديم...');
            try {
                await autoSaveCurrentList(); // يستخدم /auto-save-enhanced ويحفظ حقول الدفع
                console.log('✅ تم الحفظ التلقائي المُحسّن');
            } catch (e) {
                console.error('❌ فشل الحفظ التلقائي المُحسّن:', e);
            }
        }

        showAlert(`✅ تم تأكيد حضور "${passport?.NameArabic}" وتسجيل تاريخ النت: ${new Date(netWorkDate).toLocaleDateString('ar-EG')}`, 'success');
        
    } catch (error) {
        console.error('خطأ في تأكيد الحضور:', error);
        showAlert('❌ حدث خطأ أثناء تأكيد الحضور: ' + error.message, 'error');
    }
};

const cancelConfirmation = async (passportId) => {
    try {
        console.log(`=== فحص شامل قبل إلغاء التأكيد للجواز ${passportId} ===`);

        // ✅ فحص شامل من قاعدة البيانات أولاً
        const checkResponse = await fetch(`${config.API_BASE_URL}/api/net-registrations/check-visa-status/${passportId}`);
        
        if (!checkResponse.ok) {
            throw new Error('فشل في التحقق من حالة قسم التأشيرات');
        }

        const checkResult = await checkResponse.json();
        console.log('نتيجة الفحص:', checkResult);
        
        // ✅ إلغاء التأكيد أصبح مسموحاً دائماً
        // سيتم مسح: تاريخ النت، رقم الطلب، دخول القنصلية، التأشيرة، السفر، رقم الحدود فقط
        // وسيتم الاحتفاظ بباقي مراحل المتابعة مثل الكشف والبصمة والتأمين وتسليم الأوراق
        let warningMessage = '';

        if (checkResult.hasApplicationNumber && checkResult.applicationNumber) {
            warningMessage += `\n🧾 سيتم مسح رقم الطلب المسجل (${checkResult.applicationNumber})`;
        }
        
        if (checkResult.hasConsulateEntry && checkResult.consulateEntryDate) {
            const consulateDate = new Date(checkResult.consulateEntryDate).toLocaleDateString('ar-EG');
            warningMessage += `\n🏛️ سيتم مسح تاريخ دخول القنصلية (${consulateDate})`;
        }
        
        if (checkResult.hasVisa && checkResult.visaDate) {
            const visaDate = new Date(checkResult.visaDate).toLocaleDateString('ar-EG');
            warningMessage += `\n📄 سيتم مسح تاريخ التأشيرة (${visaDate})`;
        }
        
        if (checkResult.hasTravel && checkResult.travelDate) {
            const travelDate = new Date(checkResult.travelDate).toLocaleDateString('ar-EG');
            warningMessage += `\n✈️ سيتم مسح تاريخ السفر (${travelDate})`;
        }

        if (checkResult.hasBorderNumber && checkResult.borderNumber) {
            warningMessage += `\n🧾 سيتم مسح رقم الحدود (${checkResult.borderNumber})`;
        }
        
        if (checkResult.hasFingerprint && checkResult.fingerprintDate) {
            const fingerprintDate = new Date(checkResult.fingerprintDate).toLocaleDateString('ar-EG');
            warningMessage += `\n✅ سيتم الاحتفاظ بتاريخ البصمة (${fingerprintDate})`;
        }
        
        if (warningMessage) {
            warningMessage += `\n\nسيتم الاحتفاظ بالكشف الطبي والبصمة والتأمين الطبي وتسليم الأوراق وأي بيانات أخرى غير الحقول المطلوب مسحها.`;
        }
        
        console.log(`✅ مسموح بإلغاء التأكيد للجواز ${passportId} مع مسح الحقول المحددة فقط`);
        
        if (warningMessage) {
            const passport = selectedPassports.find(p => p.PassportID === passportId);
            const personName = passport?.NameArabic || 'غير محدد';
            
            const proceedConfirm = window.confirm(
                `${personName}${warningMessage}\n\n` +
                `هل تريد المتابعة مع إلغاء التأكيد؟\n\n` +
                `✅ نعم - إلغاء التأكيد ومسح الحقول المحددة فقط\n` +
                `❌ لا - إلغاء العملية`
            );
            
            if (!proceedConfirm) {
                console.log('المستخدم ألغى العملية');
                return;
            }
        }
        
        // ✅ المتابعة مع إلغاء التأكيد
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/passports/${passportId}/clear-net-date`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            throw new Error('فشل في إزالة تاريخ النت');
        }

        console.log('✅ تم مسح تاريخ النت من جدول Passports');

        // تحديث NetSelectionDetails
        if (currentList && currentList.ListID) {
            const updateResponse = await fetch(`${config.API_BASE_URL}/api/net-registrations/update-confirmation`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    listId: currentList.ListID,
                    passportId: passportId,
                    isConfirmed: false,
                    netWorkDate: null
                })
            });

            if (updateResponse.ok) {
                console.log('✅ تم إلغاء التأكيد في NetSelectionDetails');
            }
        }

        // تحديث الـ state المحلي
        setSelectedPassports(prev => {
            const updated = prev.map(p => 
                p.PassportID === passportId 
                    ? { 
                        ...p, 
                        status: 'pending', 
                        confirmed: false,
                        netDate: null,
                        confirmedAt: null,
                        NetWorkDate: null,
                        ApplicationNumber: null,
                        ConsulateEntryDate: null,
                        VisaDate: null,
                        VisaFilePath: null,
                        TravelDate: null,
                        BorderNumber: null
                    }
                    : p
            );

            const updatedPassport = updated.find(p => p.PassportID === passportId);
            console.log(`✅ تم إلغاء التأكيد: ${updatedPassport?.NameArabic}`);
            
            return updated;
        });

        const passport = selectedPassports.find(p => p.PassportID === passportId);
        showAlert(`✅ تم إلغاء تأكيد "${passport?.NameArabic}" ومسح تاريخ النت/رقم الطلب/القنصلية/التأشيرة/التأشيرة المحملة/السفر/رقم الحدود فقط`, 'success');
        
    } catch (error) {
        console.error('خطأ في إلغاء التأكيد:', error);
        showAlert(`❌ خطأ: ${error.message}`, 'error');
    }
};


const confirmAllAttendance = async () => {
    try {
        // فلترة الأشخاص الذين لم يتم تأكيدهم بعد
        const pendingPassports = selectedPassports.filter(p => 
            p.status === 'pending' && !p.confirmed
        );
        
        if (pendingPassports.length === 0) {
            showAlert('⚠️ لا يوجد أشخاص في الانتظار للتأكيد', 'warning');
            return;
        }

        // رسالة تأكيد
        const confirmed = window.confirm(
            `هل تريد تأكيد حضور جميع الأشخاص؟\n\n` +
            `عدد الأشخاص: ${pendingPassports.length} شخص\n` +
            `سيتم:\n` +
            `- تأكيد حضورهم\n` +
            `- تسجيل تاريخ النت لهم\n` +
            `- إضافتهم للمتابعة الدورية\n\n` +
            `هل أنت متأكد؟`
        );

        if (!confirmed) {
            return;
        }

        setIsLoading(true);
        showAlert(`⏳ جاري تأكيد ${pendingPassports.length} شخص...`, 'info');

        let successCount = 0;
        let failCount = 0;

        // تأكيد كل شخص على حدة
        for (const passport of pendingPassports) {
            try {
                const netWorkDate = new Date().toISOString().split('T')[0];
                
                // تحديث جدول Passports
                const response = await fetch(
                    `${config.API_BASE_URL}/api/net-registrations/passports/${passport.PassportID}/net-date`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            NetWorkDate: netWorkDate,
                            confirmed: true,
                            confirmedAt: new Date().toISOString()
                        })
                    }
                );

                if (response.ok) {
                    // تحديث NetSelectionDetails إذا كانت القائمة محفوظة
                    if (currentList && currentList.ListID) {
                        await fetch(
                            `${config.API_BASE_URL}/api/net-registrations/update-confirmation`,
                            {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    listId: currentList.ListID,
                                    passportId: passport.PassportID,
                                    isConfirmed: true,
                                    netWorkDate: netWorkDate
                                })
                            }
                        );
                    }

                    // إرسال إشعار لقسم التأشيرات
                    await fetch(
                        `${config.API_BASE_URL}/api/visa-net-registration/notify-new-confirmation`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                passportId: passport.PassportID,
                                passportName: passport?.NameArabic || 'غير محدد',
                                passportNumber: passport?.PassportNumber || '',
                                companyName: passport?.CompanyName || '',
                                delegateName: passport?.DelegateName || '',
                                professionName: passport?.PassportProfessionName || '',
                                phoneNumbers: getPhoneNumber(passport?.PhoneNumbers) || '',
                                netWorkDate: netWorkDate,
                                confirmedAt: new Date().toISOString(),
                                confirmedBy: 'قسم الجوازات - تأكيد جماعي',
                                listName: currentList?.ListName || 'قائمة غير محفوظة'
                            })
                        }
                    );

                    successCount++;
                } else {
                    failCount++;
                    console.error(`فشل تأكيد ${passport.NameArabic}`);
                }
            } catch (error) {
                failCount++;
                console.error(`خطأ في تأكيد ${passport.NameArabic}:`, error);
            }
        }

        // تحديث الـ state المحلي
        setSelectedPassports(prev => prev.map(p => {
            if (pendingPassports.find(pp => pp.PassportID === p.PassportID)) {
                return {
                    ...p,
                    status: 'confirmed',
                    confirmed: true,
                    netDate: new Date().toISOString().split('T')[0],
                    confirmedAt: new Date().toISOString()
                };
            }
            return p;
        }));

        // حفظ تلقائي
        if (currentList && currentList.ListID) {
            try {
                await autoSaveCurrentList();
            } catch (e) {
                console.error('فشل الحفظ التلقائي:', e);
            }
        }

        // عرض النتيجة
        if (successCount > 0) {
            showAlert(
                `✅ تم تأكيد حضور ${successCount} شخص بنجاح` +
                (failCount > 0 ? `\n⚠️ فشل تأكيد ${failCount} شخص` : ''),
                failCount > 0 ? 'warning' : 'success'
            );
        } else {
            showAlert('❌ فشل تأكيد جميع الأشخاص', 'error');
        }

    } catch (error) {
        console.error('خطأ في تأكيد الكل:', error);
        showAlert('❌ حدث خطأ: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};

// ✅ دالة إلغاء حضور الكل - النسخة النهائية المصححة
// (بدون استدعاء autoSaveCurrentList الذي يسبب المشكلة)

const cancelAllConfirmation = async () => {
    try {
        // فلترة الأشخاص المؤكدين
        const confirmedPassports = selectedPassports.filter(p => 
            p.confirmed === true || p.status === 'confirmed'
        );
        
        if (confirmedPassports.length === 0) {
            showAlert('⚠️ لا يوجد أشخاص مؤكدين لإلغاء تأكيدهم', 'warning');
            return;
        }

        // توليد رقم تأكيد عشوائي
        const confirmationNumber = Math.floor(100000 + Math.random() * 900000);
        
        const userInput = prompt(
            `⚠️ تحذير: إلغاء تأكيد جماعي

` +
            `أنت على وشك إلغاء تأكيد ${confirmedPassports.length} شخص.

` +
            `سيتم مسح الحقول المحددة فقط:
` +
            `- تاريخ النت
` +
            `- رقم الطلب
` +
            `- دخول القنصلية
` +
            `- تاريخ التأشيرة والتأشيرة المحملة
` +
            `- السفر
` +
            `- رقم الحدود

` +
            `وسيتم الاحتفاظ بالكشف الطبي والبصمة والتأمين الطبي وتسليم الأوراق.

` +
            `للتأكيد، اكتب الرقم التالي:

${confirmationNumber}`
        );

        if (!userInput) {
            return; // ألغى المستخدم
        }

        if (userInput !== confirmationNumber.toString()) {
            showAlert('❌ رقم التأكيد غير صحيح. لم يتم الإلغاء.', 'error');
            return;
        }

        setIsLoading(true);
        showAlert(`⏳ جاري فحص وإلغاء تأكيد ${confirmedPassports.length} شخص...`, 'info');

        let successCount = 0;
        let failCount = 0;
        let blockedCount = 0; // متروك للتوافق فقط - لا يوجد منع الآن
        const successfullyCleared = []; // تتبع المعرفات التي تم إلغاؤها بنجاح

        for (const passport of confirmedPassports) {
            try {
                console.log(`معالجة: ${passport.NameArabic} (ID: ${passport.PassportID})`);
                
                // فحص حالة التأشيرات أولاً
                const checkResponse = await fetch(
                    `${config.API_BASE_URL}/api/net-registrations/check-visa-status/${passport.PassportID}`
                );
                
                if (!checkResponse.ok) {
                    failCount++;
                    console.error(`فشل فحص ${passport.NameArabic}`);
                    continue;
                }

                const checkResult = await checkResponse.json();

                // ✅ لا يتم تخطي من لديهم تأشيرة أو سفر؛ سيتم مسح هذه الحقول المحددة فقط
                if (checkResult.hasVisa || checkResult.hasVisaFile || checkResult.hasTravel || checkResult.hasApplicationNumber || checkResult.hasConsulateEntry) {
                    console.log(`ℹ️ ${passport.NameArabic}: سيتم مسح بيانات النت المحددة فقط والاحتفاظ بباقي المتابعة`);
                }

                // ✅ استخدام الـ endpoint الصحيح: clear-net-date
                const response = await fetch(
                    `${config.API_BASE_URL}/api/net-registrations/passports/${passport.PassportID}/clear-net-date`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' }
                    }
                );

                if (response.ok) {
                    console.log(`✅ نجح إلغاء ${passport.NameArabic} في Passports`);
                    
                    // تحديث NetSelectionDetails إذا كانت القائمة محفوظة
                    if (currentList && currentList.ListID) {
                        const updateResponse = await fetch(
                            `${config.API_BASE_URL}/api/net-registrations/update-confirmation`,
                            {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    listId: currentList.ListID,
                                    passportId: passport.PassportID,
                                    isConfirmed: false,
                                    netWorkDate: null
                                })
                            }
                        );
                        
                        if (updateResponse.ok) {
                            console.log(`✅ نجح إلغاء ${passport.NameArabic} في NetSelectionDetails`);
                        }
                    }

                    successCount++;
                    successfullyCleared.push(passport.PassportID);
                } else {
                    const errorText = await response.text();
                    failCount++;
                    console.error(`❌ فشل إلغاء ${passport.NameArabic}: ${errorText}`);
                }
            } catch (error) {
                failCount++;
                console.error(`خطأ في معالجة ${passport.NameArabic}:`, error);
            }
        }

        console.log(`النتائج: نجح=${successCount}, فشل=${failCount}, تم تخطي=${blockedCount}`);

        // ✅ تحديث الـ state المحلي - فقط للأشخاص الذين تم إلغاؤهم بنجاح
        setSelectedPassports(prev => prev.map(p => {
            if (successfullyCleared.includes(p.PassportID)) {
                console.log(`تحديث state: ${p.NameArabic} -> pending`);
                return {
                    ...p,
                    status: 'pending',
                    confirmed: false,
                    netDate: null,
                    confirmedAt: null,
                    NetWorkDate: null,
                    ApplicationNumber: null,
                    ConsulateEntryDate: null,
                    VisaDate: null,
                    VisaFilePath: null,
                    TravelDate: null,
                    BorderNumber: null
                };
            }
            return p;
        }));

        // ⚠️ تم إزالة autoSaveCurrentList من هنا!
        // لأنه يعيد حفظ البيانات فوق التغييرات المحفوظة في الداتابيز
        // الإلغاء محفوظ بالفعل عن طريق clear-net-date و update-confirmation
        
        console.log('✅ الإلغاء محفوظ في الداتابيز مباشرة - لا حاجة لحفظ تلقائي');

        // عرض النتيجة النهائية
        let message = '';
        if (successCount > 0) message += `✅ تم إلغاء تأكيد ${successCount} شخص بنجاح\n`;
        if (blockedCount > 0) message += `ℹ️ ${blockedCount} شخص تم تخطيهم\n`;
        if (failCount > 0) message += `⚠️ فشل إلغاء ${failCount} شخص`;

        showAlert(
            message || '❌ لم يتم إلغاء أي تأكيد',
            successCount > 0 ? 'success' : 'warning'
        );

    } catch (error) {
        console.error('خطأ عام في إلغاء الكل:', error);
        showAlert('❌ حدث خطأ: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};


const autoSaveCurrentList = async () => {
    if (selectedPassports.length === 0 || !currentList) return;

    try {
        console.log('=== بدء الحفظ التلقائي المحسن ===');
        console.log('القائمة:', currentList.ListID);
        console.log('عدد الجوازات:', selectedPassports.length);
        
        // ✅ فحص شامل لكل جواز قبل الحفظ
        const validatedPassports = [];
        
        for (let i = 0; i < selectedPassports.length; i++) {
            const p = selectedPassports[i];
            const isConfirmed = p.status === 'confirmed' && p.confirmed === true;
            
            // ✅ فحص PassportID
            const passportId = parseInt(p.PassportID);
            if (!passportId || isNaN(passportId)) {
                console.error(`❌ خطأ في PassportID للجواز ${i + 1}:`, p);
                throw new Error(`الجواز "${p.NameArabic}" لا يحتوي على معرف صحيح`);
            }
            
            // ✅ فحص DelegateID - هذا الأهم
            const delegateId = parseInt(p.DelegateID);
            if (!delegateId || isNaN(delegateId)) {
                console.error(`❌ خطأ في DelegateID للجواز ${i + 1}:`, {
                    PassportID: p.PassportID,
                    NameArabic: p.NameArabic,
                    DelegateID: p.DelegateID,
                    DelegateName: p.DelegateName,
                    fullObject: p
                });
                throw new Error(`الجواز "${p.NameArabic}" لا يحتوي على مندوب صحيح (DelegateID: ${p.DelegateID})`);
            }
            
            console.log(`✅ جواز ${i + 1} صحيح: ${p.NameArabic} - PassportID=${passportId}, DelegateID=${delegateId}, confirmed=${isConfirmed}`);
            
            validatedPassports.push({
                PassportID: passportId,
                DelegateID: delegateId,
                confirmed: isConfirmed,
                NetWorkDate: isConfirmed ? p.netDate : null,
                
                // ✅ إضافة بيانات الدفع المحسنة
                PaidConfirmed: p.paidConfirmed || false,
                PaidAmount: p.paidAmount || null,
                PaidNotes: p.paidNotes || null,
                PaidConfirmedAt: p.paidConfirmed ? (p.paidAt || new Date().toISOString()) : null
            });
        }

        const confirmedCount = validatedPassports.filter(p => p.confirmed === true).length;
        const paidCount = validatedPassports.filter(p => p.PaidConfirmed === true).length;
        console.log(`سيتم حفظ ${confirmedCount} مؤكد من أصل ${validatedPassports.length}`);
        console.log(`عدد المدفوعات المؤكدة: ${paidCount}`);

        // ✅ استخدام النقطة المحسنة التي تدعم بيانات الدفع
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/auto-save-enhanced`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                listId: currentList.ListID,
                passports: validatedPassports
            })
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ فشل الحفظ التلقائي:', errorText);
            throw new Error(`فشل الحفظ التلقائي: ${errorText}`);
        }
        
        const result = await response.json();
        if (result.success) {
            console.log('✅ تم الحفظ التلقائي بنجاح:', result);
            if (result.savedCount) {
                console.log(`تم حفظ ${result.savedCount} جواز بنجاح`);
            }
            if (result.paymentCount) {
                console.log(`تم حفظ ${result.paymentCount} دفعة بنجاح`);
            }
        } else {
            throw new Error(result.error || result.details || 'فشل الحفظ التلقائي');
        }
        
    } catch (error) {
        console.error('خطأ في الحفظ التلقائي:', error);
        showAlert('تحذير: قد يكون هناك مشكلة في الحفظ التلقائي: ' + error.message, 'warning');
        throw error; // ✅ إعادة رمي الخطأ ليتعامل معه المستدعي
    }
};

    // باقي الدوال (حفظ القائمة، تحميل القائمة، إلخ...)
    // [باقي الكود مشابه للكود السابق مع التحسينات المطلوبة]

    // أضف هذه الدالة المحسنة
const saveCurrentListImproved = async () => {
    try {
        // التحقق من صحة البيانات
        if (selectedPassports.length === 0) {
            showAlert('لا توجد أشخاص في القائمة للحفظ', 'warning');
            return;
        }

        if (!selectionSettings.companyId) {
            showAlert('يجب اختيار الشركة قبل الحفظ', 'warning');
            return;
        }

        const listName = prompt('اسم القائمة:', currentList?.ListName || `قائمة ${new Date().toLocaleDateString('ar-EG')}`);
        if (!listName || listName.trim() === '') return;

        setIsLoading(true);
        showAlert('جاري حفظ القائمة...', 'info');

        // تحضير البيانات
        const saveData = {
            companyId: parseInt(selectionSettings.companyId),
            listName: listName.trim(),
            requiredCount: parseInt(selectionSettings.requiredCount) || selectedPassports.length,
            selectionDate: selectionSettings.date,
            passports: selectedPassports.map(p => ({
                PassportID: p.PassportID,
                DelegateID: p.DelegateID,
                confirmed: p.status === 'confirmed',
                NetWorkDate: p.status === 'confirmed' ? (p.netDate || new Date().toISOString().split('T')[0]) : null
            })),
            notes: selectionSettings.notes || '',
            updateExisting: !!currentList,
            listId: currentList?.ListID
        };

        const canProceedSave = await checkOtherListMembershipsBeforeAdding(saveData.passports, currentList ? 'تحديث القائمة' : 'حفظ القائمة');
        if (!canProceedSave) return;

        console.log('بيانات الحفظ:', saveData);

        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/save-list`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(saveData)
        });

        console.log('Response status:', response.status);
        
        const result = await response.json().catch(() => null);

        if (!response.ok) {
            throw new Error(result?.error || result?.details || `خطأ من السيرفر (${response.status})`);
        }

        if (!result) {
            throw new Error('استجابة غير صحيحة من السيرفر');
        }

        console.log('نتيجة الحفظ:', result);

        if (result.success) {
            setCurrentList({ ListID: result.listId, ListName: listName });
            await fetchSavedLists(); // انتظار تحديث القوائم
            showAlert(`تم حفظ القائمة "${listName}" بنجاح`, 'success');
        } else {
            throw new Error(result.message || result.error || 'فشل في حفظ القائمة');
        }

    } catch (error) {
        console.error('خطأ في حفظ القائمة:', error);
        showAlert(`خطأ في حفظ القائمة: ${error.message}`, 'error');
    } finally {
        setIsLoading(false);
    }
};


// ✅ تحديث دالة saveCurrentList
// ✅ تحديث دالة saveCurrentList لاستقبال اسم جاهز من المودال
const saveCurrentList = async (preparedListName = null) => {
    try {
        if (selectedPassports.length === 0) {
            showAlert('لا توجد أشخاص في القائمة للحفظ', 'warning');
            return;
        }

        if (!selectionSettings.companyId) {
            showAlert('يجب اختيار الشركة قبل الحفظ', 'warning');
            return;
        }

        // ✅ تحديد نوع العملية بوضوح
        const isNewList = !currentList || !currentList.ListID;
        const defaultName = isNewList 
            ? `قائمة جديدة ${new Date().toLocaleDateString('ar-EG')}` 
            : currentList.ListName;

        // لو جالي اسم جاهز من المودال أستخدمه، وإلا أرجع للـ prompt القديم (احتياطي)
        let listName = preparedListName;

        if (!listName) {
            listName = prompt(
                isNewList ? 'اسم القائمة الجديدة:' : 'تحديث اسم القائمة:',
                defaultName
            );
        }

        if (!listName || listName.trim() === '') return;

        setIsLoading(true);
        showAlert(isNewList ? 'جاري إنشاء قائمة جديدة.' : 'جاري تحديث القائمة.', 'info');

        // ❗ باقي دالة saveCurrentList يظل كما هو (الجزء اللي بيعمل map على selectedPassports
        // ويجهز saveData ثم يستدعي الـ API لا تغيره)


        // ✅ التحقق من صحة البيانات
        const validatedPassports = selectedPassports.map((p, index) => {
            const isReallyConfirmed = p.status === 'confirmed' && p.confirmed === true;
            
            const delegateId = parseInt(p.DelegateID);
            if (!delegateId || isNaN(delegateId)) {
                throw new Error(`الجواز ${p.NameArabic} لا يحتوي على مندوب صحيح`);
            }
            
            const passportId = parseInt(p.PassportID);
            if (!passportId || isNaN(passportId)) {
                throw new Error(`معرف الجواز غير صحيح للشخص ${p.NameArabic}`);
            }
            
            return {
                PassportID: passportId,
                DelegateID: delegateId,
                confirmed: isReallyConfirmed,
                NetWorkDate: isReallyConfirmed ? p.netDate : null,
                NameArabic: p.NameArabic || '',
                NameEnglish: p.NameEnglish || '',
                PassportNumber: p.PassportNumber || '',
                NationalID: p.NationalID || '',
                CompanyName: p.CompanyName || '',
                DelegateName: p.DelegateName || '',
                PhoneNumbers: p.PhoneNumbers || '',
                Notes: p.Notes || '',
                SelectionOrder: index + 1,
                selectedAt: p.selectedAt || new Date().toISOString(),
                confirmedAt: isReallyConfirmed ? (p.confirmedAt || new Date().toISOString()) : null,
                replacementFor: p.replacementFor || null,
                replacementNotes: p.replacementNotes || null
            };
        });

        const canProceedSave = await checkOtherListMembershipsBeforeAdding(validatedPassports, isNewList ? 'حفظ القائمة الجديدة' : 'تحديث القائمة');
        if (!canProceedSave) return;

        const saveData = {
            companyId: parseInt(selectionSettings.companyId),
            listName: listName.trim(),
            requiredCount: parseInt(selectionSettings.requiredCount) || selectedPassports.length,
            selectionDate: selectionSettings.date,
            passports: validatedPassports,
            notes: selectionSettings.notes || '',
            // ✅ التحكم الواضح في نوع العملية
            updateExisting: !isNewList,
            listId: isNewList ? null : currentList.ListID,
            forceNewList: isNewList // ✅ إجبار إنشاء قائمة جديدة
        };

        console.log('=== بيانات الحفظ ===');
        console.log('نوع العملية:', isNewList ? 'قائمة جديدة' : 'تحديث قائمة موجودة');
        console.log('معرف القائمة:', saveData.listId);
        console.log('عدد الجوازات:', saveData.passports.length);

        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/save-list`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(saveData)
        });

        const result = await response.json().catch(() => null);

        if (!response.ok) {
            throw new Error(result?.error || result?.details || `خطأ من السيرفر (${response.status})`);
        }

        if (!result) {
            throw new Error('استجابة غير صحيحة من السيرفر');
        }

        if (result.success) {
            // ✅ تحديث currentList بالمعرف الجديد
            setCurrentList({ 
                ListID: result.listId, 
                ListName: listName,
                isNew: isNewList
            });
            
            await fetchSavedLists();
            showAlert(
                isNewList 
                    ? `✅ تم إنشاء القائمة الجديدة "${listName}" بنجاح` 
                    : `✅ تم تحديث القائمة "${listName}" بنجاح`, 
                'success'
            );
            
            console.log('✅ تم الحفظ بنجاح:', { listId: result.listId, isNew: isNewList });
        } else {
            throw new Error(result.message || result.error || 'فشل في حفظ القائمة');
        }

    } catch (error) {
        console.error('خطأ في حفظ القائمة:', error);
        showAlert(`❌ خطأ في الحفظ: ${error.message}`, 'error');
    } finally {
        setIsLoading(false);
    }
};



    const validateSaveData = () => {
        const errors = [];
        
        if (!selectionSettings.companyId) errors.push('يجب اختيار الشركة');
        if (!selectionSettings.requiredCount) errors.push('يجب إدخال العدد المطلوب');
        if (!selectionSettings.date) errors.push('يجب إدخال تاريخ عمل النت');
        if (selectedPassports.length === 0) errors.push('لا توجد جوازات مختارة للحفظ');
        
        // تحقق من أن كل جواز له البيانات المطلوبة
        const invalidPassports = selectedPassports.filter(p => 
            !p.PassportID || !p.DelegateID
        );
        
        if (invalidPassports.length > 0) {
            errors.push(`يوجد ${invalidPassports.length} جواز بدون بيانات صحيحة`);
        }
        
        return errors;
    };

    
const loadSavedList = async (listId) => {
    try {
        setIsLoading(true);
        
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${listId}`);
        const listDetails = await response.json();
        
        if (listDetails.success) {
            const { list, passports } = listDetails;
            
            console.log('=== فحص البيانات المحملة ===');
            
            const formattedPassports = passports.map(p => {
                const isConfirmed = p.FinalConfirmationStatus === 1;
                
                console.log(`تحويل ${p.NameArabic}: DelegateID=${p.DelegateID}, PaidConfirmed=${p.PaidConfirmed}`);
                
                return {
                    ...p,
                    status: isConfirmed ? 'confirmed' : 'pending',
                    confirmed: isConfirmed,
                    netDate: p.FinalNetWorkDate ? p.FinalNetWorkDate.split('T')[0] : null,
                    confirmedAt: p.FinalConfirmedAt,
                    selectedAt: list.CreatedAt,
                    
                    // ✅ إضافة بيانات الدفع
                    paidConfirmed: p.PaidConfirmed === 1 || p.PaidConfirmed === true,
                    paidAmount: p.PaidAmount,
                    paidNotes: p.PaidNotes,
                    paidAt: p.PaidConfirmedAt,
                    
                    DelegateID: p.DelegateID
                };
            });

            setSelectedPassports(formattedPassports);
            setCurrentList(list);
            setActiveTab('management');
            
            showAlert(`تم تحميل القائمة "${list.ListName}" بنجاح`, 'success');
        }
    } catch (error) {
        console.error('خطأ في تحميل القائمة:', error);
        showAlert('خطأ في تحميل القائمة', 'error');
    } finally {
        setIsLoading(false);
    }
};

// ✅ تحديث دالة createNewList
const createNewList = () => {
    // ✅ تأكيد المستخدم إذا كان فيه قائمة نشطة
    if (selectedPassports.length > 0) {
        const confirmed = window.confirm(
            '⚠️ تحذير!\n\n' +
            'يوجد قائمة نشطة حالياً بها ' + selectedPassports.length + ' شخص\n\n' +
            'هل تريد إنشاء قائمة جديدة؟\n' +
            '(سيتم مسح القائمة الحالية من الذاكرة)\n\n' +
            '💡 نصيحة: احفظ القائمة الحالية أولاً قبل إنشاء قائمة جديدة'
        );
        
        if (!confirmed) return;
    }
    
    // ✅ مسح كل شيء وبدء جديد
    setCurrentList(null);
    setSelectedPassports([]);
    setDistributionPreview({});
    setShowPreview(false);
    setActiveTab('selection');
    
    // ✅ إعادة تعيين الإعدادات
    setSelectionSettings({
        companyId: '',
        professionId: '',
        requiredCount: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });
    
    // ✅ مسح الاختيار اليدوي
    setManualSelection({
        companyId: '',
        professionId: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        searchTerm: '',
        selectedIds: new Set(),
        availablePassports: [],
        isLoading: false,
        requiredCount: ''
    });
    
    showAlert('✅ تم إنشاء قائمة جديدة فارغة', 'success');
    console.log('✅ تم إنشاء قائمة جديدة');
};



  const fetchDelegatesFromList = async (listId) => {
    try {
        setLoadingDelegates(true);
        
        // ✅ المسار الصحيح
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${listId}`);
        const listDetails = await response.json();
        
        if (listDetails.success) {
                // تجميع الجوازات حسب المندوب
                const delegateGroups = {};
                
                listDetails.passports.forEach(passport => {
                    const delegateId = passport.DelegateID;
                    const delegate = delegates.find(d => d.DelegateID === delegateId);
                    
                    if (!delegateGroups[delegateId]) {
                        delegateGroups[delegateId] = {
                            delegateInfo: delegate,
                            passports: []
                        };
                    }
                    
                    delegateGroups[delegateId].passports.push({
                        ...passport,
                        confirmed: passport.FinalConfirmationStatus === 1,
                        netDate: passport.SavedNetWorkDate ? passport.SavedNetWorkDate.split('T')[0] : null
                    });
                });
                
                // تحويل إلى array
                const delegatesArray = Object.values(delegateGroups).map(group => ({
                    ...group,
                    totalCount: group.passports.length,
                    confirmedCount: group.passports.filter(p => p.confirmed).length,
                    pendingCount: group.passports.filter(p => !p.confirmed).length
                }));
                
                setDelegatesInSelectedList(delegatesArray);
                
                console.log('المناديب في القائمة:', delegatesArray);
            }
            
        } catch (error) {
            console.error('خطأ في جلب المناديب:', error);
            showAlert('خطأ في جلب بيانات المناديب', 'error');
        } finally {
            setLoadingDelegates(false);
        }
    };
    

    const getPassportProfession = (passport) => {
        if (passport.PassportProfessionName) {
            return passport.PassportProfessionName;
        }
        
        if (passport.ProfessionName) {
            return passport.ProfessionName;
        }
        
        if (passport.PassportProfessionID) {
            const profession = professions.find(p => p.ProfessionID === parseInt(passport.PassportProfessionID));
            if (profession) {
                return profession.ProfessionName;
            }
        }
        
        if (passport.ProfessionID) {
            const profession = professions.find(p => p.ProfessionID === parseInt(passport.ProfessionID));
            if (profession) {
                return profession.ProfessionName;
            }
        }
        
        return 'غير محدد';
    };


    // ID: الظاهر للمستخدم فقط. الربط الداخلي يظل دائماً بـ PassportID.
    const getPassportFileNumber = (passport) => {
        const candidates = [
            passport?.OfficeSerial,
            passport?.officeSerial,
            passport?.SerialNumber,
            passport?.serialNumber
        ];
        const value = candidates.find(item => item !== undefined && item !== null && String(item).trim() !== '');
        return value !== undefined ? String(value).trim() : (passport?.PassportID ? String(passport.PassportID) : 'غير محدد');
    };

    const getPassportCompanyName = (passport, fallbackCompanyName = '') => {
        if (passport?.CompanyName) return passport.CompanyName;
        if (fallbackCompanyName) return fallbackCompanyName;
        const companyId = passport?.CompanyID ?? passport?.companyId;
        const company = companies.find(c => String(c.CompanyID) === String(companyId));
        return company?.CompanyName || 'غير محدد';
    };


    const printPaymentStatus = (type = 'all') => {
        const currentDate = new Date().toLocaleDateString('ar-EG');
        const activePassports = getActivePassports(selectedPassports);
      
        let filteredPassports = activePassports;
        let title = 'تقرير حالة الدفع - الكل';
      
        if (type === 'paid') {
          filteredPassports = activePassports.filter(p => p.paidConfirmed);
          title = 'تقرير حالة الدفع - المدفوعين فقط';
        } else if (type === 'unpaid') {
          filteredPassports = activePassports.filter(p => !p.paidConfirmed);
          title = 'تقرير حالة الدفع - غير المدفوعين فقط';
        }
      
        // إحصائيات
        const paidCount = activePassports.filter(p => p.paidConfirmed).length;
        const unpaidCount = activePassports.filter(p => !p.paidConfirmed).length;
        const totalAmount = activePassports
          .filter(p => p.paidConfirmed)
          .reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);
      
        // ⚙️ إعدادات الطباعة الخاصة بتقرير الدفع (افتراضيًا true لو مش متعرفة فوق)
        const ps = (typeof printSettings === 'object' && printSettings)
        ? printSettings
        : {
            showPaymentDelegate: true,
            showPaymentAmount: true,
            showPaymentNotes: true,
            showPaymentStatus: true, // جديد
            showPaymentPhone:  true, // جديد
            showPaymentTitle:  true, // جديد
            showPaymentTotals: true
          };
      
      
      
        // رؤوس الأعمدة (ديناميكي)
        const headerCells = `
          <th style="width: 40px;">م</th>
          <th style="width: 150px;">الاسم</th>
          <th style="width: 100px;">الشركة</th>
          ${ps.showPaymentPhone  ? '<th style="width: 140px;">أرقام الهاتف</th>' : ''}

          ${ps.showPaymentDelegate ? '<th style="width: 100px;">المندوب</th>' : ''}
          ${ps.showPaymentStatus ? '<th style="width: 80px;">حالة الدفع</th>'  : ''}



          ${ps.showPaymentAmount ? '<th style="width: 80px;">المبلغ</th>' : ''}
          ${ps.showPaymentNotes ? '<th style="width: 120px;">ملاحظات الدفع</th>' : ''}
          <th style="width: 100px;">تاريخ التأكيد</th>
        `;
      
        // صفوف الجدول (ديناميكي)
        const rowsHtml = filteredPassports.map((passport, index) => {
          const isPaid = passport.paidConfirmed;
          const company = companies.find(c => c.CompanyID == passport.CompanyID);
          const delegate = delegates.find(d => d.DelegateID == passport.DelegateID);
          const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic) || [];
      
          const phonesHtml = phones.length > 0
            ? phones.map((phone, idx) =>
                `<div style="margin: 1px 0; padding: 3px; background: ${idx % 2 === 0 ? '#f9f9f9' : '#ffffff'}; border-radius: 3px; font-size: 11px;">
                    📱 ${phone}
                 </div>`
              ).join('')
            : '<div style="color: #666; font-style: italic;">غير متاح</div>';
      
          return `
            <tr>
              <td class="row-number">${index + 1}</td>
              <td style="text-align: right; font-weight: bold;">${passport.NameArabic || '-'}</td>
              <td>${company?.CompanyName || 'غير محدد'}</td>
              ${ps.showPaymentPhone
                ? '<td style="font-family: monospace; text-align: right; padding: 6px; vertical-align: top;">' + phonesHtml + '</td>'
                : ''
              }
              
              ${ps.showPaymentDelegate ? `<td>${delegate?.DelegateName || 'غير محدد'}</td>` : ''}
              ${
                ps.showPaymentStatus
                  ? `<td class="${isPaid ? 'paid-status' : 'unpaid-status'}">${isPaid ? '✅ مدفوع' : '⏳ لم يدفع'}</td>`
                  : ''
              }
              
              ${ps.showPaymentAmount
                ? `<td class="amount-cell">${isPaid && passport.paidAmount ? (parseFloat(passport.paidAmount).toLocaleString() + ' ج.م') : '-'}</td>`
                : ''
              }
              ${ps.showPaymentNotes
                ? `<td style="text-align: right; font-size: 11px;">${isPaid && passport.paidNotes ? passport.paidNotes : '-'}</td>`
                : ''
              }
              <td style="font-size: 11px;">${isPaid && passport.paidAt ? new Date(passport.paidAt).toLocaleDateString('ar-EG') : '-'}</td>
            </tr>
          `;
        }).join('');
      
        const printContent = `
          <html dir="rtl">
          <head>
           <title></title>

            <style>
              body { 
                  font-family: 'Arial Black', Arial, sans-serif; 
                  margin: 15px;
                  direction: rtl;
                  color: #000000 !important;
                  font-weight: bold;
              }
              .header { 
                  text-align: center; 
                  margin-bottom: 25px;
                  border: 3px solid #000000;
                  padding: 15px;
                  border-radius: 10px;
                  background-color: #ffffff;
              }
              .header h1 {
                  margin: 0 0 10px 0;
                  color: #000000 !important;
                  font-size: 24px;
                  font-weight: 900;
                  text-shadow: none;
              }
              .list-info {
                  background-color: #f8f8f8;
                  border: 2px solid #000000;
                  border-radius: 8px;
                  padding: 15px;
                  margin: 20px 0;
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 15px;
              }
              .info-item {
                  display: flex;
                  justify-content: space-between;
                  padding: 8px 0;
                  border-bottom: 2px dotted #000000;
                  font-weight: bold;
                  color: #000000 !important;
              }
              .info-item strong {
                  color: #000000 !important;
                  font-weight: 900;
              }
              .stats-summary {
                  background-color: #f0f0f0;
                  border: 3px solid #000000;
                  border-radius: 8px;
                  padding: 15px;
                  margin: 20px 0;
                  text-align: center;
                  font-weight: 900;
                  color: #000000 !important;
                  font-size: 14px;
              }
              table { 
                  width: 100%; 
                  border-collapse: collapse; 
                  margin: 20px 0;
                  font-size: 13px;
                  font-weight: bold;
              }
              th { 
                  background-color: #e0e0e0 !important;
                  color: #000000 !important;
                  border: 2px solid #000000;
                  padding: 12px 8px;
                  text-align: center;
                  font-weight: 900;
                  font-size: 14px;
              }
              td { 
                  border: 2px solid #000000; 
                  padding: 10px 8px;
                  text-align: center;
                  vertical-align: middle;
                  color: #000000 !important;
                  font-weight: bold;
                  background-color: #ffffff;
              }
              .row-number { 
                  background-color: #f0f0f0 !important;
                  font-weight: 900;
                  color: #000000 !important;
                  font-size: 14px;
              }
              .paid-status { 
                  background-color: #ffffff !important;
                  color: #000000 !important;
                  font-weight: 900;
                  border: 2px solid #000000 !important;
              }
              .unpaid-status { 
                  background-color: #ffffff !important;
                  color: #000000 !important;
                  font-weight: 900;
                  border: 2px solid #000000 !important;
              }
              .amount-cell {
                  font-weight: 900;
                  color: #000000 !important;
                  font-size: 13px;
              }
              .footer {
                  margin-top: 30px;
                  text-align: center;
                  color: #000000 !important;
                  border-top: 3px solid #000000;
                  padding-top: 15px;
                  font-size: 12px;
                  font-weight: bold;
              }
              @media print {
                  body { 
                      margin: 10px;
                      color: #000000 !important;
                      -webkit-print-color-adjust: exact;
                      print-color-adjust: exact;
                  }
                  .no-print { display: none; }
                  * {
                      color: #000000 !important;
                      background-color: transparent !important;
                  }
                  table {
                      border: 3px solid #000000 !important;
                  }
                  th {
                      background-color: #f0f0f0 !important;
                      color: #000000 !important;
                      border: 2px solid #000000 !important;
                      font-weight: 900 !important;
                  }
                  td {
                      background-color: #ffffff !important;
                      color: #000000 !important;
                      border: 2px solid #000000 !important;
                      font-weight: bold !important;
                  }
                  .header, .list-info, .stats-summary {
                      border: 3px solid #000000 !important;
                      background-color: #ffffff !important;
                  }
                  .paid-status::before {
                      content: "✓ ";
                      font-weight: 900;
                  }
                  .unpaid-status::before {
                      content: "✗ ";
                      font-weight: 900;
                  }
              }
              @media (-webkit-min-device-pixel-ratio: 2) {
                  body, table, th, td {
                      font-weight: 900;
                      -webkit-font-smoothing: antialiased;
                  }
              }
            </style>
          </head>
          <body>
          ${ ps.showPaymentTitle ? `<div class="header"><h1>📊 ${title}</h1></div>` : '' }

      
            <div class="list-info">
              <div class="info-item">
                <strong>📋 اسم القائمة:</strong>
                <span>${currentList?.ListName || 'قائمة جديدة'}</span>
              </div>
              <div class="info-item">
                <strong>🏢 الشركة:</strong>
                <span>${companies.find(c => c.CompanyID == currentList?.CompanyID)?.CompanyName || 'غير محدد'}</span>
              </div>
              <div class="info-item">
                <strong>📅 تاريخ النت:</strong>
                <span>${currentList?.SelectionDate ? new Date(currentList.SelectionDate).toLocaleDateString('ar-EG') : 'غير محدد'}</span>
              </div>
              <div class="info-item">
                <strong>🕒 تاريخ الطباعة:</strong>
                <span>${currentDate}</span>
              </div>
            </div>
      
            <div class="stats-summary">
              <strong>📊 ملخص القائمة:</strong>
              العدد المطلوب: <strong>${currentList?.RequiredCount || activePassports.length}</strong> |
              مؤكد الحضور: <strong>${paidCount}</strong> |
              في الانتظار: <strong>${unpaidCount}</strong>
              ${ps.showPaymentTotals ? `| إجمالي المبالغ: <strong>${totalAmount.toLocaleString()} ج.م</strong>` : ''}
            </div>
      
            <table>
              <thead><tr>${headerCells}</tr></thead>
              <tbody>${rowsHtml}</tbody>
            </table>
      
            <div class="footer">
              <p><strong>تم إنشاء هذا التقرير بواسطة نظام إدارة الجوازات</strong></p>
              <p>تاريخ ووقت الطباعة: ${new Date().toLocaleString('ar-EG')}</p>
              <p>عدد السجلات المطبوعة: <strong>${filteredPassports.length}</strong> من أصل <strong>${activePassports.length}</strong></p>
            </div>
          </body>
          </html>
        `;
      
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
      };
      

// ✅ استبدل دالة editSavedList في SelectionAndPreparation.js بهذه النسخة المحدثة:

// ✅ استبدل دالة editSavedList القديمة (السطر 3211-3296) بهذا الإصدار المُحدث

const editSavedList = async (listId, updatedData) => {
    try {
        setIsLoading(true);
        
        // ✅ 1. تحديث القائمة المحفوظة
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${listId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                listName: updatedData.listName.trim(),
                requiredCount: parseInt(updatedData.requiredCount),
                notes: updatedData.notes?.trim() || ''
            })
        });

        const result = await response.json();

        if (result.success) {
            // ✅ 2. تحديث القائمة في المتابعة الدورية (اسم القائمة + العدد المطلوب)
            try {
                console.log(`تحديث القائمة في المتابعة الدورية:`);
                console.log(`- الاسم: ${updatedData.listName}`);
                console.log(`- العدد المطلوب: ${updatedData.requiredCount}`); // ✅ إضافة log
                
                const periodicUpdateResponse = await fetch(
                    `${config.API_BASE_URL}/api/periodic-follow-up/update-list-name/${listId}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            newListName: updatedData.listName.trim(),
                            requiredCount: parseInt(updatedData.requiredCount) // ✅ إضافة العدد المطلوب
                        })
                    }
                );

                const periodicResult = await periodicUpdateResponse.json();
                
                if (periodicResult.success) {
                    console.log(`✅ تم تحديث ${periodicResult.rowsAffected} شخص في المتابعة الدورية`);
                    console.log(`✅ الحقول المُحدثة:`, periodicResult.updatedFields);
                } else {
                    console.warn('⚠️ فشل تحديث المتابعة الدورية، لكن القائمة تم تحديثها');
                }
            } catch (periodicError) {
                console.error('خطأ في تحديث المتابعة الدورية:', periodicError);
                // نكمل حتى لو فشل تحديث المتابعة الدورية
            }

            // ✅ 3. تحديث البيانات محلياً
            setSavedLists(prev => prev.map(list =>
                list.ListID === listId
                    ? {
                        ...list,
                        ListName: updatedData.listName.trim(),
                        RequiredCount: parseInt(updatedData.requiredCount),
                        Notes: updatedData.notes?.trim() || ''
                    }
                    : list
            ));

            if (currentList && currentList.ListID === listId) {
                setCurrentList(prev => ({
                    ...prev,
                    ListName: updatedData.listName.trim(),
                    RequiredCount: parseInt(updatedData.requiredCount),
                    Notes: updatedData.notes?.trim() || ''
                }));
            }

            setShowEditListModal(false);
            setEditingList(null);
            
            showAlert(`✅ تم تحديث القائمة "${updatedData.listName}" في الاختيار والمتابعة الدورية بنجاح`, 'success');
            
            // إعادة تحميل القوائم للتأكد من التحديث
            await fetchSavedLists();
            
        } else {
            throw new Error(result.error || 'فشل في التحديث');
        }
        
    } catch (error) {
        console.error('خطأ في تحديث القائمة:', error);
        showAlert(`❌ فشل تحديث القائمة: ${error.message}`, 'error');
    } finally {
        setIsLoading(false);
    }
};

// دالة فتح نافذة التعديل
const openEditListModal = (list) => {
    setEditingList(list);
    setShowEditListModal(true);
};

const printCompleteList = () => {
    if (!selectedListForSending || delegatesInSelectedList.length === 0) {
        showAlert('لا توجد بيانات للطباعة', 'warning');
        return;
    }

    const allPassports = delegatesInSelectedList.flatMap(d => d.passports);
    
    const showColumns = {
        serial: true,
        name: true,
        company: printSettings.showCompany,
        delegate: printSettings.showDelegate,
        profession: printSettings.showProfession,
        phone: printSettings.showPhone,
        status: true
    };

    const printWindow = window.open('', '_blank');
    
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>قائمة ${selectedListForSending.ListName} - جميع المناديب</title>
            <style>
                @page { size: A4; margin: 8mm 5mm; }
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                    font-family: 'Arial Black', 'Arial', sans-serif;
                    direction: rtl;
                    background: white;
                    color: #000000;
                    font-size: 14px;
                    line-height: 1.4;
                    font-weight: bold;
                }
                .print-container {
                    max-width: 100%;
                    margin: 0;
                    background: white;
                    padding: 8px;
                }
                .report-header {
                    text-align: center;
                    margin-bottom: 25px;
                    border-bottom: 3px solid #000000;
                    padding-bottom: 15px;
                }
                .company-name {
                    font-size: 28px;
                    font-weight: 900;
                    color: #000000;
                    margin-bottom: 8px;
                }
                .list-name {
                    font-size: 22px;
                    color: #000000;
                    margin-bottom: 8px;
                    background: #f0f0f0;
                    padding: 10px;
                    border: 2px solid #000000;
                    border-radius: 8px;
                    font-weight: bold;
                }
                .list-info {
                    font-size: 16px;
                    color: #000000;
                    margin-bottom: 5px;
                    font-weight: bold;
                }
                .summary-section {
                    background: white;
                    border: 2px solid #000000;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 20px;
                }
                .summary-title {
                    font-size: 20px;
                    font-weight: 900;
                    color: #000000;
                    margin-bottom: 15px;
                    text-align: center;
                    border-bottom: 1px solid #000000;
                    padding-bottom: 8px;
                }
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 15px;
                    text-align: center;
                }
                .summary-item {
                    background: white;
                    padding: 12px;
                    border: 1px solid #000000;
                    border-radius: 6px;
                }
                .summary-label {
                    font-weight: 900;
                    color: #000000;
                    margin-bottom: 6px;
                    font-size: 14px;
                }
                .summary-value {
                    font-size: 20px;
                    font-weight: 900;
                    color: #000000;
                }
                table { 
                    width: 100%; 
                    border-collapse: collapse;
                    background: white;
                    border: 3px solid #000000;
                }
                th, td { 
                    padding: 8px 4px;
                    text-align: center;
                    border: 1px solid #000000;
                    font-size: 13px;
                    color: #000000;
                    font-weight: bold;
                }
                th { 
                    background: #f0f0f0;
                    font-weight: 900;
                    font-size: 15px;
                    color: #000000;
                    border: 2px solid #000000;
                }
                tr:nth-child(even) { background: #f8f8f8; }
                tr:nth-child(odd) { background: white; }
                .row-number {
                    font-weight: 900;
                    background: #e0e0e0;
                    width: 40px;
                    color: #000000;
                    font-size: 14px;
                }
                .name-cell { 
                    font-weight: 900; 
                    font-size: 14px;
                    color: #000000;
                }
                .delegate-cell { 
                    background: #e7f3ff; 
                    font-weight: 900;
                    color: #000000;
                }
                .company-cell { 
                    background: #f0f8f0; 
                    font-weight: 900;
                    color: #000000;
                }
                .profession-cell { 
                    background: #fff8e1;
                    color: #000000;
                    font-weight: bold;
                }
                .phone-cell { 
                    font-family: 'Arial', monospace;
                    color: #000000;
                    font-weight: bold;
                }
/* ✅ تحسين خط ID: */
.passport-id {
    color: #000000 !important;
    font-size: 12px !important;
    font-family: 'Arial Black', 'Arial', sans-serif !important;
    font-weight: 900 !important;
    background: #ffffff !important;
    padding: 3px 6px !important;
    border-radius: 5px !important;
    border: 2px solid #000000 !important;
    display: inline-block;
    margin-top: 3px;
    text-shadow: 1px 1px 1px rgba(0,0,0,0.8);
    letter-spacing: 0.5px;
}
                .footer {
                    margin-top: 20px;
                    text-align: center;
                    color: #000000;
                    font-size: 12px;
                    border-top: 1px solid #000000;
                    padding-top: 10px;
                    font-weight: bold;
                }
                @media print {
                    body { 
                        font-size: 12px;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .print-container { padding: 5px; }
                    th, td { 
                        padding: 6px 3px; 
                        font-size: 11px;
                        color: #000000 !important;
                        font-weight: bold !important;
                    }
                    th {
                        font-size: 12px;
                        font-weight: 900 !important;
                        color: #000000 !important;
                    }
.passport-id {
    color: #000000 !important;
    font-size: 11px !important;
    font-weight: 900 !important;
    background: #ffffff !important;
    border: 2px solid #000000 !important;
    text-shadow: 0.8px 0.8px 0px #000000 !important;
    letter-spacing: 0.3px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
}
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <div class="report-header">
                    <div class="company-name">📋 قائمة العملاء الشاملة</div>
                    <div class="list-name">${selectedListForSending.ListName}</div>
                    <div class="list-info">الشركة: ${selectedListForSending.CompanyName}</div>
                    ${printSettings.showTravelDate ? `<div class="list-info">تاريخ السفر: ${new Date(selectedListForSending.SelectionDate).toLocaleDateString('ar-EG')}</div>` : ''}
                    <div class="list-info">تاريخ الطباعة: ${new Date().toLocaleDateString('ar-EG')}</div>
                </div>

                <div class="summary-section">
                    <div class="summary-title">ملخص شامل</div>
                    <div class="summary-grid">
                        <div class="summary-item">
                            <div class="summary-label">إجمالي العملاء</div>
                            <div class="summary-value">${allPassports.length}</div>
                        </div>
                        <div class="summary-item">
                            <div class="summary-label">مؤكد الحضور</div>
                            <div class="summary-value">${allPassports.filter(p => p.confirmed).length}</div>
                        </div>
                        <div class="summary-item">
                            <div class="summary-label">في الانتظار</div>
                            <div class="summary-value">${allPassports.filter(p => !p.confirmed).length}</div>
                        </div>
                        <div class="summary-item">
                            <div class="summary-label">عدد المناديب</div>
                            <div class="summary-value">${delegatesInSelectedList.length}</div>
                        </div>
                    </div>
                </div>

                <table>
                    <thead>
                        <tr>
                            ${showColumns.serial ? '<th style="width: 40px;">م</th>' : ''}
                            ${showColumns.name ? '<th>الاسم العربي</th>' : ''}
                            ${showColumns.company ? `<th>${printSettings.showPassportID ? 'الشركة + ID:' : 'الشركة'}</th>` : ''}
                            ${showColumns.delegate ? '<th>المندوب</th>' : ''}
                            ${showColumns.profession ? '<th>المهنة</th>' : ''}
                            ${showColumns.phone ? '<th>رقم الهاتف</th>' : ''}
                            ${showColumns.status ? '<th>الحالة</th>' : ''}
                        </tr>
                    </thead>
                    <tbody>
                        ${allPassports.map((passport, index) => {
                            const delegate = delegatesInSelectedList.find(d => 
                                d.passports.some(p => p.PassportID === passport.PassportID)
                            );
                            
                            let companyText = selectedListForSending.CompanyName;
                            if (printSettings.showPassportID && printSettings.showCompany) {
                                companyText += `<br><table style="margin-top: 3px; display: inline-block;"><tr><td style="
                                    background: black !important;
                                    color: white !important;
                                    font-size: 12px !important;
                                    font-weight: bold !important;
                                    padding: 3px 6px !important;
                                    border: none !important;
                                    font-family: Arial !important;
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                ">ID:: ${getPassportFileNumber(passport)}</td></tr></table>`;
                            }
                            
                            return `
                            <tr>
                                ${showColumns.serial ? `<td class="row-number">${index + 1}</td>` : ''}
                                ${showColumns.name ? `<td class="name-cell">${passport.NameArabic || 'غير محدد'}</td>` : ''}
                                ${showColumns.company ? `<td class="company-cell">${companyText}</td>` : ''}
                                ${showColumns.delegate ? `<td class="delegate-cell">${delegate?.delegateInfo?.DelegateName || 'غير محدد'}</td>` : ''}
                                ${showColumns.profession ? `<td class="profession-cell">${getPassportProfession(passport)}</td>` : ''}
                                ${showColumns.phone ? `<td class="phone-cell" style="text-align: right; vertical-align: top;">
                                    ${(() => {
                                        const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
                                        return phones.length > 0 ? 
                                            phones.map(phone => `<div style="margin: 2px 0; padding: 2px; background: #f9f9f9; border-radius: 3px; font-family: monospace; font-size: 12px; font-weight: bold; color: #000000;">📱 ${phone}</div>`).join('')
                                            : '<div style="color: #666; font-style: italic; font-weight: bold;">غير محدد</div>';
                                    })()}
                                </td>` : ''}
                                ${showColumns.status ? `<td style="font-weight: 900; color: #000000;">${passport.confirmed ? '✅ مؤكد' : '⏳ انتظار'}</td>` : ''}
                            </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>

                <div class="footer">
                    نظام إدارة السفر | طبع بتاريخ: ${new Date().toLocaleString('ar-EG')}
                </div>
            </div>
        </body>
        </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };
};



// أضف هذه الدوال في بداية المكون بعد باقي الدوال

// دالة تحميل مكتبة html2canvas ديناميكياً
const loadHtml2Canvas = () => {
    return new Promise((resolve, reject) => {
        if (typeof window.html2canvas !== 'undefined') {
            resolve(window.html2canvas);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = () => {
            if (typeof window.html2canvas !== 'undefined') {
                resolve(window.html2canvas);
            } else {
                reject(new Error('فشل في تحميل مكتبة html2canvas'));
            }
        };
        script.onerror = () => reject(new Error('فشل في تحميل مكتبة html2canvas'));
        document.head.appendChild(script);
    });
};

// دالة إنشاء عنصر HTML للتصوير
// تحديث دالة إنشاء عنصر HTML للتصوير
const createImageableElement = (delegateGroup, listInfo, customPassports = null) => {
    const passportsToUse = customPassports || delegateGroup.passports;
    const appointmentText = getAppointmentText();
    
    const container = document.createElement('div');
    container.style.cssText = `
        width: 800px;
        padding: 20px;
        background: white;
        font-family: 'Arial', sans-serif;
        direction: rtl;
        color: black;
        font-size: 14px;
        line-height: 1.5;
        position: absolute;
        left: -9999px;
        top: -9999px;
    `;

    container.innerHTML = `
        <div style="text-align: center; margin-bottom: 25px; border-bottom: 2px solid black; padding-bottom: 15px;">
            <div style="font-size: 26px; font-weight: bold; color: black; margin-bottom: 8px;">
                📋 قائمة العملاء - ${appointmentSettings.type === 'medical' ? 'الكشف الطبي' : 
                                       appointmentSettings.type === 'net' ? 'عمل النت' : 
                                       appointmentSettings.type === 'both' ? 'الكشف الطبي + النت' : 
                                       'موعد هام'}
            </div>
            <div style="font-size: 20px; color: black; margin-bottom: 8px; background: #f0f0f0; padding: 10px; border: 2px solid black; border-radius: 8px;">
                المندوب: ${delegateGroup.delegateInfo?.DelegateName || 'غير محدد'}
            </div>
            <div style="font-size: 16px; color: black; margin-bottom: 5px;">
                الشركة: ${listInfo.CompanyName}
            </div>
        </div>

        <div style="background: #f8f9fa; border: 2px solid black; border-radius: 8px; padding: 20px; margin-bottom: 20px; text-align: center;">
            <div style="font-size: 18px; font-weight: bold; color: black; margin-bottom: 15px;">
                🗓️ موعد الحضور المطلوب
            </div>
            <div style="font-size: 22px; font-weight: bold; color: black; margin-bottom: 10px;">
                ${appointmentText}
            </div>
            ${appointmentSettings.notes ? `
                <div style="font-size: 14px; color: black; margin-top: 15px; padding: 10px; border: 1px solid black; border-radius: 6px; background: white;">
                    📝 ${appointmentSettings.notes}
                </div>
            ` : ''}
        </div>

        <div style="background: white; border: 2px solid black; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
            <div style="font-size: 16px; font-weight: bold; color: black; margin-bottom: 15px; text-align: center; border-bottom: 1px solid black; padding-bottom: 8px;">
                ملخص العملاء
            </div>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; text-align: center;">
                <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                    <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">إجمالي العملاء</div>
                    <div style="font-size: 20px; font-weight: bold; color: black;">${passportsToUse.length}</div>
                </div>
                <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                    <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">نوع الفلتر</div>
                    <div style="font-size: 14px; font-weight: bold; color: black;">${getFilterText()}</div>
                </div>
            </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; background: white; border: 2px solid black;">
            <thead>
                <tr style="background: #f0f0f0;">
                    <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">م</th>
                    <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الاسم</th>
                    <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">رقم الجواز</th>
                    <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">رقم الهاتف</th>
                    <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الحالة</th>
                </tr>
            </thead>
            <tbody>
                ${passportsToUse.map((passport, index) => `
                    <tr style="${index % 2 === 0 ? 'background: #f8f8f8;' : 'background: white;'}">
                        <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold; background: #e0e0e0;">${index + 1}</td>
                        <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold;">${passport.NameArabic || 'غير محدد'}</td>
                        <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">${passport.PassportNumber || 'غير محدد'}</td>
                        <td style="padding: 6px 3px; text-align: right; border: 1px solid black; font-size: 11px; color: black; vertical-align: top;">
    ${(() => {
        const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px; background: #f9f9f9; border-radius: 3px;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic;">غير محدد</div>';
    })()}
</td>                        <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">
                            ${passport.confirmed ? '✅ مؤكد' : '⏳ انتظار'}
                        </td>
                    </tr>
                `).join('')}
            </tbody>
        </table>

        <div style="margin-top: 20px; text-align: center; color: black; font-size: 12px; border-top: 1px solid black; padding-top: 10px;">
            نظام إدارة السفر | طُبع بتاريخ: ${new Date().toLocaleString('ar-EG')} |
            المندوب: ${delegateGroup.delegateInfo?.DelegateName || 'غير محدد'}
        </div>
    `;

    document.body.appendChild(container);
    return container;
};

// دالة حفظ مندوب كصورة
// تحديث دالة حفظ مندوب كصورة لتدعم الفلترة
const saveAsImageDelegate = async (delegateGroup, listInfo, customPassports = null) => {
    try {
        showAlert('جاري إنشاء الصورة...', 'info');
        
        const html2canvas = await loadHtml2Canvas();
        const passportsToUse = customPassports || delegateGroup.passports;
        
        // إنشاء العنصر القابل للتصوير مع الجوازات المخصصة
        const element = createImageableElement(delegateGroup, listInfo, passportsToUse);
        
        const canvas = await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: 800,
            height: element.scrollHeight
        });
        
        document.body.removeChild(element);
        
        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `قائمة_${delegateGroup.delegateInfo?.DelegateName || 'مندوب'}_${getFilterText()}_${new Date().toISOString().split('T')[0]}.png`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showAlert('تم حفظ الصورة بنجاح!', 'success');
        }, 'image/png', 0.95);
        
    } catch (error) {
        console.error('خطأ في حفظ الصورة:', error);
        showAlert('حدث خطأ أثناء حفظ الصورة: ' + error.message, 'error');
    }
};

// دالة حفظ القائمة الكاملة كصورة
const saveCompleteListAsImage = async () => {
    if (!selectedListForSending || delegatesInSelectedList.length === 0) {
        showAlert('لا توجد بيانات لحفظها كصورة', 'warning');
        return;
    }

    try {
        showAlert('جاري إنشاء صورة القائمة الكاملة...', 'info');
        
        const html2canvas = await loadHtml2Canvas();
        const allPassports = delegatesInSelectedList.flatMap(d => d.passports);
        
        // إنشاء عنصر للقائمة الكاملة
        const container = document.createElement('div');
        container.style.cssText = `
            width: 900px;
            padding: 20px;
            background: white;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            direction: rtl;
            color: black;
            font-size: 14px;
            line-height: 1.4;
            position: absolute;
            left: -9999px;
            top: -9999px;
        `;

        container.innerHTML = `
            <div style="text-align: center; margin-bottom: 25px; border-bottom: 3px solid black; padding-bottom: 15px;">
                <div style="font-size: 30px; font-weight: bold; color: black; margin-bottom: 8px;">
                    قائمة العملاء الشاملة
                </div>
                <div style="font-size: 22px; color: black; margin-bottom: 8px; background: #f0f0f0; padding: 10px; border: 2px solid black; border-radius: 8px;">
                    القائمة: ${selectedListForSending.ListName}
                </div>
                <div style="font-size: 16px; color: black; margin-bottom: 5px;">
                    الشركة: ${selectedListForSending.CompanyName} | 
                    تاريخ السفر: ${new Date(selectedListForSending.SelectionDate).toLocaleDateString('ar-EG')}
                </div>
                <div style="font-size: 16px; color: black;">
                    تاريخ الطباعة: ${new Date().toLocaleDateString('ar-EG', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long'
                    })}
                </div>
            </div>

            <div style="background: white; border: 2px solid black; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
                <div style="font-size: 20px; font-weight: bold; color: black; margin-bottom: 15px; text-align: center; border-bottom: 1px solid black; padding-bottom: 8px;">
                    ملخص شامل
                </div>
                <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; text-align: center;">
                    <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">إجمالي العملاء</div>
                        <div style="font-size: 22px; font-weight: bold; color: black;">${allPassports.length}</div>
                    </div>
                    <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">مؤكد الحضور</div>
                        <div style="font-size: 22px; font-weight: bold; color: black;">${allPassports.filter(p => p.confirmed).length}</div>
                    </div>
                    <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">في الانتظار</div>
                        <div style="font-size: 22px; font-weight: bold; color: black;">${allPassports.filter(p => !p.confirmed).length}</div>
                    </div>
                    <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">عدد المناديب</div>
                        <div style="font-size: 22px; font-weight: bold; color: black;">${delegatesInSelectedList.length}</div>
                    </div>
                </div>
            </div>

            <table style="width: 100%; border-collapse: collapse; background: white; border: 2px solid black;">
                <thead>
                    <tr style="background: #f0f0f0;">
                        <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">م</th>
                        <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الاسم</th>
                        <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">المندوب</th>
                        <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">رقم الجواز</th>
                        <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الهاتف</th>
                        <th style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الحالة</th>
                    </tr>
                </thead>
                <tbody>
                    ${allPassports.map((passport, index) => {
                        const delegate = delegatesInSelectedList.find(d => 
                            d.passports.some(p => p.PassportID === passport.PassportID)
                        );
                        return `
                        <tr style="${index % 2 === 0 ? 'background: #f8f8f8;' : 'background: white;'}">
                            <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold; background: #e0e0e0;">${index + 1}</td>
                            <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">${passport.NameArabic || 'غير محدد'}</td>
                            <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 12px; color: black; background: #e7f3ff; font-weight: bold;">${delegate?.delegateInfo?.DelegateName || 'غير محدد'}</td>
                            <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">${passport.PassportNumber || 'غير محدد'}</td>
                            <td style="padding: 8px 4px; text-align: right; border: 1px solid black; font-size: 11px; color: black; vertical-align: top;">
    ${(() => {
        const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px; background: #f9f9f9; border-radius: 3px; font-family: monospace;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic;">غير محدد</div>';
    })()}
</td>                            <td style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">
                                <span style="${passport.confirmed ? 'background: #d4edda; color: #155724;' : 'background: #fff3cd; color: #856404;'} font-weight: bold; padding: 4px 8px; border-radius: 4px;">
                                    ${passport.confirmed ? '✅ مؤكد' : '⏳ انتظار'}
                                </span>
                            </td>
                        </tr>
                        `;
                    }).join('')}
                </tbody>
            </table>

            <div style="margin-top: 20px; text-align: center; color: black; font-size: 12px; border-top: 1px solid black; padding-top: 10px;">
                نظام إدارة السفر | طُبع بتاريخ: ${new Date().toLocaleString('ar-EG')} |
                القائمة: ${selectedListForSending.ListName}
            </div>
        `;

        document.body.appendChild(container);

        const canvas = await html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: 900,
            height: container.scrollHeight,
            scrollX: 0,
            scrollY: 0
        });

        document.body.removeChild(container);

        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `قائمة_شاملة_${selectedListForSending.ListName}_${new Date().toISOString().split('T')[0]}.png`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showAlert('تم حفظ الصورة الشاملة بنجاح!', 'success');
        }, 'image/png', 0.95);

    } catch (error) {
        console.error('خطأ في حفظ الصورة:', error);
        showAlert('حدث خطأ أثناء حفظ الصورة: ' + error.message, 'error');
    }
};





    
    // دالة تصدير PDF لمندوب محدد
    const exportDelegateToPDF = (delegateGroup, listInfo) => {
        // نفس منطق الطباعة لكن للتصدير كـ PDF
        printDelegateList(delegateGroup, listInfo);
    };



    const printSavedList = async (list) => {
        try {
            setIsLoading(true);
            showAlert('جاري تحضير القائمة للطباعة...', 'info');
            
            const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${list.ListID}`);
            const listDetails = await response.json();
            
            if (!listDetails.success) {
                throw new Error('فشل في جلب تفاصيل القائمة');
            }
            
            const { passports } = listDetails;
            
            const showColumns = {
                serial: true,
                name: true,
                company: printSettings.showCompany,
                delegate: printSettings.showDelegate,
                profession: printSettings.showProfession,
                phone: printSettings.showPhone,
                status: true
            };
            
            const printWindow = window.open('', '_blank');
            
            const htmlContent = `
                <!DOCTYPE html>
                <html dir="rtl" lang="ar">
                <head>
                    <meta charset="UTF-8">
                    <title>طباعة ${list.ListName}</title>
                    <style>
                        @page { size: A4; margin: 8mm 5mm; }
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { 
                            font-family: 'Arial', sans-serif;
                            direction: rtl;
                            background: white;
                            color: #000000;
                            font-size: 14px;
                            line-height: 1.4;
                        }
                        .print-container {
                            max-width: 100%;
                            margin: 0;
                            background: white;
                            padding: 10px;
                        }
                        .report-header {
                            text-align: center;
                            margin-bottom: 25px;
                            border-bottom: 3px solid #000000;
                            padding-bottom: 15px;
                        }
                        .main-title {
                            font-size: 32px;
                            font-weight: bold;
                            color: #000000;
                            margin-bottom: 12px;
                        }
                        .list-name {
                            font-size: 24px;
                            color: #000000;
                            margin-bottom: 10px;
                            background: #f5f5f5;
                            padding: 12px;
                            border: 2px solid #000000;
                            border-radius: 8px;
                            font-weight: bold;
                        }
                        .list-info {
                            font-size: 18px;
                            color: #000000;
                            margin-bottom: 8px;
                            font-weight: 600;
                        }
                        .summary-section {
                            background: white;
                            border: 2px solid #000000;
                            border-radius: 8px;
                            padding: 20px;
                            margin-bottom: 25px;
                        }
                        .summary-title {
                            font-size: 22px;
                            font-weight: bold;
                            color: #000000;
                            margin-bottom: 18px;
                            text-align: center;
                            border-bottom: 2px solid #000000;
                            padding-bottom: 10px;
                        }
                        .summary-grid {
                            display: grid;
                            grid-template-columns: repeat(4, 1fr);
                            gap: 15px;
                            text-align: center;
                        }
                        .summary-item {
                            background: white;
                            padding: 15px;
                            border: 2px solid #000000;
                            border-radius: 8px;
                        }
                        .summary-label {
                            font-weight: bold;
                            color: #000000;
                            margin-bottom: 8px;
                            font-size: 16px;
                        }
                        .summary-value {
                            font-size: 24px;
                            font-weight: bold;
                            color: #000000;
                        }
                        table { 
                            width: 100%; 
                            border-collapse: collapse;
                            background: white;
                            border: 3px solid #000000;
                            margin-top: 10px;
                        }
                        th, td { 
                            padding: 12px 6px;
                            text-align: center;
                            border: 1px solid #000000;
                            font-size: 14px;
                            color: #000000;
                        }
                        th { 
                            background: #e8e8e8;
                            font-weight: bold;
                            font-size: 16px;
                            color: #000000;
                        }
                        tr:nth-child(even) { background: #f8f8f8; }
                        tr:nth-child(odd) { background: white; }
                        .row-number {
                            font-weight: bold;
                            background: #d0d0d0;
                            width: 50px;
                            color: #000000;
                            font-size: 16px;
                        }
                        .name-cell { 
                            font-weight: bold; 
                            font-size: 15px;
                            color: #000000;
                        }
                        .delegate-cell { 
                            background: #e3f2fd; 
                            font-weight: bold;
                            color: #000000;
                            font-size: 14px;
                        }
                        .company-cell { 
                            background: #e8f5e8; 
                            font-weight: bold;
                            color: #000000;
                            font-size: 14px;
                        }
                        .profession-cell { 
                            background: #fff3e0;
                            color: #000000;
                            font-weight: 600;
                            font-size: 14px;
                        }
                        .phone-cell { 
                            font-family: 'Arial', monospace;
                            color: #000000;
                            font-weight: bold;
                            font-size: 14px;
                            background: #f0f0f0;
                        }
                        .status-confirmed { 
                            background: #c8e6c9; 
                            color: #000000; 
                            font-weight: bold; 
                            padding: 4px 8px; 
                            border-radius: 4px;
                            border: 1px solid #000000;
                        }
                        .status-pending { 
                            background: #fff9c4; 
                            color: #000000; 
                            font-weight: bold; 
                            padding: 4px 8px; 
                            border-radius: 4px;
                            border: 1px solid #000000;
                        }
                        .footer {
                            margin-top: 25px;
                            text-align: center;
                            color: #000000;
                            font-size: 14px;
                            border-top: 2px solid #000000;
                            padding-top: 15px;
                            font-weight: 600;
                        }
                        @media print {
                            body { font-size: 13px; }
                            .print-container { padding: 8px; }
                            th, td { padding: 8px 4px; font-size: 12px; }
                            .main-title { font-size: 28px; }
                            .list-name { font-size: 20px; }
                            .summary-value { font-size: 20px; }
                        }
                    </style>
                </head>
                <body>
                    <div class="print-container">
                        <div class="report-header">
                            <div class="main-title">📋 تقرير قائمة السفر</div>
                            <div class="list-name">${list.ListName}</div>
                            <div class="list-info">🏢 الشركة: ${list.CompanyName}</div>
                            <div class="list-info">🖨️ تاريخ الطباعة: ${new Date().toLocaleDateString('ar-EG', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                weekday: 'long'
                            })}</div>
                        </div>
    
                        <div class="summary-section">
                            <div class="summary-title">📊 ملخص القائمة</div>
                            <div class="summary-grid">
                                <div class="summary-item">
                                    <div class="summary-label">العدد المطلوب</div>
                                    <div class="summary-value">${list.RequiredCount}</div>
                                </div>
                                <div class="summary-item">
                                    <div class="summary-label">إجمالي المختارين</div>
                                    <div class="summary-value">${list.PassportCount}</div>
                                </div>
                                <div class="summary-item">
                                    <div class="summary-label">مؤكد الحضور</div>
                                    <div class="summary-value">${passports.filter(p => p.FinalConfirmationStatus === 1).length}</div>
                                </div>
                                <div class="summary-item">
                                    <div class="summary-label">في الانتظار</div>
                                    <div class="summary-value">${passports.filter(p => p.FinalConfirmationStatus !== 1).length}</div>
                                </div>
                            </div>
                        </div>
    
                        <table>
                            <thead>
                                <tr>
                                    ${showColumns.serial ? '<th style="width: 50px;">م</th>' : ''}
                                    ${showColumns.name ? '<th style="width: 200px;">الاسم العربي</th>' : ''}
                                    ${showColumns.company ? `<th style="width: 150px;">${printSettings.showPassportID ? 'الشركة + ID:' : 'الشركة'}</th>` : ''}
                                    ${showColumns.delegate ? '<th style="width: 150px;">المندوب</th>' : ''}
                                    ${showColumns.profession ? '<th style="width: 120px;">المهنة</th>' : ''}
                                    ${showColumns.phone ? '<th style="width: 130px;">رقم الهاتف</th>' : ''}
                                    ${showColumns.status ? '<th style="width: 100px;">الحالة</th>' : ''}
                                </tr>
                            </thead>
                            <tbody>
                                ${passports.map((passport, index) => {
                                    const isConfirmed = passport.FinalConfirmationStatus === 1;
                                    
                                    let professionName = 'غير محدد';
                                    if (passport.ProfessionName) {
                                        professionName = passport.ProfessionName;
                                    } else if (passport.PassportProfessionName) {
                                        professionName = passport.PassportProfessionName;
                                    } else if (passport.PassportProfessionID) {
                                        const prof = professions.find(p => p.ProfessionID === parseInt(passport.PassportProfessionID));
                                        if (prof) professionName = prof.ProfessionName;
                                    }
                                    
                                    return `
                                    <tr>
                                        ${showColumns.serial ? `<td class="row-number">${index + 1}</td>` : ''}
                                        ${showColumns.name ? `<td class="name-cell">${passport.NameArabic || 'غير محدد'}</td>` : ''}
                                        ${showColumns.company ? `<td class="company-cell">
    ${list.CompanyName}
    ${printSettings.showPassportID ? `<br><small style="color: #666; font-size: 10px; font-family: monospace;">ID:: ${getPassportFileNumber(passport)}</small>` : ''}
</td>` : ''}
                                        ${showColumns.delegate ? `<td class="delegate-cell">${passport.DelegateName || 'غير محدد'}</td>` : ''}
                                        ${showColumns.profession ? `<td class="profession-cell">${professionName}</td>` : ''}
                                        ${showColumns.phone ? `<td class="phone-cell" style="text-align: right; vertical-align: top;">
    ${(() => {
        const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 2px 0; padding: 2px; background: #f9f9f9; border-radius: 3px; font-family: monospace; font-size: 12px;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic;">غير محدد</div>';
    })()}
</td>` : ''}                                        ${showColumns.status ? `<td><span class="${isConfirmed ? 'status-confirmed' : 'status-pending'}">${isConfirmed ? '✅ مؤكد' : '⏳ انتظار'}</span></td>` : ''}
                                    </tr>
                                    `;
                                }).join('')}
                            </tbody>
                        </table>
    
                        <div class="footer">
                            <strong>نظام إدارة السفر المتطور</strong><br>
                            طُبع بتاريخ: ${new Date().toLocaleString('ar-EG')} | القائمة: ${list.ListName}
                        </div>
                    </div>
                </body>
                </html>
            `;
            
            printWindow.document.write(htmlContent);
            printWindow.document.close();
            
            printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
                printWindow.close();
            };
            
            showAlert('✅ تم تحضير القائمة للطباعة', 'success');
            
        } catch (error) {
            console.error('خطأ في طباعة القائمة:', error);
            showAlert('❌ خطأ في طباعة القائمة: ' + error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

// دالة تصدير القائمة إلى PDF
const exportSavedListToPDF = async (list) => {
    try {
        setIsLoading(true);
        showAlert('جاري تحضير ملف PDF...', 'info');
        
        // نفس كود الطباعة لكن للـ PDF
        printSavedList(list);
        
        showAlert('💡 تم فتح نافذة الطباعة - اختر "حفظ كـ PDF" من خيارات الطابعة', 'info');
        
    } catch (error) {
        console.error('خطأ في تصدير PDF:', error);
        showAlert('❌ خطأ في تصدير PDF: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};

// دالة تصدير القائمة إلى Excel
const exportSavedListToExcel = async (list) => {
    try {
        setIsLoading(true);
        showAlert('جاري تحضير ملف Excel...', 'info');
        
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${list.ListID}`);
        const listDetails = await response.json();
        
        if (!listDetails.success) {
            throw new Error('فشل في جلب تفاصيل القائمة');
        }
        
        const { passports } = listDetails;
        
        // تحضير البيانات للـ Excel بتنسيق محسن
        const excelData = [];
        
        // الصف الأول: عنوان التقرير
        excelData.push([`تقرير قائمة السفر - ${list.ListName}`]);
        excelData.push([]); // سطر فارغ
        
        // معلومات القائمة
        excelData.push(['معلومات القائمة']);
        excelData.push(['اسم القائمة', list.ListName]);
        excelData.push(['الشركة', list.CompanyName]);
        excelData.push(['تاريخ التصدير', new Date().toLocaleDateString('ar-EG')]);
        excelData.push([]); // سطر فارغ
        
        // الملخص
        excelData.push(['📊 ملخص القائمة']);
        excelData.push(['البيان', 'العدد']);
        excelData.push(['العدد المطلوب', list.RequiredCount]);
        excelData.push(['إجمالي المختارين', list.PassportCount]);
        excelData.push(['مؤكد الحضور', passports.filter(p => p.FinalConfirmationStatus === 1).length]);
        excelData.push(['في الانتظار', passports.filter(p => p.FinalConfirmationStatus !== 1).length]);
        excelData.push([]); // سطر فارغ
        
        // بيانات المسافرين
        excelData.push(['📋 قائمة المسافرين التفصيلية']);
        
        // رؤوس الجدول
        const headers = ['م', 'الاسم العربي'];
        if (printSettings.showCompany) headers.push('الشركة');
        if (printSettings.showDelegate) headers.push('المندوب');
        if (printSettings.showProfession) headers.push('المهنة');
        if (printSettings.showPhone) headers.push('رقم الهاتف');
        headers.push('الحالة');
        
        excelData.push(headers);
        
        // بيانات المسافرين
        passports.forEach((passport, index) => {
            const row = [index + 1, passport.NameArabic || 'غير محدد'];
            
            if (printSettings.showCompany) row.push(list.CompanyName);
            if (printSettings.showDelegate) row.push(passport.DelegateName || 'غير محدد');
            if (printSettings.showProfession) {
                let professionName = 'غير محدد';
                if (passport.ProfessionName) {
                    professionName = passport.ProfessionName;
                } else if (passport.PassportProfessionName) {
                    professionName = passport.PassportProfessionName;
                }
                row.push(professionName);
            }
            if (printSettings.showPhone) {
    const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
    const phoneText = phones.length > 0 ? phones.join(' | ') : 'غير محدد';
    row.push(phoneText);
}            
            row.push(passport.FinalConfirmationStatus === 1 ? 'مؤكد ✓' : 'منتظر ○');
            
            excelData.push(row);
        });
        
        // إضافة معلومات إضافية في النهاية
        excelData.push([]); // سطر فارغ
        excelData.push(['معلومات إضافية']);
        excelData.push(['تم إنشاء هذا التقرير بواسطة', 'نظام إدارة السفر المتطور']);
        excelData.push(['تاريخ ووقت الإنشاء', new Date().toLocaleString('ar-EG')]);
        excelData.push(['اسم القائمة', list.ListName]);
        excelData.push(['رقم القائمة', list.ListID]);
        
        // تحويل إلى CSV بتنسيق عربي صحيح
        const csvContent = excelData.map(row => 
            row.map(cell => {
                // معالجة النصوص العربية والخلايا الفارغة
                const cellValue = cell !== undefined && cell !== null ? String(cell) : '';
                // إحاطة النص بعلامات اقتباس للحفاظ على التنسيق
                return `"${cellValue.replace(/"/g, '""')}"`;
            }).join(',')
        ).join('\n');
        
        // إضافة BOM للدعم العربي الصحيح
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { 
            type: 'text/csv;charset=utf-8;' 
        });
        
        // تحميل الملف
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `تقرير_${list.ListName.replace(/[^\w\s]/gi, '')}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        showAlert('✅ تم تصدير ملف Excel بنجاح مع التنسيق المحسن', 'success');
        
    } catch (error) {
        console.error('خطأ في تصدير Excel:', error);
        showAlert('❌ خطأ في تصدير Excel: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};


// دالة تصدير الصورة مع تجميع المناديب (الدالة الجديدة)
const exportSavedListWithDelegatesAsImage = async (list) => {
    try {
        setIsLoading(true);
        showAlert('جاري إنشاء الصورة مع المناديب...', 'info');
        
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${list.ListID}`);
        const listDetails = await response.json();
        
        if (!listDetails.success) {
            throw new Error('فشل في جلب تفاصيل القائمة');
        }
        
        const { passports } = listDetails;
        const html2canvas = await loadHtml2Canvas();
        
        // ✅ تجميع حسب المناديب مع الفلترة
        const delegateGroups = {};
        passports.forEach(passport => {
            const delegateId = passport.DelegateID;
            if (!delegateGroups[delegateId]) {
                const delegate = delegates.find(d => d.DelegateID === delegateId);
                delegateGroups[delegateId] = {
                    delegateInfo: delegate,
                    passports: []
                };
            }
            delegateGroups[delegateId].passports.push({
                ...passport,
                confirmed: passport.FinalConfirmationStatus === 1
            });
        });

        // ✅ فلترة المناديب الذين لديهم جوازات
        const activeDelegates = Object.values(delegateGroups).filter(group => 
            group.passports.length > 0
        );

        const container = document.createElement('div');
        container.style.cssText = `
            width: 1000px;
            padding: 30px;
            background: white;
            font-family: 'Arial', sans-serif;
            direction: rtl;
            color: black;
            font-size: 14px;
            line-height: 1.5;
            position: absolute;
            left: -9999px;
            top: -9999px;
        `;

        container.innerHTML = `
            <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid black; padding-bottom: 20px;">
                <div style="font-size: 36px; font-weight: bold; color: black; margin-bottom: 12px;">
                    📋 تقرير قائمة السفر (مجمع بالمناديب)
                </div>
                <div style="font-size: 28px; color: black; margin-bottom: 10px; background: #f0f0f0; padding: 15px; border: 2px solid black; border-radius: 8px; font-weight: bold;">
                    ${list.ListName}
                </div>
                <div style="font-size: 18px; color: black; margin-bottom: 5px;">
                    الشركة: ${list.CompanyName}
                </div>
                <div style="font-size: 16px; color: black;">
                    تاريخ التصدير: ${new Date().toLocaleDateString('ar-EG', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long'
                    })} - ${new Date().toLocaleTimeString('ar-EG')}
                </div>
            </div>

            <div style="background: white; border: 2px solid black; border-radius: 8px; padding: 20px; margin-bottom: 25px;">
                <div style="font-size: 22px; font-weight: bold; color: black; margin-bottom: 15px; text-align: center; border-bottom: 1px solid black; padding-bottom: 10px;">
                    📊 ملخص شامل
                </div>
                <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; text-align: center;">
                    <div style="background: white; padding: 15px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 8px; font-size: 16px;">إجمالي العملاء</div>
                        <div style="font-size: 28px; font-weight: bold; color: black;">${list.PassportCount}</div>
                    </div>
                    <div style="background: white; padding: 15px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 8px; font-size: 16px;">مؤكد الحضور</div>
                        <div style="font-size: 28px; font-weight: bold; color: black;">${passports.filter(p => p.FinalConfirmationStatus === 1).length}</div>
                    </div>
                    <div style="background: white; padding: 15px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 8px; font-size: 16px;">في الانتظار</div>
                        <div style="font-size: 28px; font-weight: bold; color: black;">${passports.filter(p => p.FinalConfirmationStatus !== 1).length}</div>
                    </div>
                    <div style="background: white; padding: 15px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 8px; font-size: 16px;">عدد المناديب</div>
                        <div style="font-size: 28px; font-weight: bold; color: black;">${activeDelegates.length}</div>
                    </div>
                </div>
            </div>

            ${/* ✅ عرض كل مندوب في قسم منفصل */ ''}
            ${activeDelegates.map((delegateGroup, delegateIndex) => `
                <div style="margin-bottom: 30px; page-break-inside: avoid;">
                    <div style="background: #e7f3ff; border: 2px solid black; border-radius: 8px 8px 0 0; padding: 15px;">
                        <h3 style="font-size: 22px; font-weight: bold; color: black; margin: 0; text-align: center;">
                            👤 المندوب: ${delegateGroup.delegateInfo?.DelegateName || 'مندوب غير معروف'}
                        </h3>
                        <div style="text-align: center; margin-top: 8px; font-size: 16px; color: black;">
                            <span style="background: white; padding: 5px 10px; border: 1px solid black; border-radius: 5px; margin: 0 5px;">
                                إجمالي: ${delegateGroup.passports.length}
                            </span>
                            <span style="background: #d4edda; padding: 5px 10px; border: 1px solid black; border-radius: 5px; margin: 0 5px;">
                                مؤكد: ${delegateGroup.passports.filter(p => p.confirmed).length}
                            </span>
                            <span style="background: #fff3cd; padding: 5px 10px; border: 1px solid black; border-radius: 5px; margin: 0 5px;">
                                انتظار: ${delegateGroup.passports.filter(p => !p.confirmed).length}
                            </span>
                        </div>
                    </div>

                    <table style="width: 100%; border-collapse: collapse; background: white; border: 2px solid black; border-top: none;">
                        <thead>
                            <tr style="background: #f0f0f0;">
                                <th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold; width: 60px;">م</th>
                                <th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الاسم</th>
                                ${printSettings.showCompany ? `<th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">${printSettings.showPassportID ? 'الشركة + ID:' : 'الشركة'}</th>` : ''}
                                ${printSettings.showProfession ? `<th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">المهنة</th>` : ''}
                                ${printSettings.showPhone ? `<th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold; width: 160px;">أرقام الهاتف</th>` : ''}
                                <th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold; width: 100px;">الحالة</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${delegateGroup.passports.map((passport, index) => {
                                const isConfirmed = passport.FinalConfirmationStatus === 1;
                                let professionName = 'غير محدد';
                                if (passport.ProfessionName) {
                                    professionName = passport.ProfessionName;
                                } else if (passport.PassportProfessionName) {
                                    professionName = passport.PassportProfessionName;
                                } else if (passport.PassportProfessionID) {
                                    const prof = professions.find(p => p.ProfessionID === parseInt(passport.PassportProfessionID));
                                    if (prof) professionName = prof.ProfessionName;
                                }
                                
                                return `
                                <tr style="${index % 2 === 0 ? 'background: #f8f8f8;' : 'background: white;'}">
                                    <td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold; background: #e0e0e0;">${index + 1}</td>
                                    <td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">${passport.NameArabic || 'غير محدد'}</td>
                                    ${printSettings.showCompany ? `<td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black; background: #e8f5e8; font-weight: bold;">${getPassportCompanyName(passport, list.CompanyName)}${printSettings.showPassportID ? `<br><small style="color: #333; font-size: 10px; font-family: monospace;">ID:: ${getPassportFileNumber(passport)}</small>` : ''}</td>` : ''}
                                    ${printSettings.showProfession ? `<td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black; background: #fff3e0;">${professionName}</td>` : ''}
                                    ${printSettings.showPhone ? `<td style="padding: 10px 6px; text-align: right; border: 1px solid black; font-size: 11px; color: black; vertical-align: top;">
    ${(() => {
        const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px; background: #f0f0f0; border-radius: 3px; font-family: monospace;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic;">غير محدد</div>';
    })()}
</td>` : ''}                                    <td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">
                                        <span style="${isConfirmed ? 'background: #d4edda; color: black; border: 1px solid #28a745;' : 'background: #fff3cd; color: black; border: 1px solid #ffc107;'} font-weight: bold; padding: 4px 8px; border-radius: 4px;">
                                            ${isConfirmed ? '✅ مؤكد' : '⏳ انتظار'}
                                        </span>
                                    </td>
                                </tr>
                                `;
                            }).join('')}
                        </tbody>
                    </table>
                </div>
            `).join('')}

            <div style="margin-top: 25px; text-align: center; color: black; font-size: 14px; border-top: 2px solid black; padding-top: 15px;">
                <div style="margin-bottom: 8px; font-weight: bold;">
                    نظام إدارة السفر المتطور | القائمة: ${list.ListName}
                </div>
                <div style="font-size: 12px;">
                    تقرير مجمع بالمناديب - تم إنشاؤه بتاريخ: ${new Date().toLocaleString('ar-EG')}
                </div>
                <div style="font-size: 12px; margin-top: 5px; color: #666;">
                    إعدادات العرض: ${[
                        'الرقم',
                        'الاسم',
                        printSettings.showCompany ? 'الشركة' : null,
                        printSettings.showProfession ? 'المهنة' : null,
                        printSettings.showPhone ? 'الهاتف' : null,
                        'الحالة'
                    ].filter(Boolean).join(' • ')} | عدد المناديب: ${activeDelegates.length}
                </div>
            </div>
        `;

        document.body.appendChild(container);

        const canvas = await html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: 1000,
            height: container.scrollHeight,
            scrollX: 0,
            scrollY: 0
        });

        document.body.removeChild(container);

        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `تقرير_بالمناديب_${list.ListName}_${new Date().toISOString().split('T')[0]}.png`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showAlert('✅ تم حفظ الصورة المجمعة بالمناديب مع إعدادات الطباعة!', 'success');
        }, 'image/png', 0.95);

    } catch (error) {
        console.error('خطأ في إنشاء الصورة:', error);
        showAlert('❌ خطأ في إنشاء الصورة: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};

const exportSavedListAsImage = async (list) => {
    try {
        setIsLoading(true);
        showAlert('جاري إنشاء الصورة...', 'info');
        
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${list.ListID}`);
        const listDetails = await response.json();
        
        if (!listDetails.success) {
            throw new Error('فشل في جلب تفاصيل القائمة');
        }
        
        const { passports } = listDetails;
        const html2canvas = await loadHtml2Canvas();
        
        const container = document.createElement('div');
        container.style.cssText = `
            width: 1000px;
            padding: 30px;
            background: white;
            font-family: 'Arial', sans-serif;
            direction: rtl;
            color: black;
            font-size: 14px;
            line-height: 1.5;
            position: absolute;
            left: -9999px;
            top: -9999px;
        `;
 
        container.innerHTML = `
            <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid black; padding-bottom: 20px;">
                <div style="font-size: 36px; font-weight: bold; color: black; margin-bottom: 12px;">
                    📋 تقرير قائمة السفر
                </div>
                <div style="font-size: 28px; color: black; margin-bottom: 10px; background: #f0f0f0; padding: 15px; border: 2px solid black; border-radius: 8px; font-weight: bold;">
                    ${list.ListName}
                </div>
                <div style="font-size: 18px; color: black; margin-bottom: 5px;">
                    الشركة: ${list.CompanyName}
                </div>
                <div style="font-size: 16px; color: black;">
                    تاريخ التصدير: ${new Date().toLocaleDateString('ar-EG', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long'
                    })} - ${new Date().toLocaleTimeString('ar-EG')}
                </div>
            </div>
 
            <div style="background: white; border: 2px solid black; border-radius: 8px; padding: 20px; margin-bottom: 25px;">
                <div style="font-size: 22px; font-weight: bold; color: black; margin-bottom: 15px; text-align: center; border-bottom: 1px solid black; padding-bottom: 10px;">
                    📊 ملخص القائمة
                </div>
                <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; text-align: center;">
                    <div style="background: white; padding: 15px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 8px; font-size: 16px;">العدد المطلوب</div>
                        <div style="font-size: 28px; font-weight: bold; color: black;">${list.RequiredCount}</div>
                    </div>
                    <div style="background: white; padding: 15px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 8px; font-size: 16px;">إجمالي المختارين</div>
                        <div style="font-size: 28px; font-weight: bold; color: black;">${list.PassportCount}</div>
                    </div>
                    <div style="background: white; padding: 15px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 8px; font-size: 16px;">مؤكد الحضور</div>
                        <div style="font-size: 28px; font-weight: bold; color: black;">${passports.filter(p => p.FinalConfirmationStatus === 1).length}</div>
                    </div>
                    <div style="background: white; padding: 15px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 8px; font-size: 16px;">في الانتظار</div>
                        <div style="font-size: 28px; font-weight: bold; color: black;">${passports.filter(p => p.FinalConfirmationStatus !== 1).length}</div>
                    </div>
                </div>
            </div>
 
            <table style="width: 100%; border-collapse: collapse; background: white; border: 2px solid black;">
                <thead>
                    <tr style="background: #f0f0f0;">
                        <th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold; width: 50px;">م</th>
                        <th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الاسم</th>
                        ${printSettings.showCompany ? `<th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">${printSettings.showPassportID ? 'الشركة + ID:' : 'الشركة'}</th>` : ''}
                        ${printSettings.showDelegate ? `<th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">المندوب</th>` : ''}
                        ${printSettings.showProfession ? `<th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">المهنة</th>` : ''}
                        ${printSettings.showPhone ? `<th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold; width: 160px;">أرقام الهاتف</th>` : ''}
                        <th style="padding: 12px 8px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الحالة</th>
                    </tr>
                </thead>
                <tbody>
                    ${passports.map((passport, index) => {
                        const isConfirmed = passport.FinalConfirmationStatus === 1;
                        
                        let professionName = 'غير محدد';
                        if (passport.ProfessionName) {
                            professionName = passport.ProfessionName;
                        } else if (passport.PassportProfessionName) {
                            professionName = passport.PassportProfessionName;
                        } else if (passport.PassportProfessionID) {
                            const prof = professions.find(p => p.ProfessionID === parseInt(passport.PassportProfessionID));
                            if (prof) professionName = prof.ProfessionName;
                        }
                        
                        return `
                        <tr style="${index % 2 === 0 ? 'background: #f8f8f8;' : 'background: white;'}">
                            <td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold; background: #e0e0e0;">${index + 1}</td>
                            <td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 13px; color: black; font-weight: bold;">${passport.NameArabic || 'غير محدد'}</td>
                            ${printSettings.showCompany ? `<td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold; background: #e8f5e8;">${getPassportCompanyName(passport, list.CompanyName)}${printSettings.showPassportID ? `<br><small style="color: #333; font-size: 10px; font-family: monospace;">ID:: ${getPassportFileNumber(passport)}</small>` : ''}</td>` : ''}
                            ${printSettings.showDelegate ? `<td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black; background: #e7f3ff; font-weight: bold;">${passport.DelegateName || 'غير محدد'}</td>` : ''}
                            ${printSettings.showProfession ? `<td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black; background: #fff3e0;">${professionName}</td>` : ''}
                            ${printSettings.showPhone ? `<td style="padding: 10px 6px; text-align: right; border: 1px solid black; font-size: 11px; color: black; vertical-align: top;">
    ${(() => {
        const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px; background: #f0f0f0; border-radius: 3px; font-family: monospace;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic;">غير محدد</div>';
    })()}
</td>` : ''}                            <td style="padding: 10px 6px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">
                                <span style="${isConfirmed ? 'background: #d4edda; color: black; border: 1px solid black;' : 'background: #fff3cd; color: black; border: 1px solid black;'} font-weight: bold; padding: 4px 8px; border-radius: 4px;">
                                    ${isConfirmed ? '✓ مؤكد' : '○ منتظر'}
                                </span>
                            </td>
                        </tr>
                        `;
                    }).join('')}
                </tbody>
            </table>
 
            <div style="margin-top: 25px; text-align: center; color: black; font-size: 14px; border-top: 2px solid black; padding-top: 15px;">
                <div style="margin-bottom: 8px; font-weight: bold;">
                    نظام إدارة السفر المتطور | القائمة: ${list.ListName}
                </div>
                <div style="font-size: 12px;">
                    تم إنشاء هذا التقرير بتاريخ: ${new Date().toLocaleString('ar-EG')}
                </div>
                <div style="font-size: 12px; margin-top: 5px; color: #666;">
                    إعدادات العرض: ${[
                        'الرقم',
                        'الاسم',
                        printSettings.showCompany ? 'الشركة' : null,
                        printSettings.showDelegate ? 'المندوب' : null,
                        printSettings.showProfession ? 'المهنة' : null,
                        printSettings.showPhone ? 'الهاتف' : null,
                        'الحالة'
                    ].filter(Boolean).join(' • ')}
                </div>
            </div>
        `;
 
        document.body.appendChild(container);
 
        const canvas = await html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: 1000,
            height: container.scrollHeight,
            scrollX: 0,
            scrollY: 0
        });
 
        document.body.removeChild(container);
 
        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `تقرير_${list.ListName}_${new Date().toISOString().split('T')[0]}.png`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showAlert('✅ تم حفظ الصورة بنجاح مع تطبيق إعدادات الطباعة!', 'success');
        }, 'image/png', 0.95);
 
    } catch (error) {
        console.error('خطأ في إنشاء الصورة:', error);
        showAlert('❌ خطأ في إنشاء الصورة: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
 };



// تنظيف نهائي للسجلات المخفية المحفوظة التي تمنع حذف القائمة
const cleanupHiddenFollowUpsAndDeleteList = async (list, blockResult = {}) => {
    if (!list?.ListID) {
        showAlert('❌ لا توجد قائمة محددة للتنظيف', 'error');
        return;
    }

    const people = Array.isArray(blockResult.people) ? blockResult.people : [];
    const peoplePreview = people.slice(0, 10).map((person, index) => {
        const name = person.nameArabic || person.NameArabic || 'غير محدد';
        const passportNumber = person.passportNumber || person.PassportNumber || '';
        return `${index + 1}. ${name}${passportNumber ? ` - جواز: ${passportNumber}` : ''}`;
    }).join('\n');

    const confirmMessage = `⚠️ تنظيف نهائي لبيانات متابعة مخفية ⚠️\n\nالقائمة: ${list.ListName || list.listName || `رقم ${list.ListID}`}\n\nسبب المنع: توجد بيانات متابعة مخفية محفوظة مثل كشف/بصمة/تأمين/تسليم أوراق.\n\nسيتم عند التأكيد:\n- حذف سجلات المتابعة المخفية الخاصة بهذه القائمة فقط\n- حذف بيانات الكشف/البصمة/التأمين/تسليم الأوراق المخزنة داخل سجلات هذه القائمة القديمة\n- تنظيف تاريخ النت/القنصلية/التأشيرة/التأشيرة المحملة/السفر\n- تنظيف الكشف/البصمة/التأمين/تسليم الأوراق من بيانات الجواز العامة إذا لم يكن الشخص في قائمة أخرى نشطة\n- حذف القائمة حذفًا نهائيًا بدون أرشفة\n\nلن يتم تنظيف بيانات جدول الجوازات العام لأي شخص له حضور ظاهر أو مؤكد في قائمة أخرى.\n\n${peoplePreview ? `الأشخاص المتأثرون:\n${peoplePreview}${people.length > 10 ? `\n... و ${people.length - 10} آخرين` : ''}\n\n` : ''}هل تريد المتابعة؟`;

    if (!window.confirm(confirmMessage)) {
        showAlert('تم إلغاء التنظيف النهائي', 'info');
        return;
    }

    const confirmText = prompt('للتأكيد النهائي اكتب: تنظيف نهائي');
    if (confirmText !== 'تنظيف نهائي') {
        showAlert('❌ لم يتم التنظيف - يجب كتابة "تنظيف نهائي" للتأكيد', 'warning');
        return;
    }

    try {
        setIsLoading(true);
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${list.ListID}/cleanup-hidden-followups`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ confirmText })
        });

        const result = await response.json();
        if (!result.success) {
            throw new Error(result.error || 'فشل تنظيف بيانات المتابعة المخفية');
        }

        if (currentList?.ListID === list.ListID) {
            setCurrentList(null);
            setSelectedPassports([]);
            setActiveTab('selection');
        }

        await fetchSavedLists();
        showAlert(`✅ تم التنظيف النهائي وحذف القائمة\n🧹 سجلات متابعة محذوفة: ${result.deletedDetails?.followUpsDeleted || 0}\n🔄 جوازات تم تنظيفها: ${result.deletedDetails?.passportsCleaned || 0}\n🛡️ جوازات لم تُلمس لوجودها في قوائم أخرى: ${result.deletedDetails?.passportsSkippedBecauseInOtherLists || 0}`, 'success');
    } catch (error) {
        showAlert(`❌ فشل التنظيف النهائي: ${error.message}`, 'error');
    } finally {
        setIsLoading(false);
    }
};


// تنظيف كامل لشخص واحد من القائمة الحالية فقط
// زر بنفسجي للطوارئ: يمسح سجل المتابعة الخاص بهذا الشخص في هذه القائمة ويحذفه من تفاصيل القائمة
// مع حماية بيانات Passports العامة إذا كان الشخص موجوداً في قائمة أخرى نشطة.
const cleanupPersonFollowUpFull = async (passportId) => {
    const passport = selectedPassports.find(p => p.PassportID === passportId);
    if (!passport) {
        showAlert('❌ لم يتم العثور على بيانات الشخص', 'error');
        return;
    }

    if (!currentList?.ListID) {
        showAlert('❌ لا توجد قائمة حالية لتنظيف بيانات الشخص منها', 'error');
        return;
    }

    const personName = passport.NameArabic || 'غير محدد';
    const listName = currentList.ListName || currentList.listName || `رقم ${currentList.ListID}`;

    const confirmMessage = `⚠️ تنظيف كامل لبيانات شخص واحد ⚠️\n\nالشخص: ${personName}\nالقائمة: ${listName}\n\nسيتم عند التأكيد:\n- حذف سجل المتابعة الخاص بهذا الشخص من هذه القائمة فقط\n- حذف الكشف/البصمة/التأمين/تسليم الأوراق المسجلة داخل سجل هذه القائمة\n- حذف الشخص من تفاصيل القائمة\n- تنظيف تواريخ النت/القنصلية/التأشيرة/التأشيرة المحملة/السفر\n\nلو الشخص موجود في قائمة أخرى نشطة أو مؤكدة، لن يتم لمس بيانات الجواز العامة الخاصة به حتى لا تتأثر القائمة الحالية.\n\nهل تريد المتابعة؟`;

    if (!window.confirm(confirmMessage)) {
        showAlert('تم إلغاء تنظيف بيانات الشخص', 'info');
        return;
    }

    const confirmText = prompt('للتأكيد النهائي اكتب: تنظيف كامل');
    if (confirmText !== 'تنظيف كامل') {
        showAlert('❌ لم يتم التنظيف - يجب كتابة "تنظيف كامل" للتأكيد', 'warning');
        return;
    }

    try {
        setIsLoading(true);
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${currentList.ListID}/passports/${passportId}/full-cleanup-followup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ confirmText })
        });

        const result = await response.json();
        if (!response.ok || !result.success) {
            throw new Error(result.error || 'فشل تنظيف بيانات الشخص');
        }

        setSelectedPassports(prev => prev.filter(p => p.PassportID !== passportId));
        await fetchSavedLists();

        showAlert(
            `✅ تم تنظيف ${personName} من هذه القائمة\n` +
            `🧹 سجلات متابعة محذوفة: ${result.details?.followUpsDeleted || 0}\n` +
            `🗑️ حذف من تفاصيل القائمة: ${result.details?.selectionDetailsDeleted || 0}\n` +
            (result.details?.passportFullCleanupSkipped
                ? `🛡️ لم يتم لمس بيانات الجواز العامة لوجود الشخص في قائمة أخرى`
                : `🔄 تم تنظيف بيانات الجواز العامة`),
            'success'
        );
    } catch (error) {
        console.error('خطأ في تنظيف بيانات الشخص:', error);
        showAlert(`❌ فشل تنظيف بيانات الشخص: ${error.message}`, 'error');
    } finally {
        setIsLoading(false);
    }
};

// دالة تشخيص الأرقام - ضعها مؤقتاً قبل دالة exportForWhatsApp
const debugPhoneNumbers = (phoneNumbers, personName) => {
    console.log(`=== تشخيص أرقام ${personName} ===`);
    console.log('البيانات الخام:', phoneNumbers);
    console.log('نوع البيانات:', typeof phoneNumbers);
    
    if (typeof phoneNumbers === 'string' && phoneNumbers.startsWith('[')) {
        try {
            const parsed = JSON.parse(phoneNumbers);
            console.log('بعد parsing:', parsed);
        } catch (e) {
            console.log('خطأ في parsing:', e);
        }
    }
};

// استخراج اسم المحافظة من الحقل الفعلي في جدول Passports
const getGovernorateName = (passport) => {
    const value = passport?.Governorate;
    return value !== undefined && value !== null && String(value).trim() !== ''
        ? String(value).trim()
        : 'غير محدد';
};

// دالة تصدير أرقام الواتساب - كاملة مع التشخيص
const exportForWhatsApp = async (list) => {
    try {
        setIsLoading(true);
        showAlert('جاري تحضير أرقام الواتساب...', 'info');
        
        // جلب تفاصيل القائمة
        const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${list.ListID}`);
        const listDetails = await response.json();
        
        if (!listDetails.success) {
            throw new Error('فشل في جلب تفاصيل القائمة');
        }
        
        const { passports } = listDetails;
        
        // تحضير البيانات للتصدير
        const whatsappData = [];
        
        // إضافة الهيدر
        whatsappData.push(['Name', 'Given Name', 'Additional Name', 'Family Name', 'Phone 1 - Type', 'Phone 1 - Value']);
        
        // معالجة كل شخص
        passports.forEach((passport, index) => {
            const personName = passport.NameArabic || 'غير محدد';
            const delegateName = passport.DelegateName || 'غير محدد';
            const companyName = getPassportCompanyName(passport, list.CompanyName);
            const governorateName = getGovernorateName(passport);
            const fileNumber = getPassportFileNumber(passport);
            
            console.log(`=== معالجة الشخص ${index + 1}: ${personName} ===`);
            console.log('بيانات الجواز الكاملة:', passport);
            console.log('أرقام الهاتف الخام:', passport.PhoneNumbers);
            
            // استخراج كل أرقام الهاتف للشخص
            const phoneNumbers = extractAllPhoneNumbers(passport.PhoneNumbers, personName);
            
            console.log(`عدد الأرقام المستخرجة: ${phoneNumbers.length}`);
            console.log('الأرقام المستخرجة:', phoneNumbers);
            
            if (phoneNumbers.length > 0) {
                // إضافة كل رقم كجهة اتصال منفصلة
                phoneNumbers.forEach((phone, phoneIndex) => {
                    // لو الشخص له أكثر من رقم نضيف 1 / 2 / 3 بعد الاسم مباشرة.
                    // مثال: أحمد محمد 1 - المندوب - الشركة - المحافظة - رقم الملف
                    const numberedPersonName = phoneNumbers.length > 1
                        ? `${personName} ${phoneIndex + 1}`
                        : personName;

                    // الاسم النهائي الظاهر في جهات الاتصال:
                    // الاسم [رقم الهاتف عند التعدد] - المندوب - الشركة - المحافظة - رقم الملف
                    const contactName = `${numberedPersonName} - ${delegateName} - ${companyName} - ${governorateName} - ${fileNumber}`;
                    
                    console.log(`إضافة جهة اتصال: ${contactName} - ${phone}`);
                    
                    // نضع الاسم كاملاً في Name و Given Name ونترك باقي أجزاء الاسم فارغة
                    // لمنع Google Contacts من إعادة ترتيب المندوب/الشركة تلقائياً.
                    whatsappData.push([
                        contactName,           // Name
                        contactName,           // Given Name
                        '',                    // Additional Name
                        '',                    // Family Name
                        'Mobile',              // Phone 1 - Type
                        phone                  // Phone 1 - Value
                    ]);
                });
            } else {
                console.log(`❌ لا توجد أرقام صحيحة للشخص: ${personName}`);
            }
            
            console.log('================================');
        });
        
        console.log(`=== النتيجة النهائية ===`);
        console.log(`عدد جهات الاتصال (مع الهيدر): ${whatsappData.length}`);
        console.log(`عدد جهات الاتصال الفعلية: ${whatsappData.length - 1}`);
        
        // التحقق من وجود أرقام صالحة
        if (whatsappData.length <= 1) { // فقط الهيدر
            console.log('❌ لا توجد أرقام صحيحة - عرض تفاصيل إضافية...');
            
            // عرض تفاصيل أكثر للتشخيص
            passports.slice(0, 3).forEach((passport, idx) => {
                console.log(`عينة ${idx + 1}:`, {
                    name: passport.NameArabic,
                    phones: passport.PhoneNumbers,
                    phoneType: typeof passport.PhoneNumbers
                });
            });
            
            showAlert('❌ لا توجد أرقام هواتف صحيحة في هذه القائمة. تحقق من الكونسول لمزيد من التفاصيل.', 'error');
            return;
        }
        
        // تحويل إلى CSV بتنسيق Google Contacts
        const csvContent = whatsappData.map(row => 
            row.map(cell => `"${cell}"`).join(',')
        ).join('\n');
        
        // إضافة BOM للدعم العربي
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { 
            type: 'text/csv;charset=utf-8;' 
        });
        
        // تحميل الملف
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `جهات_واتساب_${list.ListName.replace(/[^\w\s]/gi, '')}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // حساب إجمالي الأرقام
        const totalNumbers = whatsappData.length - 1; // ناقص الهيدر
        
        console.log(`✅ تم تصدير ${totalNumbers} جهة اتصال بنجاح`);
        
        // عرض تعليمات الاستخدام
        showWhatsAppInstructions(totalNumbers, list.ListName);
        
    } catch (error) {
        console.error('خطأ في تصدير أرقام الواتساب:', error);
        showAlert('❌ خطأ في التصدير: ' + error.message, 'error');
    } finally {
        setIsLoading(false);
    }
};

// تعليمات بسيطة
const showSimpleInstructions = (contactCount, companyName) => {
    alert(`تم تحضير ${contactCount} جهة اتصال!

الخطوات:
1. افتح الملف المحمل (.vcf) في الجوال
2. اضغط "Add to contacts" أو "Import"
3. من الواتساب، اعمل New Group
4. ابحث عن "${companyName}"
5. حدد الكل واعمل الجروب!

الملف متوافق مع كل الجوالات! 📱✅`);
};




// دالة عرض تعليمات استخدام نظام Labels
const showLabelsInstructions = (contactCount, labelName, list) => {
    const modalHTML = `
        <div id="labelsInstructionsModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                <div class="bg-green-600 text-white p-6 rounded-t-2xl">
                    <div class="flex justify-between items-center">
                        <div>
                            <h3 class="text-xl font-bold">🏷️ تم تحضير ${contactCount} جهة اتصال مع Label منظم!</h3>
                            <p class="text-green-100">Label المقترح: ${labelName}</p>
                        </div>
                        <button id="closeLabelsBtn" class="text-white hover:bg-green-700 p-2 rounded-full">
                            ✕
                        </button>
                    </div>
                </div>
                
                <div class="p-6 space-y-6">
                    <div class="bg-blue-50 rounded-lg p-6 border border-blue-200">
                        <h4 class="font-bold text-blue-900 mb-4 flex items-center gap-2">
                            🎯 خطوات استيراد الأرقام مع Labels:
                        </h4>
                        
                        <div class="space-y-4">
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                                <div>
                                    <h5 class="font-semibold text-blue-900">افتح Google Contacts</h5>
                                    <p class="text-blue-800 text-sm">روح على contacts.google.com من الكمبيوتر</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                                <div>
                                    <h5 class="font-semibold text-blue-900">اضغط Import</h5>
                                    <p class="text-blue-800 text-sm">اختار الملف اللي اتحمل دلوقتي</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                                <div>
                                    <h5 class="font-semibold text-green-900">🏷️ تأكيد الـ Label</h5>
                                    <p class="text-green-800 text-sm"><strong>هيظهر لك Label تلقائياً:</strong> "${labelName}"</p>
                                    <p class="text-green-700 text-xs mt-1">أو ممكن تغير الاسم لو عايز</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                                <div>
                                    <h5 class="font-semibold text-green-900">انتظر التزامن</h5>
                                    <p class="text-green-800 text-sm">استنى 5-10 دقايق لحد ما الأرقام تظهر في جهات الاتصال</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-purple-50 rounded-lg p-6 border border-purple-200">
                        <h4 class="font-bold text-purple-900 mb-4 flex items-center gap-2">
                            📱 إنشاء جروب الواتساب من الـ Label:
                        </h4>
                        
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div class="space-y-3">
                                <h5 class="font-semibold text-purple-800">🔍 الطريقة الأولى: البحث</h5>
                                <ol class="text-purple-700 text-sm space-y-1">
                                    <li>1. من الواتساب، اضغط "New Group"</li>
                                    <li>2. في خانة البحث اكتب: "<strong>${list.CompanyName}</strong>"</li>
                                    <li>3. هيظهر كل الأشخاص من الـ Label ده</li>
                                    <li>4. اضغط "Select All" وكمل عمل الجروب</li>
                                </ol>
                            </div>
                            
                            <div class="space-y-3">
                                <h5 class="font-semibold text-purple-800">📋 الطريقة التانية: من جهات الاتصال</h5>
                                <ol class="text-purple-700 text-sm space-y-1">
                                    <li>1. افتح جهات الاتصال في الجوال</li>
                                    <li>2. دور على الـ Label: "<strong>${labelName}</strong>"</li>
                                    <li>3. حدد الكل واضغط "Share"</li>
                                    <li>4. اختار "WhatsApp" > "New Group"</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <div class="flex items-start gap-3">
                            <span class="text-yellow-600 text-xl">💡</span>
                            <div>
                                <h5 class="font-semibold text-yellow-900">مميزات نظام الـ Labels:</h5>
                                <ul class="text-yellow-800 text-sm mt-2 space-y-1">
                                    <li>• <strong>منظم:</strong> كل قائمة لها Label منفصل</li>
                                    <li>• <strong>مرن:</strong> تقدر تضيف قوائم جديدة بدون تداخل</li>
                                    <li>• <strong>سهل البحث:</strong> ابحث باسم الشركة أو التاريخ</li>
                                    <li>• <strong>سهل الحذف:</strong> امسح Label كامل لما تخلص</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div class="flex items-center gap-3 mb-3">
                            <span class="text-green-600 text-xl">📊</span>
                            <h5 class="font-semibold text-green-900">تفاصيل هذا الـ Label:</h5>
                        </div>
                        <div class="grid grid-cols-2 gap-4 text-sm">
                            <div class="text-green-800">
                                <strong>اسم الـ Label:</strong> ${labelName}
                            </div>
                            <div class="text-green-800">
                                <strong>عدد جهات الاتصال:</strong> ${contactCount}
                            </div>
                            <div class="text-green-800">
                                <strong>الشركة:</strong> ${list.CompanyName}
                            </div>
                            <div class="text-green-800">
                                <strong>المؤكدين:</strong> ${list.ConfirmedCount || 0} شخص
                            </div>
                        </div>
                    </div>
                    
                    <div class="flex gap-3">
                        <button 
                            id="openGoogleContactsBtn"
                            class="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                        >
                            🌐 فتح Google Contacts
                        </button>
                        <button 
                            id="copyLabelNameBtn"
                            class="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                        >
                            📋 نسخ اسم الـ Label
                        </button>
                        <button 
                            id="closeLabelsBtn2"
                            class="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium"
                        >
                            فهمت، شكراً
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    // إضافة المودال للصفحة
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // إضافة Event Listeners
    const modal = document.getElementById('labelsInstructionsModal');
    const closeBtn1 = document.getElementById('closeLabelsBtn');
    const closeBtn2 = document.getElementById('closeLabelsBtn2');
    const openContactsBtn = document.getElementById('openGoogleContactsBtn');
    const copyLabelBtn = document.getElementById('copyLabelNameBtn');

    const closeModal = () => {
        if (modal) modal.remove();
    };

    const openGoogleContacts = () => {
        window.open('https://contacts.google.com', '_blank');
    };

    const copyLabelName = async () => {
        try {
            await navigator.clipboard.writeText(labelName);
            showAlert('✅ تم نسخ اسم الـ Label! استخدمه في البحث بالواتساب', 'success');
        } catch (error) {
            // Fallback
            const textArea = document.createElement('textarea');
            textArea.value = labelName;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            showAlert('✅ تم نسخ اسم الـ Label!', 'success');
        }
    };

    // ربط الأحداث
    if (closeBtn1) closeBtn1.addEventListener('click', closeModal);
    if (closeBtn2) closeBtn2.addEventListener('click', closeModal);
    if (openContactsBtn) openContactsBtn.addEventListener('click', openGoogleContacts);
    if (copyLabelBtn) copyLabelBtn.addEventListener('click', copyLabelName);

    // إغلاق عند الضغط على الخلفية
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });
};


const extractAllPhoneNumbers = (phoneNumbers, personName = '') => {
    if (!phoneNumbers) return [];
    
    const validPhones = [];
    
    try {
        let phones = [];
        
        if (typeof phoneNumbers === 'string') {
            if (phoneNumbers.startsWith('[')) {
                // JSON array
                phones = JSON.parse(phoneNumbers);
            } else {
                // أرقام مفصولة بفاصلة أو سطر جديد
                phones = phoneNumbers.split(/[,،\n\r\t|;]/)
                    .map(p => p.trim())
                    .filter(p => p && p.length > 0);
            }
        } else if (Array.isArray(phoneNumbers)) {
            phones = phoneNumbers;
        } else {
            phones = [phoneNumbers.toString()];
        }
        
        // معالجة كل رقم
        phones.forEach((phone) => {
            if (phone && phone.toString().trim()) {
                let cleanPhone = phone.toString().trim();
                
                // ✅ إزالة +2 من بداية الرقم لو موجود
                if (cleanPhone.startsWith('+2')) {
                    cleanPhone = cleanPhone.substring(2); // إزالة +2
                }
                
                // إزالة أي رموز غير مرغوبة
                cleanPhone = cleanPhone.replace(/[^\d]/g, '');
                
                // قبول الأرقام اللي طولها معقول
                if (cleanPhone && cleanPhone.length >= 10 && cleanPhone.length <= 15) {
                    validPhones.push(cleanPhone);
                }
            }
        });
        
    } catch (error) {
        console.error('خطأ في معالجة أرقام الهاتف:', error);
        return [];
    }
    
    // إزالة التكرار
    const uniquePhones = [...new Set(validPhones)];
    
    console.log(`📱 تم استخراج ${uniquePhones.length} رقم لـ ${personName}:`, uniquePhones);
    
    return uniquePhones;
};

// دالة عرض تعليمات استخدام ملف الواتساب
const showWhatsAppInstructions = (phoneCount, listName) => {
    const modalHTML = `
        <div id="whatsappInstructionsModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div class="bg-green-600 text-white p-6 rounded-t-2xl">
                    <div class="flex justify-between items-center">
                        <div>
                            <h3 class="text-xl font-bold">📱 تم تصدير ${phoneCount} رقم واتساب بنجاح!</h3>
                            <p class="text-green-100">القائمة: ${listName}</p>
                        </div>
                        <button id="closeInstructionsBtn" class="text-white hover:bg-green-700 p-2 rounded-full">
                            ✕
                        </button>
                    </div>
                </div>
                
                <div class="p-6 space-y-6">
                    <div class="bg-blue-50 rounded-lg p-6 border border-blue-200">
                        <h4 class="font-bold text-blue-900 mb-4 flex items-center gap-2">
                            🎯 الخطوات التالية لإنشاء جروب الواتساب:
                        </h4>
                        
                        <div class="space-y-4">
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                                <div>
                                    <h5 class="font-semibold text-blue-900">افتح Google Contacts</h5>
                                    <p class="text-blue-800 text-sm">روح على contacts.google.com من الكمبيوتر</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                                <div>
                                    <h5 class="font-semibold text-blue-900">استورد الملف</h5>
                                    <p class="text-blue-800 text-sm">اضغط "Import" واختار الملف اللي اتحمل دلوقتي</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                                <div>
                                    <h5 class="font-semibold text-blue-900">إنشاء مجموعة (Label)</h5>
                                    <p class="text-blue-800 text-sm">اختار "Create a label" وسميها "${listName}"</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                                <div>
                                    <h5 class="font-semibold text-blue-900">انتظر التزامن</h5>
                                    <p class="text-blue-800 text-sm">استنى 5-10 دقايق لحد ما الأرقام تظهر في جهات الاتصال</p>
                                </div>
                            </div>
                            
                            <div class="flex gap-4">
                                <div class="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">5</div>
                                <div>
                                    <h5 class="font-semibold text-green-900">إنشاء الجروب</h5>
                                    <p class="text-green-800 text-sm">من الواتساب، اعمل "New Group" وابحث عن "${listName}" وضيف الكل!</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <div class="flex items-start gap-3">
                            <span class="text-yellow-600 text-xl">💡</span>
                            <div>
                                <h5 class="font-semibold text-yellow-900">نصائح مهمة:</h5>
                                <ul class="text-yellow-800 text-sm mt-2 space-y-1">
                                    <li>• تأكد إنك مسجل دخول بنفس حساب Google في الجوال والكمبيوتر</li>
                                    <li>• لو الأرقام مش ظاهرة، جرب تعمل Sync للجهات من إعدادات الجوال</li>
                                    <li>• الحد الأقصى للجروب واتساب 1024 شخص</li>
                                    <li>• ممكن تقسم القائمة لأكتر من جروب لو العدد كبير</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div class="flex items-center gap-3 mb-3">
                            <span class="text-green-600 text-xl">📊</span>
                            <h5 class="font-semibold text-green-900">ملف الأرقام يحتوي على:</h5>
                        </div>
                        <div class="grid grid-cols-2 gap-4 text-sm">
                            <div class="text-green-800">
                                <strong>إجمالي الأرقام:</strong> ${phoneCount}
                            </div>
                            <div class="text-green-800">
                                <strong>اسم الملف:</strong> واتساب_${listName}
                            </div>
                            <div class="text-green-800">
                                <strong>التنسيق:</strong> أرقام دولية (+20...)
                            </div>
                            <div class="text-green-800">
                                <strong>الأرقام المتعددة:</strong> 1 / 2 / 3 بعد الاسم مباشرة
                            </div>
                        </div>
                    </div>
                    
                    <div class="flex gap-3">
                        <button 
                            id="openGoogleContactsBtn"
                            class="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                        >
                            🌐 فتح Google Contacts
                        </button>
                        <button 
                            id="closeInstructionsBtn2"
                            class="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium"
                        >
                            فهمت، شكراً
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    // إضافة المودال للصفحة
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // إضافة Event Listeners بدلاً من window functions
    const modal = document.getElementById('whatsappInstructionsModal');
    const closeBtn1 = document.getElementById('closeInstructionsBtn');
    const closeBtn2 = document.getElementById('closeInstructionsBtn2');
    const openContactsBtn = document.getElementById('openGoogleContactsBtn');

    const closeModal = () => {
        if (modal) modal.remove();
    };

    const openGoogleContacts = () => {
        window.open('https://contacts.google.com', '_blank');
        closeModal();
    };

    // ربط الأحداث
    if (closeBtn1) closeBtn1.addEventListener('click', closeModal);
    if (closeBtn2) closeBtn2.addEventListener('click', closeModal);
    if (openContactsBtn) openContactsBtn.addEventListener('click', openGoogleContacts);

    // إغلاق عند الضغط على الخلفية
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });
};




const printDelegateList = (delegateGroup, filteredPassports, listInfo) => {
    const printWindow = window.open('', '_blank');
    
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>طباعة قائمة ${delegateGroup.delegateInfo?.DelegateName || 'مندوب غير معروف'}</title>
            <style>
                @page {
                    size: A4;
                    margin: 12mm 8mm;
                }
                
                * { 
                    margin: 0; 
                    padding: 0; 
                    box-sizing: border-box; 
                }
                
                body { 
                    font-family: 'Arial Black', Arial, sans-serif;
                    direction: rtl;
                    background: white;
                    color: #000000 !important;
                    font-size: 16px;
                    line-height: 1.3;
                    font-weight: bold;
                }
                
                .print-container {
                    max-width: 100%;
                    margin: 0;
                    background: white;
                    padding: 8px;
                }
                
                /* رأس التقرير */
                .report-header {
                    text-align: center;
                    margin-bottom: 20px;
                    border-bottom: 3px solid #000000;
                    padding-bottom: 12px;
                }
                
                .company-name {
                    font-size: 32px;
                    font-weight: 900;
                    color: #000000 !important;
                    margin-bottom: 8px;
                }
                
                .delegate-name {
                    font-size: 28px;
                    color: #000000 !important;
                    margin-bottom: 8px;
                    background: #ffffff;
                    padding: 10px;
                    border: 3px solid #000000;
                    border-radius: 8px;
                    font-weight: 900;
                }
                
                .list-info {
                    font-size: 16px;
                    color: #000000 !important;
                    margin-bottom: 4px;
                    font-weight: bold;
                }
                
                /* رسالة للمندوب */
                .message-section {
                    background: #ffffff;
                    border: 3px solid #000000;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 15px;
                    text-align: center;
                    page-break-inside: auto;
                }
                
                .message-title {
                    font-size: 20px;
                    font-weight: 900;
                    color: #000000 !important;
                    margin-bottom: 8px;
                }
                
                .message-text {
                    font-size: 14px;
                    color: #000000 !important;
                    line-height: 1.4;
                    font-weight: bold;
                }
                
                /* قسم الملخص */
                .summary-section {
                    background: white;
                    border: 3px solid #000000;
                    border-radius: 8px;
                    padding: 12px;
                    margin-bottom: 15px;
                }
                
                .summary-title {
                    font-size: 18px;
                    font-weight: 900;
                    color: #000000 !important;
                    margin-bottom: 10px;
                    text-align: center;
                    border-bottom: 2px solid #000000;
                    padding-bottom: 6px;
                }
                
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 10px;
                    text-align: center;
                }
                
                .summary-item {
                    background: white;
                    padding: 8px;
                    border: 2px solid #000000;
                    border-radius: 6px;
                }
                
                .summary-label {
                    font-weight: 900;
                    color: #000000 !important;
                    margin-bottom: 4px;
                    font-size: 14px;
                }
                
                .summary-value {
                    font-size: 20px;
                    font-weight: 900;
                    color: #000000 !important;
                }
                
                /* جدول البيانات */
                .table-container {
                    margin-top: 10px;
                    overflow-x: auto;
                }
                
                table { 
                    width: 100%; 
                    border-collapse: collapse;
                    background: white;
                    border: 3px solid #000000;
                }
                
                th, td { 
                    padding: 8px 6px;
                    text-align: center;
                    border: 2px solid #000000;
                    font-size: 13px;
                    color: #000000 !important;
                    font-weight: bold;
                    background: #ffffff !important;
                }
                
                th { 
                    background: #ffffff !important;
                    font-weight: 900;
                    color: #000000 !important;
                    font-size: 14px;
                    border: 3px solid #000000 !important;
                }
                
                tr:nth-child(even) { 
                    background: #ffffff !important; 
                }
                
                tr:nth-child(odd) { 
                    background: #ffffff !important; 
                }
                
                .row-number {
                    font-weight: 900;
                    background: #ffffff !important;
                    font-size: 14px;
                    color: #000000 !important;
                    border: 2px solid #000000 !important;
                }
                
                .name-cell {
                    font-weight: 900;
                    font-size: 14px;
                    color: #000000 !important;
                    background: #ffffff !important;
                }
                
                .phone-cell {
                    font-family: 'Arial', monospace;
                    text-align: right;
                    vertical-align: top;
                    padding: 6px;
                    color: #000000 !important;
                    font-weight: bold;
                    background: #ffffff !important;
                }
                
                .phone-number {
                    margin: 1px 0;
                    padding: 2px 4px;
                    background: #ffffff !important;
                    border-radius: 3px;
                    font-size: 11px;
                    border: 1px solid #000000;
                    display: block;
                    color: #000000 !important;
                    font-weight: bold;
                }
                
                .status-confirmed {
                    background: #ffffff !important;
                    color: #000000 !important;
                    font-weight: 900;
                    padding: 4px 8px;
                    border-radius: 4px;
                    border: 2px solid #000000;
                }
                
                .status-pending {
                    background: #ffffff !important;
                    color: #000000 !important;
                    font-weight: 900;
                    padding: 4px 8px;
                    border-radius: 4px;
                    border: 2px solid #000000;
                }
                
                /* التذييل */
                .footer {
                    margin-top: 15px;
                    text-align: center;
                    color: #000000 !important;
                    font-size: 12px;
                    border-top: 2px solid #000000;
                    padding-top: 8px;
                    font-weight: bold;
                }
                
                /* إعدادات الطباعة المحسنة */
                @media print {
                    body { 
                        font-size: 13px;
                        color: #000000 !important;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    
                    * {
                        color: #000000 !important;
                        background-color: #ffffff !important;
                    }
                    
                    .print-container {
                        padding: 5px;
                    }
                    
                    .company-name {
                        font-size: 26px;
                        color: #000000 !important;
                    }
                    
                    .delegate-name {
                        font-size: 22px;
                        color: #000000 !important;
                        background: #ffffff !important;
                        border: 3px solid #000000 !important;
                    }
                    
                    .list-info {
                        font-size: 14px;
                        color: #000000 !important;
                    }
                    
                    .message-section {
                        background: #ffffff !important;
                        border: 3px solid #000000 !important;
                        page-break-inside: auto;
                        margin-bottom: 10px;
                    }
                    
                    .message-title {
                        font-size: 18px;
                        color: #000000 !important;
                    }
                    
                    .message-text {
                        font-size: 13px;
                        color: #000000 !important;
                    }
                    
                    .summary-section {
                        background: white !important;
                        border: 3px solid #000000 !important;
                        margin-bottom: 10px;
                    }
                    
                    .summary-title {
                        font-size: 16px;
                        color: #000000 !important;
                        border-bottom: 2px solid #000000 !important;
                    }
                    
                    .summary-label {
                        color: #000000 !important;
                        font-size: 12px;
                    }
                    
                    .summary-value {
                        font-size: 18px;
                        color: #000000 !important;
                    }
                    
                    .summary-item {
                        border: 2px solid #000000 !important;
                        background: white !important;
                    }
                    
                    th, td { 
                        padding: 6px 4px; 
                        font-size: 11px;
                        color: #000000 !important;
                        border: 2px solid #000000 !important;
                        background: #ffffff !important;
                    }
                    
                    th {
                        font-size: 12px;
                        background: #ffffff !important;
                        color: #000000 !important;
                        border: 3px solid #000000 !important;
                    }
                    
                    tr:nth-child(even) { 
                        background: #ffffff !important; 
                    }
                    
                    tr:nth-child(odd) { 
                        background: #ffffff !important; 
                    }
                    
                    .row-number {
                        background: #ffffff !important;
                        color: #000000 !important;
                        border: 2px solid #000000 !important;
                    }
                    
                    .name-cell {
                        font-size: 12px;
                        color: #000000 !important;
                        background: #ffffff !important;
                    }
                    
                    .phone-cell {
                        background: #ffffff !important;
                    }
                    
                    .phone-number {
                        background: #ffffff !important;
                        border: 1px solid #000000 !important;
                        font-size: 10px;
                        color: #000000 !important;
                    }
                    
                    .status-confirmed {
                        background: #ffffff !important;
                        color: #000000 !important;
                        border: 2px solid #000000 !important;
                    }
                    
                    .status-pending {
                        background: #ffffff !important;
                        color: #000000 !important;
                        border: 2px solid #000000 !important;
                    }
                    
                    table {
                        border: 3px solid #000000 !important;
                        page-break-inside: auto;
                    }
                    
                    tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                    }
                    
                    .footer {
                        color: #000000 !important;
                        border-top: 2px solid #000000 !important;
                    }
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <!-- رأس التقرير -->
                <div class="report-header">
                    <div class="company-name">📋 قائمة العملاء</div>
                    <div class="delegate-name">المندوب: ${delegateGroup.delegateInfo?.DelegateName || 'غير محدد'}</div>
              <div class="list-info">
    القائمة: ${listInfo.ListName}
</div>
<div class="list-info">
    تاريخ الطباعة: ${new Date().toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
    })}
</div>
                </div>

                <!-- رسالة للمندوب -->
                <div class="message-section">
                    <div class="message-title">رسالة هامة للمندوب</div>
                    <div class="message-text">
                        السلام عليكم ورحمة الله وبركاته<br>
                        نرجو منكم إبلاغ العملاء المذكورين أدناه بموعد الحضور للمكتب للإجراءات المطلوبة.<br>
                        يرجى التأكد من حضور العملاء المؤكدين والتواصل مع العملاء في الانتظار لتأكيد حضورهم.<br>
                        <strong>مع تحيات إدارة الشركة</strong>
                    </div>
                </div>

                <!-- قسم الملخص -->
                <div class="summary-section">
                    <div class="summary-title">ملخص العملاء</div>
                    <div class="summary-grid">
                        <div class="summary-item">
                            <div class="summary-label">إجمالي العملاء</div>
                            <div class="summary-value">${filteredPassports.length}</div>
                        </div>
                        <div class="summary-item">
                            <div class="summary-label">مؤكد الحضور</div>
                            <div class="summary-value">${filteredPassports.filter(p => p.confirmed).length}</div>
                        </div>
                        <div class="summary-item">
                            <div class="summary-label">في الانتظار</div>
                            <div class="summary-value">${filteredPassports.filter(p => !p.confirmed).length}</div>
                        </div>
                    </div>
                </div>

                <!-- جدول العملاء -->
                <div class="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th style="width: 40px;">م</th>
                                <th style="width: 180px;">الاسم</th>
                                <th style="width: 100px;">رقم الجواز</th>
                                <th style="width: 160px;">أرقام الهاتف</th>
                                <th style="width: 80px;">الحالة</th>
                                <th style="width: 100px;">تاريخ النت</th>
                                <th style="width: 120px;">ملاحظات</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredPassports.map((passport, index) => `
                                <tr>
                                    <td class="row-number">${index + 1}</td>
                                    <td class="name-cell">${passport.NameArabic || 'غير محدد'}</td>
                                    <td style="font-family: monospace; color: #000000; font-weight: bold; background: #ffffff !important;">${passport.PassportNumber || 'غير محدد'}</td>
                                    <td class="phone-cell">
                                        ${(() => {
                                            const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
                                            return phones.length > 0 ? 
                                                phones.map(phone => `<span class="phone-number">📱 ${phone}</span>`).join('')
                                                : '<span style="color: #000000; font-style: italic; font-size: 11px; font-weight: bold; background: #ffffff !important;">غير محدد</span>';
                                        })()}
                                    </td>
                                    <td>
                                        <span class="${passport.confirmed ? 'status-confirmed' : 'status-pending'}">
                                            ${passport.confirmed ? '✓ مؤكد' : '○ انتظار'}
                                        </span>
                                    </td>
                                    <td style="color: #000000; font-weight: bold; font-size: 11px; background: #ffffff !important;">${passport.netDate ? new Date(passport.netDate).toLocaleDateString('ar-EG') : 'لم يتم التأكيد'}</td>
                                    <td style="font-size: 10px; color: #000000; font-weight: bold; background: #ffffff !important;">${getNotesText(passport.Notes) || '-'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>

                <!-- التذييل -->
                <div class="footer">
                    نظام إدارة السفر | 
                    طُبع بتاريخ: ${new Date().toLocaleString('ar-EG')} |
                    المندوب: ${delegateGroup.delegateInfo?.DelegateName || 'غير محدد'}
                </div>
            </div>
        </body>
        </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // انتظار تحميل المحتوى ثم الطباعة
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };
};



    // أضف هذه الدالة مع باقي الدوال قبل return
const validateListData = (listInfo, delegatesData) => {
    const totalFromDelegates = delegatesData.reduce((sum, d) => sum + d.totalCount, 0);
    const totalFromList = listInfo.PassportCount;
    const totalConfirmed = delegatesData.reduce((sum, d) => sum + d.confirmedCount, 0);
    
    console.log('=== مقارنة البيانات ===');
    console.log(`العدد من معلومات القائمة: ${totalFromList}`);
    console.log(`العدد من تجميع المناديب: ${totalFromDelegates}`);
    console.log(`إجمالي المؤكدين: ${totalConfirmed}`);
    
    const isValid = totalFromList === totalFromDelegates;
    const difference = totalFromList - totalFromDelegates;
    
    if (!isValid) {
        console.warn('⚠️ عدم تطابق في البيانات!');
        showAlert(`تحذير: عدد الأشخاص في القائمة (${totalFromList}) لا يطابق المجموع الفعلي (${totalFromDelegates})`, 'warning');
    } else {
        showAlert(`✅ البيانات متطابقة! العدد: ${totalFromList}، المؤكدين: ${totalConfirmed}`, 'success');
    }
    
    // عرض تفاصيل إضافية
    console.log('=== تفاصيل كل مندوب ===');
    delegatesData.forEach(delegate => {
        console.log(`${delegate.delegateInfo?.DelegateName}: إجمالي ${delegate.totalCount}, مؤكد ${delegate.confirmedCount}, انتظار ${delegate.pendingCount}`);
    });
    
    return {
        isValid,
        listTotal: totalFromList,
        calculatedTotal: totalFromDelegates,
        difference,
        confirmedTotal: totalConfirmed,
        pendingTotal: totalFromDelegates - totalConfirmed,
        delegatesCount: delegatesData.length,
        details: delegatesData.map(d => ({
            delegateName: d.delegateInfo?.DelegateName || 'غير معروف',
            total: d.totalCount,
            confirmed: d.confirmedCount,
            pending: d.pendingCount
        }))
    };
};

// دالة إضافية لعرض ملخص مفصل
const showDetailedSummary = () => {
    if (!selectedListForSending || delegatesInSelectedList.length === 0) {
        showAlert('لا توجد بيانات لعرضها', 'warning');
        return;
    }
    
    const validation = validateListData(selectedListForSending, delegatesInSelectedList);
    
    // إنشاء رسالة تفصيلية
    const summaryText = `
📊 ملخص تفصيلي للقائمة "${selectedListForSending.ListName}"

🏢 الشركة: ${selectedListForSending.CompanyName}
📅 تاريخ السفر: ${new Date(selectedListForSending.SelectionDate).toLocaleDateString('ar-EG')}

📈 الإحصائيات:
- إجمالي الأشخاص: ${validation.calculatedTotal}
- المؤكدين: ${validation.confirmedTotal} ✅
- في الانتظار: ${validation.pendingTotal} ⏳
- عدد المناديب: ${validation.delegatesCount} 👥

👥 تفاصيل المناديب:
${validation.details.map(d => 
    `• ${d.delegateName}: ${d.total} (${d.confirmed} مؤكد، ${d.pending} انتظار)`
).join('\n')}

${validation.isValid ? '✅ البيانات متطابقة!' : `⚠️ فرق في البيانات: ${validation.difference}`}
    `;
    
    alert(summaryText);
};


    // فلترة الجوازات
  // ✅ تحديث دالة getFilteredPassports لتتعامل مع البحث الشامل
const getFilteredPassports = () => {
    let filtered = selectedPassports;
    
    if (statusFilter !== 'all') {
        filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(p => 
            p.NameArabic?.toLowerCase().includes(term) ||
            p.NameEnglish?.toLowerCase().includes(term) ||
            p.PassportNumber?.toLowerCase().includes(term) ||
            p.NationalID?.includes(term) ||
            p.CompanyName?.toLowerCase().includes(term) ||
            p.DelegateName?.toLowerCase().includes(term) ||
            // البحث في الملاحظات
            (p.Notes && JSON.stringify(p.Notes).toLowerCase().includes(term))
        );
    }
    
    return filtered;
};

const getNotesText = (notes) => {
    if (!notes) return '';
    
    try {
        let parsedNotes = [];
        
        if (typeof notes === 'string') {
            if (notes.startsWith('[') || notes.startsWith('{')) {
                const parsed = JSON.parse(notes);
                if (Array.isArray(parsed)) {
                    parsedNotes = parsed;
                } else if (parsed.text) {
                    return parsed.text;
                }
            } else {
                return notes;
            }
        } else if (Array.isArray(notes)) {
            parsedNotes = notes;
        } else if (typeof notes === 'object' && notes.text) {
            return notes.text;
        }
        
        const validTexts = parsedNotes
            .filter(note => note && (note.text || typeof note === 'string'))
            .map(note => {
                if (typeof note === 'string') return note.trim();
                if (note.text) return note.text.trim();
                return '';
            })
            .filter(text => text.length > 0);
        
        return validTexts.join(' • ');
        
    } catch (error) {
        console.error('خطأ في معالجة الملاحظات:', error);
        return String(notes || '').replace(/[{}[\]"]/g, '');
    }
};

const getVisaDeleteStatus = async (passportId) => {
    try {
        // ✅ إضافة فحص شامل للاستجابة
        const response = await fetch(`${config.API_BASE_URL}/api/visa-net-registration/check-visa-deleted/${passportId}`);
        
        // ✅ فحص نوع المحتوى
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            console.error('الاستجابة ليست JSON:', response.status, response.statusText);
            return { success: false, isDeleted: false, error: 'خطأ في نوع الاستجابة' };
        }
        
        if (!response.ok) {
            console.error('خطأ في الاستجابة:', response.status, response.statusText);
            return { success: false, isDeleted: false, error: `خطأ ${response.status}` };
        }
        
        const result = await response.json();
        return result;
    } catch (error) {
        console.error('خطأ في فحص حالة الحذف:', error);
        // ✅ إرجاع استجابة آمنة بدلاً من undefined
        return { success: false, isDeleted: false, error: error.message };
    }
};

// دالة محسنة لعرض حالة الحذف
const getNotesWithDeleteStatus = (notes) => {
    if (!notes) return { text: '', hasDeleteNote: false, deleteReason: '', hasNoteInFile: false };
    
    try {
        let parsedNotes = [];
        let hasDeleteNote = false;
        let deleteReason = '';
        let hasNoteInFile = false;
        
        if (typeof notes === 'string') {
            if (notes.startsWith('[') || notes.startsWith('{')) {
                const parsed = JSON.parse(notes);
                if (Array.isArray(parsed)) {
                    parsedNotes = parsed;
                }
            } else {
                return { text: notes, hasDeleteNote: false, deleteReason: '', hasNoteInFile: false };
            }
        } else if (Array.isArray(notes)) {
            parsedNotes = notes;
        }
        
        // البحث عن ملاحظة الحذف
        const deleteNote = parsedNotes.find(note => 
            note && (note.type === 'حذف_تأشيرات' || note.deletedFromVisa === true)
        );
        
        if (deleteNote) {
            hasDeleteNote = true;
            hasNoteInFile = true;
            deleteReason = deleteNote.text || '';
        }
        
        const validTexts = parsedNotes
            .filter(note => note && (note.text || typeof note === 'string'))
            .map(note => {
                if (typeof note === 'string') return note.trim();
                if (note.text) return note.text.trim();
                return '';
            })
            .filter(text => text.length > 0);
        
        return {
            text: validTexts.join(' • '),
            hasDeleteNote,
            deleteReason,
            hasNoteInFile
        };
        
    } catch (error) {
        return { 
            text: String(notes || '').replace(/[{}[\]"]/g, ''), 
            hasDeleteNote: false, 
            deleteReason: '',
            hasNoteInFile: false
        };
    }
};

const [visaDeletedStatus, setVisaDeletedStatus] = useState({});
/*
// دالة لفحص حالة الحذف لكل الجوازات
useEffect(() => {
    if (selectedPassports.length > 0) {
        selectedPassports.forEach(async (passport) => {
            const deleteStatus = await getVisaDeleteStatus(passport.PassportID);
            if (deleteStatus.isDeleted) {
                setVisaDeletedStatus(prev => ({
                    ...prev,
                    [passport.PassportID]: deleteStatus
                }));
            }
        });
    }
}, [selectedPassports]);

*/

// دالة معاينة قائمة مندوب محدد
const showDelegatePreview = (delegateGroup, filteredPassports) => {
    const appointmentText = getAppointmentText();
    const filterText = getFilterText();
    
    const previewContent = `
=== معاينة قائمة المندوب ===

👤 المندوب: ${delegateGroup.delegateInfo?.DelegateName || 'غير معروف'}
🏢 الشركة: ${selectedListForSending.CompanyName}
📅 موعد الحضور: ${appointmentText}
🎯 نوع الفلتر: ${filterText}
👥 عدد العملاء: ${filteredPassports.length}

--- قائمة العملاء ---
${filteredPassports.map((passport, index) => 
    `${index + 1}. ${passport.NameArabic} - ${passport.PassportNumber || 'لا يوجد جواز'} - ${passport.confirmed ? '✅ مؤكد' : '⏳ انتظار'}`
).join('\n')}

${appointmentSettings.notes ? `\n--- ملاحظات إضافية ---\n${appointmentSettings.notes}` : ''}

--- تفاصيل الموعد ---
نوع الموعد: ${appointmentSettings.type === 'medical' ? 'الكشف الطبي' : 
              appointmentSettings.type === 'net' ? 'عمل النت' : 
              appointmentSettings.type === 'both' ? 'الكشف الطبي + النت' : 
              appointmentSettings.type === 'papers' ? 'مراجعة الأوراق' : 'مخصص'}
    `;
    
    alert(previewContent);
};

// دالة طباعة القائمة الكاملة المفلترة
const printCompleteFilteredList = () => {
    if (!selectedListForSending || delegatesInSelectedList.length === 0) {
        showAlert('لا توجد بيانات للطباعة', 'warning');
        return;
    }

    const allFilteredPassports = delegatesInSelectedList.flatMap(d => 
        getFilteredPassportsForDelegate(d.passports)
    );
    
    if (allFilteredPassports.length === 0) {
        showAlert('لا توجد عملاء بعد الفلترة', 'warning');
        return;
    }

    const printWindow = window.open('', '_blank');
    const appointmentText = getAppointmentText();
    
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>قائمة العملاء الشاملة المفلترة</title>
            <style>
                @page { size: A4; margin: 8mm 5mm; }
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                    font-family: 'Arial', sans-serif;
                    direction: rtl;
                    background: white;
                    color: black;
                    font-size: 14px;
                    line-height: 1.4;
                }
                .print-container {
                    max-width: 100%;
                    margin: 0;
                    background: white;
                    padding: 8px;
                }
                .report-header {
                    text-align: center;
                    margin-bottom: 25px;
                    border-bottom: 2px solid black;
                    padding-bottom: 15px;
                }
                .company-name {
                    font-size: 28px;
                    font-weight: bold;
                    color: black;
                    margin-bottom: 8px;
                }
                .appointment-info {
                    font-size: 20px;
                    color: black;
                    margin-bottom: 8px;
                    background: #f0f0f0;
                    padding: 10px;
                    border: 2px solid black;
                    border-radius: 8px;
                }
                .summary-section {
                    background: white;
                    border: 2px solid black;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 20px;
                }
                .summary-title {
                    font-size: 18px;
                    font-weight: bold;
                    color: black;
                    margin-bottom: 15px;
                    text-align: center;
                    border-bottom: 1px solid black;
                    padding-bottom: 8px;
                }
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 15px;
                    text-align: center;
                }
                .summary-item {
                    background: white;
                    padding: 12px;
                    border: 1px solid black;
                    border-radius: 6px;
                }
                .summary-label {
                    font-weight: bold;
                    color: black;
                    margin-bottom: 6px;
                    font-size: 14px;
                }
                .summary-value {
                    font-size: 20px;
                    font-weight: bold;
                    color: black;
                }
                table { 
                    width: 100%; 
                    border-collapse: collapse;
                    background: white;
                    border: 2px solid black;
                }
                th, td { 
                    padding: 8px 4px;
                    text-align: center;
                    border: 1px solid black;
                    font-size: 12px;
                    color: black;
                }
                th { 
                    background: #f0f0f0;
                    font-weight: bold;
                    font-size: 14px;
                }
                tr:nth-child(even) { background: #f8f8f8; }
                tr:nth-child(odd) { background: white; }
                .row-number {
                    font-weight: bold;
                    background: #e0e0e0;
                }
                .name-cell { font-weight: bold; font-size: 13px; }
                .delegate-cell {
                    background: #e7f3ff;
                    font-weight: bold;
                }
                .footer {
                    margin-top: 20px;
                    text-align: center;
                    color: black;
                    font-size: 12px;
                    border-top: 1px solid black;
                    padding-top: 10px;
                }
                @media print {
                    body { font-size: 12px; }
                    th, td { padding: 6px 3px; font-size: 11px; }
                    .print-container { padding: 5px; }
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <div class="report-header">
                    <div class="company-name">📋 قائمة العملاء الشاملة</div>
                    <div class="appointment-info">
                        موعد الحضور: ${appointmentText}
                    </div>
                    <div style="font-size: 16px; color: black; margin-bottom: 5px;">
                        الشركة: ${selectedListForSending.CompanyName} | 
                        فلتر: ${getFilterText()}
                    </div>
                    <div style="font-size: 16px; color: black;">
                        تاريخ الطباعة: ${new Date().toLocaleDateString('ar-EG', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            weekday: 'long'
                        })}
                    </div>
                </div>

                <div class="summary-section">
                    <div class="summary-title">ملخص شامل</div>
                    <div class="summary-grid">
                        <div class="summary-item">
                            <div class="summary-label">إجمالي العملاء</div>
                            <div class="summary-value">${allFilteredPassports.length}</div>
                        </div>
                        <div class="summary-item">
                            <div class="summary-label">مؤكد الحضور</div>
                            <div class="summary-value">${allFilteredPassports.filter(p => p.confirmed).length}</div>
                        </div>
                        <div class="summary-item">
                            <div class="summary-label">في الانتظار</div>
                            <div class="summary-value">${allFilteredPassports.filter(p => !p.confirmed).length}</div>
                        </div>
                        <div class="summary-item">
                            <div class="summary-label">عدد المناديب</div>
                            <div class="summary-value">${delegatesInSelectedList.filter(d => getFilteredPassportsForDelegate(d.passports).length > 0).length}</div>
                        </div>
                    </div>
                </div>

                ${appointmentSettings.notes ? `
                    <div style="background: #f8f9fa; border: 2px solid black; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
                        <div style="font-size: 16px; font-weight: bold; color: black; margin-bottom: 10px; text-align: center;">
                            📝 ملاحظات هامة
                        </div>
                        <div style="font-size: 14px; color: black; text-align: center;">
                            ${appointmentSettings.notes}
                        </div>
                    </div>
                ` : ''}

                <table>
                    <thead>
                        <tr>
                            <th style="width: 40px;">م</th>
                            <th style="width: 180px;">الاسم</th>
                            <th style="width: 150px;">المندوب</th>
                            <th style="width: 120px;">رقم الجواز</th>
                            <th style="width: 130px;">رقم الهاتف</th>
                            <th style="width: 100px;">الحالة</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${allFilteredPassports.map((passport, index) => {
                            const delegate = delegatesInSelectedList.find(d => 
                                d.passports.some(p => p.PassportID === passport.PassportID)
                            );
                            return `
                            <tr>
                                <td class="row-number">${index + 1}</td>
                                <td class="name-cell">${passport.NameArabic || 'غير محدد'}</td>
                                <td class="delegate-cell">${delegate?.delegateInfo?.DelegateName || 'غير محدد'}</td>
                                <td>${passport.PassportNumber || 'غير محدد'}</td>
                                <td>${getPhoneNumber(passport.PhoneNumbers) || 'غير محدد'}</td>
                                <td>${passport.confirmed ? '✅ مؤكد' : '⏳ انتظار'}</td>
                            </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>

                <div class="footer">
                    نظام إدارة السفر | طُبع بتاريخ: ${new Date().toLocaleString('ar-EG')} |
                    موعد الحضور: ${appointmentText}
                </div>
            </div>
        </body>
        </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };
};

// دالة حفظ القائمة الكاملة المفلترة كصورة
const saveCompleteFilteredListAsImage = async () => {
    if (!selectedListForSending || delegatesInSelectedList.length === 0) {
        showAlert('لا توجد بيانات لحفظها كصورة', 'warning');
        return;
    }

    const allFilteredPassports = delegatesInSelectedList.flatMap(d => 
        getFilteredPassportsForDelegate(d.passports)
    );
    
    if (allFilteredPassports.length === 0) {
        showAlert('لا توجد عملاء بعد الفلترة', 'warning');
        return;
    }

    try {
        showAlert('جاري إنشاء صورة القائمة الشاملة...', 'info');
        
        const html2canvas = await loadHtml2Canvas();
        const appointmentText = getAppointmentText();
        
        const container = document.createElement('div');
        container.style.cssText = `
            width: 900px;
            padding: 20px;
            background: white;
            font-family: 'Arial', sans-serif;
            direction: rtl;
            color: black;
            font-size: 14px;
            line-height: 1.4;
            position: absolute;
            left: -9999px;
            top: -9999px;
        `;

        container.innerHTML = `
            <div style="text-align: center; margin-bottom: 25px; border-bottom: 2px solid black; padding-bottom: 15px;">
                <div style="font-size: 28px; font-weight: bold; color: black; margin-bottom: 8px;">
                    📋 قائمة العملاء الشاملة المفلترة
                </div>
                <div style="font-size: 20px; color: black; margin-bottom: 8px; background: #f0f0f0; padding: 10px; border: 2px solid black; border-radius: 8px;">
                    موعد الحضور: ${appointmentText}
                </div>
                <div style="font-size: 16px; color: black; margin-bottom: 5px;">
                    الشركة: ${selectedListForSending.CompanyName} | فلتر: ${getFilterText()}
                </div>
                <div style="font-size: 16px; color: black;">
                    تاريخ الطباعة: ${new Date().toLocaleDateString('ar-EG', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long'
                    })}
                </div>
            </div>

            <div style="background: white; border: 2px solid black; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
                <div style="font-size: 18px; font-weight: bold; color: black; margin-bottom: 15px; text-align: center; border-bottom: 1px solid black; padding-bottom: 8px;">
                    ملخص شامل
                </div>
                <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; text-align: center;">
                    <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">إجمالي العملاء</div>
                        <div style="font-size: 20px; font-weight: bold; color: black;">${allFilteredPassports.length}</div>
                    </div>
                    <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">مؤكد الحضور</div>
                        <div style="font-size: 20px; font-weight: bold; color: black;">${allFilteredPassports.filter(p => p.confirmed).length}</div>
                    </div>
                    <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">في الانتظار</div>
                        <div style="font-size: 20px; font-weight: bold; color: black;">${allFilteredPassports.filter(p => !p.confirmed).length}</div>
                    </div>
                    <div style="background: white; padding: 12px; border: 1px solid black; border-radius: 6px;">
                        <div style="font-weight: bold; color: black; margin-bottom: 6px; font-size: 14px;">عدد المناديب</div>
                        <div style="font-size: 20px; font-weight: bold; color: black;">${delegatesInSelectedList.filter(d => getFilteredPassportsForDelegate(d.passports).length > 0).length}</div>
                    </div>
                </div>
            </div>

            <table style="width: 100%; border-collapse: collapse; background: white; border: 2px solid black;">
                <thead>
                    <tr style="background: #f0f0f0;">
                        <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">م</th>
                        <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الاسم</th>
                        <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">المندوب</th>
                        <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">رقم الجواز</th>
                        <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الهاتف</th>
                        <th style="padding: 8px 4px; text-align: center; border: 1px solid black; font-size: 14px; color: black; font-weight: bold;">الحالة</th>
                    </tr>
                </thead>
                <tbody>
                    ${allFilteredPassports.map((passport, index) => {
                        const delegate = delegatesInSelectedList.find(d => 
                            d.passports.some(p => p.PassportID === passport.PassportID)
                        );
                        return `
                        <tr style="${index % 2 === 0 ? 'background: #f8f8f8;' : 'background: white;'}">
                            <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold; background: #e0e0e0;">${index + 1}</td>
                            <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black; font-weight: bold;">${passport.NameArabic || 'غير محدد'}</td>
                            <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black; background: #e7f3ff; font-weight: bold;">${delegate?.delegateInfo?.DelegateName || 'غير محدد'}</td>
                            <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">${passport.PassportNumber || 'غير محدد'}</td>
                            <td style="padding: 6px 3px; text-align: right; border: 1px solid black; font-size: 11px; color: black; vertical-align: top;">
    ${(() => {
        const phones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
        return phones.length > 0 ? 
            phones.map(phone => `<div style="margin: 1px 0; padding: 2px; background: #f9f9f9; border-radius: 3px;">📱 ${phone}</div>`).join('')
            : '<div style="color: #666; font-style: italic;">غير محدد</div>';
    })()}
</td>                            <td style="padding: 6px 3px; text-align: center; border: 1px solid black; font-size: 12px; color: black;">
                                ${passport.confirmed ? '✅ مؤكد' : '⏳ انتظار'}
                            </td>
                        </tr>
                        `;
                    }).join('')}
                </tbody>
            </table>

            <div style="margin-top: 20px; text-align: center; color: black; font-size: 12px; border-top: 1px solid black; padding-top: 10px;">
                نظام إدارة السفر | طُبع بتاريخ: ${new Date().toLocaleString('ar-EG')} |
                موعد: ${appointmentText}
            </div>
        `;

        document.body.appendChild(container);

        const canvas = await html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: 900,
            height: container.scrollHeight
        });

        document.body.removeChild(container);

        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `قائمة_شاملة_مفلترة_${getFilterText()}_${new Date().toISOString().split('T')[0]}.png`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showAlert('تم حفظ الصورة الشاملة المفلترة بنجاح!', 'success');
        }, 'image/png', 0.95);

    } catch (error) {
        console.error('خطأ في حفظ الصورة:', error);
        showAlert('حدث خطأ أثناء حفظ الصورة: ' + error.message, 'error');
    }
};

// دالة إنشاء صور جميع المناديب
const generateAllDelegatesImages = async () => {
    if (!selectedListForSending || delegatesInSelectedList.length === 0) {
        showAlert('لا توجد مناديب لإنشاء الصور', 'warning');
        return;
    }

    const delegatesWithClients = delegatesInSelectedList.filter(d => 
        getFilteredPassportsForDelegate(d.passports).length > 0
    );

    if (delegatesWithClients.length === 0) {
        showAlert('لا توجد مناديب لديهم عملاء بعد الفلترة', 'warning');
        return;
    }

    try {
        showAlert(`جاري إنشاء ${delegatesWithClients.length} صور للمناديب...`, 'info');
        
        for (let i = 0; i < delegatesWithClients.length; i++) {
            const delegateGroup = delegatesWithClients[i];
            const filteredPassports = getFilteredPassportsForDelegate(delegateGroup.passports);
            
            showAlert(`إنشاء صورة ${i + 1}/${delegatesWithClients.length}: ${delegateGroup.delegateInfo?.DelegateName}`, 'info');
            
            await saveAsImageDelegate(delegateGroup, selectedListForSending, filteredPassports);
            
            // توقف قصير بين كل صورة لتجنب التحميل الزائد
            await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        showAlert(`✅ تم إنشاء ${delegatesWithClients.length} صور للمناديب بنجاح!`, 'success');
        
    } catch (error) {
        console.error('خطأ في إنشاء صور المناديب:', error);
        showAlert('حدث خطأ أثناء إنشاء صور المناديب: ' + error.message, 'error');
    }
};

// دالة عرض ملخص مفصل مفلتر
const showFilteredSummary = () => {
    if (!selectedListForSending || delegatesInSelectedList.length === 0) {
        showAlert('لا توجد بيانات لعرضها', 'warning');
        return;
    }
    
    const allFilteredPassports = delegatesInSelectedList.flatMap(d => 
        getFilteredPassportsForDelegate(d.passports)
    );
    
    const delegatesWithClients = delegatesInSelectedList.filter(d => 
        getFilteredPassportsForDelegate(d.passports).length > 0
    ).map(d => ({
        ...d,
        filteredCount: getFilteredPassportsForDelegate(d.passports).length,
        filteredConfirmed: getFilteredPassportsForDelegate(d.passports).filter(p => p.confirmed).length,
        filteredPending: getFilteredPassportsForDelegate(d.passports).filter(p => !p.confirmed).length
    }));
    
    const appointmentText = getAppointmentText();
    
    const summaryText = `
📊 ملخص تفصيلي مفلتر - "${selectedListForSending.ListName}"

🎯 إعدادات الفلتر والموعد:
- نوع الفلتر: ${getFilterText()}
- موعد الحضور: ${appointmentText}
- نوع الموعد: ${appointmentSettings.type === 'medical' ? 'الكشف الطبي' : 
                   appointmentSettings.type === 'net' ? 'عمل النت' : 
                   appointmentSettings.type === 'both' ? 'الكشف الطبي + النت' : 
                   appointmentSettings.type === 'papers' ? 'مراجعة الأوراق' : 'مخصص'}

🏢 معلومات القائمة:
- الشركة: ${selectedListForSending.CompanyName}
- تاريخ السفر: ${new Date(selectedListForSending.SelectionDate).toLocaleDateString('ar-EG')}

📈 الإحصائيات المفلترة:
- إجمالي العملاء (بعد الفلترة): ${allFilteredPassports.length}
- المؤكدين: ${allFilteredPassports.filter(p => p.confirmed).length} ✅
- في الانتظار: ${allFilteredPassports.filter(p => !p.confirmed).length} ⏳
- عدد المناديب النشطين: ${delegatesWithClients.length} 👥

👥 تفاصيل المناديب (بعد الفلترة):
${delegatesWithClients.map(d => 
    `• ${d.delegateInfo?.DelegateName || 'غير معروف'}: ${d.filteredCount} (${d.filteredConfirmed} مؤكد، ${d.filteredPending} انتظار)`
).join('\n')}

${appointmentSettings.notes ? `\n📝 ملاحظات إضافية:\n${appointmentSettings.notes}` : ''}

🗓️ تم إنشاء هذا الملخص: ${new Date().toLocaleString('ar-EG')}
    `;
    
    alert(summaryText);
};





// ✅ دالة لاستخراج رقم الهاتف
// ✅ دالة لاستخراج رقم الهاتف محسنة للواتساب
const getPhoneNumber = (phoneNumbers) => {
    if (!phoneNumbers) return '';
    
    try {
        let phone = '';
        
        if (typeof phoneNumbers === 'string') {
            if (phoneNumbers.startsWith('[')) {
                // JSON array
                const parsed = JSON.parse(phoneNumbers);
                phone = Array.isArray(parsed) ? (parsed[0] || '') : phoneNumbers;
            } else {
                // نص عادي
                phone = phoneNumbers;
            }
        } else if (Array.isArray(phoneNumbers)) {
            phone = phoneNumbers[0] || '';
        } else {
            phone = phoneNumbers.toString();
        }
        
        // تنظيف الرقم من المسافات والرموز الغير مرغوبة
        phone = phone.replace(/[^\d+]/g, '');
        
        // إزالة الأصفار الزيادة من البداية
        phone = phone.replace(/^00/, '');
        
        return phone;
        
    } catch (error) {
        console.error('خطأ في معالجة رقم الهاتف:', error);
        return phoneNumbers || '';
    }
};

const getStatistics = () => {
    const activePassports = getActivePassports(selectedPassports);
    const total = activePassports.length;
    const confirmed = activePassports.filter(p => p.status === 'confirmed').length;
    const pending = activePassports.filter(p => p.status === 'pending').length;
    const netBooked = selectedPassports.filter(p => p.status === 'net_booked').length;
    
    return { total, confirmed, pending, netBooked };
};
    // مكون نافذة الاستبدال المُحسن
const parseListNameToParts = (name) => {
    const emptyParts = {
        companyName: '',
        visaType: '',
        visaNumber: '',
        unifiedNumber: '',
        consulate: ''
    };

    if (!name) return emptyParts;

    // مثال: حسين حجازى اقامه 1305720531 - 7011615320 (السويس)
    const pattern = /^(.*?)\s+(اقامه|إقامة|مؤقت|موسمى|موسمي|تعقيب)\s+(\d+)\s*-\s*(\d+)\s*\(([^)]+)\)\s*$/;
    const match = name.trim().match(pattern);

    if (!match) {
        // لو الاسم مش ماشي على النمط نخلي كله في اسم الشركة
        return {
            ...emptyParts,
            companyName: name.trim()
        };
    }

    return {
        companyName: match[1].trim(),
        visaType: match[2].trim(),
        visaNumber: match[3].trim(),
        unifiedNumber: match[4].trim(),
        consulate: match[5].trim()
    };
};

    // مكون تعديل القائمة المحفوظة
const EditListModal = ({ isOpen, onClose, list, onSave, isLoading }) => {
    const [formData, setFormData] = useState({
        companyName: '',
        visaType: '',
        visaNumber: '',
        unifiedNumber: '',
        consulate: '',
        requiredCount: '',
        notes: ''
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (list && isOpen) {
            const parts = parseListNameToParts(list.ListName || '');
            setFormData({
                ...parts,
                requiredCount: list.RequiredCount || '',
                notes: list.Notes || ''
            });
            setErrors({});
        }
    }, [list, isOpen]);


    const validateForm = () => {
        const newErrors = {};

        if (!formData.companyName.trim()) {
            newErrors.companyName = 'اسم الشركة مطلوب';
        }

        if (!formData.visaType.trim()) {
            newErrors.visaType = 'نوع التأشيرة مطلوب';
        }

        if (!formData.visaNumber.trim()) {
            newErrors.visaNumber = 'رقم التأشيرة مطلوب';
        }

        if (!formData.unifiedNumber.trim()) {
            newErrors.unifiedNumber = 'الرقم الموحد مطلوب';
        }

        if (!formData.consulate.trim()) {
            newErrors.consulate = 'القنصلية مطلوبة';
        }

        if (!formData.requiredCount) {
            newErrors.requiredCount = 'العدد المطلوب مطلوب';
        } else if (isNaN(formData.requiredCount) || parseInt(formData.requiredCount) <= 0) {
            newErrors.requiredCount = 'العدد المطلوب يجب أن يكون رقم موجب';
        } else if (parseInt(formData.requiredCount) > 1000) {
            newErrors.requiredCount = 'العدد المطلوب لا يمكن أن يتجاوز 1000';
        }

        const finalName = buildListNameFromParts(formData);
        if (!finalName) {
            newErrors.listName = 'اسم القائمة غير مكتمل';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


       const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            const finalName = buildListNameFromParts(formData);
            onSave({
                listName: finalName,
                requiredCount: formData.requiredCount,
                notes: formData.notes
            });
        }
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
                <div className="bg-blue-600 text-white p-6 rounded-t-2xl">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold">✏️ تعديل بيانات القائمة</h2>
                            <p className="text-blue-100 mt-1">
                                القائمة: {list?.ListName}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:bg-blue-700 p-2 rounded-full transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                          <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* اسم القائمة (مبني من أجزاء) */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-1">
                            📋 بيانات اسم القائمة *
                        </h3>

                        {/* اسم الشركة */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                اسم الشركة
                            </label>
                            <input
                                type="text"
                                value={formData.companyName}
                                onChange={(e) =>
                                    setFormData(prev => ({ ...prev, companyName: e.target.value }))
                                }
                                className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 ${
                                    errors.companyName ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="مثال: حسين حجازى"
                                disabled={isLoading}
                            />
                            {errors.companyName && (
                                <p className="text-red-600 text-sm mt-1">{errors.companyName}</p>
                            )}
                        </div>

                        {/* نوع التأشيرة + القنصلية */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    نوع التأشيرة
                                </label>
                                <select
                                    value={formData.visaType}
                                    onChange={(e) =>
                                        setFormData(prev => ({ ...prev, visaType: e.target.value }))
                                    }
                                    className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 ${
                                        errors.visaType ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    disabled={isLoading}
                                >
                                    <option value="">اختر نوع التأشيرة</option>
                                    <option value="اقامه">اقامه</option>
                                    <option value="مؤقت">مؤقت</option>
                                    <option value="موسمى">موسمى</option>
                                    <option value="تعقيب">تعقيب</option>
                                </select>
                                {errors.visaType && (
                                    <p className="text-red-600 text-sm mt-1">{errors.visaType}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    القنصلية
                                </label>
                                <select
                                    value={formData.consulate}
                                    onChange={(e) =>
                                        setFormData(prev => ({ ...prev, consulate: e.target.value }))
                                    }
                                    className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 ${
                                        errors.consulate ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    disabled={isLoading}
                                >
                                    <option value="">اختر القنصلية</option>
                                    <option value="السويس">السويس</option>
                                    <option value="القاهرة">القاهرة</option>
                                    <option value="الإسكندرية">الإسكندرية</option>
                                </select>
                                {errors.consulate && (
                                    <p className="text-red-600 text-sm mt-1">{errors.consulate}</p>
                                )}
                            </div>
                        </div>

                        {/* رقم التأشيرة + الرقم الموحد */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    رقم التأشيرة
                                </label>
                                <input
                                    type="text"
                                    value={formData.visaNumber}
                                    onChange={(e) =>
                                        setFormData(prev => ({ ...prev, visaNumber: e.target.value }))
                                    }
                                    className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 ${
                                        errors.visaNumber ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="مثال: 1305720531"
                                    disabled={isLoading}
                                />
                                {errors.visaNumber && (
                                    <p className="text-red-600 text-sm mt-1">{errors.visaNumber}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    الرقم الموحد
                                </label>
                                <input
                                    type="text"
                                    value={formData.unifiedNumber}
                                    onChange={(e) =>
                                        setFormData(prev => ({ ...prev, unifiedNumber: e.target.value }))
                                    }
                                    className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 ${
                                        errors.unifiedNumber ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="مثال: 7011615320"
                                    disabled={isLoading}
                                />
                                {errors.unifiedNumber && (
                                    <p className="text-red-600 text-sm mt-1">{errors.unifiedNumber}</p>
                                )}
                            </div>
                        </div>

                        {/* معاينة الاسم النهائي */}
                        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-3 text-sm mt-2">
                            <span className="text-gray-500 block mb-1">المعاينة:</span>
                            <span className="font-medium text-gray-800">
                                {buildListNameFromParts(formData) || 'سيظهر اسم القائمة هنا بعد اكتمال الحقول'}
                            </span>
                            {errors.listName && (
                                <p className="text-red-600 text-sm mt-1">{errors.listName}</p>
                            )}
                        </div>
                    </div>


                    {/* العدد المطلوب */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            🔢 العدد المطلوب *
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="1000"
                            value={formData.requiredCount}
                            onChange={(e) => setFormData(prev => ({ ...prev, requiredCount: e.target.value }))}
                            className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 ${
                                errors.requiredCount ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="أدخل العدد المطلوب"
                            disabled={isLoading}
                        />
                        {errors.requiredCount && (
                            <p className="text-red-600 text-sm mt-1">{errors.requiredCount}</p>
                        )}
                        <p className="text-gray-500 text-sm mt-1">
                            العدد الحالي المختار: {list?.PassportCount || 0} شخص
                        </p>
                    </div>

               

                    {/* معلومات إضافية */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="font-medium text-blue-900 mb-2">📊 معلومات القائمة الحالية:</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">الشركة:</span>
                                <span className="font-medium ml-2">{list?.CompanyName}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">تاريخ الإنشاء:</span>
                                <span className="font-medium ml-2">
                                    {list?.CreatedAt ? new Date(list.CreatedAt).toLocaleDateString('ar-EG') : 'غير محدد'}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-600">إجمالي المختارين:</span>
                                <span className="font-medium ml-2 text-blue-600">{list?.PassportCount || 0}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">المؤكدين:</span>
                                <span className="font-medium ml-2 text-green-600">{list?.ConfirmedCount || 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* أزرار التحكم */}
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            إلغاء
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    جاري الحفظ...
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    حفظ التغييرات
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
    
// مكون نافذة الاستبدال المُحسن مع بدائل من كل المناديب
const ReplacementModal = ({ 
    isOpen, 
    onClose, 
    passport, 
    onConfirm, 
    isLoading,
    availableReplacements = [] 
}) => {
    const [replacementType, setReplacementType] = useState('');
    const [reason, setReason] = useState('');
    const [selectedReplacement, setSelectedReplacement] = useState(null);
    const [replacementMode, setReplacementMode] = useState('');
    const [selectedDelegateFilter, setSelectedDelegateFilter] = useState('all');

    if (!isOpen) return null;

    const reasons = [
        'الشخص مريض/تعبان',
        'اعتذر عن السفر',
        'أجل السفر لوقت لاحق',
        'مشاكل في المبلغ المطلوب',
        'مشاكل في الأوراق المطلوبة',
        'ظروف عائلية طارئة',
        'غير متاح في التاريخ المحدد',
        'رفض من العميل',
        'مشاكل في الجواز'
    ];

    const replacementTypes = [
        { 
            id: 'temporary', 
            title: '🔄 حذف مؤقت', 
            description: 'إزالة من القائمة فقط - يبقى متاح للاختيار مستقبلاً',
            color: 'blue'
        },
        { 
            id: 'permanent', 
            title: '❌ استبعاد نهائي', 
            description: 'استبعاد نهائي مع تسجيل السبب - لن يظهر في أي اختيار',
            color: 'red'
        },
        { 
            id: 'special', 
            title: '⚠️ أسباب خاصة', 
            description: 'استبعاد لأسباب خاصة بدون تفاصيل',
            color: 'yellow'
        }
    ];

    // تجميع البدائل حسب المندوب
    const groupedReplacements = availableReplacements.reduce((groups, replacement) => {
        const delegateId = replacement.DelegateID;
        if (!groups[delegateId]) {
            groups[delegateId] = {
                delegateInfo: delegates.find(d => d.DelegateID === delegateId),
                replacements: [],
                isSameDelegate: replacement.delegateType === 'same'
            };
        }
        groups[delegateId].replacements.push(replacement);
        return groups;
    }, {});

    // تصفية البدائل حسب المندوب المختار
    const filteredReplacements = selectedDelegateFilter === 'all' 
        ? availableReplacements 
        : availableReplacements.filter(r => r.DelegateID.toString() === selectedDelegateFilter);

    // احصائيات البدائل
    const sameDelegateCount = passport?.sameDelegateCount || 0;
    const otherDelegatesCount = passport?.otherDelegatesCount || 0;
    const totalReplacements = availableReplacements.length;

    const handleConfirm = () => {
        if (!replacementType || (!reason && replacementType !== 'special' && replacementType !== 'temporary')) {
            alert('يرجى اختيار نوع الاستبدال والسبب إذا لزم الأمر');
            return;
        }

        if (replacementMode === 'manual' && !selectedReplacement) {
            alert('يرجى اختيار البديل');
            return;
        }

        onConfirm({
            type: replacementType,
            reason: reason,
            mode: replacementMode,
            selectedReplacement: selectedReplacement
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold">🔄 استبدال العميل</h2>
                            <p className="text-blue-100 mt-1">
                                العميل: <span className="font-semibold">{passport?.NameArabic}</span>
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>
                    
                  {/* إحصائيات البدائل المحسنة */}
<div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
    <div className="bg-white bg-opacity-20 rounded-lg p-3">
        <div className="text-sm text-blue-100">من نفس المندوب</div>
        <div className="text-xl font-bold">{sameDelegateCount}</div>
        {sameDelegateCount > 0 ? (
            <div className="text-xs text-green-200 mt-1">✅ متوفر</div>
        ) : (
            <div className="text-xs text-red-200 mt-1">❌ غير متوفر</div>
        )}
    </div>
    <div className="bg-white bg-opacity-20 rounded-lg p-3">
        <div className="text-sm text-blue-100">من مناديب أخرى</div>
        <div className="text-xl font-bold">{otherDelegatesCount}</div>
        {otherDelegatesCount > 0 ? (
            <div className="text-xs text-green-200 mt-1">✅ متوفر</div>
        ) : (
            <div className="text-xs text-red-200 mt-1">❌ غير متوفر</div>
        )}
    </div>
    <div className="bg-white bg-opacity-20 rounded-lg p-3">
        <div className="text-sm text-blue-100">إجمالي البدائل</div>
        <div className="text-xl font-bold">{totalReplacements}</div>
        <div className={`text-xs mt-1 ${totalReplacements > 0 ? 'text-green-200' : 'text-red-200'}`}>
            {totalReplacements > 0 ? '✅ يوجد بدائل' : '❌ لا توجد بدائل'}
        </div>
    </div>
</div>
                </div>

                <div className="p-6 space-y-6">
                  {/* تحذيرات محسنة */}
{sameDelegateCount === 0 && otherDelegatesCount > 0 && (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
                <div className="font-medium text-yellow-800">
                    ⚠️ لا توجد بدائل من نفس المندوب
                </div>
                <div className="text-sm text-yellow-700 mt-1">
                    متوفر {otherDelegatesCount} بديل من مناديب أخرى في نفس الشركة.
                </div>
                <div className="text-xs text-yellow-600 mt-2 font-medium">
                    💡 اختر من المناديب الأخرى أو احذف بدون بديل
                </div>
            </div>
        </div>
    </div>
)}

{sameDelegateCount === 0 && otherDelegatesCount === 0 && (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div>
                <div className="font-medium text-red-800">
                    ❌ لا توجد أي بدائل متاحة
                </div>
                <div className="text-sm text-red-700 mt-1">
                    لا يوجد أشخاص متاحين آخرين في هذه الشركة للاستبدال.
                </div>
                <div className="text-xs text-red-600 mt-2 font-medium">
                    💡 يمكنك فقط حذف/استبعاد الشخص بدون إضافة بديل
                </div>
            </div>
        </div>
    </div>
)}

                    {/* نوع الاستبدال */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">اختر نوع الإجراء:</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {replacementTypes.map(type => (
                                <div
                                    key={type.id}
                                    onClick={() => setReplacementType(type.id)}
                                    className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                                        replacementType === type.id
                                            ? `border-${type.color}-500 bg-${type.color}-50`
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="font-medium text-gray-900">{type.title}</div>
                                    <div className="text-sm text-gray-600 mt-1">{type.description}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* السبب */}
                    {replacementType && replacementType !== 'special' && replacementType !== 'temporary' && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">اختر السبب:</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {reasons.map((reasonOption, index) => (
                                    <div
                                        key={index}
                                        onClick={() => setReason(reasonOption)}
                                        className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                                            reason === reasonOption
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        {reasonOption}
                                    </div>
                                ))}
                            </div>
                            
                            <div className="mt-4">
                                <input
                                    type="text"
                                    placeholder="أو اكتب سببً مخصص..."
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}

                    {/* طريقة الاستبدال */}
                    {replacementType && totalReplacements > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                طريقة اختيار البديل:
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div
                                    onClick={() => setReplacementMode('auto')}
                                    className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                                        replacementMode === 'auto'
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="font-medium text-gray-900">🤖 اختيار تلقائي</div>
                                    <div className="text-sm text-gray-600 mt-1">
                                        {sameDelegateCount > 0 
                                            ? 'أول بديل متاح من نفس المندوب' 
                                            : 'أول بديل متاح من أي مندوب'
                                        }
                                    </div>
                                </div>
                                
                                <div
                                    onClick={() => setReplacementMode('manual')}
                                    className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                                        replacementMode === 'manual'
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="font-medium text-gray-900">👤 اختيار يدوي</div>
                                    <div className="text-sm text-gray-600 mt-1">
                                        اختيار البديل بنفسك من القائمة
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* قائمة البدائل المتاحة */}
                    {replacementMode === 'manual' && totalReplacements > 0 && (
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    اختر البديل ({filteredReplacements.length} متاح):
                                </h3>
                                
                                {/* فلتر المناديب */}
                                <select
                                    value={selectedDelegateFilter}
                                    onChange={(e) => setSelectedDelegateFilter(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="all">كل المناديب ({totalReplacements})</option>
                                    {Object.entries(groupedReplacements).map(([delegateId, group]) => (
                                        <option key={delegateId} value={delegateId}>
                                            {group.isSameDelegate && '⭐ '}
                                            {group.delegateInfo?.DelegateName || 'مندوب غير معروف'} 
                                            ({group.replacements.length})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                                {Object.entries(groupedReplacements)
                                    .filter(([delegateId]) => 
                                        selectedDelegateFilter === 'all' || delegateId === selectedDelegateFilter
                                    )
                                    .map(([delegateId, group]) => (
                                    <div key={delegateId} className="border-b border-gray-100">
                                        {/* عنوان المندوب */}
                                        <div className={`bg-gray-100 px-4 py-2 font-medium text-sm flex items-center gap-2 ${
                                            group.isSameDelegate ? 'bg-green-100 text-green-800' : 'text-gray-700'
                                        }`}>
                                            {group.isSameDelegate && <span>⭐</span>}
                                            {group.delegateInfo?.DelegateName || 'مندوب غير معروف'}
                                            <span className="text-xs">({group.replacements.length} متاح)</span>
                                            {group.isSameDelegate && (
                                                <span className="text-xs bg-green-200 px-2 py-1 rounded">نفس المندوب</span>
                                            )}
                                        </div>
                                        
                                        {/* قائمة البدائل */}
                                        {group.replacements.map(person => (
                                            <div
                                                key={person.PassportID}
                                                onClick={() => setSelectedReplacement(person)}
                                                className={`p-3 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
                                                    selectedReplacement?.PassportID === person.PassportID
                                                        ? 'bg-blue-50 border-r-4 border-blue-500'
                                                        : ''
                                                }`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {person.NameArabic}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            جواز: {person.PassportNumber || 'غير محدد'} | 
                                                            ID:: {getPassportFileNumber(person)}
                                                        </div>
                                                    </div>
                                                    {selectedReplacement?.PassportID === person.PassportID && (
                                                        <div className="text-blue-600">
                                                            <CheckCircle2 size={20} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* حالة عدم وجود بدائل */}
                    {totalReplacements === 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center gap-2">
                                <AlertTriangle size={20} className="text-red-600" />
                                <div>
                                    <div className="font-medium text-red-800">لا توجد بدائل متاحة</div>
                                    <div className="text-sm text-red-700">
                                        سيتم فقط إزالة/استبعاد الشخص بدون إضافة بديل
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* أزرار التحكم */}
                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            إلغاء
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={isLoading}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    جاري المعالجة...
                                </>
                            ) : (
                                'تأكيد الإجراء'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};



// ==================== دالة طباعة كشف الأرصدة ====================
const printBalanceReport = (filterType, data, minBalance, delegatesList, professionsList, companiesList, list) => {
    const filtered = data.filter(r =>
        filterType === 'all' ? true :
        filterType === 'positive' ? r.isPositive : !r.isPositive
    );

    if (filtered.length === 0) {
        alert('لا توجد بيانات للطباعة في هذا الفلتر');
        return;
    }

    const typeLabel = filterType === 'all' ? 'الكل (إيجابي وسلبي)' : filterType === 'positive' ? 'الأرصدة الإيجابية ✅' : 'الأرصدة السلبية ❌';
    const listName = list?.ListName || 'القائمة الحالية';
    const today = new Date().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });

    const positiveCount = data.filter(r => r.isPositive).length;
    const negativeCount = data.filter(r => !r.isPositive).length;
    const totalBalance = filtered.reduce((s, r) => s + r.balance, 0);

    const rowsHtml = filtered.map((row, idx) => {
        const d = delegatesList.find(x => parseInt(x.DelegateID) === parseInt(row.passport.DelegateID));
        const p = professionsList.find(x => parseInt(x.ProfessionID) === parseInt(row.passport.PassportProfessionID));
        const c = companiesList.find(x => parseInt(x.CompanyID) === parseInt(row.passport.CompanyID));
        const phone = (() => {
            try {
                const phones = typeof row.passport.PhoneNumbers === 'string' ? JSON.parse(row.passport.PhoneNumbers) : row.passport.PhoneNumbers;
                if (Array.isArray(phones) && phones.length > 0) return phones[0].number || phones[0];
                return row.passport.PhoneNumbers || '—';
            } catch { return row.passport.PhoneNumbers || '—'; }
        })();
        const statusLabel = row.passport.status === 'confirmed' ? 'مؤكد' : row.passport.status === 'pending' ? 'منتظر' : (row.passport.status || '—');
        const statusColor = row.passport.status === 'confirmed' ? '#16a34a' : row.passport.status === 'pending' ? '#ca8a04' : '#6b7280';
        const balanceColor = row.isPositive ? '#15803d' : '#dc2626';
        const rowBg = idx % 2 === 0 ? '#ffffff' : '#f9fafb';
        return `
            <tr style="background:${rowBg}">
                <td style="text-align:center;color:#6b7280">${idx + 1}</td>
                <td style="font-weight:600;color:#111">${row.passport.NameArabic}</td>
                <td>${c?.CompanyName || '—'}</td>
                <td>${p?.ProfessionName || '—'}</td>
                <td dir="ltr" style="font-size:12px">${phone}</td>
                <td>${d?.DelegateName || '—'}</td>
                <td style="text-align:center;color:${statusColor};font-weight:600">${statusLabel}</td>
                <td style="text-align:center;font-weight:700;color:${row.error ? '#f87171' : (row.balance >= 0 ? '#1d4ed8' : '#dc2626')}">
                    ${row.error ? row.error : row.balance.toLocaleString() + ' ج'}
                </td>
                <td style="text-align:center;font-weight:700;color:${balanceColor}">
                    ${row.isPositive ? '✅ إيجابي' : '❌ سلبي'}
                </td>
            </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
    <meta charset="UTF-8"/>
    <title>كشف الأرصدة - ${typeLabel}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; font-size: 13px; color: #1f2937; background: #fff; }
        .page { padding: 24px 28px; }

        /* Header */
        .header { border-bottom: 3px solid #4f46e5; padding-bottom: 14px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: flex-start; }
        .header-title { font-size: 22px; font-weight: 800; color: #4f46e5; }
        .header-sub { font-size: 13px; color: #6b7280; margin-top: 4px; }
        .header-date { text-align: left; font-size: 12px; color: #6b7280; }

        /* Summary boxes */
        .summary { display: flex; gap: 12px; margin-bottom: 18px; flex-wrap: wrap; }
        .s-box { flex: 1; min-width: 120px; border-radius: 10px; padding: 10px 14px; text-align: center; }
        .s-box .num { font-size: 24px; font-weight: 800; }
        .s-box .lbl { font-size: 11px; margin-top: 2px; }
        .s-indigo { background: #eef2ff; color: #4f46e5; }
        .s-green  { background: #f0fdf4; color: #16a34a; }
        .s-red    { background: #fef2f2; color: #dc2626; }
        .s-blue   { background: #eff6ff; color: #1d4ed8; }

        /* Table */
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        thead tr { background: #4f46e5; color: #fff; }
        thead th { padding: 9px 8px; text-align: right; font-weight: 600; font-size: 12px; }
        tbody td { padding: 7px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: middle; }
        tbody tr:last-child td { border-bottom: none; }

        /* Footer */
        .footer { margin-top: 18px; border-top: 1px solid #e5e7eb; padding-top: 10px; display: flex; justify-content: space-between; font-size: 11px; color: #9ca3af; }

        @media print {
            @page { margin: 15mm 10mm; size: A4 landscape; }
            body { font-size: 11px; }
            thead th, tbody td { padding: 5px 6px; }
        }
    </style>
</head>
<body>
<div class="page">
    <div class="header">
        <div>
            <div class="header-title">كشف الأرصدة — ${typeLabel}</div>
            <div class="header-sub">📋 ${listName} &nbsp;|&nbsp; الحد الأدنى: ${minBalance.toLocaleString()} ج.م</div>
        </div>
        <div class="header-date">
            <div>📅 ${today}</div>
            <div style="margin-top:4px">إجمالي الصفوف: ${filtered.length}</div>
        </div>
    </div>

    <div class="summary">
        <div class="s-box s-indigo"><div class="num">${data.length}</div><div class="lbl">إجمالي القائمة</div></div>
        <div class="s-box s-green"><div class="num">${positiveCount}</div><div class="lbl">✅ إيجابي</div></div>
        <div class="s-box s-red"><div class="num">${negativeCount}</div><div class="lbl">❌ سلبي</div></div>
        <div class="s-box s-blue"><div class="num" style="font-size:16px">${totalBalance.toLocaleString()}</div><div class="lbl">إجمالي الأرصدة (ج.م)</div></div>
    </div>

    <table>
        <thead>
            <tr>
                <th style="width:32px">م</th>
                <th>الاسم</th>
                <th>الشركة</th>
                <th>المهنة</th>
                <th>الهاتف</th>
                <th>المندوب</th>
                <th style="width:60px">الحالة</th>
                <th style="width:80px">الرصيد</th>
                <th style="width:80px">حالة الرصيد</th>
            </tr>
        </thead>
        <tbody>
            ${rowsHtml}
        </tbody>
    </table>

    <div class="footer">
        <span>نظام إدارة الجوازات</span>
        <span>طُبع بتاريخ: ${today}</span>
    </div>
</div>
<script>window.onload = () => { window.print(); }</script>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
};


// ==================== مكون ضبط الحد الأدنى للرصيد ====================
const MinBalanceSettingModal = ({ currentAmount, onSave, onClose }) => {
    const [inputValue, setInputValue] = useState(String(currentAmount));
    const [error, setError] = useState('');

    const handleSave = () => {
        const parsed = parseFloat(inputValue);
        if (isNaN(parsed) || parsed < 0) {
            setError('أدخل مبلغاً صحيحاً أكبر من أو يساوي صفر');
            return;
        }
        onSave(parsed);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[99] flex items-center justify-center p-4" dir="rtl">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
                <div className="bg-teal-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Settings size={20} />
                        ضبط الحد الأدنى للرصيد
                    </h3>
                    <button onClick={onClose} className="hover:bg-teal-700/50 rounded-full p-1">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        حدد المبلغ الأدنى المطلوب دفعه حتى يُعتبر رصيد العميل <strong>إيجابياً</strong>. 
                        أي رصيد أقل من هذا المبلغ سيُعتبر <strong>سلبياً</strong>.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            الحد الأدنى للرصيد (ج.م)
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                value={inputValue}
                                onChange={e => { setInputValue(e.target.value); setError(''); }}
                                onKeyDown={e => e.key === 'Enter' && handleSave()}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-teal-500 focus:outline-none text-lg font-bold text-center"
                                placeholder="مثال: 1000"
                                min="0"
                                autoFocus
                            />
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">ج.م</span>
                        </div>
                        {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
                    </div>

                    {/* اقتراحات سريعة */}
                    <div>
                        <p className="text-xs text-gray-500 mb-2">مبالغ مقترحة:</p>
                        <div className="flex gap-2 flex-wrap">
                            {[500, 1000, 1500, 2000, 3000, 5000].map(amount => (
                                <button
                                    key={amount}
                                    onClick={() => { setInputValue(String(amount)); setError(''); }}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                                        parseFloat(inputValue) === amount
                                            ? 'bg-teal-600 text-white border-teal-600'
                                            : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-teal-50 hover:border-teal-300'
                                    }`}
                                >
                                    {amount.toLocaleString()} ج
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onClose}
                            className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50"
                        >
                            إلغاء
                        </button>
                        <button
                            onClick={handleSave}
                            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                        >
                            <Save size={16} />
                            حفظ
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


const SelectionRulesModal = ({ isOpen, onClose, rules, onSave, allowProcessedManualSelection = false }) => {
    const [localRules, setLocalRules] = useState(rules || DEFAULT_SELECTION_RULES);
    const [localAllowProcessedManualSelection, setLocalAllowProcessedManualSelection] = useState(!!allowProcessedManualSelection);

    useEffect(() => {
        setLocalRules(rules || DEFAULT_SELECTION_RULES);
        setLocalAllowProcessedManualSelection(!!allowProcessedManualSelection);
    }, [rules, isOpen, allowProcessedManualSelection]);

    if (!isOpen) return null;

    const handleChange = (duration, field, value) => {
        setLocalRules(prev => ({
            ...prev,
            [duration]: {
                ...(prev[duration] || {}),
                [field]: value === '' ? '' : field === 'minPassportMonths' ? (parseInt(value) || 0) : value
            }
        }));
    };

    // دالة مساعدة لعرض تاريخ الميلاد بشكل مقروء مع العمر المحسوب
    const getAgeFromDate = (dateStr) => {
        if (!dateStr) return '';
        const dob = new Date(dateStr);
        if (isNaN(dob.getTime())) return '';
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
        return age;
    };

    const handleSave = () => {
        onSave(localRules, { allowProcessedManualSelection: localAllowProcessedManualSelection });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[95] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" dir="rtl">
                <div className="bg-blue-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
                    <h2 className="text-lg font-bold flex items-center gap-2">
                        <Settings size={20} />
                        ضبط معايير الاختيار حسب مدة السفر
                    </h2>
                    <button onClick={onClose} className="hover:bg-blue-700/50 rounded-full p-1">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800">
                        <div className="font-bold mb-1">📅 الفلترة بتاريخ الميلاد</div>
                        <div>
                            حدد <strong>أقدم تاريخ ميلاد مسموح</strong> (الأكبر سناً) و<strong>أحدث تاريخ ميلاد مسموح</strong> (الأصغر سناً) لكل مدة سفر.
                            هيتم حساب العمر بالضبط باليوم والشهر — لن يتم اختيار أي شخص خارج هذا النطاق.
                        </div>
                    </div>

                    <label className="block bg-amber-50 border border-amber-300 rounded-xl p-4 text-sm text-amber-900 cursor-pointer">
                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                checked={localAllowProcessedManualSelection}
                                onChange={(e) => setLocalAllowProcessedManualSelection(e.target.checked)}
                                className="mt-1 w-5 h-5 text-amber-600 rounded"
                            />
                            <div>
                                <div className="font-bold mb-1">⚠️ وضع الطوارئ اليدوي</div>
                                <div>
                                    السماح في <strong>الاختيار اليدوي فقط</strong> بظهور من لديه تاريخ نت أو تأشيرة أو سفر حتى تقدر تعدله يدويًا عند الحاجة.
                                    الاختيار الذكي يظل آمنًا ولن يختار هؤلاء تلقائيًا.
                                </div>
                            </div>
                        </div>
                    </label>

                    <div className="border rounded-xl overflow-hidden">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-100 text-gray-800">
                                <tr>
                                    <th className="px-3 py-2 text-right">مدة السفر</th>
                                    <th className="px-3 py-2 text-right">
                                        <div>أقدم تاريخ ميلاد مسموح</div>
                                        <div className="text-xs font-normal text-gray-500">(الأكبر سناً - الحد الأقصى للعمر)</div>
                                    </th>
                                    <th className="px-3 py-2 text-right">
                                        <div>أحدث تاريخ ميلاد مسموح</div>
                                        <div className="text-xs font-normal text-gray-500">(الأصغر سناً - الحد الأدنى للعمر)</div>
                                    </th>
                                    <th className="px-3 py-2 text-right">أقل صلاحية جواز (شهور)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {TRAVEL_DURATIONS.map(duration => {
                                    const cfg = localRules[duration] || {};
                                    const oldAgeMin = getAgeFromDate(cfg.maxBirthDate); // maxBirthDate = أصغر عمر
                                    const oldAgeMax = getAgeFromDate(cfg.minBirthDate); // minBirthDate = أكبر عمر
                                    return (
                                        <tr key={duration} className="border-t hover:bg-gray-50">
                                            <td className="px-3 py-3 font-bold text-gray-900 bg-gray-50">
                                                {duration}
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="space-y-1">
                                                    <input
                                                        type="date"
                                                        className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                                                        value={cfg.minBirthDate ?? ''}
                                                        onChange={e => handleChange(duration, 'minBirthDate', e.target.value)}
                                                    />
                                                    {cfg.minBirthDate && (
                                                        <div className="text-xs text-orange-600 font-medium">
                                                            ≈ عمر {oldAgeMax} سنة
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="space-y-1">
                                                    <input
                                                        type="date"
                                                        className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                                                        value={cfg.maxBirthDate ?? ''}
                                                        onChange={e => handleChange(duration, 'maxBirthDate', e.target.value)}
                                                    />
                                                    {cfg.maxBirthDate && (
                                                        <div className="text-xs text-green-600 font-medium">
                                                            ≈ عمر {oldAgeMin} سنة
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3">
                                                <input
                                                    type="number"
                                                    className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-300"
                                                    value={cfg.minPassportMonths ?? ''}
                                                    onChange={e => handleChange(duration, 'minPassportMonths', e.target.value)}
                                                    placeholder="مثال: 6"
                                                    min="1"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-between items-center mt-4">
                        <button
                            type="button"
                            onClick={() => setLocalRules(DEFAULT_SELECTION_RULES)}
                            className="text-xs text-gray-500 hover:text-gray-700 underline"
                        >
                            ↩ استرجاع القيم الافتراضية
                        </button>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 rounded-lg border text-gray-700 text-sm hover:bg-gray-50"
                            >
                                إلغاء
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 font-medium"
                            >
                                💾 حفظ المعايير
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};




// ابحث عن const PrintSettingsModal وحدث الجزء الخاص بالبيانات المعروضة
const PrintSettingsModal = ({ isOpen, onClose, settings, onSave }) => {
    const [tempSettings, setTempSettings] = useState(settings);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
                <div className="bg-blue-600 text-white p-6 rounded-t-2xl">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold">⚙️ إعدادات الطباعة والتصدير</h2>
                        <button onClick={onClose} className="text-white hover:bg-blue-700 p-2 rounded-full">
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-4">📋 البيانات المعروضة:</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={tempSettings.showTravelDate}
                                    onChange={(e) => setTempSettings(prev => ({ ...prev, showTravelDate: e.target.checked }))}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                                <span>📅 تاريخ السفر</span>
                            </label>

                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={tempSettings.showDelegate}
                                    onChange={(e) => setTempSettings(prev => ({ ...prev, showDelegate: e.target.checked }))}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                                <span>👤 المندوب</span>
                            </label>

                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={tempSettings.showPhone}
                                    onChange={(e) => setTempSettings(prev => ({ ...prev, showPhone: e.target.checked }))}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                                <span>📱 رقم الهاتف</span>
                            </label>

                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={tempSettings.showCompany}
                                    onChange={(e) => setTempSettings(prev => ({ ...prev, showCompany: e.target.checked }))}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                                <span>🏢 الشركة</span>
                            </label>

                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={tempSettings.showProfession}
                                    onChange={(e) => setTempSettings(prev => ({ ...prev, showProfession: e.target.checked }))}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                                <span>💼 المهنة</span>
                            </label>

                            {/* ✅ الخيار الجديد */}
                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={tempSettings.showPassportID}
                                    onChange={(e) => setTempSettings(prev => ({ ...prev, showPassportID: e.target.checked }))}
                                    className="w-5 h-5 text-blue-600 rounded"
                                />
                                <span>🆔 ID:</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold mb-4">🎯 قوالب سريعة:</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <button
                                onClick={() => setTempSettings({
                                    showTravelDate: false,
                                    showDelegate: true,
                                    showPhone: true,
                                    showCompany: true,
                                    showProfession: true,
                                    showPassportID: false  // ✅ إضافة للقوالب
                                })}
                                className="p-3 border-2 border-blue-200 rounded-lg hover:bg-blue-50 text-blue-700 font-medium"
                            >
                                📋 كامل مع المندوب
                            </button>
                            
                            <button
                                onClick={() => setTempSettings({
                                    showTravelDate: false,
                                    showDelegate: false,
                                    showPhone: true,
                                    showCompany: true,
                                    showProfession: true,
                                    showPassportID: false  // ✅ إضافة للقوالب
                                })}
                                className="p-3 border-2 border-green-200 rounded-lg hover:bg-green-50 text-green-700 font-medium"
                            >
                                🏢 بدون مندوب
                            </button>
                            
                            <button
                                onClick={() => setTempSettings({
                                    showTravelDate: false,
                                    showDelegate: true,
                                    showPhone: false,
                                    showCompany: true,
                                    showProfession: true,
                                    showPassportID: true  // ✅ قالب مع ID
                                })}
                                className="p-3 border-2 border-purple-200 rounded-lg hover:bg-purple-50 text-purple-700 font-medium"
                            >
                                🆔 مع أرقام الملف
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 p-6 border-t">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        إلغاء
                    </button>
                    <button
                        onClick={() => {
                            onSave(tempSettings);
                            onClose();
                        }}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        حفظ الإعدادات
                    </button>
                </div>
            </div>
        </div>
    );
};


    // دوال جديدة للتعامل مع حالات النت المحجوز
const canModifyPassport = (passport) => {
    if (passport.status === 'confirmed') {
        return {
            allowed: false,
            reason: 'يجب إلغاء تأكيد الحضور أولاً قبل الحذف أو الاستبدال'
        };
    }
    return { allowed: true };
};

const getRowColorClass = (passport) => {
    if (passport.status === 'net_booked') {
        return 'bg-red-50 border-r-4 border-red-300';
    }
    if (passport.status === 'confirmed') {
        return 'bg-green-50';
    }
    return 'hover:bg-gray-50';
};


// مكون عرض وتعديل أرقام الهاتف
const PhoneNumbersDisplay = ({ passport, onUpdatePhones }) => {
    const [phones, setPhones] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [newPhone, setNewPhone] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const allPhones = extractAllPhoneNumbers(passport.PhoneNumbers, passport.NameArabic);
        setPhones(allPhones);
    }, [passport.PhoneNumbers]);

    const handleAddPhone = async () => {
        if (!newPhone.trim()) {
            showAlert('يرجى إدخال رقم الهاتف', 'warning');
            return;
        }

        let cleanPhone = newPhone.trim();

        if (phones.includes(cleanPhone)) {
            showAlert('هذا الرقم موجود بالفعل', 'warning');
            return;
        }

        setIsLoading(true);
        try {
            const updatedPhones = [...phones, cleanPhone];
            
            const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/update-phone`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    passportId: passport.PassportID,
                    phoneNumbers: updatedPhones
                })
            });

            if (response.ok) {
                setPhones(updatedPhones);
                setNewPhone('');
                setIsEditing(false);
                onUpdatePhones && onUpdatePhones(updatedPhones);
                showAlert('تم إضافة الرقم بنجاح', 'success');
            } else {
                throw new Error('فشل في تحديث الرقم');
            }
        } catch (error) {
            console.error('خطأ في إضافة الرقم:', error);
            showAlert('خطأ في إضافة الرقم', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemovePhone = async (phoneToRemove) => {
        if (!window.confirm(`هل تريد حذف الرقم ${phoneToRemove}؟`)) return;

        setIsLoading(true);
        try {
            const updatedPhones = phones.filter(p => p !== phoneToRemove);
            
            const response = await fetch(`${config.API_BASE_URL}/api/net-registrations/update-phone`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    passportId: passport.PassportID,
                    phoneNumbers: updatedPhones
                })
            });

            if (response.ok) {
                setPhones(updatedPhones);
                onUpdatePhones && onUpdatePhones(updatedPhones);
                showAlert('تم حذف الرقم بنجاح', 'success');
            } else {
                throw new Error('فشل في حذف الرقم');
            }
        } catch (error) {
            console.error('خطأ في حذف الرقم:', error);
            showAlert('خطأ في حذف الرقم', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-w-[180px]">
            {/* عرض الأرقام بشكل بسيط */}
            {phones.length > 0 ? (
                <div className="space-y-1">
                    {phones.map((phone, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                            <span className="font-mono text-gray-800" dir="ltr">{phone}</span>
                            <div className="flex gap-1 mr-2">
                                <a 
                                    href={`https://wa.me/${phone.replace(/[^\d]/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-800"
                                    title="واتساب"
                                >
                                    📱
                                </a>
                                <button
                                    onClick={() => handleRemovePhone(phone)}
                                    className="text-red-600 hover:text-red-800"
                                    title="حذف"
                                    disabled={isLoading}
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-gray-400 text-sm">لا توجد أرقام</div>
            )}

            {/* إضافة رقم جديد */}
            {isEditing ? (
                <div className="mt-2 pt-2 border-t">
                    <input
                        type="text"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        placeholder="01xxxxxxxxx"
                        className="w-full text-sm border rounded px-2 py-1 mb-2"
                        dir="ltr"
                    />
                    <div className="flex gap-1">
                        <button
                            onClick={handleAddPhone}
                            disabled={isLoading}
                            className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                        >
                            {isLoading ? 'جاري...' : 'حفظ'}
                        </button>
                        <button
                            onClick={() => {
                                setIsEditing(false);
                                setNewPhone('');
                            }}
                            className="bg-gray-500 text-white px-2 py-1 rounded text-xs hover:bg-gray-600"
                        >
                            إلغاء
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    onClick={() => setIsEditing(true)}
                    className="w-full mt-2 text-blue-600 hover:text-blue-800 text-xs border-t pt-2"
                >
                    + إضافة رقم
                </button>
            )}
        </div>
    );
};

const handlePhoneUpdate = (passportId, newPhones) => {
    setSelectedPassports(prev => prev.map(passport => 
        passport.PassportID === passportId 
            ? { ...passport, PhoneNumbers: JSON.stringify(newPhones) }
            : passport
    ));
};

const getActivePassports = (passports) => {
    return passports.filter(p => p.status !== 'net_booked' && p.status !== 'cancelled');
};

const markAsNetBooked = async (passportId, reason = '') => {
    const passport = selectedPassports.find(p => p.PassportID === passportId);
    if (!passport) return;

    const bookingReason = reason || prompt(
        `سبب حجز النت دون إكمال الإجراءات لـ "${passport.NameArabic}":\n\nأمثلة:\n- لم يحضر الكشف الطبي\n- مشاكل في الأوراق\n- تأجيل شخصي\n- مشاكل مالية`
    );
    
    if (bookingReason === null || bookingReason.trim() === '') return;

    try {
        setSelectedPassports(prev => prev.map(p => 
            p.PassportID === passportId 
                ? { 
                    ...p, 
                    status: 'net_booked',
                    netBookedReason: bookingReason.trim(),
                    netBookedAt: new Date().toISOString(),
                    notes: `🔴 محجوز للنت: ${bookingReason.trim()}`
                }
                : p
        ));

        await autoSaveCurrentList();
        showAlert(`تم تسجيل "${passport.NameArabic}" كمحجوز للنت مع عدم إكمال الإجراءات`, 'warning');
    } catch (error) {
        showAlert('خطأ في تسجيل حالة حجز النت', 'error');
    }
};

const cancelNetBooking = async (passportId) => {
    const passport = selectedPassports.find(p => p.PassportID === passportId);
    if (!passport) return;

    if (!window.confirm(`هل تريد إلغاء حجز النت وإعادة "${passport.NameArabic}" للحالة العادية؟`)) {
        return;
    }

    try {
        setSelectedPassports(prev => prev.map(p => 
            p.PassportID === passportId 
                ? { 
                    ...p, 
                    status: 'pending',
                    netBookedReason: null,
                    netBookedAt: null,
                    notes: ''
                }
                : p
        ));

        await autoSaveCurrentList();
        showAlert('تم إلغاء حجز النت وإعادة الشخص للحالة العادية', 'success');
    } catch (error) {
        showAlert('خطأ في إلغاء حجز النت', 'error');
    }
};


const PaymentPrintModal = () => {
    // ✅ Hooks لازم تبقى فوق وأبدًا قبل أي return شرطي
    const defaultPS = {
        showPaymentDelegate: true,
        showPaymentAmount: true,
        showPaymentNotes: true,
        showPaymentStatus: true, // جديد
        showPaymentPhone:  true, // جديد
        showPaymentTitle:  true, // جديد
        showPaymentTotals: true,
      };
      
      
    // لا تحسب القيم إلا مرة واحدة (state initializer function)
    const getInitialPS = () => {
      try {
        // لو فيه printSettings جاية من حالة أعلى، ندمجها
        if (typeof printSettings !== 'undefined' && printSettings && typeof printSettings === 'object') {
          return { ...defaultPS, ...printSettings };
        }
        // وإلا نقرأ من localStorage
        const saved = JSON.parse(localStorage.getItem('printSettings') || '{}');
        return { ...defaultPS, ...saved };
      } catch {
        return { ...defaultPS };
      }
    };
  
    const [localPS, setLocalPS] = React.useState(getInitialPS);
  
    const persistPS = React.useCallback((next) => {
      setLocalPS(next);
      // لو عندك دوال عامة للحفظ في المستوى الأعلى، استخدمها
      if (typeof savePrintSettings === 'function') {
        savePrintSettings(next);
      } else if (typeof setPrintSettings === 'function') {
        setPrintSettings(next);
      }
      try { localStorage.setItem('printSettings', JSON.stringify(next)); } catch {}
    }, []);
  
    const toggle = (key) => (e) => {
      const next = { ...localPS, [key]: e.target.checked };
      persistPS(next);
    };
  
    // ✅ بعد ما نادينا الـHooks، نقدر نعمل return شرطي
    if (!showPaymentPrintModal) return null;
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="bg-orange-600 text-white p-6 rounded-t-2xl">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">🖨️ اختيار نوع الطباعة</h2>
              <button
                onClick={() => setShowPaymentPrintModal(false)}
                className="text-white hover:bg-orange-700 p-2 rounded-full"
              >
                <X size={24} />
              </button>
            </div>
          </div>
  
          <div className="p-6 space-y-4">
            {/* ✅ خيارات الأعمدة (قبل الأزرار مباشرة) */}
            <div className="p-4 border rounded-lg bg-gray-50">
              <div className="font-semibold mb-2">خيارات الأعمدة (لتقرير الدفع):</div>
  
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={!!localPS.showPaymentDelegate}
                  onChange={toggle('showPaymentDelegate')}
                />
                إظهار عمود <strong>المندوب</strong>
              </label>
  
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={!!localPS.showPaymentAmount}
                  onChange={toggle('showPaymentAmount')}
                />
                إظهار عمود <strong>المبلغ</strong>
              </label>
              <label className="flex items-center gap-2 mb-2">
  <input
    type="checkbox"
    checked={!!localPS.showPaymentStatus}
    onChange={toggle('showPaymentStatus')}
  />
  إظهار عمود <strong>حالة الدفع</strong>
</label>

<label className="flex items-center gap-2 mb-2">
  <input
    type="checkbox"
    checked={!!localPS.showPaymentPhone}
    onChange={toggle('showPaymentPhone')}
  />
  إظهار عمود <strong>رقم الهاتف</strong>
</label>

<label className="flex items-center gap-2 mb-2">
  <input
    type="checkbox"
    checked={!!localPS.showPaymentTitle}
    onChange={toggle('showPaymentTitle')}
  />
  إظهار <strong>عنوان التقرير</strong>
</label>



  
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={!!localPS.showPaymentNotes}
                  onChange={toggle('showPaymentNotes')}
                />
                إظهار عمود <strong>ملاحظات الدفع</strong>
              </label>
  
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!localPS.showPaymentTotals}
                  onChange={toggle('showPaymentTotals')}
                />
                إظهار <strong>إجمالي المبالغ</strong> في الملخص
              </label>
            </div>
  
            {/* 👇 أزرار الطباعة */}
            <button
              onClick={() => {
                printPaymentStatus('all');
                setShowPaymentPrintModal(false);
              }}
              className="w-full bg-blue-100 hover:bg-blue-200 text-blue-700 p-4 rounded-lg border-2 border-blue-300 transition-colors"
            >
              <div className="text-lg font-bold">📋 طباعة الكل</div>
              <div className="text-sm">جميع الأشخاص مع حالة الدفع</div>
            </button>
  
            <button
              onClick={() => {
                printPaymentStatus('paid');
                setShowPaymentPrintModal(false);
              }}
              className="w-full bg-green-100 hover:bg-green-200 text-green-700 p-4 rounded-lg border-2 border-green-300 transition-colors"
            >
              <div className="text-lg font-bold">💰 المدفوعين فقط</div>
              <div className="text-sm">الأشخاص الذين دفعوا</div>
            </button>
  
            <button
              onClick={() => {
                printPaymentStatus('unpaid');
                setShowPaymentPrintModal(false);
              }}
              className="w-full bg-red-100 hover:bg-red-200 text-red-700 p-4 rounded-lg border-2 border-red-300 transition-colors"
            >
              <div className="text-lg font-bold">⏳ غير المدفوعين فقط</div>
              <div className="text-sm">الأشخاص الذين لم يدفعوا</div>
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  



// الواجهة الرئيسية
return (
    <div className="masar-selection-page min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-3 md:py-6" dir="rtl">

        <style>{`
            @media (max-width: 767px) {
                .masar-selection-page {
                    background: #f4f7fb !important;
                    overflow-x: hidden !important;
                    padding-bottom: 24px;
                }
                .masar-selection-page * {
                    box-sizing: border-box;
                }
                .masar-selection-page > .max-w-\[1600px\] {
                    max-width: 100% !important;
                    padding-left: 10px !important;
                    padding-right: 10px !important;
                }
                .masar-selection-page .sticky.top-0 {
                    position: static !important;
                }
                .masar-selection-page input,
                .masar-selection-page select,
                .masar-selection-page textarea {
                    min-height: 46px;
                    font-size: 16px !important;
                }
                .masar-selection-page button {
                    min-height: 42px;
                    touch-action: manipulation;
                }
                .masar-selection-page .rounded-xl.shadow-lg.p-6,
                .masar-selection-page .rounded-xl.shadow-lg.p-5 {
                    padding: 14px !important;
                    border-radius: 20px !important;
                    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08) !important;
                }
                .masar-selection-page .rounded-2xl.shadow-2xl {
                    border-radius: 24px 24px 0 0 !important;
                }
                .masar-selection-page .bg-gray-100.p-1.rounded-lg {
                    overflow-x: auto;
                    white-space: nowrap;
                    -webkit-overflow-scrolling: touch;
                    padding: 6px !important;
                    gap: 6px !important;
                    scrollbar-width: none;
                }
                .masar-selection-page .bg-gray-100.p-1.rounded-lg::-webkit-scrollbar {
                    display: none;
                }
                .masar-selection-page .bg-gray-100.p-1.rounded-lg > button {
                    min-width: 142px;
                    flex: 0 0 auto !important;
                    padding: 10px 12px !important;
                    font-size: 13px !important;
                }
                .masar-selection-page .fixed.inset-0.bg-black,
                .masar-selection-page .fixed.inset-0.bg-black\/50,
                .masar-selection-page .fixed.inset-0.bg-black.bg-opacity-50,
                .masar-selection-page .fixed.inset-0.bg-black.bg-opacity-60 {
                    align-items: flex-end !important;
                    justify-content: center !important;
                    padding: 0 !important;
                    z-index: 9999 !important;
                }
                .masar-selection-page .fixed.inset-0.bg-black > .bg-white,
                .masar-selection-page .fixed.inset-0.bg-black\/50 > .bg-white,
                .masar-selection-page .fixed.inset-0.bg-black.bg-opacity-50 > .bg-white,
                .masar-selection-page .fixed.inset-0.bg-black.bg-opacity-60 > .bg-white {
                    width: 100% !important;
                    max-width: 100% !important;
                    max-height: calc(100vh - 12px) !important;
                    overflow-y: auto !important;
                    border-radius: 24px 24px 0 0 !important;
                    margin: 0 !important;
                }
                .masar-selection-page .fixed.inset-0.bg-black .p-6,
                .masar-selection-page .fixed.inset-0.bg-black.bg-opacity-50 .p-6,
                .masar-selection-page .fixed.inset-0.bg-black.bg-opacity-60 .p-6 {
                    padding: 16px !important;
                }
                .masar-selection-page .fixed.inset-0.bg-black h2,
                .masar-selection-page .fixed.inset-0.bg-black.bg-opacity-50 h2,
                .masar-selection-page .fixed.inset-0.bg-black.bg-opacity-60 h2 {
                    font-size: 20px !important;
                    line-height: 1.35 !important;
                }
                .masar-selection-page table {
                    min-width: 900px;
                }
                .masar-selection-page .overflow-x-auto {
                    -webkit-overflow-scrolling: touch;
                }
                .masar-selection-page .mobile-action-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 8px;
                }
                .masar-selection-page .mobile-action-grid button {
                    width: 100%;
                    justify-content: center;
                    border-radius: 14px;
                    padding: 10px 8px;
                    font-size: 12px;
                    font-weight: 700;
                    white-space: normal;
                    line-height: 1.35;
                }
                .masar-selection-page .md\:hidden,
                .masar-selection-page .md\:hidden * {
                    max-width: 100%;
                }
                .masar-selection-page .md\:hidden button {
                    white-space: normal;
                    line-height: 1.35;
                }
                .masar-selection-page .md\:hidden .grid > * {
                    min-width: 0;
                }
                .masar-selection-page .selection-mobile-more-actions summary::-webkit-details-marker {
                    display: none;
                }
                .masar-selection-page .selection-mobile-more-actions[open] summary span:last-child {
                    transform: rotate(180deg);
                }
            }
        `}</style>

        {/* 🚨 Alert System المحسن */}
        <div className="fixed top-4 right-4 z-50 space-y-2">
            {alerts.map(alert => (
                <div
                    key={alert.id}
                    className={`p-4 rounded-lg shadow-lg flex items-start gap-3 transition-all duration-300 max-w-md ${
                        alert.type === 'success' ? 'bg-green-500 text-white' :
                        alert.type === 'warning' ? 'bg-yellow-500 text-white' :
                        alert.type === 'error' ? 'bg-red-500 text-white' :
                        'bg-blue-500 text-white'
                    }`}
                >
                    <div className="flex-shrink-0 mt-0.5">
                        {alert.type === 'success' && <CheckCircle2 size={20} />}
                        {alert.type === 'warning' && <AlertTriangle size={20} />}
                        {alert.type === 'error' && <AlertTriangle size={20} />}
                        {alert.type === 'info' && <RefreshCw size={20} />}
                    </div>
                    <span className="font-medium text-sm leading-relaxed whitespace-pre-line">
                        {alert.message}
                    </span>
                </div>
            ))}
        </div>

        <div className="max-w-[1600px] mx-auto px-3 sm:px-5 lg:px-7">

        {/* 🎯 Header */}
                <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-4 md:mb-6 sticky top-0 z-30">

                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-5 md:mb-6">
                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="p-3 bg-blue-600 text-white rounded-lg">
                            <FileText size={28} />
                        </div>
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">نظام الاختيار و التجهيزات المطور</h1>
                            <p className="text-sm md:text-base text-gray-600">توزيع عادل وذكي مع نظام الأولوية التراكمية</p>
                        </div>
                        {currentList && (
                            <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg font-medium">
                                📋 {currentList.ListName}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full md:w-auto">
                        <button
                            onClick={createNewList}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 shadow-md"
                        >
                            <UserPlus size={20} />
                            قائمة جديدة
                        </button>

                            <button
        type="button"
        onClick={() => setShowSelectionRulesModal(true)}
        className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 shadow-sm transition-all duration-200 text-sm font-medium"
    >
        <Settings className="ml-2 h-4 w-4" />
        ضبط معايير الاختيار
    </button>


                        <button
                            onClick={() => navigate('/')}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 shadow-md"
                        >
                            <ArrowLeft size={20} />
                            الرئيسية
                        </button>
                    </div>
                </div>

                {/* 📊 Statistics Bar */}
                {selectedPassports.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                        {(() => {
                            const stats = getStatistics();
                            return (
                                <>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                                        <div className="text-sm text-gray-600">إجمالي المختارين</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">{stats.confirmed}</div>
                                        <div className="text-sm text-gray-600">مؤكد الحضور</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                                        <div className="text-sm text-gray-600">في الانتظار</div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* 🎛️ Tabs */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mt-5 md:mt-6 overflow-x-auto md:overflow-visible">
                    <button
                        onClick={() => setActiveTab('selection')}
                        className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
                            activeTab === 'selection' 
                                ? 'bg-white text-blue-600 shadow-md' 
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <Calculator size={20} />
                        الاختيار المُطور
                    </button>

                    <button
                        onClick={() => setActiveTab('manual')}
                        className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
                            activeTab === 'manual' 
                                ? 'bg-white text-blue-600 shadow-md' 
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <UserPlus size={20} />
                        الاختيار اليدوي
                    </button>







                    <button
                        onClick={() => setActiveTab('management')}
                        className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
                            activeTab === 'management' 
                                ? 'bg-white text-blue-600 shadow-md' 
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <Users size={20} />
                        إدارة القائمة {selectedPassports.length > 0 && `(${selectedPassports.length})`}
                    </button>
                    <button
                        onClick={() => { setActiveTab('excluded'); fetchExcludedPassports(selectionSettings.companyId); }}
                        className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
                            activeTab === 'excluded' 
                                ? 'bg-white text-blue-600 shadow-md' 
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <UserX size={20} />
                        المستبعدين {excludedPassports.length > 0 && `(${excludedPassports.length})`}
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
                            activeTab === 'history' 
                                ? 'bg-white text-blue-600 shadow-md' 
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <BarChart3 size={20} />
                        تاريخ التوزيع
                    </button>
                    <button
                        onClick={() => setActiveTab('savedLists')}
                        className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
                            activeTab === 'savedLists' 
                                ? 'bg-white text-blue-600 shadow-md' 
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <FileText size={20} />
                        القوائم المحفوظة {savedLists.length > 0 && `(${savedLists.length})`}
                    </button>


                    <button
    onClick={() => setActiveTab('sendToDelegates')}
    className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
        activeTab === 'sendToDelegates' 
            ? 'bg-white text-blue-600 shadow-md' 
            : 'text-gray-600 hover:text-gray-800'
    }`}
>
    <FileText size={20} />
    إرسال للمناديب
</button>



                </div>
            </div>

            {/* 🎯 Selection Tab */}
           {activeTab === 'selection' && (
    <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Calculator size={24} />
                نظام الاختيار المُطور مع الأولوية التراكمية
            </h2>

          
        </div>
       <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
    {/* الشركة */}
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
            الشركة *
        </label>
        <select
            value={selectionSettings.companyId}
            onChange={(e) => {
                const value = e.target.value;
                setSelectionSettings(prev => ({ 
                    ...prev, 
                    companyId: value 
                }));
                if (value) {
                    // نحافظ على نفس السلوك القديم
                    fetchDelegateHistory(value);
                    fetchExcludedPassports(value);
                }
            }}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        >
            <option value="">اختر الشركة</option>
            {companies.map(company => (
                <option key={company.CompanyID} value={company.CompanyID}>
                    {company.CompanyName}
                </option>
            ))}
        </select>
    </div>

    {/* ✅ مدة السفر (إجباري) */}
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
            مدة السفر (إجباري)
        </label>
        <select
            value={selectionSettings.travelDuration}
            onChange={(e) => {
                const value = e.target.value;
                setSelectionSettings(prev => ({
                    ...prev,
                    travelDuration: value
                }));
            }}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        >
            <option value="">اختر مدة السفر</option>
            {TRAVEL_DURATIONS.map(duration => (
                <option key={duration} value={duration}>
                    {duration}
                </option>
            ))}
        </select>
    </div>

    {/* المهنة (اختياري) */}
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
            المهنة (اختياري)
        </label>
        <select
            value={selectionSettings.professionId}
            onChange={(e) => setSelectionSettings(prev => ({ 
                ...prev, 
                professionId: e.target.value 
            }))}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        >
            <option value="">كل المهن</option>
            {professions.map(profession => (
                <option key={profession.ProfessionID} value={profession.ProfessionID}>
                    {profession.ProfessionName}
                </option>
            ))}
        </select>
    </div>

    {/* العدد المطلوب */}
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
            العدد المطلوب *
        </label>
        <input
            type="number"
            min="1"
            value={selectionSettings.requiredCount}
            onChange={(e) => setSelectionSettings(prev => ({ 
                ...prev, 
                requiredCount: e.target.value 
            }))}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            placeholder="عدد الأشخاص"
        />
    </div>

    {/* تاريخ عمل النت */}
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
            تاريخ عمل النت
        </label>
        <input
            type="date"
            value={selectionSettings.date}
            onChange={(e) => setSelectionSettings(prev => ({ 
                ...prev, 
                date: e.target.value 
            }))}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        />
    </div>

    {/* زر التحليل */}
    <div className="flex items-end">
        <button
            onClick={performSmartSelection}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg px-4 py-3 flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-200 shadow-md font-medium"
        >
            {isLoading ? (
                <RefreshCw className="animate-spin" size={20} />
            ) : (
                <BarChart3 size={20} />
            )}
            تحليل واختيار
        </button>
    </div>
</div>


                    {/* ملاحظات */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            ملاحظات (اختياري)
                        </label>
                        <textarea
                            value={selectionSettings.notes}
                            onChange={(e) => setSelectionSettings(prev => ({ 
                                ...prev, 
                                notes: e.target.value 
                            }))}
                            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                            placeholder="أضف أي ملاحظات حول هذه القائمة..."
                            rows="3"
                        />
                    </div>

                    {/* عرض تاريخ المناديب */}
                    {selectionSettings.companyId && delegateHistory.length > 0 && (
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200 mb-6">
                            <h3 className="text-lg font-bold text-yellow-900 mb-4 flex items-center gap-2">
                                📊 تاريخ التوزيع السابق (نظام الأولوية التراكمية)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {delegateHistory.map(delegate => (
                                    <div key={delegate.DelegateID} className="bg-white rounded-lg p-4 shadow-sm">
                                        <div className="font-semibold text-gray-900 mb-2">
                                            {delegate.DelegateName}
                                        </div>
                                        <div className="space-y-1 text-sm">
                                            <div className="flex justify-between">
                                                <span>إجمالي المختارين:</span>
                                                <span className="font-medium">{delegate.TotalSelected}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>إجمالي المتاحين:</span>
                                                <span className="font-medium">{delegate.TotalAvailable}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>نسبة الاختيار:</span>
                                                <span className={`font-medium ${
                                                    delegate.SelectionRatio > 0.6 ? 'text-red-600' :
                                                    delegate.SelectionRatio > 0.4 ? 'text-yellow-600' :
                                                    'text-green-600'
                                                }`}>
                                                    {(delegate.SelectionRatio * 100).toFixed(1)}%
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>مستوى الأولوية:</span>
                                                <span className={`font-medium px-2 py-1 rounded text-xs ${
                                                    delegate.SelectionRatio < 0.3 ? 'bg-green-100 text-green-800' :
                                                    delegate.SelectionRatio < 0.6 ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-red-100 text-red-800'
                                                }`}>
                                                    {delegate.SelectionRatio < 0.3 ? 'عالية' :
                                                     delegate.SelectionRatio < 0.6 ? 'متوسطة' : 'منخفضة'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 📊 Distribution Preview */}
                   {/* 📊 Distribution Preview - محسن */}
{showPreview && Object.keys(distributionPreview).length > 0 && (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
                <BarChart3 size={22} />
                معاينة توزيع الباقي مع الأولوية التراكمية
            </h3>
            <div className="flex gap-2">
                <button
                    onClick={confirmFairSelection}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 shadow-md"
                >
                    <CheckCircle2 size={18} />
                    تأكيد الاختيار
                </button>
                <button
                    onClick={() => setShowPreview(false)}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
                >
                    إلغاء
                </button>
            </div>
        </div>

        {/* إحصائيات سريعة */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-white rounded-lg shadow-sm">
            {(() => {
                const totalAvailable = Object.values(distributionPreview).reduce((sum, d) => sum + d.totalCount, 0);
                const totalSelected = Object.values(distributionPreview).reduce((sum, d) => sum + d.allocatedCount, 0);
                const totalExcluded = Object.values(distributionPreview).reduce((sum, d) => sum + (d.excludedCount || 0), 0);
                const avgPriority = Object.values(distributionPreview).reduce((sum, d) => sum + (d.priorityWeight || 0), 0) / Object.keys(distributionPreview).length;

                return (
                    <>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{totalAvailable}</div>
                            <div className="text-sm text-gray-600">إجمالي المتاح</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{totalSelected}</div>
                            <div className="text-sm text-gray-600">سيتم اختيارهم</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">{totalExcluded}</div>
                            <div className="text-sm text-gray-600">سيتم استبعادهم</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{avgPriority.toFixed(2)}</div>
                            <div className="text-sm text-gray-600">متوسط وزن الأولوية</div>
                        </div>
                    </>
                );
            })()}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(distributionPreview)
                .sort(([, a], [, b]) => (b.priorityWeight || 0) - (a.priorityWeight || 0)) // ترتيب حسب الأولوية
                .map(([delegateId, data]) => {
                    const excludedCount = data.excludedCount || 0;
                    const priorityLevel = (data.priorityWeight || 0) > 0.7 ? 'عالية' : 
                                        (data.priorityWeight || 0) > 0.4 ? 'متوسطة' : 'منخفضة';
                    const priorityColor = (data.priorityWeight || 0) > 0.7 ? 'green' : 
                                        (data.priorityWeight || 0) > 0.4 ? 'yellow' : 'red';

                    return (
                        <div key={delegateId} className="bg-white rounded-lg p-4 shadow-md border-l-4 border-blue-500">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-900 text-sm">
                                    {data.delegateInfo?.DelegateName || 'مندوب غير معروف'}
                                </h4>
                                <div className="flex gap-1">
                                    <span className={`bg-${priorityColor}-100 text-${priorityColor}-800 px-2 py-1 rounded-full text-xs font-medium`}>
                                        أولوية {priorityLevel}
                                    </span>
                                </div>
                            </div>
                            
                            {/* شريط التوزيع المرئي */}
                            <div className="mb-3">
                                <div className="flex justify-between text-xs text-gray-600 mb-1">
                                    <span>التوزيع</span>
                                    <span>{data.totalCount || 0} إجمالي</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                    <div className="h-full flex">
                                        <div 
                                            className="bg-green-500 transition-all duration-500"
                                            style={{ 
                                                width: `${data.totalCount > 0 ? ((data.allocatedCount || 0) / data.totalCount * 100) : 0}%` 
                                            }}
                                            title={`مختار: ${data.allocatedCount || 0}`}
                                        ></div>
                                        <div 
                                            className="bg-red-400 transition-all duration-500"
                                            style={{ 
                                                width: `${data.totalCount > 0 ? (excludedCount / data.totalCount * 100) : 0}%` 
                                            }}
                                            title={`مستبعد: ${excludedCount}`}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">المتاح:</span>
                                    <span className="font-medium text-blue-600">{data.totalCount || 0}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">سيتم اختيار:</span>
                                    <span className="font-bold text-green-600">{data.allocatedCount || 0}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">سيتم استبعاد:</span>
                                    <span className="font-medium text-red-600">{excludedCount}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">النسبة:</span>
                                    <span className="font-medium text-purple-600">
                                        {data.totalCount > 0 ? ((data.allocatedCount || 0) / data.totalCount * 100).toFixed(1) : '0.0'}%
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">التاريخ:</span>
                                    <span className={`text-xs font-medium ${
                                        (data.historicalRatio || 0) < 0.3 ? 'text-green-600' :
                                        (data.historicalRatio || 0) < 0.6 ? 'text-yellow-600' : 'text-red-600'
                                    }`}>
                                        {((data.historicalRatio || 0) * 100).toFixed(1)}%
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">وزن الأولوية:</span>
                                    <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                                        {(data.priorityWeight || 0).toFixed(3)}
                                    </span>
                                </div>
                            </div>

                            {/* عرض أسماء المختارين */}
                            {data.allocatedCount > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-xs text-gray-500 mb-2 flex items-center justify-between">
                                        <span>المختارون:</span>
                                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                            {data.allocatedCount}
                                        </span>
                                    </div>
                                    <div className="max-h-32 overflow-y-auto space-y-1">
                                        {data.passports && data.passports.slice(0, data.allocatedCount).map(p => (
                                            <div key={p.PassportID} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded flex justify-between items-center">
                                                <span>{p.NameArabic}</span>
                                                <span className="text-gray-500">ملف #{getPassportFileNumber(p)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* عرض المستبعدين إذا وجدوا */}
                            {excludedCount > 0 && (
                                <div className="mt-2">
                                    <div className="text-xs text-gray-500 mb-1">
                                        سيتم استبعاد {excludedCount} عميل حسب ترتيب الـ ID
                                    </div>
                                    <div className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded">
                                        📝 الاستبعاد يبدأ من نهاية القائمة
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
        </div>

        {/* تفسير النظام المُطور */}
        <div className="mt-6 p-4 bg-blue-100 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                🎯 آلية عمل نظام توزيع الباقي:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                <div>
                    <h5 className="font-medium mb-1">📊 الخطوات:</h5>
                    <ul className="space-y-1 text-xs">
                        <li>• حساب الباقي = (المتاح - المطلوب)</li>
                        <li>• حساب وزن الأولوية = (1 - النسبة التاريخية)</li>
                        <li>• توزيع الباقي عكسياً حسب الأولوية</li>
                        <li>• المختار = (المتاح - نصيبه من الباقي)</li>
                    </ul>
                </div>
                <div>
                    <h5 className="font-medium mb-1">⚖️ العدالة:</h5>
                    <ul className="space-y-1 text-xs">
                        <li>• الأولوية العالية = استبعاد أقل</li>
                        <li>• تعويض المناديب الأقل اختياراً</li>
                        <li>• ضمان العدالة طويلة المدى</li>
                        <li>• توزيع متوازن ومنطقي</li>
                    </ul>
                </div>
            </div>
            
            {/* معلومات إضافية حسب النتائج */}
            {(() => {
                const highPriorityCount = Object.values(distributionPreview).filter(d => (d.priorityWeight || 0) > 0.7).length;
                const lowPriorityCount = Object.values(distributionPreview).filter(d => (d.priorityWeight || 0) <= 0.4).length;
                
                return (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <div className="text-xs text-blue-700">
                            <strong>تحليل التوزيع:</strong> {highPriorityCount} مندوب بأولوية عالية، {lowPriorityCount} مندوب بأولوية منخفضة. 
                            {highPriorityCount > 0 && " المناديب عالية الأولوية حصلوا على حصة أكبر للتعويض."}
                        </div>
                    </div>
                );
            })()}
        </div>
    </div>
)}

                    {/* رسالة عدم وجود معاينة - محسنة */}
{!showPreview && (
    <div className="text-center py-12">
        <Calculator className="mx-auto h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">جاهز لنظام توزيع الباقي المُطور</h3>
        <p className="text-gray-600 mb-4">اختر الشركة وأدخل العدد المطلوب لبدء التحليل الذكي</p>
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 max-w-4xl mx-auto">
            <h4 className="font-semibold text-blue-900 mb-4">🚀 آلية العمل الجديدة:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xs">1</div>
                        <span className="font-medium text-gray-900">حساب الباقي</span>
                    </div>
                    <p className="text-gray-600 text-xs">الباقي = المتاح - المطلوب</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-xs">2</div>
                        <span className="font-medium text-gray-900">حساب الأولوية</span>
                    </div>
                    <p className="text-gray-600 text-xs">الأقل اختياراً = أولوية أعلى</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-xs">3</div>
                        <span className="font-medium text-gray-900">توزيع عكسي</span>
                    </div>
                    <p className="text-gray-600 text-xs">الأولوية العالية = استبعاد أقل</p>
                </div>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2">
                    <div className="text-yellow-600">💡</div>
                    <div className="text-yellow-800 text-sm">
                        <strong>المثال:</strong> 25 متاح، 15 مطلوب → الباقي 10 يتوزع حسب الأولوية التراكمية
                    </div>
                </div>
            </div>
        </div>
    </div>
)}
                   </div>
               )}





{/* 🎯 Manual Selection Tab - تبويب الاختيار اليدوي المحسن */}
{activeTab === 'manual' && (
    <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <UserPlus size={24} />
            الاختيار اليدوي للأشخاص
        </h2>

        {allowProcessedManualSelection && (
            <div className="mb-4 bg-amber-50 border border-amber-300 rounded-xl p-4 text-amber-900 text-sm">
                <strong>⚠️ وضع الطوارئ مفعل:</strong> سيظهر في الاختيار اليدوي من لديهم تاريخ نت/تأشيرة/سفر، مع تمييزهم بعلامات تحذير حتى تعدلهم يدويًا عند الحاجة. الاختيار الذكي غير متأثر.
            </div>
        )}
        
        {/* إعدادات الاختيار المحسنة */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    الشركة *
                </label>
                <select
                    value={manualSelection.companyId}
onChange={(e) => {
    const value = e.target.value;
    setManualSelection(prev => ({ 
        ...prev,
        companyId: value,
        selectedIds: new Set(),
        availablePassports: []
    }));
    if (value) {
        fetchAvailableForManualSelection(
            value,
            manualSelection.professionId,
            manualSelection.travelDuration || ''
        );
    }
}}


                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">اختر الشركة</option>
                    {companies.map(company => (
                        <option key={company.CompanyID} value={company.CompanyID}>
                            {company.CompanyName}
                        </option>
                    ))}
                </select>
            </div>

{/* ✅ مدة السفر الجديدة بجانب الشركة */}
<div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
        مدة السفر (إجباري)
    </label>
    <select
        value={manualSelection.travelDuration}
        onChange={(e) => {
            const value = e.target.value;
            setManualSelection(prev => ({
                ...prev,
                travelDuration: value,
                selectedIds: new Set(),
                availablePassports: []
            }));
            if (manualSelection.companyId) {
                fetchAvailableForManualSelection(
                    manualSelection.companyId,
                    manualSelection.professionId,
                    value
                );
            }
        }}
        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
    >
        <option value="">اختر مدة السفر</option>
        {TRAVEL_DURATIONS.map(duration => (
            <option key={duration} value={duration}>
                {duration}
            </option>
        ))}
    </select>
</div>


            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    المهنة (اختياري)
                </label>
                <select
                    value={manualSelection.professionId}
                 onChange={(e) => {
    const value = e.target.value;
    setManualSelection(prev => ({ 
        ...prev, 
        professionId: value,
        selectedIds: new Set()
    }));
    if (manualSelection.companyId) {
        fetchAvailableForManualSelection(
            manualSelection.companyId,
            value,
            manualSelection.travelDuration || ''
        );
    }
}}

                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">كل المهن</option>
                    {professions.map(profession => (
                        <option key={profession.ProfessionID} value={profession.ProfessionID}>
                            {profession.ProfessionName}
                        </option>
                    ))}
                </select>
            </div>

            {/* ✅ إضافة حقل العدد المطلوب */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    العدد المطلوب *
                </label>
                <input
                    type="number"
                    min="1"
                    value={manualSelection.requiredCount}
                    onChange={(e) => setManualSelection(prev => ({ 
                        ...prev, 
                        requiredCount: e.target.value 
                    }))}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                    placeholder="عدد الأشخاص"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    تاريخ عمل النت
                </label>
                <input
                    type="date"
                    value={manualSelection.date}
                    onChange={(e) => setManualSelection(prev => ({ 
                        ...prev, 
                        date: e.target.value 
                    }))}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                />
            </div>

            <div className="flex items-end">
       <button
    onClick={() => {
        if (!manualSelection.companyId) {
            showAlert('اختر الشركة أولاً', 'warning');
            return;
        }
        if (!manualSelection.travelDuration) {
            showAlert('اختر مدة السفر أولاً', 'warning');
            return;
        }

        fetchAvailableForManualSelection(
            manualSelection.companyId,
            manualSelection.professionId,
            manualSelection.travelDuration
        );
    }}

    disabled={!manualSelection.companyId || !manualSelection.travelDuration || manualSelection.isLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {manualSelection.isLoading ? (
                        <RefreshCw className="animate-spin" size={20} />
                    ) : (
                        <Search size={20} />
                    )}
                    جلب المتاحين
                </button>
            </div>
        </div>

        {/* ملاحظات */}
        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                ملاحظات (اختياري)
            </label>
            <textarea
                value={manualSelection.notes}
                onChange={(e) => setManualSelection(prev => ({ 
                    ...prev, 
                    notes: e.target.value 
                }))}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                placeholder="أضف ملاحظات حول هذا الاختيار..."
                rows="3"
            />
        </div>

        {/* قسم الاختيار المحسن */}
        {manualSelection.availablePassports.length > 0 && (
            <>
                {/* إحصائيات وأدوات التحكم المحسنة */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-6">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                    {manualSelection.availablePassports.length}
                                </div>
                                <div className="text-sm text-gray-600">إجمالي المتاح</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    {manualSelection.selectedIds.size}
                                </div>
                                <div className="text-sm text-gray-600">مختار حالياً</div>
                            </div>
                            {/* ✅ عرض العدد المطلوب والمتبقي */}
                            {manualSelection.requiredCount && (
                                <>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-purple-600">
                                            {manualSelection.requiredCount}
                                        </div>
                                        <div className="text-sm text-gray-600">العدد المطلوب</div>
                                    </div>
                                    <div className="text-center">
                                        <div className={`text-2xl font-bold ${
                                            (parseInt(manualSelection.requiredCount) - manualSelection.selectedIds.size) > 0 
                                                ? 'text-orange-600' 
                                                : 'text-green-600'
                                        }`}>
                                            {Math.max(0, parseInt(manualSelection.requiredCount) - manualSelection.selectedIds.size)}
                                        </div>
                                        <div className="text-sm text-gray-600">متبقي</div>
                                    </div>
                                </>
                            )}
                        </div>
                        
                        <div className="flex gap-2">
                            <button
                                onClick={selectAllAvailable}
                                disabled={manualSelection.requiredCount && manualSelection.selectedIds.size >= parseInt(manualSelection.requiredCount)}
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                            >
                                <Plus size={16} />
                                تحديد الكل ({Math.min(
                                    getFilteredManualPassports().length,
                                    manualSelection.requiredCount ? 
                                        Math.max(0, parseInt(manualSelection.requiredCount) - manualSelection.selectedIds.size) :
                                        getFilteredManualPassports().length
                                )})
                            </button>
                            <button
                                onClick={clearAllSelection}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                            >
                                <X size={16} />
                                إلغاء الكل
                            </button>
                            {manualSelection.selectedIds.size > 0 && (
                                <button
                                    onClick={confirmManualSelection}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2"
                                >
                                    <CheckCircle2 size={16} />
                                    تأكيد الاختيار ({manualSelection.selectedIds.size})
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ✅ تحذير عند الوصول للحد الأقصى */}
                    {manualSelection.requiredCount && manualSelection.selectedIds.size >= parseInt(manualSelection.requiredCount) && (
                        <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-4">
                            <div className="flex items-center gap-2 text-green-800">
                                <CheckCircle2 size={20} />
                                <span className="font-medium">تم الوصول للعدد المطلوب ({manualSelection.requiredCount} أشخاص)</span>
                            </div>
                            <div className="text-sm text-green-700 mt-1">
                                يمكنك إضافة المزيد لاحقاً من تبويب "إدارة القائمة" إذا كنت تريد زيادة العدد
                            </div>
                        </div>
                    )}

                    {/* شريط البحث */}
                    <div className="relative">
                        <Search size={20} className="absolute right-3 top-3 text-gray-400" />
                        <input
                            type="text"
                            placeholder="ابحث بالاسم أو رقم الجواز أو الرقم القومي أو المندوب..."
                            value={manualSelection.searchTerm}
                            onChange={(e) => setManualSelection(prev => ({ 
                                ...prev, 
                                searchTerm: e.target.value 
                            }))}
                            className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {/* قائمة الأشخاص */}

                {/* 📱 عرض كروت للموبايل فقط */}
                <div className="md:hidden space-y-3 mb-4">
                    {getFilteredManualPassports().map((passport, index) => {
                        const isSelected = manualSelection.selectedIds.has(passport.PassportID);
                        const delegate = delegates.find(d => d.DelegateID === passport.DelegateID);
                        const profession = professions.find(p => p.ProfessionID === passport.PassportProfessionID);
                        return (
                            <div
                                key={`mobile-manual-${passport.PassportID}`}
                                onClick={() => togglePassportSelection(passport.PassportID)}
                                className={`rounded-2xl border p-4 shadow-sm cursor-pointer transition-all ${
                                    isSelected ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-100' : 'bg-white border-gray-200'
                                }`}
                            >
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="min-w-0">
                                        <div className="font-bold text-gray-900 leading-6 truncate">{passport.NameArabic || 'اسم غير محدد'}</div>
                                        <div className="text-xs text-blue-700 bg-blue-50 rounded-full px-2 py-1 inline-block mt-1">ID: #{getPassportFileNumber(passport)}</div>
                                        {getProcessedEmergencyBadges(passport).length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {getProcessedEmergencyBadges(passport).map(badge => (
                                                    <span key={badge} className="text-[11px] bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
                                                        ⚠️ {badge}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => togglePassportSelection(passport.PassportID)}
                                        className="w-6 h-6 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div className="bg-gray-50 rounded-xl p-3">
                                        <div className="text-xs text-gray-500 mb-1">رقم الجواز</div>
                                        <div className="font-mono font-semibold text-gray-900">{passport.PassportNumber || 'غير محدد'}</div>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-3">
                                        <div className="text-xs text-gray-500 mb-1">المندوب</div>
                                        <div className="font-semibold text-gray-900 truncate">{delegate?.DelegateName || 'غير محدد'}</div>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-3">
                                        <div className="text-xs text-gray-500 mb-1">المهنة</div>
                                        <div className="font-semibold text-gray-900 truncate">{profession?.ProfessionName || 'غير محدد'}</div>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-3">
                                        <div className="text-xs text-gray-500 mb-1">الهاتف</div>
                                        <PhoneNumbersDisplay
                                            passport={passport}
                                            onUpdatePhones={(newPhones) => handlePhoneUpdate(passport.PassportID, newPhones)}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="hidden md:block max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="w-full">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-4 py-3 text-right font-medium text-gray-700">اختيار</th>
                                <th className="px-4 py-3 text-right font-medium text-gray-700">الاسم</th>
                                <th className="px-4 py-3 text-right font-medium text-gray-700">رقم الجواز</th>
                                <th className="px-4 py-3 text-right font-medium text-gray-700">المندوب</th>
                                <th className="px-4 py-3 text-right font-medium text-gray-700">المهنة</th>
                                <th className="px-4 py-3 text-right font-medium text-gray-700">الهاتف</th>
                                <th className="px-4 py-3 text-right font-medium text-gray-700">تنبيه</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {getFilteredManualPassports().map((passport, index) => {
                                const isSelected = manualSelection.selectedIds.has(passport.PassportID);
                                const delegate = delegates.find(d => d.DelegateID === passport.DelegateID);
                                const profession = professions.find(p => p.ProfessionID === passport.PassportProfessionID);
                                
                                return (
                                    <tr 
                                        key={passport.PassportID}
                                        className={`transition-colors duration-200 cursor-pointer ${
                                            isSelected ? 'bg-blue-50 border-r-4 border-blue-500' : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => togglePassportSelection(passport.PassportID)}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => togglePassportSelection(passport.PassportID)}
                                                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">
                                                {passport.NameArabic}
                                            </div>
                                            <div className="text-sm text-blue-600">
                                                ID:: {getPassportFileNumber(passport)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-sm">
                                            {passport.PassportNumber || 'غير محدد'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                                                {delegate?.DelegateName || 'غير محدد'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                                {profession?.ProfessionName || 'غير محدد'}
                                            </span>
                                        </td>
                                       <td className="px-4 py-3 text-sm">
    <PhoneNumbersDisplay 
        passport={passport}
        onUpdatePhones={(newPhones) => handlePhoneUpdate(passport.PassportID, newPhones)}
    />
</td>
                                        <td className="px-4 py-3 text-sm">
                                            {getProcessedEmergencyBadges(passport).length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {getProcessedEmergencyBadges(passport).map(badge => (
                                                        <span key={badge} className="text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-1">
                                                            ⚠️ {badge}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {getFilteredManualPassports().length === 0 && (
                    <div className="text-center py-8">
                        <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد نتائج</h3>
                        <p className="text-gray-600">جرب تغيير كلمة البحث</p>
                    </div>
                )}
            </>
        )}

        {/* رسالة عدم وجود بيانات */}
        {manualSelection.availablePassports.length === 0 && !manualSelection.isLoading && (
            <div className="text-center py-12">
                <UserPlus className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">جاهز للاختيار اليدوي</h3>
                <p className="text-gray-600 mb-4">اختر الشركة واضغط "جلب المتاحين" لبدء الاختيار</p>
                <div className="bg-blue-50 rounded-lg p-6 max-w-2xl mx-auto">
                    <h4 className="font-semibold text-blue-900 mb-2">مميزات الاختيار اليدوي:</h4>
                    <ul className="text-blue-800 text-sm space-y-1">
                        <li>• اختيار الأشخاص حسب تفضيلك الشخصي</li>
                        <li>• تحديد العدد المطلوب مسبقاً</li>
                        <li>• إمكانية البحث والفلترة السريعة</li>
                        <li>• عرض تفاصيل كل شخص قبل الاختيار</li>
                        <li>• تحكم كامل في عدد المختارين</li>
                        <li>• إمكانية الإضافة اللاحقة للعدد المحدد</li>
                    </ul>
                </div>
            </div>
        )}
    </div>
)}





             {/* ✅ تحديث جدول الجوازات في management tab */}
{activeTab === 'management' && (
    <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Users size={24} />
                إدارة القائمة المختارة ({selectedPassports.length})
            </h2>
            <div className="flex gap-2">
             <button
    onClick={openListNameBuilder}
    disabled={isLoading || selectedPassports.length === 0}
    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 transition-all duration-200"
>
    <Save size={18} />
    حفظ يدوي
</button>

                
                {currentList && (
                    <button
                        onClick={() => {
                            const confirmMessage = `⚠️ حذف القائمة النشطة ⚠️

هل تريد حذف القائمة النشطة "${currentList.ListName}" حذفًا نهائيًا؟

⚠️ سيتم:
- حذف القائمة فعليًا من قاعدة البيانات إذا لم تكن مرتبطة ببيانات متابعة محفوظة
- إزالة تاريخ النت/القنصلية/التأشيرة/السفر من الأشخاص المرتبطين بها
- عدم أرشفة القائمة تلقائيًا
- لو توجد بيانات متابعة محفوظة مثل كشف/بصمة/تأمين/تسليم أوراق سيتم منع الحذف ويطلب منك الأرشفة أو التنظيف اليدوي

اكتب "حذف نهائي" للتأكيد:`;

                            const userInput = prompt(confirmMessage);
                            
                            if (userInput === 'حذف نهائي') {
                                setIsLoading(true);
                                fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${currentList.ListID}`, {
                                    method: 'DELETE'
                                })
                                .then(response => response.json())
                                .then(result => {
                                    if (result.success) {
                                        setCurrentList(null);
                                        setSelectedPassports([]);
                                        fetchSavedLists();
                                        setActiveTab('selection');
                                        showAlert(`✅ تم حذف القائمة حذفًا نهائيًا بدون أرشفة، وتم تنظيف ${result.deletedDetails?.passportsCleaned || 0} جواز من بيانات النت/التأشيرة/السفر`, 'success');
                                    } else {
                                        if (result.requiresArchiveOrManualCleanup) {
                                            return cleanupHiddenFollowUpsAndDeleteList(currentList, result);
                                        }
                                        showAlert(`❌ فشل في الحذف: ${result.error}`, 'error');
                                    }
                                })
                                .catch(error => {
                                    showAlert(`❌ خطأ في الحذف: ${error.message}`, 'error');
                                })
                                .finally(() => {
                                    setIsLoading(false);
                                });
                            } else if (userInput !== null) {
                                showAlert('❌ لم يتم الحذف - يجب كتابة "حذف نهائي" للتأكيد', 'warning');
                            }
                        }}
                        disabled={isLoading}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                    >
                        {isLoading ? (
                            <RefreshCw className="animate-spin" size={16} />
                        ) : (
                            <Trash2 size={16} />
                        )}
                        حذف هذه القائمة نهائياً
                    </button>
                )}
            </div>
        </div>

{/* ✅ عرض معلومات القائمة وإمكانية الإضافة */}
{selectedPassports.length > 0 && (
    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex justify-between items-center">
            <div>
                <div className="font-medium text-blue-900">
                    القائمة الحالية: {selectedPassports.length} شخص
                    {selectionSettings.requiredCount && (
                        <span className="text-sm text-blue-700 ml-2">
                            (من أصل {selectionSettings.requiredCount} مطلوب)
                        </span>
                    )}
                </div>
                {currentList && (
                    <div className="text-sm text-blue-700 mt-1">
                        📋 القائمة المحفوظة: {currentList.ListName}
                    </div>
                )}
                {getRemainingSlots() > 0 && (
                    <div className="text-sm text-green-700 mt-1">
                        ✅ يمكن إضافة {getRemainingSlots()} شخص إضافي
                    </div>
                )}
            </div>
            <div className="flex gap-2">
                <button
                    onClick={() => {
                        // نسخ الإعدادات للاختيار اليدوي
                        setManualSelection(prev => ({
                            ...prev,
                            companyId: selectionSettings.companyId,
                            professionId: selectionSettings.professionId,
                            requiredCount: selectionSettings.requiredCount,
                            date: selectionSettings.date,
                            notes: selectionSettings.notes
                        }));
                        setActiveTab('manual');
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <Plus size={16} />
                    إضافة المزيد
                </button>
                
            {currentList && (
    <button
        onClick={() => {
            const confirmed = window.confirm(
                `هل تريد حفظ التغييرات على القائمة "${currentList.ListName}"؟`
            );
            if (confirmed) {
                openListNameBuilder();
            }
        }}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
    >
        <Save size={16} />
        حفظ التحديثات
    </button>
)}

            </div>
        </div>
    </div>
)}

        {selectedPassports.length > 0 ? (
            <>
                {/* 🔍 Search and Filter */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="relative">
                        <Search size={20} className="absolute right-3 top-3 text-gray-400" />
                        <input
                            type="text"
                            placeholder="ابحث بالاسم أو رقم الجواز أو الرقم القومي أو الشركة أو المندوب أو الملاحظات..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="relative">
                        <Filter size={20} className="absolute right-3 top-3 text-gray-400" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">كل الحالات</option>
                            <option value="pending">في الانتظار</option>
                            <option value="confirmed">مؤكد الحضور</option>
                        </select>
                    </div>
                </div>

                {/* 📱 ملخص وإجراءات إدارة القائمة للموبايل */}
                <div className="md:hidden mb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white rounded-2xl border border-blue-100 p-3 shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">إجمالي النشط</div>
                            <div className="text-2xl font-bold text-blue-700">{getActivePassports(selectedPassports).length}</div>
                        </div>
                        <div className="bg-white rounded-2xl border border-green-100 p-3 shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">مؤكد الحضور</div>
                            <div className="text-2xl font-bold text-green-700">{getActivePassports(selectedPassports).filter(p => p.confirmed === true).length}</div>
                        </div>
                        <div className="bg-white rounded-2xl border border-yellow-100 p-3 shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">في الانتظار</div>
                            <div className="text-2xl font-bold text-yellow-700">{getActivePassports(selectedPassports).filter(p => p.status === 'pending').length}</div>
                        </div>
                        <div className="bg-white rounded-2xl border border-red-100 p-3 shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">محجوز للنت</div>
                            <div className="text-2xl font-bold text-red-700">{selectedPassports.filter(p => p.status === 'net_booked').length}</div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-3 shadow-sm space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={confirmAllAttendance}
                                disabled={
                                    isLoading ||
                                    selectedPassports.filter(p => p.status === 'pending' && !p.confirmed).length === 0
                                }
                                className="bg-green-600 hover:bg-green-700 text-white rounded-2xl px-3 py-3 flex items-center justify-center gap-2 disabled:opacity-50 font-bold text-sm"
                                title="تأكيد حضور جميع الأشخاص في الانتظار"
                            >
                                <CheckCircle2 size={17} />
                                <span>تأكيد الكل</span>
                                {selectedPassports.filter(p => p.status === 'pending' && !p.confirmed).length > 0 && (
                                    <span className="bg-white text-green-700 px-2 py-0.5 rounded-full text-xs font-bold">
                                        {selectedPassports.filter(p => p.status === 'pending' && !p.confirmed).length}
                                    </span>
                                )}
                            </button>

                            <button
                                onClick={cancelAllConfirmation}
                                disabled={
                                    isLoading ||
                                    selectedPassports.filter(p => p.confirmed === true).length === 0
                                }
                                className="bg-red-500 hover:bg-red-600 text-white rounded-2xl px-3 py-3 flex items-center justify-center gap-2 disabled:opacity-50 font-bold text-sm"
                                title="إلغاء تأكيد جميع الأشخاص المؤكدين مع مسح بيانات النت والتأشيرة المحملة فقط"
                            >
                                <X size={17} />
                                <span>إلغاء الكل</span>
                                {selectedPassports.filter(p => p.confirmed === true).length > 0 && (
                                    <span className="bg-white text-red-600 px-2 py-0.5 rounded-full text-xs font-bold">
                                        {selectedPassports.filter(p => p.confirmed === true).length}
                                    </span>
                                )}
                            </button>
                        </div>

                        <button
                            onClick={async () => {
                                setIsLoading(true);
                                try {
                                    await autoSaveCurrentList();
                                    showAlert('✅ تم حفظ جميع التغييرات', 'success');
                                } catch (error) {
                                    showAlert('❌ فشل في الحفظ: ' + error.message, 'error');
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            disabled={isLoading || selectedPassports.length === 0}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-2xl px-3 py-3 flex items-center justify-center gap-2 disabled:opacity-50 font-bold text-sm"
                        >
                            {isLoading ? (
                                <RefreshCw className="animate-spin" size={17} />
                            ) : (
                                <Save size={17} />
                            )}
                            حفظ التغييرات
                        </button>

                        <details className="selection-mobile-more-actions rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
                            <summary className="cursor-pointer list-none px-4 py-3 font-bold text-gray-700 flex items-center justify-between">
                                <span>إجراءات إضافية</span>
                                <span className="text-gray-400">⌄</span>
                            </summary>
                            <div className="grid grid-cols-2 gap-2 p-3 pt-0">
                                <button
                                    onClick={() => {
                                        const confirmedCount = selectedPassports.filter(p => p.confirmed === true).length;
                                        const totalCount = selectedPassports.length;
                                        showAlert(`الإحصائيات: ${confirmedCount} مؤكد من أصل ${totalCount}`, 'info');
                                    }}
                                    className="bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-xl px-3 py-3 flex items-center justify-center gap-2 text-sm font-bold"
                                >
                                    <BarChart3 size={16} />
                                    إحصائيات
                                </button>

                                <button
                                    onClick={() => setShowPaymentPrintModal(true)}
                                    disabled={selectedPassports.length === 0}
                                    className="bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-xl px-3 py-3 flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-bold"
                                >
                                    <Printer size={16} />
                                    الدفع
                                </button>

                                <button
                                    onClick={() => setShowMinBalanceSettingModal(true)}
                                    className="bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-xl px-3 py-3 flex items-center justify-center gap-2 text-sm font-bold"
                                    title={`الحد الأدنى الحالي: ${minBalanceAmount.toLocaleString()} ج.م`}
                                >
                                    <Settings size={16} />
                                    حد الرصيد
                                </button>

                                {canViewBalanceReportBtn && (
                                    <button
                                        onClick={fetchAllBalancesReport}
                                        disabled={selectedPassports.length === 0 || balanceReportLoading}
                                        className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-xl px-3 py-3 flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-bold"
                                        title="طباعة كشف الأرصدة الإيجابية والسلبية"
                                    >
                                        <FileText size={16} />
                                        كشف الأرصدة
                                    </button>
                                )}
                            </div>
                        </details>
                    </div>
                </div>

                <div className="hidden md:flex justify-between items-center mb-4 p-4 bg-gray-50 rounded-lg">
    <div className="text-sm text-gray-600">
        إجمالي النشط: <span className="text-blue-600 font-medium">{getActivePassports(selectedPassports).length}</span> | 
        مؤكد: <span className="text-green-600 font-medium">{getActivePassports(selectedPassports).filter(p => p.confirmed === true).length}</span> | 
        في الانتظار: <span className="text-yellow-600 font-medium">{getActivePassports(selectedPassports).filter(p => p.status === 'pending').length}</span> | 
        محجوز للنت: <span className="text-red-600 font-medium">{selectedPassports.filter(p => p.status === 'net_booked').length}</span>
    </div>
                    
                  <div className="flex gap-2">
    {/* ✅ زر تأكيد حضور الكل - جديد */}
    <button
        onClick={confirmAllAttendance}
        disabled={
            isLoading || 
            selectedPassports.filter(p => p.status === 'pending' && !p.confirmed).length === 0
        }
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 transition-all duration-200 text-sm"
        title="تأكيد حضور جميع الأشخاص في الانتظار"
    >
        <CheckCircle2 size={16} />
        تأكيد حضور الكل
        {selectedPassports.filter(p => p.status === 'pending' && !p.confirmed).length > 0 && (
            <span className="bg-white text-green-600 px-2 py-0.5 rounded-full text-xs font-bold">
                {selectedPassports.filter(p => p.status === 'pending' && !p.confirmed).length}
            </span>
        )}
    </button>

    {/* ✅ زر إلغاء حضور الكل - جديد */}
    <button
        onClick={cancelAllConfirmation}
        disabled={
            isLoading || 
            selectedPassports.filter(p => p.confirmed === true).length === 0
        }
        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 transition-all duration-200 text-sm"
        title="إلغاء تأكيد جميع الأشخاص المؤكدين مع مسح بيانات النت والتأشيرة المحملة فقط"
    >
        <X size={16} />
        إلغاء حضور الكل
        {selectedPassports.filter(p => p.confirmed === true).length > 0 && (
            <span className="bg-white text-red-600 px-2 py-0.5 rounded-full text-xs font-bold">
                {selectedPassports.filter(p => p.confirmed === true).length}
            </span>
        )}
    </button>

    {/* زر حفظ التغييرات - موجود مسبقاً */}
    <button
        onClick={async () => {
            setIsLoading(true);
            try {
                await autoSaveCurrentList();
                showAlert('✅ تم حفظ جميع التغييرات', 'success');
            } catch (error) {
                showAlert('❌ فشل في الحفظ: ' + error.message, 'error');
            } finally {
                setIsLoading(false);
            }
        }}
        disabled={isLoading || selectedPassports.length === 0}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 transition-all duration-200 text-sm"
    >
        {isLoading ? (
            <RefreshCw className="animate-spin" size={16} />
        ) : (
            <Save size={16} />
        )}
        حفظ التغييرات
    </button>

    {/* زر الإحصائيات - موجود مسبقاً */}
    <button
        onClick={() => {
            const confirmedCount = selectedPassports.filter(p => p.confirmed === true).length;
            const totalCount = selectedPassports.length;
            showAlert(`الإحصائيات: ${confirmedCount} مؤكد من أصل ${totalCount}`, 'info');
        }}
        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
    >
        <BarChart3 size={16} />
        إحصائيات
    </button>

    {/* زر طباعة حالة الدفع - موجود مسبقاً */}
    <button
        onClick={() => setShowPaymentPrintModal(true)}
        disabled={selectedPassports.length === 0}
        className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 text-sm"
    >
        <Printer size={16} />
        طباعة حالة الدفع
    </button>

    {/* ✅ زر ضبط الحد الأدنى للرصيد */}
    <button
        onClick={() => setShowMinBalanceSettingModal(true)}
        className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
        title={`الحد الأدنى الحالي: ${minBalanceAmount.toLocaleString()} ج.م`}
    >
        <Settings size={16} />
        ضبط حد الرصيد ({minBalanceAmount.toLocaleString()} ج)
    </button>

    {/* ✅ زر كشف الرصيد الشامل */}
    {canViewBalanceReportBtn && (
    <button
        onClick={fetchAllBalancesReport}
        disabled={selectedPassports.length === 0 || balanceReportLoading}
        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 text-sm"
        title="طباعة كشف الأرصدة الإيجابية والسلبية"
    >
        <FileText size={16} />
        كشف الأرصدة
    </button>
    )}
</div>
                </div>


{/* 📱 كروت إدارة القائمة للموبايل فقط */}
<div className="md:hidden space-y-4 mb-4">
    {getFilteredPassports().map((passport, index) => {
        const delegate = delegates.find(d => parseInt(d.DelegateID) === parseInt(passport.DelegateID));
        const profession = professions.find(p => parseInt(p.ProfessionID) === parseInt(passport.PassportProfessionID));
        const company = companies.find(c => parseInt(c.CompanyID) === parseInt(passport.CompanyID));
        const cleanNotes = getNotesText(passport.Notes);
        const orderNumber = passport.status === 'net_booked' ? '⚠️' : (getActivePassports(selectedPassports).findIndex(p => p.PassportID === passport.PassportID) + 1);
        return (
            <div key={`mobile-management-${passport.PassportID}`} className={`rounded-2xl border p-4 shadow-sm overflow-hidden ${passport.status === 'net_booked' ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${passport.status === 'net_booked' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                {orderNumber}
                            </span>
                            <h3 className="font-bold text-gray-900 leading-6 break-words">{passport.NameArabic || 'اسم غير محدد'}</h3>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs">
                            <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-mono">ID: #{getPassportFileNumber(passport)}</span>
                            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-mono">{passport.PassportNumber || 'بدون جواز'}</span>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        {passport.status === 'confirmed' && <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">مؤكد</span>}
                        {passport.status === 'pending' && <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">منتظر</span>}
                        {passport.status === 'net_booked' && <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800">محجوز</span>}
                    </div>
                </div>

                {passport.status === 'net_booked' && passport.netBookedReason && (
                    <div className="mb-3 text-xs text-red-700 bg-red-100 border border-red-200 rounded-xl p-3">
                        السبب: {passport.netBookedReason}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="bg-gray-50 rounded-xl p-3">
                        <div className="text-xs text-gray-500 mb-1">الشركة</div>
                        <div className="font-semibold text-gray-900 truncate">{company?.CompanyName || passport.CompanyName || 'غير محدد'}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                        <div className="text-xs text-gray-500 mb-1">المهنة</div>
                        <div className="font-semibold text-gray-900 truncate">{profession?.ProfessionName || 'غير محدد'}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                        <div className="text-xs text-gray-500 mb-1">المندوب</div>
                        <div className="font-semibold text-gray-900 truncate">{delegate?.DelegateName || 'غير محدد'}</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                        <div className="text-xs text-gray-500 mb-1">الهاتف</div>
                        <PhoneNumbersDisplay
                            passport={passport}
                            onUpdatePhones={(newPhones) => handlePhoneUpdate(passport.PassportID, newPhones)}
                        />
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                        <div className="text-xs text-gray-500 mb-1">حالة الدفع</div>
                        {passport.paidConfirmed ? (
                            <div className="text-green-700 font-bold text-xs">💰 مدفوع {passport.paidAmount ? `- ${passport.paidAmount} ج` : ''}</div>
                        ) : (
                            <div className="text-gray-600 font-bold text-xs">⏳ لم يدفع</div>
                        )}
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                        <div className="text-xs text-gray-500 mb-1">تاريخ النت</div>
                        <div className="font-semibold text-gray-900 text-xs">
                            {passport.netDate ? new Date(passport.netDate).toLocaleDateString('ar-EG') : 'لم يتم التأكيد'}
                        </div>
                    </div>
                </div>

                {cleanNotes && cleanNotes.trim().length > 0 && (
                    <div className="mb-3 bg-yellow-50 border-r-4 border-yellow-400 rounded-xl p-3 text-sm text-gray-700">
                        📝 {cleanNotes}
                    </div>
                )}

                <div className="mobile-action-grid border-t pt-3">
                    {!passport.paidConfirmed ? (
                        <button
                            onClick={() => {
                                const amount = prompt('المبلغ المدفوع (اختياري):');
                                const notes = prompt('ملاحظات (اختياري):');
                                confirmPayment(passport.PassportID, amount ? parseFloat(amount) : null, notes);
                            }}
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700"
                        >
                            💰 تأكيد الدفع
                        </button>
                    ) : (
                        <button
                            onClick={() => cancelPaymentConfirmation(passport.PassportID)}
                            className="bg-yellow-100 hover:bg-yellow-200 text-yellow-700"
                        >
                            💸 إلغاء الدفع
                        </button>
                    )}

                    {passport.status === 'pending' && (
                        <button
                            onClick={() => confirmAttendance(passport.PassportID)}
                            className="bg-green-100 hover:bg-green-200 text-green-700"
                        >
                            ✅ تأكيد الحضور
                        </button>
                    )}
                    {passport.status === 'confirmed' && (
                        <button
                            onClick={() => cancelConfirmation(passport.PassportID)}
                            className="bg-yellow-100 hover:bg-yellow-200 text-yellow-700"
                        >
                            ↩️ إلغاء الحضور
                        </button>
                    )}
                    {canViewBalanceBtn && (
                        <button
                            onClick={() => checkClientBalance(passport)}
                            className="bg-teal-100 hover:bg-teal-200 text-teal-700"
                        >
                            💳 فحص الرصيد
                        </button>
                    )}
                    <button
                        onClick={() => replacePassport(passport.PassportID)}
                        className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700"
                    >
                        🔄 استبدال
                    </button>
                    <button
                        onClick={() => cleanupPersonFollowUpFull(passport.PassportID)}
                        className="bg-purple-100 hover:bg-purple-200 text-purple-700"
                    >
                        🧹 تنظيف كامل
                    </button>
                    <button
                        onClick={() => removeFromList(passport.PassportID)}
                        className="bg-red-100 hover:bg-red-200 text-red-700"
                    >
                        🗑️ حذف من القائمة
                    </button>
                </div>
            </div>
        );
    })}
</div>
{/* ✅ الجدول المُصحح في management tab */}
<div className="hidden md:block overflow-x-auto">
    <table className="w-full">
        <thead className="bg-gray-50">
            <tr>
                <th className="px-4 py-3 text-right font-medium text-gray-700">م</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">الاسم</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">الشركة + ID:</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">المهنة</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">رقم الجواز</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">الهاتف</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">المندوب</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">الملاحظات</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">حالة الدفع</th>

                <th className="px-4 py-3 text-right font-medium text-gray-700">الحالة</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">تاريخ النت</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">الإجراءات</th>
            </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
            {getFilteredPassports().map((passport, index) => {
                // ✅ البحث الصحيح عن المندوب مع معالجة أفضل للأرقام
                const delegate = delegates.find(d => {
                    const delegateId = parseInt(d.DelegateID);
                    const passportDelegateId = parseInt(passport.DelegateID);
                    return delegateId === passportDelegateId;
                });
                
                const profession = professions.find(p => {
                    const professionId = parseInt(p.ProfessionID);
                    const passportProfessionId = parseInt(passport.PassportProfessionID);
                    return professionId === passportProfessionId;
                });
                
                const company = companies.find(c => {
                    const companyId = parseInt(c.CompanyID);
                    const passportCompanyId = parseInt(passport.CompanyID);
                    return companyId === passportCompanyId;
                });
                
                const phoneNumber = getPhoneNumber(passport.PhoneNumbers);
                const notesText = getNotesText(passport.Notes);

                return (
                    <tr 
                    key={passport.PassportID} 
                    className={`transition-colors duration-200 ${getRowColorClass(passport)}`}
                >
                     <td className="px-4 py-3">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        passport.status === 'net_booked' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
    }`}>
        {passport.status === 'net_booked' ? '⚠️' : (getActivePassports(selectedPassports).findIndex(p => p.PassportID === passport.PassportID) + 1)}
    </div>
</td>


                        
<td className="px-4 py-3">
  <div
    className={`font-medium ${
      passport.status === "net_booked" ? "text-red-900" : "text-gray-900"
    }`}
  >
    {passport.NameArabic || "اسم غير محدد"}
    {passport.status === "net_booked" && (
      <div className="mt-1">
        <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium border border-red-200">
          🔴 محجوز للنت - لم يكمل
        </span>
      </div>
    )}
  </div>

  {passport.status === "net_booked" && passport.netBookedReason && (
    <div className="text-xs text-red-600 mt-1 bg-red-50 px-2 py-1 rounded border-r-2 border-red-300">
      السبب: {passport.netBookedReason}
      <div className="text-xs text-gray-500 mt-1">
        تاريخ الحجز:{" "}
        {new Date(passport.netBookedAt).toLocaleDateString("ar-EG")}
      </div>
    </div>
  )}

  {(() => {
    const notesStatus = getNotesWithDeleteStatus(passport.Notes);
    const deleteStatus = visaDeletedStatus[passport.PassportID];

    if (notesStatus.hasDeleteNote || deleteStatus?.isDeleted) {
      return (
        <div className="mt-1">
          <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium border border-red-200">
            🔴 محذوف من التأشيرات
          </span>

          {(notesStatus.hasNoteInFile || deleteStatus?.reason) && (
            <div className="text-xs text-red-600 mt-1 bg-red-50 px-2 py-1 rounded border-r-2 border-red-300">
              {notesStatus.deleteReason
                ? notesStatus.deleteReason.replace(
                    "🔴 تم الحذف من قسم التأشيرات: ",
                    ""
                  )
                : deleteStatus?.reason || "بدون سبب محدد"}
              {deleteStatus?.deletedDate && (
                <div className="text-xs text-gray-500 mt-1">
                  تاريخ الحذف:{" "}
                  {new Date(deleteStatus.deletedDate).toLocaleDateString(
                    "ar-EG"
                  )}
                </div>
              )}
            </div>
          )}

          {!notesStatus.hasNoteInFile && !deleteStatus?.reason && (
            <div className="text-xs text-gray-500 mt-1 bg-gray-50 px-2 py-1 rounded italic">
              حُذف بدون تسجيل سبب
              {deleteStatus?.deletedDate && (
                <div className="text-xs text-gray-400 mt-1">
                  {new Date(deleteStatus.deletedDate).toLocaleDateString(
                    "ar-EG"
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  })()}
</td>


                        
                        {/* ✅ الشركة + ID: الظاهر */}
                        <td className="px-4 py-3">
                            <div>
                                <div className="font-medium text-gray-900 text-sm">
                                    {company?.CompanyName || passport.CompanyName || 'غير محدد'}
                                </div>
                                <div className="text-sm text-blue-700 bg-blue-50 px-2 py-1 rounded font-mono font-medium">
                                    ID:: {getPassportFileNumber(passport)}
                                </div>
                            </div>
                        </td>
                        
                        {/* المهنة */}
                        <td className="px-4 py-3">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                {profession?.ProfessionName || 'غير محدد'}
                            </span>
                        </td>
                        
                        {/* رقم الجواز الفعلي */}
                        <td className="px-4 py-3">
                            <span className="font-mono text-sm bg-blue-50 px-2 py-1 rounded">
                                {passport.PassportNumber || 'غير محدد'}
                            </span>
                        </td>
                        
                      {/* الهاتف */}
<td className="px-4 py-3 text-sm">
    <PhoneNumbersDisplay 
        passport={passport}
        onUpdatePhones={(newPhones) => handlePhoneUpdate(passport.PassportID, newPhones)}
    />
</td>
                        
                        {/* ✅ المندوب - مع معالجة محسنة */}
                        <td className="px-4 py-3">
                            {delegate ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                                    {delegate.DelegateName}
                                </span>
                            ) : (
                                <div>
                                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                        غير محدد
                                    </span>
                                    <div className="text-xs text-gray-400 mt-1">
                                        D-ID: {passport.DelegateID}
                                    </div>
                                </div>
                            )}
                        </td>
                        
                        {/* ✅ الملاحظات المحسنة */}
                        <td className="px-4 py-3 max-w-xs">
                            {(() => {
                                const cleanNotes = getNotesText(passport.Notes);
                                
                                if (cleanNotes && cleanNotes.trim().length > 0) {
                                    return (
                                        <div 
                                            className="text-sm text-gray-700 cursor-help bg-yellow-50 px-3 py-2 rounded-lg border-r-4 border-yellow-400" 
                                            title={cleanNotes}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span>📝</span>
                                                <span className="truncate">
                                                    {cleanNotes.length > 40 ? 
                                                        `${cleanNotes.substring(0, 40)}...` : 
                                                        cleanNotes
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    );
                                }
                                
                                return (
                                    <span className="text-gray-400 text-xs italic">لا توجد ملاحظات</span>
                                );
                            })()}
                        </td>
                        
                        <td className="px-4 py-3">
    {passport.paidConfirmed ? (
        <div>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                💰 مدفوع
            </span>
            {passport.paidAmount && (
                <div className="text-xs text-gray-600 mt-1">
                    المبلغ: {passport.paidAmount} ج.م
                </div>
            )}
            {passport.paidNotes && (
                <div className="text-xs text-gray-500 mt-1">
                    {passport.paidNotes}
                </div>
            )}
        </div>
    ) : (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            ⏳ لم يدفع
        </span>
    )}
</td>

                        <td className="px-4 py-3">
    {passport.status === 'confirmed' && (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle2 size={14} className="ml-1" />
            مؤكد
        </span>
    )}
    {passport.status === 'pending' && (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <Clock size={14} className="ml-1" />
            منتظر
        </span>
    )}
    {passport.status === 'net_booked' && (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <AlertTriangle size={14} className="ml-1" />
            محجوز - لم يكمل
        </span>
    )}
</td>
                        
                        {/* تاريخ النت */}
                        <td className="px-4 py-3 text-sm">
                            {passport.netDate ? (
                                <div className="text-green-600 font-medium">
                                    {new Date(passport.netDate).toLocaleDateString('ar-EG')}
                                </div>
                            ) : (
                                <span className="text-gray-400 text-xs">لم يتم التأكيد</span>
                            )}
                            {passport.confirmedAt && (
                                <div className="text-xs text-gray-500 mt-1">
                                    {new Date(passport.confirmedAt).toLocaleString('ar-EG')}
                                </div>
                            )}
                        </td>
                        
                        {/* الإجراءات */}
                        <td className="px-4 py-3">
                            <div className="flex items-center gap-1">

                              {/* الزر الجديد للتأكيد البسيط */}
        {!passport.paidConfirmed ? (
            <button
                onClick={() => {
                    const amount = prompt('المبلغ المدفوع (اختياري):');
                    const notes = prompt('ملاحظات (اختياري):');
                    confirmPayment(passport.PassportID, amount ? parseFloat(amount) : null, notes);
                }}
                className="bg-blue-100 hover:bg-blue-200 text-blue-700 p-2 rounded-lg transition-colors"
                title="تأكيد الدفع/الحضور (بدون تاريخ نت)"
            >
                💰
            </button>
        ) : (
            <button
                onClick={() => cancelPaymentConfirmation(passport.PassportID)}
                className="bg-yellow-100 hover:bg-yellow-200 text-yellow-700 p-2 rounded-lg transition-colors"
                title="إلغاء تأكيد الدفع"
            >
                💸
            </button>
        )}

                                {passport.status === 'pending' && (
                                    <button
                                        onClick={() => confirmAttendance(passport.PassportID)}
                                        className="bg-green-100 hover:bg-green-200 text-green-700 p-2 rounded-lg transition-colors"
                                        title="تأكيد الحضور"
                                    >
                                        <UserCheck size={16} />
                                    </button>
                                )}
{passport.status === 'confirmed' && (
    <button
        onClick={() => cancelConfirmation(passport.PassportID)}
        className={`p-2 rounded-lg transition-colors ${
            // فحص سريع للحالة المحظورة (يمكن تحسينه)
            passport.applicationNumber || passport.fingerprintDate || 
            passport.visaStatus === 'مكتمل' || passport.visaStatus === 'جارى العمل'
                ? 'bg-red-100 text-red-400 cursor-not-allowed' 
                : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700'
        }`}
        title={
            passport.applicationNumber || passport.fingerprintDate || 
            passport.visaStatus === 'مكتمل' || passport.visaStatus === 'جارى العمل'
                ? '🔒 محظور - جاري العمل في قسم التأشيرات' 
                : 'إلغاء التأكيد'
        }
    >
        {passport.applicationNumber || passport.fingerprintDate || 
         passport.visaStatus === 'مكتمل' || passport.visaStatus === 'جارى العمل' ? (
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
            </svg>
        ) : (
            <RotateCcw size={16} />
        )}
    </button>
)}
                                {canViewBalanceBtn && (
                                <button
                                    onClick={() => checkClientBalance(passport)}
                                    className="bg-teal-100 hover:bg-teal-200 text-teal-700 p-2 rounded-lg transition-colors"
                                    title="فحص رصيد العميل في الحسابات"
                                >
                                    💳
                                </button>
                                )}
                                <button
                                    onClick={() => replacePassport(passport.PassportID)}
                                    className="bg-blue-100 hover:bg-blue-200 text-blue-700 p-2 rounded-lg transition-colors"
                                    title="استبدال/استبعاد"
                                >
                                    <Replace size={16} />
                                </button>
                                <button
                                    onClick={() => cleanupPersonFollowUpFull(passport.PassportID)}
                                    className="bg-purple-100 hover:bg-purple-200 text-purple-700 p-2 rounded-lg transition-colors"
                                    title="تنظيف كامل لهذا الشخص من هذه القائمة"
                                >
                                    <AlertTriangle size={16} />
                                </button>
                                <button
                                    onClick={() => removeFromList(passport.PassportID)}
                                    className="bg-red-100 hover:bg-red-200 text-red-700 p-2 rounded-lg transition-colors"
                                    title="حذف من القائمة"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </td>
                    </tr>
                );
            })}
        </tbody>
    </table>
</div>

                {/* عرض رسالة عند عدم وجود نتائج */}
                {getFilteredPassports().length === 0 && (
                    <div className="text-center py-8">
                        <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد نتائج</h3>
                        <p className="text-gray-600">جرب تغيير معايير البحث أو الفلترة</p>
                    </div>
                )}
            </>
        ) : (
            <div className="text-center py-12">
                <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد قائمة نشطة</h3>
                <p className="text-gray-600 mb-4">انتقل إلى تبويب "الاختيار المُطور" لإنشاء قائمة جديدة</p>
                <button
                    onClick={() => setActiveTab('selection')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto"
                >
                    <Calculator size={20} />
                    بدء الاختيار
                </button>
            </div>
        )}
    </div>
)}

               {/* 📚 Excluded Tab */}
               {activeTab === 'excluded' && (
                   <div className="bg-white rounded-xl shadow-lg p-6">
                       <div className="flex justify-between items-center mb-6">
                           <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                               <UserX size={24} />
                               الأشخاص المستبعدين ({excludedPassports.length})
                           </h2>
                           <button
                               onClick={() => fetchExcludedPassports(selectionSettings.companyId)}
                               className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                           >
                               <RefreshCw size={18} />
                               تحديث
                           </button>
                       </div>

                       {excludedPassports.length > 0 ? (
                           <>

                               {/* 📱 كروت المستبعدين للموبايل فقط */}
                               <div className="md:hidden space-y-3 mb-4">
                                   {excludedPassports.map((excluded) => (
                                       <div key={`mobile-excluded-${excluded.ExclusionID}`} className="bg-white border border-red-100 rounded-2xl p-4 shadow-sm">
                                           <div className="flex items-start justify-between gap-3 mb-3">
                                               <div>
                                                   <div className="font-bold text-gray-900">{excluded.NameArabic}</div>
                                                   <div className="text-xs text-gray-500 mt-1">المندوب: {excluded.DelegateName || 'غير محدد'}</div>
                                               </div>
                                               <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-bold">مستبعد</span>
                                           </div>
                                           <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-800 mb-3">
                                               السبب: {excluded.Reason}
                                           </div>
                                           <div className="text-xs text-gray-500 mb-3">
                                               تاريخ الاستبعاد: {new Date(excluded.ExcludedAt).toLocaleDateString('ar-EG')}
                                           </div>
                                           <button
                                               onClick={() => reactivatePassport(excluded.ExclusionID, excluded.NameArabic)}
                                               className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2 font-bold"
                                           >
                                               <RotateCcw size={16} />
                                               إعادة تفعيل
                                           </button>
                                       </div>
                                   ))}
                               </div>
                           <div className="hidden md:block overflow-x-auto">
                               <table className="w-full">
                                   <thead className="bg-gray-50">
                                       <tr>
                                           <th className="px-4 py-3 text-right font-medium text-gray-700">الاسم</th>
                                           <th className="px-4 py-3 text-right font-medium text-gray-700">المندوب</th>
                                           <th className="px-4 py-3 text-right font-medium text-gray-700">السبب</th>
                                           <th className="px-4 py-3 text-right font-medium text-gray-700">تاريخ الاستبعاد</th>
                                           <th className="px-4 py-3 text-right font-medium text-gray-700">الإجراءات</th>
                                       </tr>
                                   </thead>
                                   <tbody className="divide-y divide-gray-200">
                                       {excludedPassports.map((excluded) => (
                                           <tr key={excluded.ExclusionID} className="hover:bg-gray-50">
                                               <td className="px-4 py-3 font-medium">{excluded.NameArabic}</td>
                                               <td className="px-4 py-3">{excluded.DelegateName}</td>
                                               <td className="px-4 py-3">
                                                   <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                                       {excluded.Reason}
                                                   </span>
                                               </td>
                                               <td className="px-4 py-3">
                                                   {new Date(excluded.ExcludedAt).toLocaleDateString('ar-EG')}
                                               </td>
                                               <td className="px-4 py-3">
                                                   <button
                                                       onClick={() => reactivatePassport(excluded.ExclusionID, excluded.NameArabic)}
                                                       className="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded text-sm flex items-center gap-1"
                                                   >
                                                       <RotateCcw size={14} />
                                                       إعادة تفعيل
                                                   </button>
                                               </td>
                                           </tr>
                                       ))}
                                   </tbody>
                               </table>
                           </div>
                           </>
                       ) : (
                           <div className="text-center py-8 text-gray-500">
                               لا توجد أشخاص مستبعدين حالياً
                           </div>
                       )}
                   </div>
               )}

               {/* 📊 History Tab */}
               {activeTab === 'history' && (
                   <div className="bg-white rounded-xl shadow-lg p-6">
                       <div className="flex justify-between items-center mb-6">
                           <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                               <BarChart3 size={24} />
                               تاريخ التوزيع والأولوية التراكمية
                           </h2>
                           <button
                               onClick={() => fetchDelegateHistory(selectionSettings.companyId)}
                               className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                           >
                               <RefreshCw size={18} />
                               تحديث
                           </button>
                       </div>

                       {delegateHistory.length > 0 ? (
                           <div className="space-y-6">
                               {/* إحصائيات عامة */}
                               <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6">
                                   <h3 className="text-lg font-semibold text-purple-900 mb-4">📈 الإحصائيات العامة</h3>
                                   <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                       <div className="bg-white rounded-lg p-4 text-center">
                                           <div className="text-2xl font-bold text-blue-600">
                                               {delegateHistory.reduce((sum, d) => sum + d.TotalSelected, 0)}
                                           </div>
                                           <div className="text-sm text-gray-600">إجمالي المختارين</div>
                                       </div>
                                       <div className="bg-white rounded-lg p-4 text-center">
                                           <div className="text-2xl font-bold text-green-600">
                                               {delegateHistory.reduce((sum, d) => sum + d.TotalAvailable, 0)}
                                           </div>
                                           <div className="text-sm text-gray-600">إجمالي المتاحين</div>
                                       </div>
                                       <div className="bg-white rounded-lg p-4 text-center">
                                           <div className="text-2xl font-bold text-purple-600">
                                               {(delegateHistory.reduce((sum, d) => sum + d.SelectionRatio, 0) / delegateHistory.length * 100).toFixed(1)}%
                                           </div>
                                           <div className="text-sm text-gray-600">متوسط نسبة الاختيار</div>
                                       </div>
                                       <div className="bg-white rounded-lg p-4 text-center">
                                           <div className="text-2xl font-bold text-orange-600">
                                               {delegateHistory.length}
                                           </div>
                                           <div className="text-sm text-gray-600">عدد المناديب</div>
                                       </div>
                                   </div>
                               </div>

                               {/* تفاصيل المناديب */}
                               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                   {delegateHistory
                                       .sort((a, b) => a.SelectionRatio - b.SelectionRatio) // ترتيب حسب الأولوية (الأقل أولاً)
                                       .map((delegate, index) => (
                                       <div key={delegate.DelegateID} className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
                                           <div className="flex justify-between items-start mb-4">
                                               <div>
                                                   <h4 className="font-bold text-gray-900 text-lg">
                                                       {delegate.DelegateName}
                                                   </h4>
                                                   <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                                                       index < delegateHistory.length / 3 ? 'bg-green-100 text-green-800' :
                                                       index < (delegateHistory.length * 2) / 3 ? 'bg-yellow-100 text-yellow-800' :
                                                       'bg-red-100 text-red-800'
                                                   }`}>
                                                       {index < delegateHistory.length / 3 ? '🔥 أولوية عالية' :
                                                        index < (delegateHistory.length * 2) / 3 ? '⚡ أولوية متوسطة' :
                                                        '⏸️ أولوية منخفضة'}
                                                   </div>
                                               </div>
                                               <div className="text-right">
                                                   <div className="text-2xl font-bold text-blue-600">
                                                       #{index + 1}
                                                   </div>
                                                   <div className="text-xs text-gray-500">ترتيب الأولوية</div>
                                               </div>
                                           </div>
                                           
                                           <div className="space-y-3">
                                               <div className="flex justify-between items-center">
                                                   <span className="text-gray-600">إجمالي المختارين:</span>
                                                   <span className="font-bold text-blue-600">{delegate.TotalSelected}</span>
                                               </div>
                                               <div className="flex justify-between items-center">
                                                   <span className="text-gray-600">إجمالي المتاحين:</span>
                                                   <span className="font-bold text-green-600">{delegate.TotalAvailable}</span>
                                               </div>
                                               <div className="flex justify-between items-center">
                                                   <span className="text-gray-600">نسبة الاختيار:</span>
                                                   <span className={`font-bold ${
                                                       delegate.SelectionRatio < 0.3 ? 'text-green-600' :
                                                       delegate.SelectionRatio < 0.6 ? 'text-yellow-600' :
                                                       'text-red-600'
                                                   }`}>
                                                       {(delegate.SelectionRatio * 100).toFixed(1)}%
                                                   </span>
                                               </div>
                                               
                                               {/* شريط التقدم */}
                                               <div className="mt-3">
                                                   <div className="flex justify-between text-xs text-gray-500 mb-1">
                                                       <span>نسبة الاختيار</span>
                                                       <span>{(delegate.SelectionRatio * 100).toFixed(1)}%</span>
                                                   </div>
                                                   <div className="w-full bg-gray-200 rounded-full h-2">
                                                   <div 
                                                           className={`h-2 rounded-full transition-all duration-300 ${
                                                               delegate.SelectionRatio < 0.3 ? 'bg-green-500' :
                                                               delegate.SelectionRatio < 0.6 ? 'bg-yellow-500' :
                                                               'bg-red-500'
                                                           }`}
                                                           style={{ width: `${Math.min(delegate.SelectionRatio * 100, 100)}%` }}
                                                       ></div>
                                                   </div>
                                               </div>

                                               {/* معلومات الأولوية */}
                                               <div className="bg-gray-50 rounded-lg p-3 mt-4">
                                                   <div className="text-xs font-medium text-gray-700 mb-2">تأثير على الاختيار القادم:</div>
                                                   <div className="text-sm">
                                                       {delegate.SelectionRatio < 0.3 ? (
                                                           <div className="text-green-700">
                                                               <span className="font-medium">سيحصل على أولوية عالية</span> في الاختيار القادم
                                                           </div>
                                                       ) : delegate.SelectionRatio < 0.6 ? (
                                                           <div className="text-yellow-700">
                                                               <span className="font-medium">سيحصل على أولوية متوسطة</span> في الاختيار القادم
                                                           </div>
                                                       ) : (
                                                           <div className="text-red-700">
                                                               <span className="font-medium">أولوية منخفضة</span> نظراً للاختيار المتكرر
                                                           </div>
                                                       )}
                                                   </div>
                                               </div>
                                           </div>
                                       </div>
                                   ))}
                               </div>

                               {/* شرح النظام */}
                               <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                                   <h3 className="text-lg font-semibold text-blue-900 mb-4">📚 كيف يعمل نظام الأولوية التراكمية؟</h3>
                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-blue-800">
                                       <div>
                                           <h4 className="font-medium mb-2">🎯 حساب الأولوية:</h4>
                                           <ul className="text-sm space-y-1">
                                               <li>• كلما قلت نسبة الاختيار السابقة، زادت الأولوية</li>
                                               <li>• النظام يعوض المناديب الذين لم يحصلوا على فرص كافية</li>
                                               <li>• الحساب يتم على مستوى كل شركة منفصلة</li>
                                           </ul>
                                       </div>
                                       <div>
                                           <h4 className="font-medium mb-2">⚖️ تطبيق العدالة:</h4>
                                           <ul className="text-sm space-y-1">
                                               <li>• التوزيع يأخذ في الاعتبار التاريخ والحاضر</li>
                                               <li>• المناديب ذوو الأولوية العالية يحصلون على حصة أكبر</li>
                                               <li>• النظام يتطور تلقائياً مع كل اختيار جديد</li>
                                           </ul>
                                       </div>
                                   </div>
                               </div>
                           </div>
                       ) : (
                           <div className="text-center py-12">
                               <BarChart3 className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                               <h3 className="text-lg font-medium text-gray-900 mb-2">لا يوجد تاريخ متاح</h3>
                               <p className="text-gray-600 mb-4">اختر شركة لعرض تاريخ التوزيع والأولوية التراكمية</p>
                           </div>
                       )}
                   </div>
               )}

{activeTab === 'savedLists' && (
    <div className="bg-white rounded-xl shadow-lg p-6">
        {/* العنوان والإعدادات */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 gap-4">
            <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FileText size={24} />
                    إدارة القوائم المحفوظة
                </h2>
                <div className="flex items-center gap-2 text-sm">
                    {(() => {
                        const stats = getPaginatedLists();
                        return (
                            <>
                                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                                    المعروض: {stats.lists.length}
                                </span>
                                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                                    الكل: {stats.totalLists}
                                </span>
                            </>
                        );
                    })()}
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() => setShowPrintSettingsModal(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                >
                    ⚙️ إعدادات الطباعة
                </button>
                <button
                    onClick={fetchSavedLists}
                    disabled={isLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 text-sm"
                >
                    {isLoading ? (
                        <RefreshCw className="animate-spin" size={16} />
                    ) : (
                        <RefreshCw size={16} />
                    )}
                    تحديث
                </button>
            </div>
        </div>

        {/* شريط التحكم والفلترة المحسن */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-4">
            {/* الصف الأول: الفلاتر */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* فلتر النوع */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">📂 نوع القوائم:</label>
                    <select
                        value={listViewSettings.filterBy}
                        onChange={(e) => updateViewSettings({ filterBy: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="active">النشطة فقط ({savedLists.filter(l => !archivedLists.has(l.ListID)).length})</option>
                        <option value="archived">المؤرشفة فقط ({savedLists.filter(l => archivedLists.has(l.ListID)).length})</option>
                        <option value="all">كل القوائم ({savedLists.length})</option>
                    </select>
                </div>

                {/* ترتيب حسب */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">🔄 ترتيب حسب:</label>
                    <select
                        value={listViewSettings.sortBy}
                        onChange={(e) => updateViewSettings({ sortBy: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="date">التاريخ</option>
                        <option value="name">اسم القائمة</option>
                        <option value="company">الشركة</option>
                    </select>
                </div>

                {/* اتجاه الترتيب */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">↕️ الاتجاه:</label>
                    <select
                        value={listViewSettings.sortOrder}
                        onChange={(e) => updateViewSettings({ sortOrder: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="desc">من الأحدث للأقدم</option>
                        <option value="asc">من الأقدم للأحدث</option>
                    </select>
                </div>

                {/* عدد العناصر */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">📄 عدد القوائم:</label>
                    <select
                        value={listViewSettings.itemsPerPage}
                        onChange={(e) => updateViewSettings({ itemsPerPage: parseInt(e.target.value), currentPage: 1 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={6}>6 قوائم</option>
                        <option value={12}>12 قائمة</option>
                        <option value={24}>24 قائمة</option>
                        <option value={48}>48 قائمة</option>
                        <option value={100}>100 قائمة</option>
                    </select>
                </div>
            </div>

            {/* الصف الثاني: الإجراءات المتعددة */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                <div className="text-sm font-medium text-gray-700 flex items-center">
                    ⚡ إجراءات متعددة:
                </div>
                
                <button
                    onClick={() => {
                        const activeCount = savedLists.filter(l => !archivedLists.has(l.ListID)).length;
                        if (activeCount === 0) return;
                        
                        // إنشاء رقم عشوائي للتأكيد
                        const confirmationCode = Math.floor(1000 + Math.random() * 9000);
                        
                        const confirmMessage = `🔐 تأكيد أرشفة جماعية 🔐

هل أنت متأكد من أرشفة جميع القوائم النشطة؟

📦 سيتم أرشفة ${activeCount} قائمة نشطة
⚠️ يمكنك استعادتها لاحقاً من قسم "المؤرشفة فقط"

🔢 أدخل الرقم التالي للتأكيد: ${confirmationCode}`;

                        const userInput = prompt(confirmMessage);
                        
                        if (userInput === confirmationCode.toString()) {
                            bulkArchiveAction('archive');
                        } else if (userInput !== null) {
                            showAlert('❌ لم يتم تنفيذ العملية - الرقم غير صحيح', 'warning');
                        }
                    }}
                    disabled={isLoading || savedLists.filter(l => !archivedLists.has(l.ListID)).length === 0}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                >
                    📦 أرشفة كل النشطة
                </button>

                <button
                    onClick={() => {
                        const archivedCount = savedLists.filter(l => archivedLists.has(l.ListID)).length;
                        if (archivedCount === 0) return;
                        
                        // إنشاء رقم عشوائي للتأكيد
                        const confirmationCode = Math.floor(1000 + Math.random() * 9000);
                        
                        const confirmMessage = `🔐 تأكيد استعادة جماعية 🔐

هل أنت متأكد من استعادة جميع القوائم المؤرشفة؟

📋 سيتم استعادة ${archivedCount} قائمة مؤرشفة
✅ ستصبح كلها قوائم نشطة مرة أخرى

🔢 أدخل الرقم التالي للتأكيد: ${confirmationCode}`;

                        const userInput = prompt(confirmMessage);
                        
                        if (userInput === confirmationCode.toString()) {
                            bulkArchiveAction('unarchive');
                        } else if (userInput !== null) {
                            showAlert('❌ لم يتم تنفيذ العملية - الرقم غير صحيح', 'warning');
                        }
                    }}
                    disabled={isLoading || savedLists.filter(l => archivedLists.has(l.ListID)).length === 0}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                >
                    📋 استرداد كل المؤرشفة
                </button>

                <button
                    onClick={() => updateViewSettings({ 
                        filterBy: 'active', 
                        sortBy: 'date', 
                        sortOrder: 'desc', 
                        currentPage: 1 
                    })}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
                >
                    🔄 إعادة تعيين الفلاتر
                </button>
            </div>
        </div>

        {/* عرض القوائم */}
        {(() => {
            const paginatedData = getPaginatedLists();
            
            if (paginatedData.totalLists === 0) {
                return (
                    <div className="text-center py-12">
                        <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {listViewSettings.filterBy === 'archived' 
                                ? 'لا توجد قوائم مؤرشفة' 
                                : listViewSettings.filterBy === 'active'
                                ? 'لا توجد قوائم نشطة'
                                : 'لا توجد قوائم محفوظة'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {listViewSettings.filterBy === 'archived' 
                                ? 'لم يتم أرشفة أي قوائم بعد'
                                : 'ابدأ بإنشاء قائمة جديدة واحفظها للوصول إليها لاحقاً'}
                        </p>
                        {listViewSettings.filterBy !== 'archived' && (
                            <button
                                onClick={() => setActiveTab('selection')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto"
                            >
                                <Calculator size={20} />
                                إنشاء قائمة جديدة
                            </button>
                        )}
                    </div>
                );
            }

            return (
                <>
                    {/* شبكة القوائم */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                        {paginatedData.lists.map(list => {
                            const isArchived = archivedLists.has(list.ListID);
                            
                            return (
                                <div 
                                    key={list.ListID} 
                                    className={`border rounded-xl p-5 transition-all duration-200 hover:shadow-lg relative ${
                                        currentList?.ListID === list.ListID 
                                            ? 'border-blue-500 bg-blue-50 shadow-md' 
                                            : isArchived
                                            ? 'border-orange-200 bg-orange-50 opacity-75'
                                            : 'border-gray-200 hover:border-blue-300'
                                    }`}
                                >
                                    {/* علامة الأرشفة */}
                                    {isArchived && (
                                        <div className="absolute top-2 left-2">
                                            <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                                                📦 مؤرشفة
                                            </span>
                                        </div>
                                    )}

                                    {/* معلومات القائمة */}
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="font-semibold text-gray-900 flex items-center gap-2 flex-1">
                                            📋 {list.ListName}
                                            {currentList?.ListID === list.ListID && (
                                                <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs">
                                                    نشطة
                                                </span>
                                            )}
                                        </h3>
                                    </div>
                                    
                                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                                        <p><strong>الشركة:</strong> {list.CompanyName}</p>
                                        <p><strong>العدد المطلوب:</strong> {list.RequiredCount}</p>
                                        <p><strong>المختارين:</strong> {list.PassportCount}</p>
                                        <p><strong>المؤكدين:</strong> 
                                            <span className="text-green-600 font-medium ml-1">
                                                {list.ConfirmedCount}
                                            </span>
                                        </p>
                                        <p><strong>التاريخ:</strong> {new Date(list.SelectionDate).toLocaleDateString('ar-EG')}</p>
                                        <p className="text-xs text-gray-500">
                                            أُنشئت: {new Date(list.CreatedAt).toLocaleDateString('ar-EG')}
                                        </p>
                                    </div>
                                    
                                    {/* أزرار الإجراءات الأساسية */}
                                    <div className="flex gap-2 mb-3">
                                        <button
                                            onClick={() => loadSavedList(list.ListID)}
                                            disabled={currentList?.ListID === list.ListID || isLoading}
                                            className={`flex-1 px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-1 transition-all duration-200 ${
                                                currentList?.ListID === list.ListID
                                                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                                    : isArchived
                                                    ? 'bg-orange-100 hover:bg-orange-200 text-orange-700'
                                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                            }`}
                                        >
                                            <Eye size={14} />
                                            {currentList?.ListID === list.ListID ? 'محملة' : 'تحميل'}
                                        </button>

                                        <button
                                            onClick={() => {
                                                // إنشاء رقم عشوائي للتأكيد
                                                const confirmationCode = Math.floor(1000 + Math.random() * 9000);
                                                
                                                const confirmMessage = isArchived 
                                                    ? `🔐 تأكيد استعادة القائمة 🔐

هل أنت متأكد من استعادة القائمة "${list.ListName}"؟

📋 ستصبح القائمة نشطة مرة أخرى
✅ ستظهر في قسم "النشطة فقط"

🔢 أدخل الرقم التالي للتأكيد: ${confirmationCode}`
                                                    : `🔐 تأكيد أرشفة القائمة 🔐

هل أنت متأكد من أرشفة القائمة "${list.ListName}"؟

📦 ستنتقل القائمة إلى قسم "المؤرشفة فقط"
⚠️ يمكنك استعادتها لاحقاً

🔢 أدخل الرقم التالي للتأكيد: ${confirmationCode}`;

                                                const userInput = prompt(confirmMessage);
                                                
                                                if (userInput === confirmationCode.toString()) {
                                                    toggleListArchive(list.ListID, isArchived);
                                                } else if (userInput !== null) {
                                                    showAlert('❌ لم يتم تنفيذ العملية - الرقم غير صحيح', 'warning');
                                                }
                                            }}
                                            disabled={isLoading}
                                            className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 disabled:opacity-50 ${
                                                isArchived
                                                    ? 'bg-green-100 hover:bg-green-200 text-green-700'
                                                    : 'bg-orange-100 hover:bg-orange-200 text-orange-700'
                                            }`}
                                            title={isArchived ? 'استعادة القائمة' : 'أرشفة القائمة'}
                                        >
                                            {isLoading ? (
                                                <RefreshCw className="animate-spin" size={14} />
                                            ) : isArchived ? (
                                                '📋'
                                            ) : (
                                                '📦'
                                            )}
                                        </button>
                                        
                                        <button
    onClick={() => openEditListModal(list)}
    disabled={isLoading}
    className="bg-yellow-100 hover:bg-yellow-200 text-yellow-700 px-3 py-2 rounded-lg text-sm transition-all duration-200 disabled:opacity-50"
    title="تعديل بيانات القائمة"
>
    {isLoading ? (
        <RefreshCw className="animate-spin" size={14} />
    ) : (
        <Edit size={14} />
    )}
</button>

                                        <button
                                            onClick={() => cleanupHiddenFollowUpsAndDeleteList(list, {})}
                                            disabled={isLoading}
                                            className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-2 rounded-lg text-sm transition-all duration-200 disabled:opacity-50"
                                            title="تنظيف بيانات المتابعة المخفية ثم حذف القائمة نهائيًا"
                                        >
                                            {isLoading ? (
                                                <RefreshCw className="animate-spin" size={14} />
                                            ) : (
                                                <AlertTriangle size={14} />
                                            )}
                                        </button>

                                        <button
                                            onClick={() => {
                                                const confirmMessage = `⚠️ حذف نهائي للقائمة ⚠️

هل أنت متأكد من حذف القائمة "${list.ListName}"؟

⚠️ سيتم:
- حذف القائمة فعليًا إذا لم تكن مرتبطة ببيانات متابعة محفوظة
- تنظيف تاريخ النت/القنصلية/التأشيرة/السفر من الأشخاص المرتبطين بها
- عدم أرشفة القائمة تلقائيًا

لو توجد بيانات متابعة محفوظة مثل كشف/بصمة/تأمين/تسليم أوراق سيتم منع الحذف النهائي، وساعتها استخدم الأرشفة أو نظف البيانات يدويًا.

اكتب "حذف" للتأكيد:`;

                                                const userInput = prompt(confirmMessage);
                                                
                                                if (userInput === 'حذف') {
                                                    setIsLoading(true);
                                                    fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists/${list.ListID}`, {
                                                        method: 'DELETE'
                                                    })
                                                    .then(response => response.json())
                                                    .then(result => {
                                                        if (result.success) {
                                                            fetchSavedLists();
                                                            if (currentList?.ListID === list.ListID) {
                                                                setCurrentList(null);
                                                                setSelectedPassports([]);
                                                                setActiveTab('selection');
                                                            }
                                                            showAlert(`✅ تم حذف القائمة "${list.ListName}" حذفًا نهائيًا بدون أرشفة
🔄 تم تنظيف ${result.deletedDetails?.passportsCleaned || 0} جواز من بيانات النت/التأشيرة/السفر`, 'success');
                                                        } else {
                                                            if (result.requiresArchiveOrManualCleanup) {
                                                                return cleanupHiddenFollowUpsAndDeleteList(list, result);
                                                            }
                                                            showAlert(`❌ فشل في حذف القائمة: ${result.error}`, 'error');
                                                        }
                                                    })
                                                    .catch(error => {
                                                        showAlert(`❌ خطأ في حذف القائمة: ${error.message}`, 'error');
                                                    })
                                                    .finally(() => {
                                                        setIsLoading(false);
                                                    });
                                                } else if (userInput !== null) {
                                                    showAlert('❌ لم يتم الحذف - يجب كتابة "حذف" للتأكيد', 'warning');
                                                }
                                            }}
                                            disabled={isLoading}
                                            className="bg-red-100 hover:bg-red-200 text-red-600 px-3 py-2 rounded-lg text-sm transition-all duration-200 disabled:opacity-50"
                                            title="حذف القائمة ومسح تواريخ النت"
                                        >
                                            {isLoading ? (
                                                <RefreshCw className="animate-spin" size={14} />
                                            ) : (
                                                <Trash2 size={14} />
                                            )}
                                        </button>
                                    </div>

                                    {/* أزرار الطباعة والتصدير الكاملة */}
                                    <div className="border-t pt-3 space-y-2">
                                        <div className="text-xs font-medium text-gray-700 mb-2">📄 طباعة وتصدير:</div>
                                        
                                        {/* الصف الأول: طباعة + PDF */}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => printSavedList(list)}
                                                className="flex-1 bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-xs transition-all duration-200"
                                                title="طباعة القائمة"
                                            >
                                                <Printer size={14} />
                                                طباعة
                                            </button>
                                            <button
                                                onClick={() => exportSavedListToPDF(list)}
                                                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-xs transition-all duration-200"
                                                title="تصدير PDF"
                                            >
                                                <FileText size={14} />
                                                PDF
                                            </button>
                                        </div>
                                        
                                        {/* الصف الثاني: Excel + صورة + صورة مع المناديب */}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => exportSavedListToExcel(list)}
                                                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-xs transition-all duration-200"
                                                title="تصدير Excel"
                                            >
                                                <Download size={14} />
                                                Excel
                                            </button>
                                            <button
                                                onClick={() => exportSavedListAsImage(list)}
                                                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-xs transition-all duration-200"
                                                title="حفظ كصورة عادية"
                                            >
                                                <Camera size={14} />
                                                صورة
                                            </button>
                                            <button
                                                onClick={() => exportSavedListWithDelegatesAsImage(list)}
                                                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-xs transition-all duration-200"
                                                title="صورة مع المناديب"
                                            >
                                                <Users size={14} />
                                                بالمناديب
                                            </button>
                                        </div>

                                        {/* الصف الثالث: واتساب */}
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                onClick={() => exportForWhatsApp(list)}
                                                className="w-full bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-xs transition-all duration-200 font-medium"
                                                title="تصدير أرقام الواتساب"
                                            >
                                                📱 تصدير للواتساب
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* التصفح (Pagination) */}
                    {paginatedData.totalPages > 1 && (
                        <div className="flex justify-between items-center bg-gray-50 rounded-lg p-4">
                            <div className="text-sm text-gray-600">
                                صفحة {listViewSettings.currentPage} من {paginatedData.totalPages} 
                                ({paginatedData.totalLists} قائمة إجمالي)
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => updateViewSettings({ currentPage: 1 })}
                                    disabled={listViewSettings.currentPage === 1}
                                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100 text-sm"
                                >
                                    الأولى
                                </button>
                                
                                <button
                                    onClick={() => updateViewSettings({ currentPage: listViewSettings.currentPage - 1 })}
                                    disabled={!paginatedData.hasPrev}
                                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100 text-sm"
                                >
                                    السابقة
                                </button>

                                {/* أرقام الصفحات */}
                                {Array.from({ length: Math.min(5, paginatedData.totalPages) }, (_, i) => {
                                    const page = Math.max(1, Math.min(
                                        paginatedData.totalPages - 4,
                                        listViewSettings.currentPage - 2
                                    )) + i;
                                    
                                    if (page > paginatedData.totalPages) return null;
                                    
                                    return (
                                        <button
                                            key={page}
                                            onClick={() => updateViewSettings({ currentPage: page })}
                                            className={`px-3 py-2 border rounded-lg text-sm ${
                                                page === listViewSettings.currentPage
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'border-gray-300 hover:bg-gray-100'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    );
                                })}

                                <button
                                    onClick={() => updateViewSettings({ currentPage: listViewSettings.currentPage + 1 })}
                                    disabled={!paginatedData.hasNext}
                                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100 text-sm"
                                >
                                    التالية
                                </button>
                                
                                <button
                                    onClick={() => updateViewSettings({ currentPage: paginatedData.totalPages })}
                                    disabled={listViewSettings.currentPage === paginatedData.totalPages}
                                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100 text-sm"
                                >
                                    الأخيرة
                                </button>
                            </div>
                        </div>
                    )}
                </>
            );
        })()}
    </div>
)}



{/* 🧾 مودال بناء اسم القائمة */}
{showListNameBuilderModal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">
                    تسمية القائمة تلقائياً
                </h2>
                <button
                    onClick={() => setShowListNameBuilderModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <X size={20} />
                </button>
            </div>

            <div className="space-y-4">
                {/* اسم الشركة */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        اسم الشركة
                    </label>
                    <input
                        type="text"
                        value={listNameParts.companyName}
                        onChange={(e) =>
                            setListNameParts(prev => ({
                                ...prev,
                                companyName: e.target.value
                            }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="مثال: حسين حجازى"
                    />
                </div>

                {/* نوع التأشيرة */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        نوع التأشيرة
                    </label>
                    <select
                        value={listNameParts.visaType}
                        onChange={(e) =>
                            setListNameParts(prev => ({
                                ...prev,
                                visaType: e.target.value
                            }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">اختر نوع التأشيرة</option>
                        <option value="اقامه">اقامه</option>
                        <option value="مؤقت">مؤقت</option>
                        <option value="موسمى">موسمى</option>
                        <option value="تعقيب">تعقيب</option>
                    </select>
                </div>

                {/* رقم التأشيرة */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        رقم التأشيرة
                    </label>
                    <input
                        type="text"
                        value={listNameParts.visaNumber}
                        onChange={(e) =>
                            setListNameParts(prev => ({
                                ...prev,
                                visaNumber: e.target.value
                            }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="مثال: 1305720531"
                    />
                </div>

                {/* الرقم الموحد */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        الرقم الموحد
                    </label>
                    <input
                        type="text"
                        value={listNameParts.unifiedNumber}
                        onChange={(e) =>
                            setListNameParts(prev => ({
                                ...prev,
                                unifiedNumber: e.target.value
                            }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="مثال: 7011615320"
                    />
                </div>

                {/* القنصلية */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        القنصلية
                    </label>
                    <select
                        value={listNameParts.consulate}
                        onChange={(e) =>
                            setListNameParts(prev => ({
                                ...prev,
                                consulate: e.target.value
                            }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">اختر القنصلية</option>
                        <option value="السويس">السويس</option>
                        <option value="القاهرة">القاهرة</option>
                        <option value="الإسكندرية">الإسكندرية</option>
                    </select>
                </div>

                {/* المعاينة */}
                <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-3 text-sm">
                    <span className="text-gray-500 block mb-1">المعاينة:</span>
                    <span className="font-medium text-gray-800">
                        {buildListNameFromParts(listNameParts) || 'سيظهر الاسم هنا بعد اكتمال الحقول'}
                    </span>
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
                <button
                    type="button"
                    onClick={() => setShowListNameBuilderModal(false)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                    إلغاء
                </button>
                <button
                    type="button"
                    onClick={handleConfirmListNameAndSave}
                    disabled={!buildListNameFromParts(listNameParts) || isLoading}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                    حفظ الاسم وحفظ القائمة
                </button>
            </div>
        </div>
    </div>
)}

               {/* نافذة الاستبدال */}
               <ReplacementModal
                   isOpen={showReplacementModal}
                   onClose={() => setShowReplacementModal(false)}
                   passport={selectedPassportForReplacement}
                   onConfirm={handleReplacementConfirm}
                   isLoading={isLoading}
                   availableReplacements={availableReplacements}
               />
           </div>


{/* 📤 Send to Delegates Tab - محسن وشامل */}
{activeTab === 'sendToDelegates' && (
    <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText size={24} />
                إرسال قوائم العملاء للمناديب
            </h2>
            <div className="flex gap-3">
    <button
        onClick={() => setShowPrintSettingsModal(true)}
        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
    >
        ⚙️ إعدادات الطباعة
    </button>
    <div className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
        📋 نظام إبلاغ المناديب بمواعيد الحضور
    </div>
</div>








        </div>
        
      {/* الخطوة 1: اختيار القائمة - محسن */}
<div className="mb-8">
    <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
        <h3 className="text-lg font-semibold text-gray-800">اختر القائمة المراد إرسالها:</h3>
    </div>
    
    {/* إذا كانت هناك قائمة نشطة حالياً، اعرضها أولاً */}
    {currentList ? (
        <div className="mb-6">
            <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4">
                <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                        ✅ القائمة النشطة الحالية: {currentList.ListName}
                    </h4>
                    <button
                        onClick={() => {
                            setSelectedListForSending(currentList);
                            fetchDelegatesFromList(currentList.ListID);
                        }}
                        className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                            selectedListForSending?.ListID === currentList.ListID
                                ? 'bg-green-600 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {selectedListForSending?.ListID === currentList.ListID 
                            ? '✅ محددة' 
                            : '📤 استخدم هذه القائمة'
                        }
                    </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="bg-white rounded-lg p-2 text-center">
                        <div className="font-bold text-blue-600">{selectedPassports.length}</div>
                        <div className="text-gray-600">إجمالي العملاء</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                        <div className="font-bold text-green-600">
                            {selectedPassports.filter(p => p.confirmed).length}
                        </div>
                        <div className="text-gray-600">مؤكدين</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                        <div className="font-bold text-yellow-600">
                            {selectedPassports.filter(p => !p.confirmed).length}
                        </div>
                        <div className="text-gray-600">في الانتظار</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                        <div className="font-bold text-purple-600">
                            {companies.find(c => c.CompanyID == currentList.CompanyID)?.CompanyName || 'غير محدد'}
                        </div>
                        <div className="text-gray-600">الشركة</div>
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-800">
                <AlertTriangle size={20} />
                <span className="font-medium">لا توجد قائمة نشطة حالياً</span>
            </div>
            <p className="text-yellow-700 text-sm mt-1">
                يجب تحميل قائمة أولاً من تبويب "القوائم المحفوظة" أو إنشاء قائمة جديدة
            </p>
        </div>
    )}

    {/* عرض القوائم الأخرى النشطة فقط (غير المؤرشفة) */}
    {(() => {
        // فلترة القوائم النشطة فقط (غير المؤرشفة)
        const activeLists = savedLists.filter(list => !archivedLists.has(list.ListID));
        
        // إخفاء القائمة الحالية من القائمة إذا كانت موجودة
        const otherActiveLists = activeLists.filter(list => 
            !currentList || list.ListID !== currentList.ListID
        );

        if (otherActiveLists.length === 0) {
            return (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {currentList ? 'لا توجد قوائم نشطة أخرى' : 'لا توجد قوائم نشطة'}
                    </h3>
                    <p className="text-gray-600">
                        {currentList 
                            ? 'يمكنك استخدام القائمة النشطة الحالية أعلاه'
                            : 'يجب حفظ قائمة أولاً لإرسالها للمناديب'
                        }
                    </p>
                    {!currentList && (
                        <button
                            onClick={() => setActiveTab('selection')}
                            className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                        >
                            إنشاء قائمة جديدة
                        </button>
                    )}
                </div>
            );
        }

        return (
            <div>
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    📋 القوائم النشطة الأخرى ({otherActiveLists.length}):
                    <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        المؤرشفة مخفية
                    </span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {otherActiveLists.map(list => (
                        <div 
                            key={list.ListID} 
                            className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                                selectedListForSending?.ListID === list.ListID 
                                    ? 'border-blue-500 bg-blue-50 shadow-md' 
                                    : 'border-gray-200 hover:border-blue-300'
                            }`}
                            onClick={() => {
                                setSelectedListForSending(list);
                                fetchDelegatesFromList(list.ListID);
                            }}
                        >
                            <div className="flex justify-between items-start mb-3">
                                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                    📋 {list.ListName}
                                    {selectedListForSending?.ListID === list.ListID && (
                                        <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs">
                                            ✓ مختارة
                                        </span>
                                    )}
                                </h4>
                            </div>
                            
                            <div className="space-y-1 text-sm text-gray-600">
                                <p><strong>الشركة:</strong> {list.CompanyName}</p>
                                <p><strong>إجمالي العملاء:</strong> {list.PassportCount} شخص</p>
                                <p><strong>المؤكدين:</strong> 
                                    <span className="text-green-600 font-medium ml-1">
                                        {list.ConfirmedCount}
                                    </span>
                                </p>
                                <p><strong>في الانتظار:</strong> 
                                    <span className="text-yellow-600 font-medium ml-1">
                                        {list.PassportCount - list.ConfirmedCount}
                                    </span>
                                </p>
                                <p><strong>تاريخ السفر:</strong> {new Date(list.SelectionDate).toLocaleDateString('ar-EG')}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    })()}
</div>

        {/* الخطوة 2: إعدادات الموعد والرسالة */}
        {selectedListForSending && (
            <div className="mb-8 border-t pt-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <h3 className="text-lg font-semibold text-gray-800">حدد موعد الحضور والرسالة:</h3>
                </div>
                
                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                📅 تاريخ الحضور المطلوب *
                            </label>
                            <input
                                type="date"
                                value={appointmentSettings.date}
                                onChange={(e) => setAppointmentSettings(prev => ({ 
                                    ...prev, 
                                    date: e.target.value 
                                }))}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500"
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                🕐 وقت الحضور (اختياري)
                            </label>
                            <input
                                type="time"
                                value={appointmentSettings.time}
                                onChange={(e) => setAppointmentSettings(prev => ({ 
                                    ...prev, 
                                    time: e.target.value 
                                }))}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500"
                            />
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            📝 ملاحظات إضافية للمندوب (اختياري)
                        </label>
                        <textarea
                            value={appointmentSettings.notes}
                            onChange={(e) => setAppointmentSettings(prev => ({ 
                                ...prev, 
                                notes: e.target.value 
                            }))}
                            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500"
                            placeholder="مثال: يرجى إحضار الأوراق التالية معهم..."
                            rows="3"
                        />
                    </div>
                    
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            🎯 نوع الموعد
                        </label>
                        <select
                            value={appointmentSettings.type}
                            onChange={(e) => setAppointmentSettings(prev => ({ 
                                ...prev, 
                                type: e.target.value 
                            }))}
                            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500"
                        >
                            <option value="medical">الكشف الطبي</option>
                            <option value="net">عمل النت</option>
                            <option value="both">الكشف الطبي + النت</option>
                            <option value="papers">مراجعة الأوراق</option>
                            <option value="custom">مخصص</option>
                        </select>
                    </div>
                </div>
            </div>
        )}

        {/* الخطوة 3: فلترة العملاء وعرض المناديب */}
        {selectedListForSending && delegatesInSelectedList.length > 0 && (
            <div className="border-t pt-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <h3 className="text-lg font-semibold text-gray-800">اختر المناديب والعملاء المراد إرسالهم:</h3>
                </div>
                
                {/* فلترة العملاء */}
                <div className="bg-purple-50 rounded-xl p-4 mb-6 border border-purple-200">
                    <h4 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
                        <Filter size={18} />
                        فلترة العملاء:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <button
                            onClick={() => setClientFilter('all')}
                            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                                clientFilter === 'all'
                                    ? 'border-purple-500 bg-purple-100 text-purple-800'
                                    : 'border-gray-200 hover:border-purple-300'
                            }`}
                        >
                            <div className="font-medium">👥 جميع العملاء</div>
                            <div className="text-sm text-gray-600 mt-1">
                                المؤكدين + في الانتظار ({delegatesInSelectedList.reduce((sum, d) => sum + d.totalCount, 0)})
                            </div>
                        </button>
                        
                        <button
                            onClick={() => setClientFilter('confirmed')}
                            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                                clientFilter === 'confirmed'
                                    ? 'border-green-500 bg-green-100 text-green-800'
                                    : 'border-gray-200 hover:border-green-300'
                            }`}
                        >
                            <div className="font-medium">✅ المؤكدين فقط</div>
                            <div className="text-sm text-gray-600 mt-1">
                                الذين حضروا ({delegatesInSelectedList.reduce((sum, d) => sum + d.confirmedCount, 0)})
                            </div>
                        </button>
                        
                        <button
                            onClick={() => setClientFilter('pending')}
                            className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                                clientFilter === 'pending'
                                    ? 'border-yellow-500 bg-yellow-100 text-yellow-800'
                                    : 'border-gray-200 hover:border-yellow-300'
                            }`}
                        >
                            <div className="font-medium">⏳ في الانتظار فقط</div>
                            <div className="text-sm text-gray-600 mt-1">
                                لم يحضروا بعد ({delegatesInSelectedList.reduce((sum, d) => sum + d.pendingCount, 0)})
                            </div>
                        </button>
                    </div>
                </div>

                {/* قائمة المناديب مع الأزرار المحسنة */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {delegatesInSelectedList.map((delegateGroup, index) => {
                        const filteredPassports = getFilteredPassportsForDelegate(delegateGroup.passports);
                        
                        if (filteredPassports.length === 0) return null;
                        
                        return (
                            <div key={delegateGroup.delegateInfo?.DelegateID || index} 
                                 className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                                
                                {/* معلومات المندوب */}
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                                            👤 {delegateGroup.delegateInfo?.DelegateName || 'مندوب غير معروف'}
                                        </h4>
                                        <div className="text-sm text-gray-500 mt-1">
                                            ID: {delegateGroup.delegateInfo?.DelegateID || 'غير محدد'}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* إحصائيات محدثة حسب الفلتر */}
                                <div className="grid grid-cols-3 gap-3 mb-4">
                                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                                        <div className="text-xl font-bold text-blue-600">{filteredPassports.length}</div>
                                        <div className="text-xs text-gray-600">سيتم إرسالهم</div>
                                    </div>
                                    <div className="text-center p-3 bg-green-50 rounded-lg">
                                        <div className="text-xl font-bold text-green-600">
                                            {filteredPassports.filter(p => p.confirmed).length}
                                        </div>
                                        <div className="text-xs text-gray-600">مؤكد</div>
                                    </div>
                                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                                        <div className="text-xl font-bold text-yellow-600">
                                            {filteredPassports.filter(p => !p.confirmed).length}
                                        </div>
                                        <div className="text-xs text-gray-600">انتظار</div>
                                    </div>
                                </div>
                                
                                {/* معاينة سريعة للأسماء */}
                                <div className="mb-4">
                                    <h5 className="font-medium text-gray-700 mb-2">العملاء المفلترين:</h5>
                                    <div className="max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-3">
                                        {filteredPassports.slice(0, 5).map((passport, idx) => (
                                            <div key={idx} className="flex justify-between items-center py-1 text-sm">
                                                <span className="font-medium">{passport.NameArabic}</span>
                                                <span className={`px-2 py-1 rounded text-xs ${
                                                    passport.confirmed 
                                                        ? 'bg-green-100 text-green-700' 
                                                        : 'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                    {passport.confirmed ? '✅' : '⏳'}
                                                </span>
                                            </div>
                                        ))}
                                        {filteredPassports.length > 5 && (
                                            <div className="text-center text-gray-500 text-xs mt-2">
                                                و {filteredPassports.length - 5} آخرين...
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {/* أزرار الإجراءات المحسنة */}
                                <div className="space-y-2">
                                    {/* الصف الأول: المعاينة والطباعة */}
                                    <div className="grid grid-cols-3 gap-2">
                                        <button
                                            onClick={() => showDelegatePreview(delegateGroup, filteredPassports)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                                        >
                                            <Eye size={16} />
                                            معاينة
                                        </button>
                                        <button
                                            onClick={() => printDelegateList(delegateGroup, filteredPassports, selectedListForSending)}
                                            className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                                        >
                                            <Printer size={16} />
                                            طباعة
                                        </button>
                                   
                                    </div>
                                    
                                    {/* الصف الثاني: مشاركة الصورة */}
                                    <button
                                        onClick={() => generateImageForWhatsApp(delegateGroup, filteredPassports, selectedListForSending)}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-medium"
                                    >
                                        <Camera size={18} />
                                        📱 إنشاء صورة للواتساب (معاينة + نسخ)
                                    </button>
                                </div>
                            </div>
                        );
                    }).filter(Boolean)}
                </div>

                {/* إجراءات شاملة */}
                <div className="mt-8 bg-gray-50 rounded-xl p-6">
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Download size={20} />
                        إجراءات شاملة لجميع المناديب:
                    </h4>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <button
                            onClick={() => printCompleteFilteredList()}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 text-sm"
                        >
                            <Printer size={16} />
                            طباعة الكل
                        </button>
                        <button
                            onClick={() => saveCompleteFilteredListAsImage()}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 text-sm"
                        >
                            <Camera size={16} />
                            صورة شاملة
                        </button>
                        <button
                            onClick={() => generateAllDelegatesImages()}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 text-sm"
                        >
                            <FileText size={16} />
                            كل المناديب PDF
                        </button>
                        <button
                            onClick={showFilteredSummary}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 text-sm"
                        >
                            <Info size={16} />
                            ملخص تفصيلي
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
)}



{/* نافذة معاينة الصورة للواتساب */}
{showImagePreview && previewImageData && (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-green-600 text-white p-4 flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold">📱 معاينة صورة الواتساب</h3>
                    <p className="text-green-100">المندوب: {previewImageData.delegateName} - {previewImageData.clientCount} عميل</p>
                </div>
                <button
                    onClick={() => {
                        setShowImagePreview(false);
                        URL.revokeObjectURL(previewImageData.url);
                        setPreviewImageData(null);
                    }}
                    className="text-white hover:bg-green-700 p-2 rounded-full"
                >
                    <X size={24} />
                </button>
            </div>
            
            <div className="p-4">
                <div className="mb-4 max-h-96 overflow-auto border border-gray-300 rounded-lg">
                    <img 
                        src={previewImageData.url} 
                        alt="معاينة قائمة العملاء"
                        className="w-full h-auto"
                    />
                </div>
                
                <div className="flex gap-3">
                    <button
                        onClick={async () => {
                            try {
                                if (navigator.clipboard && navigator.clipboard.write) {
                                    const item = new ClipboardItem({ 'image/png': previewImageData.blob });
                                    await navigator.clipboard.write([item]);
                                    showAlert('✅ تم نسخ الصورة! يمكنك الآن لصقها في الواتساب', 'success');
                                } else {
                                    throw new Error('المتصفح لا يدعم نسخ الصور');
                                }
                            } catch (error) {
                                console.error('خطأ في نسخ الصورة:', error);
                                showAlert('تعذر نسخ الصورة تلقائياً. جرب النسخ اليدوي بالضغط طويل على الصورة', 'warning');
                            }
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 font-medium"
                    >
                        📋 نسخ للواتساب
                    </button>
                    
                    <button
                        onClick={() => {
                            const link = document.createElement('a');
                            link.href = previewImageData.url;
                            link.download = `قائمة_${previewImageData.delegateName}_${new Date().toISOString().split('T')[0]}.png`;
                            link.click();
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 font-medium"
                    >
                        💾 تحميل الصورة
                    </button>
                    
                    <button
                        onClick={() => {
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(`
                                <html>
                                    <head><title>طباعة قائمة ${previewImageData.delegateName}</title></head>
                                    <body style="margin: 0; padding: 20px;">
                                        <img src="${previewImageData.url}" style="width: 100%; height: auto;" />
                                    </body>
                                </html>
                            `);
                            printWindow.document.close();
                            printWindow.onload = () => {
                                printWindow.print();
                                printWindow.close();
                            };
                        }}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                    >
                        <Printer size={18} />
                    </button>
                </div>
                
                <div className="mt-3 p-3 bg-green-50 rounded-lg text-sm text-green-800">
                    💡 <strong>نصيحة:</strong> اضغط "نسخ للواتساب" ثم اذهب لجروب الواتساب والصق الصورة مباشرة (Ctrl+V)
                </div>
            </div>
        </div>
    </div>
)}

<PaymentPrintModal />

{/* ==================== نافذة كشف الأرصدة الشامل ==================== */}
{balanceReportModal.isOpen && (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">

            {/* Header */}
            <div className="bg-indigo-700 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
                <div>
                    <h3 className="text-xl font-bold flex items-center gap-2">
                        <FileText size={22} />
                        كشف الأرصدة الشامل
                    </h3>
                    {!balanceReportLoading && balanceReportData.length > 0 && (
                        <p className="text-indigo-200 text-sm mt-0.5">
                            الحد الأدنى: {minBalanceAmount.toLocaleString()} ج.م &nbsp;|&nbsp;
                            إيجابي: <span className="text-green-300 font-bold">{balanceReportData.filter(r => r.isPositive).length}</span> &nbsp;|&nbsp;
                            سلبي: <span className="text-red-300 font-bold">{balanceReportData.filter(r => !r.isPositive).length}</span>
                        </p>
                    )}
                </div>
                <button
                    onClick={() => setBalanceReportModal({ isOpen: false })}
                    className="hover:bg-indigo-800/50 rounded-full p-2 transition"
                >
                    <X size={22} />
                </button>
            </div>

            {/* Loading */}
            {balanceReportLoading ? (
                <div className="flex-1 flex flex-col items-center justify-center py-16 gap-6">
                    <div className="relative">
                        <div className="w-20 h-20 rounded-full border-4 border-indigo-100"></div>
                        <div className="w-20 h-20 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin absolute inset-0"></div>
                    </div>
                    <div className="text-center">
                        <p className="text-gray-700 font-semibold text-lg mb-1">جاري جلب الأرصدة...</p>
                        <p className="text-gray-500">{balanceReportProgress.current} / {balanceReportProgress.total}</p>
                        <div className="mt-3 w-64 bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${balanceReportProgress.total ? (balanceReportProgress.current / balanceReportProgress.total) * 100 : 0}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            ) : balanceReportData.length > 0 ? (
                <>
                    {/* أدوات التحكم */}
                    <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between gap-4 flex-wrap flex-shrink-0">
                        {/* فلتر العرض */}
                        <div className="flex gap-2">
                            {[
                                { key: 'all',      label: `الكل (${balanceReportData.length})`,                        color: 'indigo' },
                                { key: 'positive', label: `✅ إيجابي (${balanceReportData.filter(r=>r.isPositive).length})`, color: 'green'  },
                                { key: 'negative', label: `❌ سلبي (${balanceReportData.filter(r=>!r.isPositive).length})`,  color: 'red'    },
                            ].map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => setBalanceReportFilter(f.key)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                                        balanceReportFilter === f.key
                                            ? f.color === 'indigo' ? 'bg-indigo-600 text-white border-indigo-600'
                                            : f.color === 'green'  ? 'bg-green-600 text-white border-green-600'
                                            : 'bg-red-600 text-white border-red-600'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>

                        {/* أزرار الطباعة */}
                        <div className="flex gap-2">
                            {/* طباعة المعروض حالياً */}
                            <button
                                onClick={() => printBalanceReport(balanceReportFilter, balanceReportData, minBalanceAmount, delegates, professions, companies, currentList)}
                                className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                            >
                                <Printer size={16} />
                                طباعة {balanceReportFilter === 'all' ? 'الكل' : balanceReportFilter === 'positive' ? 'الإيجابيين' : 'السلبيين'}
                            </button>
                            {/* طباعة الإيجابيين فقط */}
                            <button
                                onClick={() => printBalanceReport('positive', balanceReportData, minBalanceAmount, delegates, professions, companies, currentList)}
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                            >
                                <Printer size={16} />
                                ✅ إيجابيين فقط
                            </button>
                            {/* طباعة السلبيين فقط */}
                            <button
                                onClick={() => printBalanceReport('negative', balanceReportData, minBalanceAmount, delegates, professions, companies, currentList)}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                            >
                                <Printer size={16} />
                                ❌ سلبيين فقط
                            </button>
                        </div>
                    </div>

                    {/* الجدول */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        {(() => {
                            const filtered = balanceReportData.filter(r =>
                                balanceReportFilter === 'all' ? true :
                                balanceReportFilter === 'positive' ? r.isPositive : !r.isPositive
                            );

                            if (filtered.length === 0) return (
                                <div className="text-center py-12 text-gray-400">
                                    <FileText size={48} className="mx-auto mb-3 opacity-40" />
                                    <p>لا توجد نتائج لهذا الفلتر</p>
                                </div>
                            );

                            return (
                                <table className="w-full text-sm border-collapse">
                                    <thead className="sticky top-0 z-10">
                                        <tr className="bg-indigo-50 text-indigo-900">
                                            <th className="px-3 py-2 text-right font-semibold border border-indigo-200">م</th>
                                            <th className="px-3 py-2 text-right font-semibold border border-indigo-200">الاسم</th>
                                            <th className="px-3 py-2 text-right font-semibold border border-indigo-200">الشركة</th>
                                            <th className="px-3 py-2 text-right font-semibold border border-indigo-200">المهنة</th>
                                            <th className="px-3 py-2 text-right font-semibold border border-indigo-200">الهاتف</th>
                                            <th className="px-3 py-2 text-right font-semibold border border-indigo-200">المندوب</th>
                                            <th className="px-3 py-2 text-right font-semibold border border-indigo-200">الحالة</th>
                                            {canViewBalanceInReport && <th className="px-3 py-2 text-right font-semibold border border-indigo-200">الرصيد</th>}
                                            <th className="px-3 py-2 text-right font-semibold border border-indigo-200">حالة الرصيد</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((row, idx) => {
                                            const d = delegates.find(x => parseInt(x.DelegateID) === parseInt(row.passport.DelegateID));
                                            const p = professions.find(x => parseInt(x.ProfessionID) === parseInt(row.passport.PassportProfessionID));
                                            const c = companies.find(x => parseInt(x.CompanyID) === parseInt(row.passport.CompanyID));
                                            const phone = getPhoneNumber(row.passport.PhoneNumbers);
                                            return (
                                                <tr key={row.passport.PassportID} className={`border-b ${row.isPositive ? 'hover:bg-green-50' : 'hover:bg-red-50'} ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                                    <td className="px-3 py-2 border border-gray-200 text-center text-gray-500">{idx + 1}</td>
                                                    <td className="px-3 py-2 border border-gray-200 font-medium text-gray-900">{row.passport.NameArabic}</td>
                                                    <td className="px-3 py-2 border border-gray-200 text-gray-700">{c?.CompanyName || '—'}</td>
                                                    <td className="px-3 py-2 border border-gray-200 text-gray-700">{p?.ProfessionName || '—'}</td>
                                                    <td className="px-3 py-2 border border-gray-200 text-gray-700 text-xs" dir="ltr">{phone || '—'}</td>
                                                    <td className="px-3 py-2 border border-gray-200 text-gray-700">{d?.DelegateName || '—'}</td>
                                                    <td className="px-3 py-2 border border-gray-200 text-center">
                                                        {row.passport.status === 'confirmed' ? (
                                                            <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">مؤكد</span>
                                                        ) : row.passport.status === 'pending' ? (
                                                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs">منتظر</span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">{row.passport.status || '—'}</span>
                                                        )}
                                                    </td>
                                                    {canViewBalanceInReport && (
                                                    <td className={`px-3 py-2 border border-gray-200 font-bold text-center ${row.balance >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                                                        {row.error ? <span className="text-red-400 text-xs font-normal">{row.error}</span> : `${row.balance.toLocaleString()} ج`}
                                                    </td>
                                                    )}
                                                    <td className="px-3 py-2 border border-gray-200 text-center">
                                                        {row.isPositive ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">✅ إيجابي</span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold">❌ سلبي</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            );
                        })()}
                    </div>

                    {/* ملخص سفلي */}
                    <div className="px-6 py-3 bg-gray-50 border-t rounded-b-2xl flex gap-6 text-sm flex-shrink-0">
                        <span className="text-gray-600">إجمالي: <strong>{balanceReportData.length}</strong></span>
                        <span className="text-green-700">✅ إيجابي: <strong>{balanceReportData.filter(r=>r.isPositive).length}</strong></span>
                        <span className="text-red-700">❌ سلبي: <strong>{balanceReportData.filter(r=>!r.isPositive).length}</strong></span>
                        {canViewTotalBalances && <span className="text-blue-700">💰 إجمالي الأرصدة: <strong>{balanceReportData.reduce((s,r)=>s+r.balance,0).toLocaleString()} ج.م</strong></span>}
                    </div>
                </>
            ) : null}
        </div>
    </div>
)}

{/* ==================== نافذة فحص رصيد العميل ==================== */}
{balanceCheckModal.isOpen && (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className={`px-6 py-4 rounded-t-2xl flex items-center justify-between text-white ${
                balanceCheckModal.isLoading ? 'bg-gray-500' :
                balanceCheckModal.balanceData?.error ? 'bg-red-600' :
                (balanceCheckModal.balanceData?.CurrentBalance ?? 0) >= minBalanceAmount ? 'bg-green-600' : 'bg-red-600'
            }`}>
                <div>
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        💳 حالة رصيد العميل
                    </h3>
                    <p className="text-sm opacity-90">{balanceCheckModal.passport?.NameArabic}</p>
                </div>
                <button
                    onClick={() => setBalanceCheckModal({ isOpen: false, passport: null, balanceData: null, isLoading: false })}
                    className="hover:bg-white/20 rounded-full p-1.5 transition"
                >
                    <X size={20} />
                </button>
            </div>

            <div className="p-6">
                {balanceCheckModal.isLoading ? (
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent mx-auto mb-4"></div>
                        <p className="text-gray-600">جاري فحص الرصيد...</p>
                    </div>
                ) : balanceCheckModal.balanceData?.error ? (
                    <div className="text-center py-6">
                        <div className="text-5xl mb-3">⚠️</div>
                        <p className="text-red-600 font-medium">تعذر جلب البيانات</p>
                        <p className="text-gray-500 text-sm mt-1">{balanceCheckModal.balanceData.error}</p>
                        <p className="text-gray-400 text-xs mt-2">قد لا يكون للعميل حساب في نظام الحسابات بعد</p>
                    </div>
                ) : balanceCheckModal.balanceData ? (() => {
                    const balance = balanceCheckModal.balanceData.CurrentBalance ?? 0;
                    const isPositive = balance >= minBalanceAmount;
                    return (
                        <div className="space-y-4">
                            {/* نتيجة الفحص الكبيرة */}
                            <div className={`rounded-xl p-5 text-center ${isPositive ? 'bg-green-50 border-2 border-green-300' : 'bg-red-50 border-2 border-red-300'}`}>
                                <div className="text-5xl mb-2">{isPositive ? '✅' : '❌'}</div>
                                <div className={`text-2xl font-bold ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                                    {isPositive ? 'رصيد إيجابي' : 'رصيد سلبي / غير كافٍ'}
                                </div>
                                <div className={`text-sm mt-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                    {isPositive ? `الرصيد يتجاوز الحد الأدنى المطلوب (${minBalanceAmount.toLocaleString()} ج.م)` : `الرصيد أقل من الحد الأدنى المطلوب (${minBalanceAmount.toLocaleString()} ج.م)`}
                                </div>
                            </div>

                            {/* تفاصيل الأرقام */}
                            <div className="grid grid-cols-2 gap-3">
                                {canViewCurrentBalance && (
                                <div className="bg-gray-50 rounded-lg p-3 text-center">
                                    <div className={`text-xl font-bold ${balance >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                                        {balance.toLocaleString()} ج.م
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">الرصيد الحالي</div>
                                </div>
                                )}
                                <div className="bg-gray-50 rounded-lg p-3 text-center">
                                    <div className="text-xl font-bold text-teal-700">
                                        {minBalanceAmount.toLocaleString()} ج.م
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">الحد الأدنى</div>
                                </div>
                                {canViewTotalDeposits && (
                                <div className="bg-green-50 rounded-lg p-3 text-center">
                                    <div className="text-lg font-bold text-green-700">
                                        {(balanceCheckModal.balanceData.TotalDeposits ?? 0).toLocaleString()} ج.م
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">إجمالي الإيداعات</div>
                                </div>
                                )}
                                {canViewTotalWithdrawals && (
                                <div className="bg-red-50 rounded-lg p-3 text-center">
                                    <div className="text-lg font-bold text-red-700">
                                        {(balanceCheckModal.balanceData.TotalWithdrawals ?? 0).toLocaleString()} ج.م
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">إجمالي السحوبات</div>
                                </div>
                                )}
                            </div>

                            {/* الفرق */}
                            {!isPositive && (
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                                    <div className="text-orange-700 font-medium text-sm">
                                        المبلغ المطلوب إضافته: <span className="font-bold text-base">{(minBalanceAmount - balance).toLocaleString()} ج.م</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })() : null}

                {/* أزرار التحكم */}
                <div className="flex gap-3 mt-5">
                    <button
                        onClick={() => setBalanceCheckModal({ isOpen: false, passport: null, balanceData: null, isLoading: false })}
                        className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 font-medium"
                    >
                        إغلاق
                    </button>
                    {!balanceCheckModal.isLoading && (
                        <button
                            onClick={() => checkClientBalance(balanceCheckModal.passport)}
                            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                        >
                            <RefreshCw size={16} />
                            إعادة الفحص
                        </button>
                    )}
                </div>
            </div>
        </div>
    </div>
)}

{/* ==================== نافذة ضبط الحد الأدنى للرصيد ==================== */}
{showMinBalanceSettingModal && (
    <MinBalanceSettingModal
        currentAmount={minBalanceAmount}
        onSave={saveMinBalanceAmount}
        onClose={() => setShowMinBalanceSettingModal(false)}
    />
)}






{/* ==================== نافذة إعدادات الطباعة ==================== */}
<PrintSettingsModal
    isOpen={showPrintSettingsModal}
    onClose={() => setShowPrintSettingsModal(false)}
    settings={printSettings}
    onSave={savePrintSettings}
/>

{/* ==================== نافذة ضبط معايير الاختيار ==================== */}
<SelectionRulesModal
    isOpen={showSelectionRulesModal}
    onClose={() => setShowSelectionRulesModal(false)}
    rules={selectionRules}
    onSave={saveSelectionRules}
    allowProcessedManualSelection={allowProcessedManualSelection}
/>

{/* نافذة تعديل القائمة */}
<EditListModal
    isOpen={showEditListModal}
    onClose={() => {
        setShowEditListModal(false);
        setEditingList(null);
    }}
    list={editingList}
    onSave={(data) => editSavedList(editingList.ListID, data)}
    isLoading={isLoading}
/>

       </div>
   );
};

export default NetRegistration;